import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, Component, Fragment } from "react";
// I testi legali stanno in un file di dati: li legge l'app e li legge lo script
// che, al build, scrive /privacy /terms /cookies già renderizzati (#69).
import LEGAL from "./legal/sections.json";
// Phase 4 W1.T2 — routing imports. HashRedirect handles legacy v1.0 share URLs
// (/#hash → /app#hash per D-07 + Pitfall #2). On the web `/` is the store page
// (#74); the legal pages are reached via AppShell with initialScreen.
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import StoreLanding from "./pages/StoreLanding";
import SEO from "./components/SEO";
import Coachmarks from "./components/Coachmarks";
import WelcomeIntro from "./components/WelcomeIntro";
import {
  createPuzzle as fbCreatePuzzle,
  fetchPuzzle as fbFetchPuzzle,
  checkPassword as fbCheckPassword,
  checkAnswer as fbCheckAnswer,
  isSealed,
  generatePuzzleId,
  extractPuzzleId,
  deletePuzzle,
  saveDraft,
  fetchDraft,
  deleteDraft,
  updatePlayerProgress,
} from "./services/puzzleService";
import { uploadPuzzleMedia, isStorageUrl } from "./services/mediaService";
import { isCreatorV2Enabled, draftResumeScreen } from "./config/flags";
import CreatorFlow from "./features/creator/CreatorFlow";
import HomeScreen from "./features/home/HomeScreen";
import logger from "./services/logger";
import { isUpdateAvailable } from "./services/updateCheck";

// Screens where reloading onto a newer build loses nothing (see updateReady).
const SAFE_RELOAD_SCREENS = new Set(["splash", "role", "theme", "history"]);
import { useTranslation } from "react-i18next";
import i18n, { useThemeMicrocopy, SUPPORTED_LANGS, LANG_LABELS } from "./i18n";
import { isSeasonalTheme } from "./features/themes/seasons";
import { useActiveSeason } from "./features/themes/useActiveSeason";
import { applySeasonFavicon, watchNativeIcon } from "./features/themes/seasonIcon";
import { withExternalFlow, trackFilePickers, externalFlowActive } from "./features/themes/externalFlow";
import { paletteVars } from "./features/creator/chassis/palettes";
import { loadPuzzleHistory, savePuzzleHistory, coachmarksSeen, markCoachmarksSeen, introSeen, markIntroSeen } from "./services/storage";
import {
  bootstrapAnonymous,
  subscribeToAuthState,
  ensureUserProfile,
  completeMagicLinkIfPresent,
  loadUserPuzzleHistory,
  addPuzzleToUserHistory,
  signOutUser,
  signInWithGoogle,
  signInWithApple,
  appleSignInAvailable,
  linkAppleToCurrentUser,
  APPLE_EMAIL_IN_USE,
} from "./services/authService";
import { doc, onSnapshot } from "firebase/firestore";
import { auth, db } from "./services/firebase";
import SignUpPrompt from "./components/SignUpPrompt";
import SignInModal from "./components/SignInModal";
import ProAttivazioneModal from "./components/ProAttivazioneModal";
import EmailReentryModal from "./components/EmailReentryModal";
import AccountMenu from "./components/AccountMenu";
// Phase 2 / W3.T6 — paywall + cap UX components and cap predicate.
// PaywallModal / CapBlockerModal / CapUsageIndicator are mounted at the App
// level (modals) or inside the two creator flows (indicator). canCreatePuzzle
// is the client-side predicate that gates the publish handlers; the Rules
// remain the authoritative gate (locked decision #1, two-layer defence).
import PaywallModal from "./components/PaywallModal";
import CapBlockerModal from "./components/CapBlockerModal";
import CapUsageIndicator from "./components/CapUsageIndicator";
import { canCreatePuzzle } from "./services/capService";
import { activeLimit } from "./config/plans";
import {
  isSignUpPromptEligible,
  markSignUpPromptShown,
  clearSignUpPromptShown,
} from "./services/signupTriggers";
import { getBilling } from "./services/billing";
// Phase 3 Wave 4 — GDPR right-to-erasure cascade (LEGAL-07).
import { deleteMyAccount } from "./services/accountService";
import DeleteAccountModal from "./components/DeleteAccountModal";
// #65 — segnalare e bloccare, e le regole sui contenuti accettate prima di creare.
import ReportSheet from "./components/ReportSheet";
import ContentRulesModal from "./components/ContentRulesModal";
import ModerationNotice from "./components/ModerationNotice";
import { reportPuzzle, blockOwner, isBlocked } from "./services/reportService";
import { registraRichiestaConsenso, isRulesDeclined } from "./services/contentRules";
import { isNativeApp } from "./services/platform";
import { condividi, consigliaPuzzly } from "./services/condivisione";
import { segnaCondivisione, chiediRecensione, apriSchedaStore } from "./services/recensione";
import { shouldOfferPush, markPushOffered, segnaPropostaPush, propostaPushAperta, enablePush, registerPushToken, unregisterPushToken, listenToPush } from "./services/pushService";
import PushOfferModal from "./components/PushOfferModal";
import ReactionViewer from "./components/ReactionViewer";
import PushBanner from "./components/PushBanner";
import { safeEmbedUrl, safeMediaUrl } from "./utils/safeMedia";
import { App as CapApp } from "@capacitor/app";

// Quanto si aspetta il Pro dal webhook dopo la conferma dello store, prima di
// dire «ci mette più del solito» (ProAttivazioneModal).
const ATTESA_PRO_MS = 45000;

/* ═══════════════════════════════════════════════════════
   PUZZLY — A Shareable Quiz-Puzzle for Someone Special
   ═══════════════════════════════════════════════════════ */

// ── LZ-String compression (minimal implementation) ──
const LZ = (() => {
  const f = String.fromCharCode;
  const keyStrBase64 = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=";
  const baseReverseDic = {};

  function getBaseValue(alphabet, character) {
    if (!baseReverseDic[alphabet]) {
      baseReverseDic[alphabet] = {};
      for (let i = 0; i < alphabet.length; i++) {
        baseReverseDic[alphabet][alphabet.charAt(i)] = i;
      }
    }
    return baseReverseDic[alphabet][character];
  }

  function compressToBase64(input) {
    if (input == null) return "";
    let res = _compress(input, 6, (a) => keyStrBase64.charAt(a));
    switch (res.length % 4) {
      case 0: return res;
      case 1: return res + "===";
      case 2: return res + "==";
      case 3: return res + "=";
    }
  }

  function decompressFromBase64(input) {
    if (input == null || input === "") return null;
    return _decompress(input.length, 32, (index) => getBaseValue(keyStrBase64, input.charAt(index)));
  }

  function _compress(uncompressed, bitsPerChar, getCharFromInt) {
    if (uncompressed == null) return "";
    let i, value,
      context_dictionary = {},
      context_dictionaryToCreate = {},
      context_c = "",
      context_wc = "",
      context_w = "",
      context_enlargeIn = 2,
      context_dictSize = 3,
      context_numBits = 2,
      context_data = [],
      context_data_val = 0,
      context_data_position = 0;

    for (let ii = 0; ii < uncompressed.length; ii++) {
      context_c = uncompressed.charAt(ii);
      if (!Object.prototype.hasOwnProperty.call(context_dictionary, context_c)) {
        context_dictionary[context_c] = context_dictSize++;
        context_dictionaryToCreate[context_c] = true;
      }
      context_wc = context_w + context_c;
      if (Object.prototype.hasOwnProperty.call(context_dictionary, context_wc)) {
        context_w = context_wc;
      } else {
        if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
          if (context_w.charCodeAt(0) < 256) {
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1);
              if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; }
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 8; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; }
              value = value >> 1;
            }
          } else {
            value = 1;
            for (i = 0; i < context_numBits; i++) {
              context_data_val = (context_data_val << 1) | value;
              if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; }
              value = 0;
            }
            value = context_w.charCodeAt(0);
            for (i = 0; i < 16; i++) {
              context_data_val = (context_data_val << 1) | (value & 1);
              if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; }
              value = value >> 1;
            }
          }
          context_enlargeIn--;
          if (context_enlargeIn == 0) { context_enlargeIn = Math.pow(2, context_numBits); context_numBits++; }
          delete context_dictionaryToCreate[context_w];
        } else {
          value = context_dictionary[context_w];
          for (i = 0; i < context_numBits; i++) {
            context_data_val = (context_data_val << 1) | (value & 1);
            if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; }
            value = value >> 1;
          }
        }
        context_enlargeIn--;
        if (context_enlargeIn == 0) { context_enlargeIn = Math.pow(2, context_numBits); context_numBits++; }
        context_dictionary[context_wc] = context_dictSize++;
        context_w = String(context_c);
      }
    }
    if (context_w !== "") {
      if (Object.prototype.hasOwnProperty.call(context_dictionaryToCreate, context_w)) {
        if (context_w.charCodeAt(0) < 256) {
          for (i = 0; i < context_numBits; i++) { context_data_val = (context_data_val << 1); if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 8; i++) { context_data_val = (context_data_val << 1) | (value & 1); if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } value = value >> 1; }
        } else {
          value = 1;
          for (i = 0; i < context_numBits; i++) { context_data_val = (context_data_val << 1) | value; if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } value = 0; }
          value = context_w.charCodeAt(0);
          for (i = 0; i < 16; i++) { context_data_val = (context_data_val << 1) | (value & 1); if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } value = value >> 1; }
        }
        context_enlargeIn--;
        if (context_enlargeIn == 0) { context_enlargeIn = Math.pow(2, context_numBits); context_numBits++; }
        delete context_dictionaryToCreate[context_w];
      } else {
        value = context_dictionary[context_w];
        for (i = 0; i < context_numBits; i++) { context_data_val = (context_data_val << 1) | (value & 1); if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } value = value >> 1; }
      }
      context_enlargeIn--;
      if (context_enlargeIn == 0) { context_enlargeIn = Math.pow(2, context_numBits); context_numBits++; }
    }
    value = 2;
    for (i = 0; i < context_numBits; i++) { context_data_val = (context_data_val << 1) | (value & 1); if (context_data_position == bitsPerChar - 1) { context_data_position = 0; context_data.push(getCharFromInt(context_data_val)); context_data_val = 0; } else { context_data_position++; } value = value >> 1; }
    while (true) { context_data_val = (context_data_val << 1); if (context_data_position == bitsPerChar - 1) { context_data.push(getCharFromInt(context_data_val)); break; } else context_data_position++; }
    return context_data.join("");
  }

  function _decompress(length, resetValue, getNextValue) {
    let dictionary = [], next, enlargeIn = 4, dictSize = 4, numBits = 3, entry = "", result = [], i, w, bits, resb, maxpower, power, c,
      data = { val: getNextValue(0), position: resetValue, index: 1 };

    for (i = 0; i < 3; i++) dictionary[i] = i;

    bits = 0; maxpower = Math.pow(2, 2); power = 1;
    while (power != maxpower) {
      resb = data.val & data.position;
      data.position >>= 1;
      if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); }
      bits |= (resb > 0 ? 1 : 0) * power;
      power <<= 1;
    }

    switch (next = bits) {
      case 0: bits = 0; maxpower = Math.pow(2, 8); power = 1;
        while (power != maxpower) { resb = data.val & data.position; data.position >>= 1; if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); } bits |= (resb > 0 ? 1 : 0) * power; power <<= 1; }
        c = f(bits); break;
      case 1: bits = 0; maxpower = Math.pow(2, 16); power = 1;
        while (power != maxpower) { resb = data.val & data.position; data.position >>= 1; if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); } bits |= (resb > 0 ? 1 : 0) * power; power <<= 1; }
        c = f(bits); break;
      case 2: return "";
    }
    dictionary[3] = c; w = c; result.push(c);

    while (true) {
      if (data.index > length) return "";
      bits = 0; maxpower = Math.pow(2, numBits); power = 1;
      while (power != maxpower) { resb = data.val & data.position; data.position >>= 1; if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); } bits |= (resb > 0 ? 1 : 0) * power; power <<= 1; }

      switch (c = bits) {
        case 0: bits = 0; maxpower = Math.pow(2, 8); power = 1;
          while (power != maxpower) { resb = data.val & data.position; data.position >>= 1; if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); } bits |= (resb > 0 ? 1 : 0) * power; power <<= 1; }
          dictionary[dictSize++] = f(bits); c = dictSize - 1; enlargeIn--;
          break;
        case 1: bits = 0; maxpower = Math.pow(2, 16); power = 1;
          while (power != maxpower) { resb = data.val & data.position; data.position >>= 1; if (data.position == 0) { data.position = resetValue; data.val = getNextValue(data.index++); } bits |= (resb > 0 ? 1 : 0) * power; power <<= 1; }
          dictionary[dictSize++] = f(bits); c = dictSize - 1; enlargeIn--;
          break;
        case 2: return result.join("");
      }

      if (enlargeIn == 0) { enlargeIn = Math.pow(2, numBits); numBits++; }

      if (dictionary[c]) { entry = dictionary[c]; }
      else { if (c === dictSize) { entry = w + w.charAt(0); } else { return null; } }

      result.push(entry);
      dictionary[dictSize++] = w + entry.charAt(0);
      enlargeIn--;
      if (enlargeIn == 0) { enlargeIn = Math.pow(2, numBits); numBits++; }
      w = entry;
    }
  }

  return { compressToBase64, decompressFromBase64 };
})();

// ── Theme Definitions ──
const FONT_IMPORT_URL = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Be+Vietnam+Pro:wght@400;500;600;700&family=JetBrains+Mono:wght@400;700&display=swap";

// Phase 2 / W3.T1 — cross-theme premium gradient used by lock ribbons
// (ThemeSelect, AccountMenu "Upgrade to Pro" badge). Fixed so the chip stays
// legible on both light (default) and dark (gaming/spooky) backgrounds
// (Pitfall #10 resolution).
export const LOCK_CHIP_GRADIENT = "linear-gradient(135deg, #a8275a, #ff709f)";

// ADMIN email allowlist — these accounts auto-unlock all themes / Pro UI for
// testing. View-unlock only (never writes Firestore entitlements), but the
// create now actually succeeds: firestore.rules carries the same allowlist in
// isAdmin() and waives the theme gate + slot cap for it. Keep the two lists in
// sync — tests/rules/admin.rules.test.js covers the Rules side.
const ADMIN_EMAILS = ["erisa@giadalabs.com", "admin@giadalabs.com"];

// 24-pointed star clip-path for Funny theme "burst stickers" (POW! WHAT?! etc).
// Sourced from designer themes/funny.html .burst-sm.
const BURST_CLIP =
  "polygon(50% 0%, 61% 14%, 78% 8%, 78% 25%, 95% 24%, 88% 40%, 100% 50%, 88% 60%, 95% 76%, 78% 75%, 78% 92%, 61% 86%, 50% 100%, 39% 86%, 22% 92%, 22% 75%, 5% 76%, 12% 60%, 0% 50%, 12% 40%, 5% 24%, 22% 25%, 22% 8%, 39% 14%)";

// AmbientBlob — organic morphing ambient background shape (designer motion.jsx).
// Renamed from `Blob` so it no longer SHADOWS the global Blob constructor — the
// shadowing made `new Blob(...)` in the video recorder build this component
// instead of a real Blob (size undefined → createObjectURL "Overload
// resolution failed"). Uses the global `blob` + `float-slow` keyframes.
function AmbientBlob({ size = 300, color = "var(--color-primaryBright)", style }) {
  return (
    <div aria-hidden="true" style={{
      width: size, height: size, background: color, opacity: 0.5,
      animation: "blob 12s ease-in-out infinite, float-slow 6s ease-in-out infinite",
      filter: "blur(2px)", pointerEvents: "none", ...style,
    }} />
  );
}

// LevelStepper — discrete tappable number chips (3–10) replacing the easy-to-miss
// range slider for level count. `accent` is the theme's primary color. Each chip
// is a real button → far more noticeable + directly selectable than a thumb.
function LevelStepper({ value, onChange, accent = "var(--color-primary)" }) {
  const opts = [3, 4, 5, 6, 7, 8, 9, 10];
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 8 }}>
      {opts.map((n) => {
        const on = n === value;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            aria-pressed={on}
            aria-label={`${n} levels`}
            style={{
              width: "100%", height: 44, borderRadius: 12, cursor: "pointer",
              border: "none", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16,
              background: on ? accent : "rgba(0,0,0,0.05)",
              color: on ? "#fff" : "var(--color-textMuted)",
              boxShadow: on ? `0 6px 14px -6px ${accent}` : "none",
              transform: on ? "translateY(-1px)" : "none",
              transition: "background 0.15s, transform 0.15s, box-shadow 0.15s",
            }}
          >{n}</button>
        );
      })}
    </div>
  );
}

// Shared "go back a level" control used by every themed level builder so the
// back affordance is consistent across themes (candy_heart frosted pill, theme
// accent, generous hit area). Replaces the old per-theme one-offs.
function BackLevelButton({ onClick, label = "Previous level", accent = "var(--color-primary)" }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        background: "color-mix(in srgb, var(--color-surface) 72%, transparent)",
        backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
        border: "none", borderRadius: 999, cursor: "pointer",
        padding: "10px 18px 10px 14px", marginBottom: 18,
        fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14,
        letterSpacing: "-0.01em", color: accent,
        boxShadow: "0 6px 18px -10px rgba(0,0,0,0.32)",
        transition: "transform 0.15s ease",
      }}
      onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.97)"; }}
      onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
    >
      <span aria-hidden="true" style={{ fontSize: 17, lineHeight: 1, marginTop: -1 }}>←</span>
      {label}
    </button>
  );
}

// Phase 4 W1.T2 — exported for ThemeGallery import (D-14 + D-20 named export).
export const THEMES = {
  romantic: {
    name: "Romantic",
    emoji: "💕",
    description: "Warm & heartfelt",
    locked: true, // Phase 2 / W3.T1 — Pro-only theme

    fonts: {
      display: "'Plus Jakarta Sans', sans-serif",
      body: "'Be Vietnam Pro', sans-serif",
    },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/romantic.html): blush stationery + wine ink, rose primary, wax + gold accents
      bg: "#fdf2f2",
      bgAlt: "#f5d8db",
      surface: "#ffffff",
      surfaceHover: "#fbe4e8",
      border: "rgba(74,10,31,0.12)",
      primary: "#c8345f",
      primaryBright: "#e05c82",
      secondary: "#a01a30",
      accent: "#c9a14a",
      text: "#4a0a1f",
      textMuted: "#6e1e3a",
      textDim: "#a87a8a",
      correct: "#2e7d32",
      wrong: "#b41340",
      glow: "rgba(200,52,95,0.18)",
      onPrimary: "#ffffff",
    },
    particles: "hearts",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/romantic.html)
      // Voice: the love letter — Cormorant Garamond italic moments,
      // wax-seal stamps, "sealed with a kiss" payoff. Pressed petals replace confetti.
      passwordLabel: "Tell me a secret",
      passwordPlaceholder: "the one only you'd know",
      hintButton: "Read between the lines",
      checkAnswer: "Seal it",
      wrongPassword: "Not quite, my dear.",
      correctAnswer: "Sealed with a kiss.",
      levelComplete: "And then…",
      finalePrelude: "What was always true",
      finaleSignoff: "— yours, always",
      emptyHistory: "No puzzles yet. Create one for someone you love.",
      builderHeadingPrefix: "Craft Your",
      builderHeadingAccent: "Love Letter",
      builderSubhead: "Design a personalized puzzle level that tells your story. Every piece is a memory waiting to be discovered.",
      levelTagline: "Unlocking a moment of heart...",
      footerQuote: "Every puzzle piece brings us closer to the heart.",
      signUpPromptTitle: "Keep your love story safe",
      signUpPromptBody: "Sign in once — your puzzles follow you to every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
    },
  },
  funny: {
    name: "Funny",
    emoji: "😂",
    description: "Playful & lighthearted",
    locked: false, // Phase 2 / W3.T1 — Free-tier theme

    fonts: {
      display: "'Plus Jakarta Sans', sans-serif",
      body: "'Be Vietnam Pro', sans-serif",
    },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/funny.html): sunshine yellow + heavy ink, hot-pink pop, teal burst
      bg: "#ffe55c",
      bgAlt: "#f5c842",
      surface: "#fffbed",
      surfaceHover: "#fff6dc",
      border: "rgba(26,10,20,0.1)",
      primary: "#ff2e6e",
      primaryBright: "#ff6b9d",
      secondary: "#00b3b3",
      accent: "#d11a55",
      text: "#1a0a14",
      textMuted: "#3a1b28",
      textDim: "#8a6a5a",
      correct: "#2e7d32",
      wrong: "#d11a55",
      glow: "rgba(255,46,110,0.25)",
      onPrimary: "#fffbed",
    },
    particles: "emojis",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/funny.html)
      // Voice: comic strip — heavy ink, hot-pink CTA, eye-roll affection.
      passwordLabel: "Prove it's you",
      passwordPlaceholder: "come on, you know this",
      hintButton: "Beg for a hint",
      checkAnswer: "Send it",
      wrongPassword: "LOL no. Try again.",
      correctAnswer: "OK genius.",
      levelComplete: "Don't get cocky.",
      finalePrelude: "The truth comes out",
      finaleSignoff: "— love you anyway 🙄",
      emptyHistory: "No puzzles yet. Time to cook up something ridiculous.",
      builderHeadingPrefix: "Cook Up a",
      builderHeadingAccent: "Knee-Slapper",
      builderSubhead: "Build a ridiculous level that'll make them snort-laugh. Every piece sets up the next gag.",
      levelTagline: "Loading the punchline...",
      footerQuote: "Stick the landing — every punchline counts.",
      signUpPromptTitle: "Don't lose your jokes",
      signUpPromptBody: "Sign in so your gags follow you to every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Maybe later",
    },
  },
  gaming: {
    name: "Gaming",
    emoji: "🎮",
    description: "Neon & electric",
    locked: true, // Phase 2 / W3.T1 — Pro-only theme

    fonts: {
      display: "'Plus Jakarta Sans', sans-serif",
      body: "'Be Vietnam Pro', sans-serif",
    },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/gaming.html): arcade cabinet #0a0a1f, neon magenta primary, cyan + coin gold
      bg: "#0a0a1f",
      bgAlt: "#050514",
      surface: "#16143a",
      surfaceHover: "#221c4a",
      border: "rgba(255,46,184,0.18)",
      primary: "#ff2eb8",
      primaryBright: "#ff5ccb",
      secondary: "#00e5ff",
      accent: "#ffd84d",
      text: "#e8e4ff",
      textMuted: "#a8a0d0",
      textDim: "#6e6890",
      correct: "#4dffb8",
      wrong: "#ff3a5c",
      glow: "rgba(255,46,184,0.4)",
      onPrimary: "#0a0a1f",
    },
    particles: "pixels",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/gaming.html)
      // Voice: arcade cabinet — UPPERCASE labels, +XP rewards, Konami code reference.
      passwordLabel: "ENTER CHEAT CODE",
      passwordPlaceholder: "↑ ↑ ↓ ↓ ← → ← →",
      hintButton: "Use a power-up",
      checkAnswer: "PRESS START",
      wrongPassword: "GAME OVER. Continue?",
      correctAnswer: "+100 XP",
      levelComplete: "STAGE CLEAR",
      finalePrelude: "ACHIEVEMENT UNLOCKED",
      finaleSignoff: "— P1 // co-op for life",
      emptyHistory: "No quests created. Build your first challenge.",
      builderHeadingPrefix: "Build Your",
      builderHeadingAccent: "Quest",
      builderSubhead: "Design a level in the arcade. Every stage tests a different skill — make it unforgettable.",
      levelTagline: "Loading next stage...",
      footerQuote: "One more level down. Victory awaits.",
      signUpPromptTitle: "Save your high scores",
      signUpPromptBody: "Sign in so your quests carry across every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Skip for now",
    },
  },
  birthday: {
    locked: false, // Phase 2 / W3.T1 — Free-tier theme
    name: "Birthday",
    emoji: "🎂",
    description: "Festive & celebratory",
    fonts: { display: "'Plus Jakarta Sans', sans-serif", body: "'Be Vietnam Pro', sans-serif" },
    fontImport: "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Be+Vietnam+Pro:wght@400;500;600;700&display=swap",
    colors: {
      // Designer port (themes/birthday.html): blush + party pink primary, mint + candle gold
      bg: "#fef0f5",
      bgAlt: "#f9dde6",
      surface: "#ffffff",
      surfaceHover: "#fff4f8",
      border: "rgba(90,26,58,0.1)",
      primary: "#ff5b94",
      primaryBright: "#ff85b3",
      secondary: "#7ad8c4",
      secondaryContainer: "#ffc94d",
      accent: "#ffc94d",
      text: "#5a1a3a",
      textMuted: "#7d3155",
      textDim: "#b88aa0",
      correct: "#2e7d32",
      wrong: "#d63a72",
      glow: "rgba(255,91,148,0.25)",
      onPrimary: "#ffffff",
    },
    particles: "emojis",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/birthday.html)
      // Voice: birthday card — handwritten signoff, candle-flame gold, wish-granted payoff.
      passwordLabel: "Sign your name",
      passwordPlaceholder: "it's your special day",
      hintButton: "Peek inside the card",
      checkAnswer: "Make a wish",
      wrongPassword: "That's not the birthday person.",
      correctAnswer: "Wish granted ✨",
      levelComplete: "Another candle lit.",
      finalePrelude: "Blow out the candle",
      finaleSignoff: "— with love, on your day",
      emptyHistory: "No puzzles yet. Plan a surprise for someone's big day!",
      builderHeadingPrefix: "Plan the",
      builderHeadingAccent: "Big Surprise",
      builderSubhead: "Design a puzzle level for their special day. Every piece builds up to the surprise.",
      levelTagline: "Unwrapping the next surprise...",
      footerQuote: "Another ribbon pulled loose. Keep going.",
      signUpPromptTitle: "Save your party plans",
      signUpPromptBody: "Sign in once and your surprises follow you anywhere.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
    },
  },
  friendship: {
    locked: true, // Phase 2 / W3.T1 — Pro-only theme
    name: "Friendship",
    emoji: "🤝",
    description: "For your ride-or-die bestie",
    fonts: { display: "'Plus Jakarta Sans', sans-serif", body: "'Be Vietnam Pro', sans-serif" },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/friendship.html): warm cream + sage green primary, butter + coral
      bg: "#faf3e3",
      bgAlt: "#f5ebd0",
      surface: "#ffffff",
      surfaceHover: "#fdf8ee",
      border: "rgba(44,62,52,0.1)",
      primary: "#5c8c6b",
      primaryBright: "#7aa888",
      secondary: "#f5c542",
      accent: "#3d6b4c",
      text: "#2c3e34",
      textMuted: "#4d6857",
      textDim: "#94a99e",
      correct: "#2db678",
      wrong: "#c84d6e",
      glow: "rgba(92,140,107,0.25)",
      onPrimary: "#ffffff",
    },
    particles: "confetti",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/friendship.html)
      // Voice: scrapbook + inside-joke — sage greens, casual warmth, "your person" signoff.
      passwordLabel: "The secret handshake",
      passwordPlaceholder: "we know what it is",
      hintButton: "Need a nudge?",
      checkAnswer: "Lock it in",
      wrongPassword: "Nope — that's not us.",
      correctAnswer: "I knew you'd get it!",
      levelComplete: "On to the next.",
      finalePrelude: "P.S. — read this",
      finaleSignoff: "— love, your person",
      emptyHistory: "No puzzles yet. Make one for your best friend!",
      builderHeadingPrefix: "Craft Your",
      builderHeadingAccent: "Bestie Quest",
      builderSubhead: "Design a puzzle only your ride-or-die could solve. Every piece is an inside joke waiting to land.",
      levelTagline: "Unlocking bestie energy...",
      footerQuote: "Pieces of your friendship — scattered and found.",
      signUpPromptTitle: "Keep your inside jokes safe",
      signUpPromptBody: "Sign in so your bestie quests follow you anywhere.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
    },
  },
  spooky: {
    locked: true, // Phase 2 / W3.T1 — Pro-only theme
    name: "Spooky",
    emoji: "👻",
    description: "Thrills, chills, and mystery",
    fonts: { display: "'Plus Jakarta Sans', sans-serif", body: "'Be Vietnam Pro', sans-serif" },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/spooky.html): violet night #1a1025, lavender primary, pumpkin + slime green
      bg: "#1a1025",
      bgAlt: "#241634",
      surface: "#241634",
      surfaceHover: "#2f1d44",
      border: "rgba(184,141,214,0.18)",
      primary: "#b88dd6",
      primaryBright: "#cda8e4",
      secondary: "#ff8c42",
      accent: "#7d4ca8",
      text: "#f4e8ff",
      textMuted: "#c9b8df",
      textDim: "#8b7aa4",
      correct: "#7ad7a4",
      wrong: "#ff5c7a",
      glow: "rgba(184,141,214,0.35)",
      onPrimary: "#1a1025",
    },
    particles: "float",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/spooky.html)
      // Voice: seance — whispered, deep midnight purple, "veil lifts" payoff.
      passwordLabel: "Whisper the secret",
      passwordPlaceholder: "listening…",
      hintButton: "A whisper from the beyond",
      checkAnswer: "Conjure",
      wrongPassword: "The spirits aren't convinced.",
      correctAnswer: "The veil lifts.",
      levelComplete: "Chapter closed.",
      finalePrelude: "What was hidden",
      finaleSignoff: "— sent from beyond ✦",
      emptyHistory: "No puzzles lurk here yet. Conjure your first.",
      builderHeadingPrefix: "Weave Your",
      builderHeadingAccent: "Haunted Riddle",
      builderSubhead: "Craft a level that lingers in the dark corners of their mind. Every piece pulls them deeper into the mystery.",
      levelTagline: "Unsealing the next chamber...",
      footerQuote: "One step deeper into the dark.",
      signUpPromptTitle: "Don't let your puzzles disappear",
      signUpPromptBody: "Sign in to bind your riddles to every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
    },
  },
  travel: {
    locked: true, // Phase 2 / W3.T1 — Pro-only theme
    name: "Travel",
    emoji: "✈️",
    description: "For adventurers and travel buddies",
    fonts: { display: "'Plus Jakarta Sans', sans-serif", body: "'Be Vietnam Pro', sans-serif" },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Designer port (themes/travel.html): vintage map paper + navy ink primary, stamp red + sun gold
      bg: "#f4ecd8",
      bgAlt: "#ead9b8",
      surface: "#fffaf0",
      surfaceHover: "#f7f0de",
      border: "rgba(28,46,82,0.12)",
      primary: "#1c2e52",
      primaryBright: "#34508a",
      secondary: "#c0392b",
      accent: "#d4a72a",
      text: "#1c2e52",
      textMuted: "#3d5278",
      textDim: "#8c9bb5",
      correct: "#3a8c5f",
      wrong: "#c0392b",
      glow: "rgba(28,46,82,0.2)",
      onPrimary: "#fffaf0",
    },
    particles: "rise",
    microcopy: {
      // Designer port (artifact S7kaRCw5APtgzjD_UOOFfA / themes/travel.html)
      // Voice: passport + postcard — navy ink, ticket-stock cream, "wish you were here" signoff.
      passwordLabel: "Show your passport",
      passwordPlaceholder: "passport no.",
      hintButton: "Open the guidebook",
      checkAnswer: "Get it stamped",
      wrongPassword: "Not on the manifest.",
      correctAnswer: "Stamped ✓ Approved",
      levelComplete: "Next destination →",
      finalePrelude: "Final destination",
      finaleSignoff: "— wish you were here",
      emptyHistory: "No adventures planned yet. Start your first journey!",
      builderHeadingPrefix: "Chart Your",
      builderHeadingAccent: "Journey",
      builderSubhead: "Design a level stamped with shared adventures. Every piece marks another mile on the road.",
      levelTagline: "Boarding the next leg...",
      footerQuote: "One more stamp. One more story.",
      signUpPromptTitle: "Save your travel log",
      signUpPromptBody: "Sign in so your journey follows you across every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
    },
  },
  christmas: {
    locked: true, // seasonal (15 → 25 Dec), Pro-only
    name: "Christmas",
    emoji: "🎄",
    description: "Warmth, lights and gifts",
    fonts: { display: "'Plus Jakarta Sans', sans-serif", body: "'Be Vietnam Pro', sans-serif" },
    fontImport: FONT_IMPORT_URL,
    colors: {
      // Seasonal proposal (themes-canvas "Natale"): warm page, pine ink, cranberry buttons, gold accents
      bg: "#FBF4EA",
      bgAlt: "#F6E7C8",
      surface: "#ffffff",
      surfaceHover: "#fdf8ef",
      border: "rgba(30,58,43,0.12)",
      primary: "#B3263A",
      primaryBright: "#C93D50",
      secondary: "#C9A14A",
      accent: "#1E4D36",
      text: "#1E3A2B",
      textMuted: "#4F6658",
      textDim: "#8A9A8F",
      correct: "#2E7D5B",
      wrong: "#B3263A",
      glow: "rgba(179,38,58,0.22)",
      onPrimary: "#ffffff",
    },
    particles: "snow",
    microcopy: {
      // Voice: gifts under the tree. Clues are packages to unwrap, the elves help.
      passwordLabel: "The magic word",
      passwordPlaceholder: "the one on the gift tag",
      hintButton: "A little help from the elves",
      checkAnswer: "Unwrap",
      wrongPassword: "Not time to unwrap it yet.",
      correctAnswer: "Unwrapped! 🎁",
      levelComplete: "One more gift opened.",
      finalePrelude: "Under the tree",
      finaleSignoff: "— with all my love ✦",
      emptyHistory: "No puzzles yet. Wrap one up for someone you love!",
      builderHeadingPrefix: "Wrap Up a",
      builderHeadingAccent: "Christmas Surprise",
      builderSubhead: "Make a puzzle to leave under the tree. Every clue is a package they get to unwrap.",
      levelTagline: "Untying the next ribbon...",
      footerQuote: "One more ribbon untied.",
      signUpPromptTitle: "Keep your gifts safe",
      signUpPromptBody: "Sign in and your Christmas puzzles follow you to every device.",
      signUpCtaGoogle: "Continue with Google",
      signUpCtaEmail: "Continue with Email",
      signUpSkip: "Not now",
      clueUnit: "Package",
    },
  },
};


// ── Image compression for celebrations ──
function compressImage(file, maxSize = 800, quality = 0.8) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let w = img.width, h = img.height;
        if (w > h) { if (w > maxSize) { h = (h * maxSize) / w; w = maxSize; } }
        else { if (h > maxSize) { w = (w * maxSize) / h; h = maxSize; } }
        canvas.width = w;
        canvas.height = h;
        canvas.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

// ── YouTube URL → embed URL ──
// Creator-provided: only a YouTube embed built from the video id ever comes out
// (src/utils/safeMedia.js), "" otherwise. Native app → yt.html (PR #94).
const getEmbedUrl = safeEmbedUrl;

// ── Utility: encode/decode config ──
function encodeConfig(config) {
  return LZ.compressToBase64(JSON.stringify(config));
}

function decodeConfig(hash) {
  try {
    const json = LZ.decompressFromBase64(hash);
    if (!json) return null;
    return JSON.parse(json);
  } catch { return null; }
}

// Older builds meet theme keys they don't know (seasonal themes shipped later):
// fall back to romantic instead of crashing on THEMES[key].colors.
export function resolveThemeKey(key) {
  // hasOwnProperty, not `in`/truthiness: "constructor", "toString"… are not themes.
  return key && Object.prototype.hasOwnProperty.call(THEMES, key) ? key : "romantic";
}

// ── Helper: is this a dark theme? ──
function isDarkTheme(themeKey) {
  return themeKey === "gaming" || themeKey === "spooky";
}

// ── Particles Component ──
function Particles({ theme }) {
  const t = THEMES[resolveThemeKey(theme)];
  const dark = isDarkTheme(theme);
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    let particles = [];
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);

    const count = dark ? 25 : 18;
    const getChar = (i) => {
      switch (t.particles) {
        case "hearts": return "♥";
        case "pixels": return "■";
        case "confetti": return ["■","●","◆","▲","★"][i % 5];
        case "float": return ["👻","🦇","🕯️","🔮","💀","🕸️"][i % 6];
        case "rise": return ["✈️","🗺️","⛵","🏔️","🌍","🧳"][i % 6];
        case "snow": return ["❄","❅","•"][i % 3];
        case "emojis":
          if (theme === "birthday") return ["🎂","🎉","⭐","🎈","🎁","🥳"][i % 6];
          return ["😄","🎉","⭐","🔥","💫","🎈"][i % 6];
        default: return "♥";
      }
    };
    for (let i = 0; i < count; i++) {
      const isFloat = t.particles === "float";
      const isSnow = t.particles === "snow"; // snow falls instead of rising
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * (isFloat ? 0.15 : 0.3),
        vy: isSnow ? (Math.random() * 0.4 + 0.15) : isFloat ? (-Math.random() * 0.3 - 0.1) : (-Math.random() * 0.5 - 0.2),
        size: dark ? (Math.random() * 10 + 6) : (Math.random() * 8 + 4),
        opacity: dark ? (Math.random() * 0.4 + 0.1) : (Math.random() * 0.15 + 0.05),
        char: getChar(i),
      });
    }

    function draw() {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.y < -20) { p.y = canvas.height + 20; p.x = Math.random() * canvas.width; }
        if (p.y > canvas.height + 20) { p.y = -20; p.x = Math.random() * canvas.width; }
        if (p.x < -20) p.x = canvas.width + 20;
        if (p.x > canvas.width + 20) p.x = -20;
        ctx.globalAlpha = p.opacity;
        ctx.font = `${p.size}px ${t.particles === "pixels" ? "monospace" : "serif"}`;
        // Snow in pine: cranberry flakes read as confetti on the warm page.
        ctx.fillStyle = t.particles === "snow" ? t.colors.accent : t.colors.primary;
        ctx.fillText(p.char, p.x, p.y);
      });
      animId = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [theme]);

  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 0 }} />;
}

// ── Confetti Burst ──
// Celebration effects a theme may draw from. Christmas skips hearts and fireworks
// (romantic/party, not festive) and gets falling snowflakes in its own palette.
export const CONFETTI_STYLES = {
  default: ["confetti", "fireworks", "hearts", "stars", "sparkleRain"],
  christmas: ["stars", "sparkleRain", "snowfall"],
};
export function pickConfettiStyle(theme, rand = Math.random) {
  const list = CONFETTI_STYLES[theme] || CONFETTI_STYLES.default;
  return list[Math.floor(rand() * list.length)];
}
const CHRISTMAS_CONFETTI_COLORS = ["#B3263A", "#C93D50", "#C9A14A", "#A9822F", "#1E4D36", "#E4C77E"];

function ConfettiBurst({ theme, active }) {
  const canvasRef = useRef(null);
  const effectIdRef = useRef(0);
  const t = THEMES[theme] || THEMES.romantic;

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return; // jsdom / no-canvas envs
    canvas.width = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;
    const W = canvas.width, H = canvas.height;
    const colors = theme === "christmas"
      ? CHRISTMAS_CONFETTI_COLORS
      : [t.colors.primary, t.colors.secondary, t.colors.accent, t.colors.primaryBright, "#ffffff", "#FFD700"];

    // Pick a random celebration style each time (from the theme's own list)
    const effectId = ++effectIdRef.current;
    const style = pickConfettiStyle(theme);

    let pieces = [];
    let animId;

    // Shape drawers
    const drawHeart = (ctx, x, y, size) => {
      ctx.beginPath();
      const s = size * 0.6;
      ctx.moveTo(x, y + s * 0.3);
      ctx.bezierCurveTo(x, y - s * 0.3, x - s, y - s * 0.3, x - s, y + s * 0.1);
      ctx.bezierCurveTo(x - s, y + s * 0.6, x, y + s, x, y + s * 1.1);
      ctx.bezierCurveTo(x, y + s, x + s, y + s * 0.6, x + s, y + s * 0.1);
      ctx.bezierCurveTo(x + s, y - s * 0.3, x, y - s * 0.3, x, y + s * 0.3);
      ctx.fill();
    };

    const drawStar = (ctx, cx, cy, spikes, outerR, innerR) => {
      ctx.beginPath();
      for (let i = 0; i < spikes * 2; i++) {
        const r = i % 2 === 0 ? outerR : innerR;
        const angle = (Math.PI * i) / spikes - Math.PI / 2;
        const fn = i === 0 ? "moveTo" : "lineTo";
        ctx[fn](cx + Math.cos(angle) * r, cy + Math.sin(angle) * r);
      }
      ctx.closePath();
      ctx.fill();
    };

    // Six-armed snowflake with a little V on each arm, stroked in the fill colour.
    const drawSnowflake = (ctx, size) => {
      const r = size / 2;
      ctx.strokeStyle = ctx.fillStyle;
      ctx.lineWidth = Math.max(1.2, size / 9);
      ctx.lineCap = "round";
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k;
        const cos = Math.cos(a), sin = Math.sin(a);
        ctx.moveTo(0, 0);
        ctx.lineTo(cos * r, sin * r);
        const bx = cos * r * 0.55, by = sin * r * 0.55;
        for (const d of [-1, 1]) {
          const b = a + d * 0.7;
          ctx.moveTo(bx, by);
          ctx.lineTo(bx + Math.cos(b) * r * 0.35, by + Math.sin(b) * r * 0.35);
        }
      }
      ctx.stroke();
    };

    const drawCircle = (ctx, x, y, r) => {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fill();
    };

    // === CONFETTI: classic rectangles + circles bursting from center ===
    if (style === "confetti") {
      for (let i = 0; i < 100; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 12 + 4;
        pieces.push({
          x: W / 2 + (Math.random() - 0.5) * 80,
          y: H / 2 + (Math.random() - 0.5) * 40,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 6,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 8 + 3,
          rotation: Math.random() * 360,
          rv: (Math.random() - 0.5) * 14,
          life: 1,
          shape: Math.random() > 0.5 ? "rect" : "circle",
        });
      }
    }

    // === FIREWORKS: multiple burst points ===
    if (style === "fireworks") {
      const bursts = 3 + Math.floor(Math.random() * 3);
      for (let b = 0; b < bursts; b++) {
        const bx = W * 0.2 + Math.random() * W * 0.6;
        const by = H * 0.15 + Math.random() * H * 0.4;
        const burstColor = colors[Math.floor(Math.random() * colors.length)];
        const sparkCount = 25 + Math.floor(Math.random() * 20);
        for (let i = 0; i < sparkCount; i++) {
          const angle = Math.random() * Math.PI * 2;
          const speed = Math.random() * 8 + 2;
          pieces.push({
            x: bx, y: by,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            color: Math.random() > 0.3 ? burstColor : "#ffffff",
            size: Math.random() * 4 + 1.5,
            life: 1,
            delay: b * 8,
            shape: "circle",
            trail: true,
          });
        }
      }
    }

    // === HEARTS: floating hearts rising up ===
    if (style === "hearts") {
      const heartColors = [
        t.colors.primary,
        t.colors.primaryBright,
        t.colors.secondary || t.colors.accent,
        t.colors.accent,
        t.colors.primaryBright,
        "#ffffff",
      ];
      for (let i = 0; i < 35; i++) {
        pieces.push({
          x: Math.random() * W,
          y: H + Math.random() * 100,
          vx: (Math.random() - 0.5) * 2,
          vy: -(Math.random() * 4 + 2),
          color: heartColors[Math.floor(Math.random() * heartColors.length)],
          size: Math.random() * 16 + 8,
          life: 1,
          shape: "heart",
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.05 + 0.02,
        });
      }
    }

    // === STARS: spinning stars exploding outward ===
    if (style === "stars") {
      for (let i = 0; i < 60; i++) {
        const angle = Math.random() * Math.PI * 2;
        const speed = Math.random() * 10 + 3;
        pieces.push({
          x: W / 2, y: H / 2,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 3,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 10 + 5,
          rotation: Math.random() * 360,
          rv: (Math.random() - 0.5) * 15,
          life: 1,
          shape: "star",
          spikes: Math.random() > 0.5 ? 5 : 4,
        });
      }
    }

    // === SPARKLE RAIN: glittery particles falling from top ===
    if (style === "sparkleRain") {
      for (let i = 0; i < 80; i++) {
        pieces.push({
          x: Math.random() * W,
          y: -Math.random() * H * 0.5,
          vx: (Math.random() - 0.5) * 1.5,
          vy: Math.random() * 3 + 1.5,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 6 + 2,
          life: 1,
          shape: Math.random() > 0.6 ? "star" : "circle",
          spikes: 4,
          rotation: Math.random() * 360,
          rv: (Math.random() - 0.5) * 8,
          twinkle: Math.random() * Math.PI * 2,
          twinkleSpeed: Math.random() * 0.1 + 0.05,
        });
      }
    }

    // === SNOWFALL: snowflakes drifting down and swaying ===
    if (style === "snowfall") {
      for (let i = 0; i < 45; i++) {
        pieces.push({
          x: Math.random() * W,
          y: -Math.random() * H * 0.6,
          vx: (Math.random() - 0.5) * 0.8,
          vy: Math.random() * 2.2 + 1.4,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: Math.random() * 14 + 10,
          rotation: Math.random() * 360,
          rv: (Math.random() - 0.5) * 3,
          life: 1,
          shape: "snowflake",
          wobble: Math.random() * Math.PI * 2,
          wobbleSpeed: Math.random() * 0.04 + 0.02,
        });
      }
    }

    let frame = 0;
    function draw() {
      if (effectId !== effectIdRef.current) return;
      ctx.clearRect(0, 0, W, H);
      let alive = false;
      frame++;

      pieces.forEach((p) => {
        if (p.life <= 0) return;
        if (p.delay && frame < p.delay) { alive = true; return; }
        alive = true;

        // Physics
        p.x += p.vx;
        p.y += p.vy;
        if (style === "hearts" || style === "snowfall") {
          p.wobble = (p.wobble || 0) + (p.wobbleSpeed || 0.03);
          p.x += Math.sin(p.wobble) * 1.5;
        } else if (style !== "sparkleRain") {
          p.vy += 0.25;
        }
        if (p.rotation !== undefined) p.rotation += (p.rv || 0);
        p.life -= style === "sparkleRain" || style === "snowfall" ? 0.006 : style === "hearts" ? 0.005 : 0.009;

        ctx.save();
        ctx.translate(p.x, p.y);
        if (p.rotation !== undefined) ctx.rotate((p.rotation * Math.PI) / 180);

        let alpha = Math.max(0, p.life);
        if (p.twinkle !== undefined) {
          p.twinkle += p.twinkleSpeed;
          alpha *= 0.5 + 0.5 * Math.sin(p.twinkle);
        }
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;

        if (p.shape === "heart") {
          drawHeart(ctx, 0, 0, p.size);
        } else if (p.shape === "snowflake") {
          drawSnowflake(ctx, p.size);
        } else if (p.shape === "star") {
          drawStar(ctx, 0, 0, p.spikes || 5, p.size / 2, p.size / 4.5);
        } else if (p.shape === "circle") {
          drawCircle(ctx, 0, 0, p.size / 2);
          if (p.trail) {
            ctx.globalAlpha = alpha * 0.3;
            drawCircle(ctx, -p.vx * 1.5, -p.vy * 1.5, p.size / 3);
          }
        } else {
          ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        }

        ctx.restore();
      });
      if (alive) animId = requestAnimationFrame(draw);
    }
    draw();
    return () => { cancelAnimationFrame(animId); };
  }, [active, theme]);

  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 100 }} />;
}

// ── Styled Button ──
function Btn({ children, onClick, variant = "primary", disabled, style = {}, small, ...rest }) {
  const base = {
    padding: small ? "12px 22px" : "16px 32px",
    border: "none",
    borderRadius: "16px",
    cursor: disabled ? "not-allowed" : "pointer",
    fontFamily: "var(--font-display)",
    fontWeight: 700,
    fontSize: small ? "14px" : "16px",
    letterSpacing: "0.01em",
    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
    opacity: disabled ? 0.4 : 1,
    position: "relative",
    overflow: "hidden",
  };
  const variants = {
    primary: {
      background: "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
      color: "var(--color-onPrimary)",
      boxShadow: `0 4px 20px var(--color-glow)`,
      // Maintains AA contrast for white text on the bright end of light gradients
      // (friendship/spooky/travel themes have light-purple/teal primaryBright stops)
      textShadow: "0 1px 2px rgba(0,0,0,0.18)",
    },
    secondary: {
      background: "var(--color-bgAlt)",
      color: "var(--color-primary)",
      border: "none",
    },
    ghost: {
      background: "transparent",
      color: "var(--color-textMuted)",
    },
  };
  // Track hover + press state so combined transforms don't fight each other.
  // Use currentTarget so events from text-node children still resolve to the <button>.
  const applyTransform = (el, hover, press) => {
    const t = press ? "scale(0.96)" : (hover ? "translateY(-2px) scale(1)" : "scale(1) translateY(0)");
    el.style.transform = t;
  };
  return (
    <button
      {...rest}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      style={{ ...base, ...variants[variant], ...style, minHeight: small ? 40 : 48 }}
      onMouseDown={(e) => { if (!disabled) applyTransform(e.currentTarget, true, true); }}
      onMouseUp={(e) => { if (!disabled) applyTransform(e.currentTarget, true, false); }}
      onMouseEnter={(e) => { if (!disabled) applyTransform(e.currentTarget, true, false); }}
      onMouseLeave={(e) => applyTransform(e.currentTarget, false, false)}
      onFocus={(e) => { e.currentTarget.style.boxShadow = `0 4px 20px var(--color-glow), 0 0 0 3px var(--color-glow)`; }}
      onBlur={(e) => { e.currentTarget.style.boxShadow = variant === "primary" ? `0 4px 20px var(--color-glow)` : "none"; }}
    >
      {children}
    </button>
  );
}

// ── Text Input ──
const Input = forwardRef(function Input({ value, onChange, placeholder, type = "text", style = {}, autoFocus, maxLength }, ref) {
  return (
    <input
      ref={ref}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      autoFocus={autoFocus}
      maxLength={maxLength}
      style={{
        width: "100%",
        padding: "16px 24px",
        background: "var(--color-bgAlt)",
        border: "none",
        borderRadius: "16px",
        color: "var(--color-text)",
        fontFamily: "var(--font-body)",
        fontSize: "16px",
        outline: "none",
        transition: "box-shadow 0.2s, background 0.2s",
        boxSizing: "border-box",
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-glow)`;
        if (!style.background) e.currentTarget.style.background = "var(--color-surface)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.background = style.background || "var(--color-bgAlt)";
      }}
    />
  );
});

// ── TextArea ──
function TextArea({ value, onChange, placeholder, rows = 3, style = {}, maxLength, ...rest }) {
  // Preserve consumer-provided background through focus/blur — otherwise the
  // imperative onBlur write would clobber custom bg colors (e.g. white inset
  // textareas inside gradient cards), causing dark text to vanish on dark themes.
  const customBg = style.background;
  return (
    <textarea
      {...rest}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      maxLength={maxLength}
      style={{
        width: "100%",
        padding: "16px 24px",
        background: "var(--color-bgAlt)",
        border: "none",
        borderRadius: "16px",
        color: "var(--color-text)",
        fontFamily: "var(--font-body)",
        fontSize: "16px",
        outline: "none",
        resize: "vertical",
        transition: "box-shadow 0.2s, background 0.2s",
        boxSizing: "border-box",
        ...style,
      }}
      onFocus={(e) => {
        e.currentTarget.style.boxShadow = `0 0 0 2px var(--color-glow)`;
        if (!customBg) e.currentTarget.style.background = "var(--color-surface)";
      }}
      onBlur={(e) => {
        e.currentTarget.style.boxShadow = "none";
        e.currentTarget.style.background = customBg || "var(--color-bgAlt)";
      }}
    />
  );
}

// ── Card Wrapper ──
function Card({ children, style = {}, gaming }) {
  return (
    <div style={{
      background: "var(--color-surface)",
      border: gaming ? "1px solid rgba(185,164,255,0.1)" : "none",
      borderRadius: "2rem",
      padding: "32px",
      boxShadow: "0 8px 32px rgba(49,46,48,0.05)",
      ...style,
    }}>
      {children}
    </div>
  );
}

// ── Progress Bar ──
function ProgressBar({ current, total }) {
  const pct = ((current) / total) * 100;
  return (
    <div style={{ width: "100%", marginBottom: 24 }}>
      <div style={{ width: "100%", height: 12, background: "var(--color-bgAlt)", borderRadius: 999, overflow: "hidden" }}>
        <div style={{
          width: `${pct}%`,
          height: "100%",
          background: `linear-gradient(90deg, var(--color-primary), var(--color-primaryBright))`,
          borderRadius: 999,
          transition: "width 0.6s cubic-bezier(0.4,0,0.2,1)",
        }} />
      </div>
    </div>
  );
}

// ── Ambient Glow Blurs (decorative background) ──
function AmbientGlow({ theme }) {
  const t = THEMES[resolveThemeKey(theme)];
  if (isDarkTheme(theme)) return null;
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{
        position: "absolute", top: "-20%", right: "-10%", width: "500px", height: "500px",
        borderRadius: "50%", background: t.colors.primary, opacity: 0.04, filter: "blur(120px)",
      }} />
      <div style={{
        position: "absolute", bottom: "-15%", left: "-10%", width: "400px", height: "400px",
        borderRadius: "50%", background: t.colors.accent, opacity: 0.04, filter: "blur(100px)",
      }} />
    </div>
  );
}

// ═══════════════════════════════════
// SCREENS
// ═══════════════════════════════════

// ── Splash ──
function HowItWorksModal({ onClose }) {
  const { t: tr } = useTranslation();
  const steps = [
    { icon: "✏️", title: tr("app.howItWorks.steps.create.title"), desc: tr("app.howItWorks.steps.create.desc") },
    { icon: "🔗", title: tr("app.howItWorks.steps.share.title"), desc: tr("app.howItWorks.steps.share.desc") },
    { icon: "🧩", title: tr("app.howItWorks.steps.play.title"), desc: tr("app.howItWorks.steps.play.desc") },
  ];
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)",
      zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--color-surface)", borderRadius: "2rem", padding: 32,
        maxWidth: 420, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.15)",
        position: "relative",
      }}>
        <button onClick={onClose} style={{
          position: "absolute", top: 16, right: 16, background: "var(--color-bgAlt)",
          border: "none", borderRadius: "50%", width: 32, height: 32,
          display: "flex", alignItems: "center", justifyContent: "center",
          cursor: "pointer", color: "var(--color-textMuted)", fontSize: 16, fontWeight: 700,
        }}>✕</button>
        <h2 style={{
          fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800,
          color: "var(--color-primary)", margin: "0 0 4px", textAlign: "center",
        }}>{tr("app.howItWorks.title")}</h2>
        <p style={{
          fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)",
          textAlign: "center", margin: "0 0 28px",
        }}>{tr("app.howItWorks.subtitle")}</p>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
              <div style={{
                width: 52, height: 52, borderRadius: 16, flexShrink: 0,
                background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
                display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24,
                boxShadow: "0 4px 16px var(--color-glow)",
              }}>{s.icon}</div>
              <div>
                <div style={{
                  fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700,
                  color: "var(--color-text)", marginBottom: 4,
                }}>{s.title}</div>
                <div style={{
                  fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)",
                  lineHeight: 1.5,
                }}>{s.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 28 }}>
          <Btn onClick={onClose} style={{ width: "100%" }}>{tr("app.howItWorks.gotIt")}</Btn>
        </div>
      </div>
    </div>
  );
}

// ── Theme Select ──
// Phase 2 / W3.T2 — ThemeSelect now accepts `profile` and `onLocked`.
//
// When `theme.locked === true` AND the user is NOT Pro
// (`!profile?.entitlements?.pro`), the card renders at full colour with:
//   - "Pro" ribbon top-left (LOCK_CHIP_GRADIENT background)
//   - 🔒 emoji top-right
// Tapping a locked card calls `onLocked()` instead of `onSelect(key)` — so
// the parent can open PaywallModal without navigating into the creator
// flow (PAY-03 anti-pattern: never enter creator then bounce back).
//
// Pro users (`profile.entitlements.pro === true`) see NO lock badges and
// all taps fire `onSelect` normally (PAY-05).
//
// Exported so tests/components/theme-select.test.js can render in isolation.
export function ThemeSelect({ onSelect, profile, onLocked, onBack }) {
  const { t: tr } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [selected, setSelected] = useState(null);
  const [previewKey, setPreviewKey] = useState(null);
  const [name, setName] = useState("");
  const [burst, setBurst] = useState(false); // confetti on vibe pick
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  // Picking a vibe IS the action (create.jsx): confetti, then open its studio.
  const pickVibe = (key) => {
    setSelected(key);
    setBurst(true);
    setTimeout(() => onSelect(key, name.trim()), 480);
  };

  const isPro = !!(
    profile && profile.entitlements && profile.entitlements.pro === true
  );

  // Designer "Create · Step 1" screen (artifact create.jsx · "who's this one for?"):
  // a recipient-name field + a 2-col grid of vibe cards. Picking a vibe IS the
  // action — it opens that theme's studio. The typed name carries into the
  // puzzle title. Locked (Pro) cards route to the paywall; 👁 opens a preview.
  // Vibe taglines mirror create.jsx VIBES.
  const VIBE_TAGS = {
    birthday: "The year in clues",
    romantic: "Our story, levelled up",
    funny: "Inside jokes only",
    friendship: "Years of nonsense",
    gaming: "Co-op for life",
    travel: "The trip we owe",
    spooky: "A little bit haunted",
    christmas: "Unwrap it slowly",
  };
  const VIBE_ROT = { birthday: -2.5, romantic: 2, funny: 1.6, friendship: -2, gaming: 2.4, travel: -1.8, spooky: 1.4, christmas: -1.4 };

  const selectedName = selected ? tr(`app.themes.${selected}.name`) : null;

  return (
    <div style={{
      display: "flex", flexDirection: "column", position: "relative", overflow: "hidden",
      minHeight: "100vh", padding: "40px 22px calc(140px + env(safe-area-inset-bottom, 0px))",
      maxWidth: 600, margin: "0 auto", width: "100%",
      background: "linear-gradient(180deg, var(--cream) 0%, #fff 26%, var(--color-bg) 100%)",
      opacity: visible ? 1 : 0, transition: "opacity 0.6s",
    }}>
      {/* ambient blobs — wow background */}
      <AmbientBlob size={340} color="var(--butter)" style={{ position: "absolute", top: -150, left: -120, opacity: 0.5, zIndex: 0 }} />
      <AmbientBlob size={300} color="var(--lilac)" style={{ position: "absolute", top: 220, right: -150, opacity: 0.4, zIndex: 0 }} />
      {/* one-shot confetti when a vibe is chosen */}
      <ConfettiBurst theme={selected || "romantic"} active={burst} />
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column" }}>
      {/* Back to where we came from (e.g. a finished puzzle's message) — only when there's history */}
      {onBack && (
        <button onClick={onBack} aria-label={tr("app.common.goBackAria")} style={{
          alignSelf: "flex-start", background: "transparent", border: "none", cursor: "pointer",
          padding: "6px 2px", marginBottom: 10, fontFamily: "var(--font-body)", fontWeight: 700,
          fontSize: 14, color: "var(--color-textMuted)",
        }}>{tr("app.common.backArrow")}</button>
      )}
      {/* slim 4-step progress */}
      <div style={{ display: "flex", gap: 6, maxWidth: 240, marginBottom: 22 }}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} style={{ flex: 1, height: 5, borderRadius: 999, background: i === 0 ? "var(--color-primary)" : "var(--color-bgAlt)" }} />
        ))}
      </div>

      {/* Hero copy */}
      <div style={{
        fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12,
        letterSpacing: "0.18em", textTransform: "uppercase", color: "var(--color-primary)", marginBottom: 12,
      }}>{tr("app.themeSelect.eyebrow")}</div>
      <h2 style={{
        fontFamily: "var(--font-display)", fontWeight: 800, fontSize: "clamp(36px, 11vw, 54px)",
        lineHeight: 0.94, letterSpacing: "-0.04em", margin: 0, color: "var(--color-text)",
      }}>
        {tr("app.themeSelect.headingPre")} <span style={{ color: "var(--color-primary)", fontStyle: "italic" }}>{tr("app.themeSelect.headingAccent")}</span>
      </h2>
      <p style={{ fontFamily: "var(--font-body)", fontSize: 15, lineHeight: 1.5, color: "var(--color-textMuted)", margin: "16px 0 0", maxWidth: "32ch" }}>
        {tr("app.themeSelect.subtitle")}
      </p>

      {/* Name field */}
      <div style={{ marginTop: 26 }}>
        <label style={{
          display: "block", fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-textMuted)", marginBottom: 6,
        }}>{tr("app.themeSelect.nameLabel")}</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 24))}
          placeholder={tr("app.themeSelect.namePlaceholder")}
          style={{
            width: "100%", background: "transparent", border: "none", outline: "none",
            fontFamily: "var(--font-display)", fontWeight: 800,
            fontSize: "clamp(26px, 7vw, 36px)", letterSpacing: "-0.025em",
            color: "var(--color-text)", padding: "2px 2px 8px",
            borderBottom: `3px solid ${name.trim() ? "var(--color-primary)" : "var(--color-bgAlt)"}`,
            transition: "border-color 0.2s",
          }}
        />
      </div>

      {/* Vibe picker */}
      <div style={{ marginTop: 28 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.02em", color: "var(--color-text)" }}>{tr("app.themeSelect.pickVibe")}</span>
          <span style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textMuted)" }}>{selected ? tr("app.themeSelect.chosen") : tr("app.themeSelect.chooseOne")}</span>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          {/* Seasonal themes are offered only by the v2 creator, not this v1 picker. */}
          {Object.entries(THEMES).filter(([key]) => !isSeasonalTheme(key)).map(([key, t]) => {
            const isActive = selected === key;
            const locked = t.locked === true && !isPro;
            const handleClick = () => {
              if (locked) { if (onLocked) onLocked(key); return; }
              pickVibe(key);
            };
            return (
              <div
                key={key}
                onClick={handleClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleClick(); } }}
                data-testid={`theme-card-${key}`}
                style={{
                  position: "relative", cursor: "pointer", textAlign: "left",
                  background: "var(--color-surface)", borderRadius: 22, padding: "18px 16px 16px",
                  boxShadow: isActive
                    ? `0 18px 34px -16px ${t.colors.primary}, inset 0 0 0 2px ${t.colors.primary}`
                    : "0 6px 18px -10px rgba(0,0,0,0.25)",
                  transform: isActive ? "translateY(-4px) rotate(0deg)" : `rotate(${VIBE_ROT[key] || 0}deg)`,
                  transition: "transform 0.35s cubic-bezier(.2,.7,.2,1.1), box-shadow 0.25s",
                  overflow: "hidden",
                }}
              >
                {/* Themed gradient swatch */}
                <div style={{
                  width: 44, height: 44, borderRadius: 13,
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22,
                  background: `linear-gradient(145deg, ${t.colors.primaryBright}, ${t.colors.primary})`,
                  boxShadow: `0 6px 14px -6px ${t.colors.primary}`,
                  filter: locked ? "grayscale(0.12)" : "none",
                }}>{t.emoji}</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16, letterSpacing: "-0.01em", color: "var(--color-text)", marginTop: 10, display: "flex", alignItems: "center", gap: 6 }}>
                  {tr(`app.themes.${key}.name`)}
                  {locked && (
                    <span data-testid={`theme-ribbon-${key}`} style={{
                      background: LOCK_CHIP_GRADIENT, color: "#fff", padding: "2px 7px", borderRadius: 8,
                      fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 9, letterSpacing: 0.4,
                    }}>{tr("app.themeSelect.proBadge")}</span>
                  )}
                </div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textMuted)", opacity: 0.85, marginTop: 2, lineHeight: 1.3 }}>{tr(`app.themeSelect.vibeTags.${key}`, { defaultValue: tr(`app.themes.${key}.description`) })}</div>

                {/* Preview eye */}
                <button
                  type="button"
                  aria-label={tr("app.themeSelect.previewAria", { name: tr(`app.themes.${key}.name`) })}
                  data-testid={`theme-preview-${key}`}
                  onClick={(e) => { e.stopPropagation(); setPreviewKey(key); }}
                  style={{
                    position: "absolute", bottom: 10, right: 10, background: "transparent", border: "none",
                    cursor: "pointer", fontSize: 16, padding: 4, lineHeight: 1, opacity: 0.55,
                  }}
                >👁</button>

                {/* Selected check / lock */}
                {locked ? (
                  <span aria-hidden="true" data-testid={`theme-lock-${key}`} style={{ position: "absolute", top: 12, right: 12, fontSize: 16 }}>🔒</span>
                ) : (
                  <span style={{
                    position: "absolute", top: 12, right: 12, width: 24, height: 24, borderRadius: "50%",
                    background: t.colors.primary, color: t.colors.onPrimary || "#fff",
                    display: "grid", placeItems: "center", fontWeight: 800, fontSize: 13,
                    transform: isActive ? "scale(1)" : "scale(0)", transition: "transform 0.25s cubic-bezier(.2,1.6,.5,1)",
                    boxShadow: `0 4px 10px -3px ${t.colors.primary}`,
                  }}>✓</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Live CTA hint — mirrors create.jsx */}
      <div style={{
        textAlign: "center", marginTop: 22,
        fontFamily: "var(--font-body)", fontSize: 13,
        color: selected ? "var(--color-primary)" : "var(--color-textMuted)",
        transition: "color 0.2s",
      }}>
        {selectedName ? tr("app.themeSelect.openingStudio", { name: selectedName }) : tr("app.themeSelect.tapVibe")}
      </div>
      {previewKey && (
        <ThemePreviewModal
          themeKey={previewKey}
          locked={THEMES[previewKey]?.locked === true && !isPro}
          onClose={() => setPreviewKey(null)}
          onUse={() => {
            const k = previewKey;
            setPreviewKey(null);
            if (THEMES[k]?.locked === true && !isPro) {
              if (onLocked) onLocked(k);
            } else {
              pickVibe(k);
            }
          }}
        />
      )}
      </div>
    </div>
  );
}

// ── Theme Preview Modal ──
// Read-only look at a theme so creators can SEE every theme (incl. locked
// Pro themes) before committing. Renders a small themed sample using the
// theme's own palette + microcopy. CTA selects the theme (unlocked) or
// routes to the paywall (locked → onLocked, wired by parent onUse).
function ThemePreviewModal({ themeKey, locked, onClose, onUse }) {
  const { t: tr } = useTranslation();
  const t = THEMES[themeKey];
  const mc = useThemeMicrocopy(themeKey, t?.microcopy);
  if (!t) return null;
  const c = t.colors;
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: 340, borderRadius: 28, overflow: "hidden",
          background: c.bg, color: c.text,
          boxShadow: "0 30px 70px -20px rgba(0,0,0,0.6)",
          fontFamily: t.fonts?.body || "var(--font-body)",
        }}
      >
        <div style={{
          padding: "22px 22px 18px",
          background: `linear-gradient(145deg, ${c.primaryBright}, ${c.primary})`,
          color: c.onPrimary || "#fff",
        }}>
          <div style={{ fontSize: 30, marginBottom: 8 }}>{t.emoji}</div>
          <div style={{ fontFamily: t.fonts?.display || "var(--font-display)", fontSize: 22, fontWeight: 800, letterSpacing: "-0.01em" }}>
            {tr(`app.themes.${themeKey}.name`)} {locked && <span style={{ fontSize: 14 }}>🔒</span>}
          </div>
          <div style={{ fontSize: 13, opacity: 0.92, marginTop: 2 }}>{tr(`app.themes.${themeKey}.description`)}</div>
        </div>

        <div style={{ padding: 22, display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ background: c.surface, borderRadius: 16, padding: 16, boxShadow: `0 8px 20px -12px ${c.primary}` }}>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", color: c.textMuted, marginBottom: 6 }}>
              {mc.passwordLabel || "Secret password"}
            </div>
            <div style={{ fontFamily: t.fonts?.display || "var(--font-display)", fontSize: 16, fontWeight: 700, color: c.text }}>
              {tr("app.themePreview.sampleQuestion")}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1, background: c.surface, borderRadius: 12, padding: "12px 14px", color: c.textDim, fontSize: 13 }}>
              {mc.passwordPlaceholder || "type your answer…"}
            </div>
            <div style={{
              borderRadius: 12, padding: "12px 12px", fontWeight: 800, fontSize: 13,
              background: `linear-gradient(135deg, ${c.primary}, ${c.primaryBright})`,
              color: c.onPrimary || "#fff", whiteSpace: "nowrap",
            }}>
              {mc.checkAnswer || "Check"}
            </div>
          </div>
          <div style={{ fontFamily: "var(--font-body)", fontSize: 13, color: c.correct, fontWeight: 700 }}>
            {mc.correctAnswer || "Correct!"}
          </div>
          <div style={{ fontFamily: t.fonts?.display || "var(--font-display)", fontSize: 13, color: c.textMuted, fontStyle: "italic" }}>
            {mc.finaleSignoff || "— the end"}
          </div>
        </div>

        <div style={{ padding: "0 22px 22px", display: "flex", gap: 10 }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              flex: "0 0 auto", background: "transparent", border: "none", cursor: "pointer",
              padding: "13px 16px", borderRadius: 14, fontFamily: "var(--font-body)",
              fontWeight: 700, fontSize: 14, color: c.textMuted,
            }}
          >
            {tr("app.common.close")}
          </button>
          <button
            type="button"
            onClick={onUse}
            style={{
              flex: 1, border: "none", cursor: "pointer", padding: "13px 16px", borderRadius: 14,
              background: `linear-gradient(135deg, ${c.primary}, ${c.primaryBright})`,
              color: c.onPrimary || "#fff", fontFamily: "var(--font-display)",
              fontWeight: 800, fontSize: 14,
            }}
          >
            {locked ? tr("app.themePreview.unlockWithPro") : tr("app.themePreview.useTheme", { name: tr(`app.themes.${themeKey}.name`) })}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Quick Puzzle Prompts ──
const QUICK_PROMPTS = {
  romantic: [
    "What was the first thing I said to you?",
    "Where did we have our first date?",
    "What's my favorite thing about you?",
    "What song reminds you of us?",
    "What's our inside joke?",
    "What's the pet name I call you?",
    "What movie did we watch on our first night in?",
  ],
  funny: [
    "What's my most embarrassing moment?",
    "What food could I eat every day forever?",
    "What's the dumbest thing I've ever said?",
    "What's my hidden talent (that isn't actually a talent)?",
    "What would I do with a million dollars?",
    "What's my worst habit?",
    "If I were an animal, what would I be?",
  ],
  gaming: [
    "What's my all-time favorite game?",
    "What's my go-to character/class?",
    "What game did I rage-quit the hardest?",
    "What game did we first play together?",
    "What's the cheat code I always use?",
    "What boss took me the most attempts?",
  ],
  birthday: [
    "What's my favorite birthday memory?",
    "What cake flavor do I always pick?",
    "What was my best birthday gift ever?",
    "What's the one thing I always wish for?",
    "What's the party theme I'd choose every time?",
    "Who's always the first to call me on my birthday?",
  ],
  friendship: [
    "Where did we first meet?",
    "What's our favorite thing to do together?",
    "What's our inside joke?",
    "What song is 'our song'?",
    "What would we do on a perfect best-friend day?",
    "What's the funniest memory we share?",
  ],
  spooky: [
    "What's the scariest movie I've ever watched?",
    "What's my biggest fear?",
    "What's the creepiest thing that ever happened to me?",
    "What's my favorite Halloween costume?",
    "What would I do in a zombie apocalypse?",
    "What's the most haunted place I'd visit?",
  ],
  travel: [
    "What's my dream destination?",
    "What's the best trip we've taken together?",
    "What do I always forget to pack?",
    "What's my favorite travel snack?",
    "What country's food do I love the most?",
    "Window seat or aisle?",
  ],
  christmas: [
    "What's the best gift I ever gave you?",
    "What's my favorite Christmas movie?",
    "What do I always eat too much of at Christmas?",
    "Where did we spend our best Christmas?",
    "Which Christmas song do I sing all December?",
    "What's our holiday tradition?",
  ],
};

const QUICK_LEVEL_TITLES = {
  romantic: ["First Memories", "Only You Would Know"],
  funny: ["Easy Warm-Up", "The Real Test"],
  gaming: ["Level 1", "Level 2"],
  birthday: ["Unwrap This!", "One More Surprise"],
  friendship: ["Do You Remember?", "Bestie Trivia"],
  spooky: ["The First Trial", "The Dark Secret"],
  travel: ["First Stop", "Final Destination"],
  christmas: ["The First Package", "The Last Ribbon"],
};

// ── Quick Puzzle Creator ──
export function QuickPuzzleCreator({ theme, initialConfig, onPublished, onBack, onPrivacy, user, authReady, profile, onCapBlocked, onSaveDraft }) {
  const { t: tr } = useTranslation();
  const seededLevels = initialConfig?.levels || [];
  const t = THEMES[resolveThemeKey(theme)];
  const dark = isDarkTheme(theme);
  const [password, setPassword] = useState(initialConfig?.password ?? "");
  const [passwordHint, setPasswordHint] = useState(initialConfig?.passwordHint ?? "");
  const [questions, setQuestions] = useState([seededLevels[0]?.question || "", seededLevels[1]?.question || ""]);
  const [answers, setAnswers] = useState([seededLevels[0]?.answer || "", seededLevels[1]?.answer || ""]);
  const [hints, setHints] = useState([seededLevels[0]?.hint || "", seededLevels[1]?.hint || ""]);
  const [showHint, setShowHint] = useState([false, false]);
  const [finalMessage, setFinalMessage] = useState(initialConfig?.finalMessage ?? "");
  const [error, setError] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [copied, setCopied] = useState(false);

  const prompts = tr(`app.builder.quickPrompts.${theme}`, { returnObjects: true, defaultValue: tr("app.builder.quickPrompts.romantic", { returnObjects: true }) });
  const levelTitles = tr(`app.builder.quickLevelTitles.${theme}`, { returnObjects: true, defaultValue: tr("app.builder.quickLevelTitles.romantic", { returnObjects: true }) });

  const selectPrompt = (levelIdx, prompt) => {
    setQuestions((prev) => prev.map((q, i) => i === levelIdx ? prompt : q));
  };

  const handlePublish = async () => {
    if (!password.trim() || password.trim().length < 3) { setError(tr("app.builder.quick.validation.password")); return; }
    if (!questions[0].trim() || !answers[0].trim()) { setError(tr("app.builder.quick.validation.level1")); return; }
    if (!questions[1].trim() || !answers[1].trim()) { setError(tr("app.builder.quick.validation.level2")); return; }
    if (!finalMessage.trim()) { setError(tr("app.builder.quick.validation.finalMessage")); return; }
    setError("");
    // Phase 2 / W3.T6 — pre-flight cap predicate. Suppresses the Firestore
    // round-trip when getCapState(profile).capped === true (Free user at the
    // 5/week limit). The Rules remain authoritative; the post-write catch
    // below handles the rare race (e.g. snapshot lag, another tab publishing
    // mid-flow) by re-routing to the same blocker modal.
    const config = {
      version: 1,
      theme,
      title: tr("app.builder.common.quickTitleFallback", { name: tr(`app.themes.${theme}.name`) }),
      password: password.trim(),
      passwordHint: passwordHint.trim(),
      finalMessage,
      levels: [0, 1].map((i) => ({
        id: i + 1,
        title: levelTitles[i],
        question: questions[i],
        answer: answers[i],
        hint: hints[i] || "",
        celebrationType: "default",
        celebrationContent: "",
      })),
    };
    if (!canCreatePuzzle(profile)) {
      if (onSaveDraft) await onSaveDraft(config, "quick");
      return;
    }
    setPublishing(true);
    try {
      const result = await fbCreatePuzzle(config, { profile });
      setShareUrl(result.shareUrl);
      if (onPublished) onPublished(result.id, result.shareUrl, config);
    } catch (err) {
      // Phase 2 / W3.T6 — Rules-side cap denial (race or stale profile). Open
      // CapBlockerModal instead of the generic error band. Other errors fall
      // through to the existing inline error path.
      if (err && err.code === "permission-denied") {
        if (onCapBlocked) onCapBlocked();
        setPublishing(false);
        return;
      }
      // «Non ora» sulle regole sui contenuti (#65): annullato, non fallito.
      if (isRulesDeclined(err)) { setPublishing(false); return; }
      setError(err.message || tr("app.builder.quick.validation.publishFailed"));
      setPublishing(false);
    }
  };

  // Share view after publishing
  if (shareUrl) {
    const shareText = tr("app.builder.share.shareText", { url: shareUrl });
    const copy = async () => {
      try { await navigator.clipboard.writeText(shareUrl); } catch { /* fallback */ }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: "32px 24px", textAlign: "center" }}>
        <div style={{ width: "100%", maxWidth: 420 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, color: "var(--color-text)", margin: "0 0 8px" }}>
            {tr("app.builder.quick.live.title")}
          </h2>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: "0 0 24px" }}>
            {tr("app.builder.quick.live.subtitle")}
          </p>
          <Card gaming={dark} style={{ marginBottom: 16 }}>
            <div style={{
              fontFamily: "monospace", fontSize: 13, color: "var(--color-text)", wordBreak: "break-all",
              background: "var(--color-bgAlt)", padding: "14px 18px", borderRadius: 12, marginBottom: 12,
            }}>{shareUrl}</div>
            <Btn onClick={copy} style={{ width: "100%" }}>
              {copied ? tr("app.builder.quick.live.copied") : tr("app.builder.quick.live.copyLink")}
            </Btn>
          </Card>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginBottom: 24 }}>
            <button onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank")} style={{
              background: "#25D366", color: "white", border: "none", borderRadius: 999,
              padding: "12px 20px", cursor: "pointer", fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
            }}>{tr("app.builder.quick.live.whatsapp")}</button>
            <button onClick={() => { if (navigator.share) withExternalFlow(() => navigator.share({ title: "Puzzly", text: shareText, url: shareUrl })).catch(() => {}); else copy(); }} style={{
              background: "var(--color-primary)", color: "var(--color-onPrimary)", border: "none", borderRadius: 999,
              padding: "12px 20px", cursor: "pointer", fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
            }}>{tr("app.builder.quick.live.share")}</button>
          </div>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)" }}>
            {tr("app.builder.quick.live.channels")}
          </p>
          {onPrivacy && (
            <button onClick={onPrivacy} style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 500,
              color: "var(--color-textDim)", padding: "4px 8px", marginTop: 8,
              textDecoration: "underline", textUnderlineOffset: 3,
            }}>
              {tr("app.builder.quick.live.privacyPolicy")}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "32px 24px" }}>
      <div style={{ width: "100%", maxWidth: 520 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <button onClick={onBack} style={{
            background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--color-primary)",
          }}>←</button>
          <div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800, color: "var(--color-text)", margin: 0 }}>
              {tr("app.builder.quick.header")}
            </h2>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)", margin: "4px 0 0" }}>
              {tr("app.builder.quick.subtitle")}
            </p>
          </div>
        </div>

        {/* Password */}
        <Card gaming={dark} style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 8 }}>
            {tr("app.builder.quick.passwordLabel")}
          </label>
          <Input value={password} onChange={setPassword} placeholder={tr("app.builder.quick.passwordPlaceholder")} maxLength={40} />
          <label style={{ fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", margin: "16px 0 8px" }}>
            {tr("app.builder.common.passwordHintLabel")}
          </label>
          <Input value={passwordHint} onChange={setPasswordHint} placeholder={tr("app.builder.common.passwordHintPlaceholder")} maxLength={120} />
          <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 4 }}>{tr("app.builder.common.passwordHintHelper")}</div>
        </Card>

        {/* Levels */}
        {[0, 1].map((idx) => (
          <Card gaming={dark} key={idx} style={{ marginBottom: 16 }}>
            <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-primary)", marginBottom: 12 }}>
              {levelTitles[idx]}
            </div>

            {/* Prompt suggestions */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {prompts.slice(0, 4).map((p, pi) => (
                <button key={pi} onClick={() => selectPrompt(idx, p)} style={{
                  background: questions[idx] === p ? "var(--color-primary)" : "var(--color-bgAlt)",
                  color: questions[idx] === p ? "var(--color-onPrimary)" : "var(--color-textMuted)",
                  border: "none", borderRadius: 999, padding: "6px 14px", cursor: "pointer",
                  fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600,
                }}>
                  {p.length > 35 ? p.slice(0, 35) + "..." : p}
                </button>
              ))}
            </div>

            <label style={{ fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600, color: "var(--color-textMuted)", display: "block", marginBottom: 4 }}>{tr("app.builder.quick.questionLabel")}</label>
            <TextArea value={questions[idx]} onChange={(v) => setQuestions((prev) => prev.map((q, i) => i === idx ? v : q))} placeholder={tr("app.builder.quick.questionPlaceholder")} rows={2} maxLength={500} />

            <label style={{ fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600, color: "var(--color-textMuted)", display: "block", marginTop: 12, marginBottom: 4 }}>{tr("app.builder.quick.answerLabel")}</label>
            <Input value={answers[idx]} onChange={(v) => setAnswers((prev) => prev.map((a, i) => i === idx ? v : a))} placeholder={tr("app.builder.quick.answerPlaceholder")} maxLength={100} />

            {showHint[idx] ? (
              <div style={{ marginTop: 8 }}>
                <label style={{ fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600, color: "var(--color-textMuted)", display: "block", marginBottom: 4 }}>{tr("app.builder.quick.hintLabel")}</label>
                <Input value={hints[idx]} onChange={(v) => setHints((prev) => prev.map((h, i) => i === idx ? v : h))} placeholder={tr("app.builder.quick.hintPlaceholder")} maxLength={200} />
              </div>
            ) : (
              <button onClick={() => setShowHint((prev) => prev.map((h, i) => i === idx ? true : h))} style={{
                marginTop: 8, background: "none", border: "none", cursor: "pointer",
                fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-primary)", fontWeight: 600,
              }}>
                {tr("app.builder.quick.addHint")}
              </button>
            )}
          </Card>
        ))}

        {/* Final Message */}
        <Card gaming={dark} style={{ marginBottom: 16 }}>
          <label style={{ fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.08em", display: "block", marginBottom: 8 }}>
            {tr("app.builder.quick.surpriseMessageLabel")}
          </label>
          <TextArea value={finalMessage} onChange={(v) => setFinalMessage(v.slice(0, 2000))} placeholder={tr("app.builder.quick.surpriseMessagePlaceholder")} rows={3} maxLength={2000} />
        </Card>

        {error && <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-wrong)", textAlign: "center", marginBottom: 16 }}>{error}</div>}

        {/* Phase 3 / Task 5 — at-limit draft banner */}
        {!canCreatePuzzle(profile) && (
          <div data-testid="draft-banner" style={{
            background: "var(--color-bgAlt)", color: "var(--color-text)", borderRadius: 14,
            padding: "12px 14px", fontFamily: "var(--font-body)", fontSize: 14, marginBottom: 14,
          }}>
            {tr("app.builder.common.atLimitBanner")}
          </div>
        )}

        {/* Phase 2 / W3.T6 — cap usage indicator (renders null for Pro and
            hidden when profile is missing). Sits just above the publish CTA
            so the user sees how many credits remain before tapping Create. */}
        <CapUsageIndicator profile={profile} />

        <Btn onClick={handlePublish} disabled={publishing} style={{ width: "100%" }}>
          {publishing ? tr("app.builder.quick.creating") : (canCreatePuzzle(profile) ? tr("app.builder.quick.createPuzzle") : tr("app.common.saveAsDraft"))}
        </Btn>
      </div>
    </div>
  );
}

// ── Creator Wizard ──
export function CreatorWizard({ theme, initialTitle, initialConfig, onComplete, onBack, onPuzzlePublished, onPrivacy, user, authReady, profile, onCapBlocked, onMediaLocked, onSaveDraft, onShared }) {
  const { t: tr } = useTranslation();
  const t = THEMES[resolveThemeKey(theme)];
  const mc = useThemeMicrocopy(theme, t?.microcopy);
  const dark = isDarkTheme(theme);
  const [step, setStep] = useState(0); // 0=globals, 1=levels, 2=preview
  const [title, setTitle] = useState(initialConfig?.title ?? initialTitle ?? "");
  const [numLevels, setNumLevels] = useState(initialConfig?.levels?.length || 5);
  const [password, setPassword] = useState(initialConfig?.password ?? "");
  const [passwordHint, setPasswordHint] = useState(initialConfig?.passwordHint ?? "");
  const [finalMessage, setFinalMessage] = useState(initialConfig?.finalMessage ?? "");
  const [levels, setLevels] = useState(initialConfig?.levels ? initialConfig.levels.map((l) => ({ ...l })) : []);
  const [currentLevel, setCurrentLevel] = useState(0);
  const [error, setError] = useState("");
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);
  const [showCoach, setShowCoach] = useState(false);
  const [coachChecked, setCoachChecked] = useState(false);
  // Songs are uploaded to Storage under the id this puzzle will be published with,
  // so the id is reserved now (and kept across a resumed draft via config._puzzleId).
  const [reservedPuzzleId] = useState(() => initialConfig?._puzzleId || generatePuzzleId());
  const [songUploading, setSongUploading] = useState(false);

  // First-run coach-mark walkthrough — fires the first time the creator reaches
  // the level-building step. Persisted so it never auto-shows again. No replay.
  useEffect(() => {
    if (step !== 1 || coachChecked) return;
    setCoachChecked(true);
    coachmarksSeen().then((seen) => {
      if (!seen) setShowCoach(true);
    });
  }, [step, coachChecked]);

  const COACH_STEPS = useMemo(() => [
    {
      target: "question",
      title: tr("app.builder.coach.step1.title"),
      body: tr("app.builder.coach.step1.body"),
    },
    {
      target: "next",
      title: tr("app.builder.coach.step2.title"),
      body: tr("app.builder.coach.step2.body"),
    },
    {
      title: tr("app.builder.coach.step3.title"),
      body: tr("app.builder.coach.step3.body"),
    },
  ], [tr]);

  const dismissCoach = useCallback(() => {
    setShowCoach(false);
    markCoachmarksSeen();
  }, []);

  const resetCreator = () => {
    setStep(0);
    setTitle("");
    setNumLevels(5);
    setPassword("");
    setPasswordHint("");
    setFinalMessage("");
    setLevels([]);
    setCurrentLevel(0);
    setError("");
    setShowRestartConfirm(false);
  };

  // When entering step 1, create/resize levels array to match numLevels.
  // NOTE: when seeded from a draft (initialConfig), numLevels already equals
  // the seeded levels.length, so the early-return below preserves seeded
  // content instead of replacing it with blank levels.
  useEffect(() => {
    if (step === 1) {
      setLevels((prev) => {
        if (prev.length === numLevels) return prev;
        const newLevels = Array.from({ length: numLevels }, (_, i) => {
          // Preserve existing level data if available
          if (i < prev.length) return { ...prev[i], id: i + 1 };
          return {
            id: i + 1, title: "", question: "", answer: "", hint: "",
            celebrationType: "default", celebrationContent: "",
          };
        });
        return newLevels;
      });
      // Make sure currentLevel is within bounds
      setCurrentLevel((prev) => Math.min(prev, numLevels - 1));
    }
  }, [step, numLevels]);

  // Input length limits
  const MAX_TITLE = 100;
  const MAX_QUESTION = 500;
  const MAX_ANSWER = 100;
  const MAX_HINT = 200;
  const MAX_FINAL_MESSAGE = 2000;
  const MAX_LEVEL_TITLE = 100;

  const updateLevel = (idx, field, val) => {
    const limits = { title: MAX_LEVEL_TITLE, question: MAX_QUESTION, answer: MAX_ANSWER, hint: MAX_HINT };
    const limited = limits[field] ? val.slice(0, limits[field]) : val;
    setLevels((prev) => prev.map((l, i) => i === idx ? { ...l, [field]: limited } : l));
  };

  const validateGlobals = () => {
    if (!title.trim()) return tr("app.builder.validation.titleRequired");
    if (title.length > MAX_TITLE) return tr("app.builder.validation.titleTooLong", { max: MAX_TITLE });
    if (!password.trim() || password.trim().length < 3) return tr("app.builder.validation.passwordMin");
    if (!finalMessage.trim()) return tr("app.builder.validation.finalMessageRequired");
    if (finalMessage.length > MAX_FINAL_MESSAGE) return tr("app.builder.validation.finalMessageTooLong", { max: MAX_FINAL_MESSAGE });
    return null;
  };

  const validateLevel = (l) => {
    if (!l.title.trim()) return tr("app.builder.validation.levelTitleRequired");
    if (!l.question.trim()) return tr("app.builder.validation.levelQuestionRequired");
    if (l.question.length > MAX_QUESTION) return tr("app.builder.validation.levelQuestionTooLong", { max: MAX_QUESTION });
    if (!l.answer.trim()) return tr("app.builder.validation.levelAnswerRequired");
    if (l.answer.length > MAX_ANSWER) return tr("app.builder.validation.levelAnswerTooLong", { max: MAX_ANSWER });
    return null;
  };

  const labelStyle = {
    fontFamily: "var(--font-body)",
    fontSize: 12,
    fontWeight: 600,
    color: "var(--color-primary)",
    display: "block",
    marginBottom: 8,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  };

  // ── Theme-specific builder styles (must be before any returns for hooks rules) ──
  const themedBuilder = useMemo(() => {
    if (theme === "gaming") {
      return {
        wrapper: { background: "#0F172A", minHeight: "100vh", position: "relative" },
        scanline: true,
        card: {
          background: "#1E293B", borderRadius: 0, padding: 24,
          clipPath: "polygon(0 10px, 10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px))",
          border: "none", boxShadow: "none",
        },
        sectionCard: (accentColor) => ({
          background: "#1E293B", borderRadius: 0, padding: 24, borderLeft: `4px solid ${accentColor}`,
          clipPath: "polygon(0 10px, 10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px))",
        }),
        input: {
          background: "#0F172A", border: "1px solid rgba(71,85,105,0.3)", borderRadius: 0,
          fontFamily: "'JetBrains Mono', 'Be Vietnam Pro', monospace", fontSize: 14, color: "#F8FAFC",
        },
        label: {
          fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.15em",
        },
        badge: {
          background: "#3B82F6", color: "#fff", fontFamily: "'JetBrains Mono', monospace",
          fontWeight: 700, fontSize: 12, padding: "6px 14px", borderRadius: 0,
          clipPath: "polygon(0 4px, 4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px))",
          letterSpacing: "0.2em", textTransform: "uppercase",
        },
        heading: {
          fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800,
          textTransform: "uppercase", fontStyle: "italic", letterSpacing: "-0.02em",
        },
        primaryBtn: {
          borderRadius: 0,
          clipPath: "polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))",
          boxShadow: "0 0 15px rgba(168, 85, 247, 0.4)", fontFamily: "'JetBrains Mono', monospace",
          letterSpacing: "0.2em", textTransform: "uppercase",
        },
        accentColors: { title: "#A855F7", question: "#3B82F6", answer: "#22c55e", hint: "#475569" },
      };
    }
    if (theme === "funny") {
      // Funny "comic strip" (designer themes/funny.html): butter-yellow halftone
      // canvas, chunky 2.5px ink outlines, 3-4px ink offset drop shadows, hot-pink
      // CTA with text-shadow. Ink #1a0a14, pink #ff2e6e / deep #d11a55, cream #fffbed.
      return {
        wrapper: {
          background: "#ffe55c", minHeight: "100vh",
          backgroundImage: "radial-gradient(circle, rgba(26,10,20,.07) 1px, transparent 1.5px)",
          backgroundSize: "13px 13px",
        },
        scanline: false,
        card: {
          background: "#fffbed", borderRadius: 16, padding: 28,
          border: "2.5px solid #1a0a14", boxShadow: "5px 5px 0 #1a0a14",
        },
        sectionCard: () => ({
          background: "#fffbed", borderRadius: 14, padding: 18,
          border: "2.5px solid #1a0a14", boxShadow: "4px 4px 0 #1a0a14",
        }),
        input: {
          background: "#fffbed", border: "2.5px solid #1a0a14", borderRadius: 12,
          fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 16, fontWeight: 600, color: "#1a0a14",
          boxShadow: "3px 3px 0 #1a0a14",
        },
        label: {
          fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 800,
          textTransform: "uppercase", letterSpacing: "0.12em", color: "#3a1b28",
        },
        badge: {
          background: "#fff", color: "#d11a55", fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 800, fontSize: 13, padding: "6px 16px", borderRadius: 999,
          border: "2.5px solid #1a0a14", boxShadow: "3px 3px 0 #1a0a14", transform: "rotate(-2deg)",
        },
        heading: { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, color: "#1a0a14", letterSpacing: "-0.025em" },
        primaryBtn: {
          borderRadius: 12, background: "#ff2e6e", color: "#fff",
          border: "2.5px solid #1a0a14", boxShadow: "4px 4px 0 #1a0a14",
          fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 15,
          letterSpacing: "0.04em", textTransform: "uppercase", textShadow: "1px 1px 0 #d11a55",
        },
        accentColors: { title: "#d11a55", question: "#d11a55", answer: "#d11a55", hint: "#3a1b28" },
      };
    }
    if (theme === "birthday") {
      return {
        wrapper: { background: "#fcf5f7", minHeight: "100vh", position: "relative" },
        scanline: false,
        card: {
          background: "#ffffff", borderRadius: "1rem", padding: 32,
          border: "none", boxShadow: "0 8px 32px rgba(255,112,159,0.15)",
        },
        sectionCard: () => ({
          background: "#ffffff", borderRadius: "1rem", padding: 24,
          border: "none", boxShadow: "0 4px 16px rgba(49,46,48,0.06)",
        }),
        input: {
          background: "#ede6e9", border: "none", borderRadius: "1rem",
          fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 16, fontWeight: 500, color: "#312e30",
        },
        label: {
          fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.1em", color: "#a8275a",
        },
        badge: {
          background: "#ffc87d", color: "#644000", fontFamily: "'Be Vietnam Pro', sans-serif",
          fontWeight: 700, fontSize: 13, padding: "8px 20px", borderRadius: 999,
          letterSpacing: "0.08em", textTransform: "uppercase",
        },
        heading: { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, letterSpacing: "-0.02em" },
        primaryBtn: {
          borderRadius: "1rem", background: "linear-gradient(135deg, #a8275a, #ff709f)",
          boxShadow: "0 8px 32px rgba(255,112,159,0.3)", fontFamily: "'Plus Jakarta Sans', sans-serif",
          fontWeight: 700, fontSize: 17,
        },
        heroGradient: "linear-gradient(135deg, #a8275a 0%, #ff709f 100%)",
        accentColors: { title: "#7e5200", question: "#6046be", answer: "#a8275a", hint: "#7e5200" },
      };
    }
    // Romantic / default (also shared by friendship, spooky, travel — styled via theme CSS vars)
    return {
      wrapper: { background: "var(--color-bg)", minHeight: "100vh" },
      scanline: false,
      card: {
        background: "var(--color-surface)", borderRadius: "2rem", padding: 32,
        border: "none", boxShadow: "0 8px 32px rgba(49,46,48,0.05)",
      },
      sectionCard: () => ({
        background: "var(--color-surface)", borderRadius: "1rem", padding: 24,
        border: "none", boxShadow: "0 4px 16px rgba(49,46,48,0.06)",
      }),
      input: {
        background: "var(--color-bgAlt)", border: "none", borderRadius: "1rem",
        fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 16, color: "var(--color-text)",
      },
      label: {
        fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--color-primary)",
      },
      badge: {
        background: "var(--color-glow)", color: "var(--color-primary)", fontFamily: "'Be Vietnam Pro', sans-serif",
        fontWeight: 700, fontSize: 13, padding: "6px 16px", borderRadius: 999,
      },
      heading: { fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 },
      primaryBtn: { borderRadius: "1rem", boxShadow: "0 4px 20px var(--color-glow)" },
      accentColors: { title: "var(--color-primary)", question: "var(--color-accent)", answer: "var(--color-correct)", hint: "var(--color-secondary)" },
    };
  }, [theme]);

  // Restart confirmation modal (render function, not component — avoids re-mount issues)
  const renderRestartConfirmModal = () => showRestartConfirm ? (
    <div onClick={() => setShowRestartConfirm(false)} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(8px)",
      zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "var(--color-surface)", borderRadius: "1.5rem", padding: 32,
        maxWidth: 360, width: "100%", boxShadow: "0 24px 64px rgba(0,0,0,0.15)", textAlign: "center",
      }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔄</div>
        <h3 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700, color: "var(--color-text)", margin: "0 0 8px" }}>
          {tr("app.builder.restart.title")}
        </h3>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: "0 0 24px", lineHeight: 1.5 }}>
          {tr("app.builder.restart.body")}
        </p>
        <div style={{ display: "flex", gap: 12 }}>
          <Btn onClick={() => setShowRestartConfirm(false)} variant="secondary" style={{ flex: 1 }}>{tr("app.common.cancel")}</Btn>
          <Btn onClick={resetCreator} style={{ flex: 1, background: "var(--color-wrong)", boxShadow: "none" }}>{tr("app.common.reset")}</Btn>
        </div>
      </div>
    </div>
  ) : null;

  // Step 0: Global Settings
  if (step === 0) {
    // ── Gaming "build a quest" globals setup (designer themes/gaming.html) ──
    if (theme === "gaming") {
      const magenta = "#ff2eb8", cyan = "#00e5ff", coin = "#ffd84d", muted = "#6e6890";
      const pixel = "'JetBrains Mono', monospace";
      const glabel = {
        fontFamily: pixel, fontSize: 10, fontWeight: 700, textTransform: "uppercase",
        letterSpacing: "0.15em", color: cyan, marginBottom: 8, display: "block",
      };
      const ginput = {
        width: "100%", background: "#0a0a1f", border: "1px solid rgba(0,229,255,.35)", borderRadius: 0,
        padding: "13px 16px", fontFamily: pixel, fontSize: 13, color: "#fff", outline: "none", boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "#0a0a1f", position: "relative",
        }}>
          <div style={{
            position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.12,
            backgroundImage: "linear-gradient(rgba(255,255,255,0) 50%, rgba(0,0,0,0.4) 50%)", backgroundSize: "100% 4px",
          }} />
          <div style={{ width: "100%", maxWidth: 480, position: "relative", zIndex: 1 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: pixel, fontWeight: 700, fontSize: 10, letterSpacing: "0.15em", color: magenta, marginBottom: 6 }}>{tr("app.builder.setup.gaming.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, color: "#fff", letterSpacing: "-0.02em", margin: 0, textTransform: "uppercase", fontStyle: "italic" }}>{tr("app.builder.setup.gaming.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{
              background: "linear-gradient(135deg, #16143a, #221c4a)", padding: 24, position: "relative",
              clipPath: "polygon(0 10px, 10px 0, calc(100% - 10px) 0, 100% 10px, 100% calc(100% - 10px), calc(100% - 10px) 100%, 10px 100%, 0 calc(100% - 10px))",
              border: `1px solid ${magenta}`,
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={glabel}>{tr("app.builder.setup.gaming.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.gaming.titlePlaceholder")} style={ginput} />
                </div>
                <div>
                  <label style={glabel}>{tr("app.builder.setup.gaming.stagesLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={magenta} />
                </div>
                <div>
                  <label style={glabel}>{tr("app.builder.setup.gaming.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.gaming.passwordPlaceholder")} style={ginput} />
                  <div style={{ fontFamily: pixel, fontSize: 10, color: muted, marginTop: 6 }}>{tr("app.builder.setup.gaming.passwordHint")}</div>
                  <label style={{ ...glabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={ginput} />
                  <div style={{ fontFamily: pixel, fontSize: 10, color: muted, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={glabel}>{tr("app.builder.setup.gaming.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.gaming.finalPlaceholder")} rows={4}
                    style={{ ...ginput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: pixel, fontSize: 12, color: magenta, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 0, cursor: "pointer",
                  clipPath: "polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))",
                  background: magenta, color: "#fff", border: "none",
                  fontFamily: pixel, fontWeight: 700, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase",
                  boxShadow: "0 0 15px rgba(255,46,184,.5)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Birthday "make a birthday card" globals setup (designer themes/birthday.html) ──
    if (theme === "birthday") {
      const pink = "#ff5b94", pinkDeep = "#d63a72", muted = "#b88aa0", text = "#5a1a3a";
      const hand = "'Caveat', cursive";
      const blabel = {
        fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.12em", color: muted, marginBottom: 8, display: "block",
      };
      const binput = {
        width: "100%", background: "#fff5f8", border: "none", borderRadius: 14, padding: "12px 16px",
        fontFamily: "var(--font-body)", fontSize: 15, color: text, outline: "none",
        boxShadow: "inset 0 0 0 1px rgba(214,58,114,.15)", boxSizing: "border-box",
      };
      // Scattered confetti dots (designer mockup) — sky/mint/candle/pink specks.
      const dots = [
        { left: "8%", top: "16%", c: "#7fb8e8" }, { left: "88%", top: "12%", c: "#ffc94d" },
        { left: "14%", top: "54%", c: "#7ad8c4" }, { left: "92%", top: "48%", c: "#ff5b94" },
        { left: "6%", top: "82%", c: "#ffc94d" }, { left: "90%", top: "78%", c: "#7fb8e8" },
        { left: "80%", top: "30%", c: "#7ad8c4" }, { left: "20%", top: "34%", c: "#ff5b94" },
      ];
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "var(--color-bg)", position: "relative", overflow: "hidden",
        }}>
          {/* Festive confetti speck layer */}
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }}>
            {dots.map((d, i) => (
              <span key={i} style={{ position: "absolute", left: d.left, top: d.top, width: 9, height: 9, borderRadius: "50%", background: d.c, opacity: 0.65 }} />
            ))}
          </div>
          <div style={{ width: "100%", maxWidth: 480, position: "relative", zIndex: 1 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: hand, fontWeight: 700, fontSize: 20, color: pinkDeep, marginBottom: 2 }}>{tr("app.builder.setup.birthday.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, color: text, letterSpacing: "-0.02em", margin: 0 }}>{tr("app.builder.setup.birthday.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{ background: "#fff", borderRadius: 18, padding: 24, boxShadow: "0 8px 32px -10px rgba(214,58,114,.18)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={blabel}>{tr("app.builder.setup.birthday.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.birthday.titlePlaceholder")} style={binput} />
                </div>
                <div>
                  <label style={blabel}>{tr("app.builder.setup.birthday.candlesLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={pink} />
                </div>
                <div>
                  <label style={blabel}>{tr("app.builder.setup.birthday.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.birthday.passwordPlaceholder")} style={binput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.setup.birthday.passwordHint")}</div>
                  <label style={{ ...blabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={binput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={blabel}>{tr("app.builder.setup.birthday.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.birthday.finalPlaceholder")} rows={4}
                    style={{ ...binput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: hand, fontSize: 16, color: pinkDeep, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 14, cursor: "pointer",
                  background: `linear-gradient(135deg, ${pink}, ${pinkDeep})`, color: "#fff", border: "none",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, boxShadow: "0 8px 18px -6px rgba(255,91,148,.5)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Travel "issue a boarding pass" globals setup (designer themes/travel.html) ──
    if (theme === "travel") {
      const navy = "#1c2e52", navyDeep = "#0f1d3a", stamp = "#c0392b", muted = "#8c9bb5", text = "#1c2e52";
      const mono = "'JetBrains Mono', monospace";
      const tlabel = {
        fontFamily: mono, fontSize: 10, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.14em", color: muted, marginBottom: 8, display: "block",
      };
      const tinput = {
        width: "100%", background: "#fff", border: "none", borderRadius: 8, padding: "12px 14px",
        fontFamily: mono, fontSize: 13, color: text, outline: "none",
        boxShadow: "inset 0 0 0 1px rgba(28,46,82,.15)", boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "var(--color-bg)",
        }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 10, letterSpacing: "0.16em", textTransform: "uppercase", color: muted, marginBottom: 6 }}>{tr("app.builder.setup.travel.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, color: text, letterSpacing: "-0.02em", margin: 0 }}>{tr("app.builder.setup.travel.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{ background: "#fffaf0", borderRadius: 12, padding: 24, boxShadow: "0 8px 28px -10px rgba(28,46,82,.22)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={tlabel}>{tr("app.builder.setup.travel.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.travel.titlePlaceholder")} style={tinput} />
                </div>
                <div>
                  <label style={tlabel}>{tr("app.builder.setup.travel.legsLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={navy} />
                </div>
                <div>
                  <label style={tlabel}>{tr("app.builder.setup.travel.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.travel.passwordPlaceholder")} style={tinput} />
                  <div style={{ fontFamily: mono, fontSize: 10, color: muted, marginTop: 6 }}>{tr("app.builder.setup.travel.passwordHint")}</div>
                  <label style={{ ...tlabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={tinput} />
                  <div style={{ fontFamily: mono, fontSize: 10, color: muted, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={tlabel}>{tr("app.builder.setup.travel.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.travel.finalPlaceholder")} rows={4}
                    style={{ ...tinput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: mono, fontSize: 12, color: stamp, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 8, cursor: "pointer",
                  background: `linear-gradient(135deg, ${navy}, ${navyDeep})`, color: "#fffaf0", border: "none",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, boxShadow: "0 8px 18px -6px rgba(28,46,82,.5)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Friendship "pass them a note" globals setup (designer themes/friendship.html) ──
    if (theme === "friendship") {
      const sage = "#5c8c6b", sageDeep = "#3d6b4c", muted = "#94a99e", text = "#2c3e34";
      const flabel = {
        fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.12em", color: muted, marginBottom: 8, display: "block",
      };
      const finput = {
        width: "100%", background: "#f6faf2", border: "none", borderRadius: 12, padding: "12px 12px",
        fontFamily: "var(--font-body)", fontSize: 15, color: text, outline: "none",
        boxShadow: "inset 0 0 0 1px rgba(92,140,107,.2)", boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "var(--color-bg)",
          backgroundImage: "repeating-linear-gradient(var(--color-bg), var(--color-bg) 27px, rgba(92,140,107,.08) 28px)",
        }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: sage, marginBottom: 6 }}>{tr("app.builder.setup.friendship.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, color: text, letterSpacing: "-0.02em", margin: 0 }}>{tr("app.builder.setup.friendship.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{ position: "relative", background: "#fff", borderRadius: 14, padding: 24, boxShadow: "0 8px 28px -10px rgba(60,80,68,.22)" }}>
              <div style={{ position: "absolute", top: -8, left: 28, width: 60, height: 16, background: "rgba(245,197,66,.7)", transform: "rotate(-3deg)" }} />
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={flabel}>{tr("app.builder.setup.friendship.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.friendship.titlePlaceholder")} style={finput} />
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.setup.friendship.questionsLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={sage} />
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.setup.friendship.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.friendship.passwordPlaceholder")} style={finput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.setup.friendship.passwordHint")}</div>
                  <label style={{ ...flabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={finput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.setup.friendship.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.friendship.finalPlaceholder")} rows={4}
                    style={{ ...finput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "#c84d6e", textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 12, cursor: "pointer",
                  background: `linear-gradient(135deg, ${sage}, ${sageDeep})`, color: "#fff", border: "none",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, boxShadow: "0 8px 18px -6px rgba(92,140,107,.5)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Spooky "compose a haunting" globals setup (designer themes/spooky.html) ──
    if (theme === "spooky") {
      const accent = "#b88dd6", accentDeep = "#7d4ca8", muted = "#8b7aa4";
      const slabel = {
        fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 600, textTransform: "uppercase",
        letterSpacing: "0.12em", color: muted, marginBottom: 8, display: "block",
      };
      const sinput = {
        width: "100%", background: "var(--color-surface)", border: `1px solid ${accentDeep}`, borderRadius: 14,
        padding: "13px 16px", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-text)",
        outline: "none", boxShadow: "0 0 14px rgba(184,141,214,.12)", boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "var(--color-bg)", position: "relative", overflow: "hidden",
          backgroundImage: "radial-gradient(ellipse at 50% -10%, rgba(184,141,214,.22), transparent 55%), radial-gradient(circle at 85% 80%, rgba(125,76,168,.14), transparent 50%)",
        }}>
          <GhostDrift count={3} />
          <div style={{ width: "100%", maxWidth: 480, position: "relative", zIndex: 1 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: accent, marginBottom: 6 }}>{tr("app.builder.setup.spooky.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, fontStyle: "italic", color: "var(--color-text)", letterSpacing: "-0.02em", margin: 0 }}>{tr("app.builder.setup.spooky.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{ background: "var(--color-surface)", borderRadius: 18, padding: 24, boxShadow: "0 8px 32px -10px rgba(0,0,0,.5)" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={slabel}>{tr("app.builder.setup.spooky.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.spooky.titlePlaceholder")} style={sinput} />
                </div>
                <div>
                  <label style={slabel}>{tr("app.builder.setup.spooky.chaptersLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={accent} />
                </div>
                <div>
                  <label style={slabel}>{tr("app.builder.setup.spooky.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.spooky.passwordPlaceholder")} style={sinput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.setup.spooky.passwordHint")}</div>
                  <label style={{ ...slabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={sinput} />
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: muted, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={slabel}>{tr("app.builder.setup.spooky.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.spooky.finalPlaceholder")} rows={4}
                    style={{ ...sinput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 14, color: accent, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 14, cursor: "pointer",
                  background: `linear-gradient(135deg, ${accent}, ${accentDeep})`, color: "#1a1025", border: "none",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, boxShadow: "0 0 18px rgba(184,141,214,.4)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Romantic "love letter" globals setup (designer themes/romantic.html · "Write to someone you love") ──
    if (theme === "romantic") {
      const wine = "#4a0a1f", roseDeep = "#9c1a44", textSoft = "#6e1e3a", muted = "#a87a8a";
      const serif = "'Cormorant Garamond', serif";
      const rlabel = {
        fontFamily: "var(--font-display)", fontSize: 11, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: "0.12em", color: muted, marginBottom: 8, display: "block",
      };
      const rinput = {
        width: "100%", background: "#fff", borderRadius: 14, padding: "12px 16px",
        fontFamily: serif, fontStyle: "italic", fontSize: 16, color: wine, outline: "none", border: "none",
        boxShadow: "0 4px 12px -4px rgba(74,10,31,.2), inset 0 0 0 1px rgba(74,10,31,.1)", boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "var(--color-bg)",
          backgroundImage: "radial-gradient(circle at 12% 14%, rgba(200,52,95,.16), transparent 42%), radial-gradient(circle at 88% 84%, rgba(201,161,74,.12), transparent 46%), radial-gradient(circle at 50% 50%, rgba(244,184,196,.10), transparent 70%)",
        }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 17, color: roseDeep, marginBottom: 4 }}>{tr("app.builder.setup.romantic.eyebrow")}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, fontWeight: 800, color: wine, letterSpacing: "-0.025em", margin: 0 }}>
                {tr("app.builder.setup.romantic.headingPre")} <span style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, color: roseDeep }}>{tr("app.builder.setup.romantic.headingAccent")}</span>
              </h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{
              position: "relative", background: "#fff", borderRadius: 18, padding: 24,
              boxShadow: "0 12px 28px -10px rgba(74,10,31,.25)",
            }}>
              <div style={{ position: "absolute", top: -16, right: 18 }}><WaxSeal char="M" size={40} /></div>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={rlabel}>{tr("app.builder.setup.romantic.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.romantic.titlePlaceholder")} style={rinput} />
                </div>
                <div>
                  <label style={rlabel}>{tr("app.builder.setup.romantic.pagesLabel", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={roseDeep} />
                </div>
                <div>
                  <label style={rlabel}>{tr("app.builder.setup.romantic.passwordLabel")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.romantic.passwordPlaceholder")} style={rinput} />
                  <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 13, color: textSoft, marginTop: 6 }}>{tr("app.builder.setup.romantic.passwordHint")}</div>
                  <label style={{ ...rlabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={rinput} />
                  <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 13, color: textSoft, marginTop: 6 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={rlabel}>{tr("app.builder.setup.romantic.finalLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.romantic.finalPlaceholder")} rows={4}
                    style={{ ...rinput, resize: "vertical" }} />
                </div>
                {error && <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 14, color: roseDeep, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "14px", borderRadius: 14, cursor: "pointer",
                  background: "linear-gradient(135deg, #c8345f, #9c1a44)", color: "#fff", border: "none",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14,
                  boxShadow: "0 8px 18px -6px rgba(200,52,95,.5)",
                }}>{tr("app.common.continueArrow")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }
    // ── Funny "comic strip" globals setup (designer themes/funny.html · "Cook up a roast") ──
    if (theme === "funny") {
      const ink = "#1a0a14";
      const pink = "#ff2e6e";
      const pinkDeep = "#d11a55";
      const cream = "#fffbed";
      const flabel = {
        fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 11, fontWeight: 800,
        textTransform: "uppercase", letterSpacing: "0.12em", color: pinkDeep,
        marginBottom: 8, display: "block",
      };
      const finput = {
        width: "100%", background: cream, border: `2.5px solid ${ink}`, borderRadius: 12,
        padding: "14px 16px", fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 16, fontWeight: 600,
        color: ink, outline: "none", boxShadow: `3px 3px 0 ${ink}`, boxSizing: "border-box",
      };
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "40px 24px",
          background: "#ffe55c",
          backgroundImage: "radial-gradient(circle, rgba(26,10,20,.07) 1px, transparent 1.5px)",
          backgroundSize: "13px 13px",
        }}>
          <div style={{ width: "100%", maxWidth: 480 }}>
            <div style={{ marginBottom: 18 }}>
              <div style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 11,
                letterSpacing: "0.2em", textTransform: "uppercase", color: pinkDeep, marginBottom: 6,
              }}>{tr("app.builder.setup.funny.eyebrow")}</div>
              <h2 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 32, fontWeight: 800,
                color: ink, margin: 0, letterSpacing: "-0.025em",
              }}>{tr("app.builder.setup.funny.heading")}</h2>
            </div>
            <CapUsageIndicator profile={profile} />
            <div style={{
              background: cream, border: `2.5px solid ${ink}`, borderRadius: 16, padding: 24,
              boxShadow: `5px 5px 0 ${ink}`, position: "relative",
            }}>
              {/* Corner burst sticker */}
              <div style={{
                position: "absolute", top: -18, right: -10, zIndex: 3,
                width: 60, height: 60, display: "grid", placeItems: "center",
                background: pink, color: "#fff", transform: "rotate(-12deg)",
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 10, lineHeight: 1, textAlign: "center",
                textShadow: `1.5px 1.5px 0 ${ink}`, clipPath: BURST_CLIP,
              }}>{tr("app.builder.setup.funny.sticker").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
                <div>
                  <label style={flabel}>{tr("app.builder.common.titleLabel")}</label>
                  <input value={title} onChange={(e) => setTitle(e.target.value.slice(0, MAX_TITLE))}
                    placeholder={tr("app.builder.setup.funny.titlePlaceholder")} style={finput} />
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.common.levelsCount", { n: numLevels })}</label>
                  <LevelStepper value={numLevels} onChange={setNumLevels} accent={pink} />
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.common.passwordLabelPlain")}</label>
                  <input value={password} onChange={(e) => setPassword(e.target.value.slice(0, 40))}
                    placeholder={tr("app.builder.setup.funny.passwordPlaceholder")} style={finput} />
                  <div style={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, color: "#3a1b28", marginTop: 6, fontWeight: 500 }}>{tr("app.builder.common.passwordHint")}</div>
                  <label style={{ ...flabel, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                  <input value={passwordHint} onChange={(e) => setPasswordHint(e.target.value.slice(0, 120))}
                    placeholder={tr("app.builder.common.passwordHintPlaceholder")} style={finput} />
                  <div style={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 12, color: "#3a1b28", marginTop: 6, fontWeight: 500 }}>{tr("app.builder.common.passwordHintHelper")}</div>
                </div>
                <div>
                  <label style={flabel}>{tr("app.builder.common.finalMessageLabel")}</label>
                  <textarea value={finalMessage} onChange={(e) => setFinalMessage(e.target.value.slice(0, MAX_FINAL_MESSAGE))}
                    placeholder={tr("app.builder.setup.funny.finalPlaceholder")} rows={4}
                    style={{ ...finput, resize: "vertical", fontStyle: "italic" }} />
                </div>
                {error && <div style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 14, fontWeight: 700, color: pinkDeep, textAlign: "center" }}>{error}</div>}
                <button onClick={() => {
                  const err = validateGlobals();
                  if (err) { setError(err); return; }
                  setError(""); setStep(1);
                }} style={{
                  width: "100%", padding: "16px", borderRadius: 12, cursor: "pointer",
                  background: pink, color: "#fff", border: `2.5px solid ${ink}`, boxShadow: `4px 4px 0 ${ink}`,
                  fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 15,
                  letterSpacing: "0.04em", textTransform: "uppercase", textShadow: `1px 1px 0 ${pinkDeep}`,
                }}>{tr("app.builder.common.nextConfigureLevels")}</button>
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
        </div>
      );
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "100vh", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 480 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: "rgba(168,39,90,0.08)", fontSize: 24,
              }}>{t.emoji}</div>
              <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800, color: "var(--color-text)", margin: 0 }}>
                {tr("app.builder.setup.default.heading")}
              </h2>
            </div>
          </div>
          {/* Phase 2 / W3.T6 — cap usage indicator above the setup card.
              Renders null for Pro / missing-profile / non-capped Free users
              under threshold; only surfaces when the count is meaningful. */}
          <CapUsageIndicator profile={profile} />
          <Card gaming={dark}>
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              <div>
                <label style={labelStyle}>{tr("app.builder.common.titleLabel")}</label>
                <Input value={title} onChange={setTitle} placeholder={tr("app.builder.setup.default.titlePlaceholder")} maxLength={MAX_TITLE} />
              </div>
              <div>
                <label style={labelStyle}>{tr("app.builder.common.levelsCount", { n: numLevels })}</label>
                <LevelStepper value={numLevels} onChange={setNumLevels} accent="var(--color-primary)" />
              </div>
              <div>
                <label style={labelStyle}>{tr("app.builder.common.passwordLabelPlain")}</label>
                <Input value={password} onChange={setPassword} placeholder={tr("app.builder.setup.default.passwordPlaceholder")} maxLength={40} />
                <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 4 }}>{tr("app.builder.common.passwordHint")}</div>
                <label style={{ ...labelStyle, marginTop: 14 }}>{tr("app.builder.common.passwordHintLabel")}</label>
                <Input value={passwordHint} onChange={setPasswordHint} placeholder={tr("app.builder.common.passwordHintPlaceholder")} maxLength={120} />
                <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 4 }}>{tr("app.builder.common.passwordHintHelper")}</div>
              </div>
              <div>
                <label style={labelStyle}>{tr("app.builder.common.finalMessageLabel")}</label>
                <TextArea value={finalMessage} onChange={(v) => setFinalMessage(v.slice(0, MAX_FINAL_MESSAGE))} placeholder={tr("app.builder.setup.default.finalPlaceholder")} rows={4} maxLength={MAX_FINAL_MESSAGE} />
              </div>
              {error && <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-wrong)", textAlign: "center" }}>{error}</div>}
              <Btn onClick={() => {
                const err = validateGlobals();
                if (err) { setError(err); return; }
                setError("");
                setStep(1);
              }}>{tr("app.builder.common.nextConfigureLevels")}</Btn>
            </div>
          </Card>
        </div>
        {renderRestartConfirmModal()}
      </div>
    );
  }

  // ── Celebration type selector (shared across themes) ──
  // Phase 7 / 07-05: Photo, Video, Song are gated behind Pro (media gate).
  // celebIsPro is derived here (render function, not a component — CLAUDE.md rule).
  // Locked tiles open PaywallModal with trigger="media-gate" instead of selecting.
  const renderCelebrationSection = (l) => {
    const celebIsPro = !!(profile?.entitlements?.pro === true);
    const celebOptions = [
      { value: "default", label: tr("app.builder.celebration.optionDefault"), icon: "🎉" },
      { value: "message", label: tr("app.builder.celebration.optionMessage"), icon: "💬" },
      { value: "photo",   label: tr("app.builder.celebration.optionPhoto"),   icon: "📷", proOnly: true },
      { value: "video",   label: tr("app.builder.celebration.optionVideo"),   icon: "🎬", proOnly: true },
      { value: "song",    label: tr("app.builder.celebration.optionSong"),    icon: "🎵", proOnly: true },
    ];
    return (
    <div>
      <label style={{ ...labelStyle, ...themedBuilder.label, color: themedBuilder.label.color || labelStyle.color }}>
        {tr("app.builder.celebration.label")}
      </label>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
        {celebOptions.map((opt) => {
          const isLocked = opt.proOnly && !celebIsPro;
          const isSelected = (l.celebrationType || "default") === opt.value;
          return (
          <button
            key={opt.value}
            onClick={() => {
              if (isLocked) {
                onMediaLocked?.();
                return;
              }
              updateLevel(currentLevel, "celebrationType", opt.value);
              if (opt.value === "default") updateLevel(currentLevel, "celebrationContent", "");
            }}
            style={{
              position: "relative",
              padding: "8px 14px",
              borderRadius: theme === "gaming" ? 0 : 12,
              border: "none",
              background: isSelected
                ? "var(--color-primary)"
                : isLocked
                  ? `var(--color-bgAlt)`
                  : "var(--color-bgAlt)",
              color: isSelected
                ? "var(--color-onPrimary)"
                : "var(--color-textMuted)",
              cursor: isLocked ? "pointer" : "pointer",
              fontFamily: themedBuilder.label.fontFamily || "var(--font-body)",
              fontSize: 13,
              fontWeight: 600,
              transition: "all 0.2s",
              opacity: isLocked ? 0.6 : 1,
              ...(theme === "gaming" ? {
                clipPath: "polygon(0 3px, 3px 0, calc(100% - 3px) 0, 100% 3px, 100% calc(100% - 3px), calc(100% - 3px) 100%, 3px 100%, 0 calc(100% - 3px))",
              } : {}),
            }}
          >
            {opt.icon} {opt.label}
            {isLocked && (
              <span
                aria-label={tr("app.builder.celebration.lockedAria")}
                style={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  width: 18,
                  height: 18,
                  borderRadius: 9999,
                  background: "rgba(168,39,90,0.9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 10,
                  lineHeight: 1,
                  pointerEvents: "none",
                }}
              >🔒</span>
            )}
          </button>
          );
        })}
      </div>
      {l.celebrationType === "message" && (
        <TextArea
          value={l.celebrationContent || ""}
          onChange={(v) => updateLevel(currentLevel, "celebrationContent", v)}
          placeholder={tr("app.builder.celebration.messagePlaceholder")}
          rows={2}
          style={themedBuilder.input}
        />
      )}
      {l.celebrationType === "photo" && (
        <div>
          {safeMediaUrl(l.celebrationContent, "image") ? (
            <div style={{ position: "relative", marginBottom: 10 }}>
              <img src={safeMediaUrl(l.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")}
                style={{ width: "100%", borderRadius: theme === "gaming" ? 0 : 16, maxHeight: 200, objectFit: "cover" }} />
              <button onClick={() => updateLevel(currentLevel, "celebrationContent", "")}
                style={{
                  position: "absolute", top: 8, right: 8, width: 28, height: 28, borderRadius: theme === "gaming" ? 0 : "50%",
                  background: "rgba(0,0,0,0.6)", border: "none", color: "#fff", fontSize: 14, cursor: "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>✕</button>
            </div>
          ) : (
            <label style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "24px 16px", borderRadius: theme === "gaming" ? 0 : 16,
              border: `2px dashed ${theme === "gaming" ? "rgba(71,85,105,0.3)" : "var(--color-border)"}`,
              cursor: "pointer", background: "var(--color-bgAlt)",
              color: "var(--color-textMuted)", fontFamily: themedBuilder.label.fontFamily || "var(--font-body)", fontSize: 14,
            }}>
              <span style={{ fontSize: 32, marginBottom: 8 }}>📷</span>
              <span>{tr("app.builder.celebration.photoUpload")}</span>
              <span style={{ fontSize: 12, color: "var(--color-textDim)", marginTop: 4 }}>{tr("app.builder.celebration.photoUploadHint")}</span>
              <input type="file" accept="image/*" style={{ display: "none" }}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const compressed = await compressImage(file);
                  updateLevel(currentLevel, "celebrationContent", compressed);
                }} />
            </label>
          )}
        </div>
      )}
      {l.celebrationType === "video" && (
        <div>
          <Input value={l.celebrationContent || ""}
            onChange={(v) => updateLevel(currentLevel, "celebrationContent", v)}
            placeholder={tr("app.builder.celebration.videoPlaceholder")}
            style={themedBuilder.input} />
          {l.celebrationContent && getEmbedUrl(l.celebrationContent) && (
            <div style={{ marginTop: 10, borderRadius: theme === "gaming" ? 0 : 16, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480 }}>
              <iframe src={getEmbedUrl(l.celebrationContent)}
                style={{ width: "100%", height: "100%", border: "none" }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
            </div>
          )}
        </div>
      )}
      {l.celebrationType === "song" && (
        <div>
          {safeMediaUrl(l.celebrationContent, "audio") ? (
            <div style={{ position: "relative", marginBottom: 10 }}>
              <div style={{
                padding: "16px 20px", borderRadius: theme === "gaming" ? 0 : 16,
                background: "var(--color-bgAlt)", display: "flex", alignItems: "center", gap: 12,
              }}>
                <span style={{ fontSize: 28 }}>🎵</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--color-text)", marginBottom: 6 }}>
                    {tr("app.builder.celebration.songUploaded")}
                  </div>
                  <audio controls src={safeMediaUrl(l.celebrationContent, "audio")} style={{ width: "100%", height: 36 }} />
                </div>
                <button onClick={() => updateLevel(currentLevel, "celebrationContent", "")}
                  style={{
                    width: 28, height: 28, borderRadius: theme === "gaming" ? 0 : "50%",
                    background: "rgba(0,0,0,0.1)", border: "none", color: "var(--color-textMuted)", fontSize: 14, cursor: "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                  }}>✕</button>
              </div>
            </div>
          ) : (
            <label style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "24px 16px", borderRadius: theme === "gaming" ? 0 : 16,
              border: `2px dashed ${theme === "gaming" ? "rgba(71,85,105,0.3)" : "var(--color-border)"}`,
              cursor: "pointer", background: "var(--color-bgAlt)",
              color: "var(--color-textMuted)", fontFamily: themedBuilder.label.fontFamily || "var(--font-body)", fontSize: 14,
            }}>
              <span style={{ fontSize: 32, marginBottom: 8 }}>🎵</span>
              <span>{songUploading ? tr("app.builder.celebration.songUploading") : tr("app.builder.celebration.songUpload")}</span>
              <span style={{ fontSize: 12, color: "var(--color-textDim)", marginTop: 4 }}>{tr("app.builder.celebration.songUploadHint")}</span>
              <input type="file" accept="audio/*" style={{ display: "none" }} disabled={songUploading}
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  e.target.value = "";
                  if (!file) return;
                  // Uploaded to Storage; the level keeps the URL. Embedding the song as
                  // base64 made any real song exceed Firestore's 1 MiB document limit.
                  const levelIdx = currentLevel;
                  setSongUploading(true);
                  try {
                    const url = await uploadPuzzleMedia({ puzzleId: reservedPuzzleId, file, kind: "song" }, { profile });
                    updateLevel(levelIdx, "celebrationContent", url);
                  } catch (err) {
                    if (isRulesDeclined(err)) return; // «Non ora» sulle regole (#65)
                    alert(err && err.code === "too-large"
                      ? tr("app.builder.celebration.songTooLarge")
                      : tr("app.builder.celebration.songUploadFailed"));
                  } finally {
                    setSongUploading(false);
                  }
                }} />
            </label>
          )}
        </div>
      )}
    </div>
    );
  };

  // Step 1: Level Editor (themed)
  if (step === 1) {
    const lvl = levels[currentLevel];
    if (!lvl) return null;

    const themedLabel = (text, accentKey) => ({
      ...labelStyle,
      ...themedBuilder.label,
      color: themedBuilder.accentColors[accentKey] || themedBuilder.label.color || labelStyle.color,
      marginBottom: 8,
      display: "flex",
      alignItems: "center",
      gap: 6,
    });

    const startOverBtn = (
      <button onClick={() => setShowRestartConfirm(true)} style={{
        background: "transparent", border: "none", cursor: "pointer",
        fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 600,
        color: dark ? "rgba(255,51,85,0.7)" : "var(--color-wrong)",
        padding: "6px 12px", borderRadius: 8, marginBottom: 12, alignSelf: "center",
        transition: "opacity 0.2s", opacity: 0.7,
      }}
      onMouseEnter={(e) => e.target.style.opacity = "1"}
      onMouseLeave={(e) => e.target.style.opacity = "0.7"}
      >{tr("app.builder.common.startOver")}</button>
    );

    // Gaming-themed level builder
    if (theme === "gaming") {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: 24, ...themedBuilder.wrapper }}>
          {/* Scanline overlay */}
          <div style={{
            position: "fixed", inset: 0, pointerEvents: "none", zIndex: 100, opacity: 0.1,
            backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 50%)",
            backgroundSize: "100% 4px",
          }} />
          <div style={{ width: "100%", maxWidth: 480, position: "relative", zIndex: 1 }}>
            {/* PUZZLY OS header */}
            <div style={{
              textAlign: "center", marginBottom: 20, paddingBottom: 16,
              borderBottom: "1px solid rgba(71,85,105,0.2)",
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono', monospace", fontSize: 14, fontWeight: 700,
                color: "#94A3B8", letterSpacing: "0.3em", textTransform: "uppercase",
              }}>{tr("app.builder.level.gaming.os")}</span>
            </div>

            {currentLevel > 0 && (
              <BackLevelButton onClick={() => setCurrentLevel(currentLevel - 1)} accent="#A855F7" />
            )}
            {/* Stage badge */}
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <span style={themedBuilder.badge}>{tr("app.builder.level.gaming.stageBadge", { n: currentLevel + 1, total: numLevels })}</span>
            </div>
            <h2 style={{
              ...themedBuilder.heading, fontSize: 28, color: "#F8FAFC", margin: "0 0 4px", textAlign: "center",
            }}>
              {tr("app.builder.level.gaming.headingPre")} <span style={{ color: "#A855F7", textShadow: "0 0 8px rgba(168,85,247,0.8)" }}>{tr("app.builder.level.gaming.headingLevel", { nn: String(currentLevel + 1).padStart(2, "0") })}</span>
            </h2>
            <p style={{
              fontFamily: "'JetBrains Mono', monospace", color: "#3B82F6", fontSize: 13,
              textAlign: "center", marginBottom: 24,
            }}>{tr("app.builder.level.gaming.subhead")}</p>

            <ProgressBar current={currentLevel + 1} total={numLevels} />

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Level Identity */}
              <div style={themedBuilder.sectionCard("#A855F7")}>
                <label style={themedLabel("Level Identity", "title")}>{tr("app.builder.level.gaming.identityLabel")}</label>
                <Input value={lvl.title} onChange={(v) => updateLevel(currentLevel, "title", v)}
                  placeholder={tr("app.builder.level.gaming.identityPlaceholder")} maxLength={60} style={themedBuilder.input} />
              </div>

              {/* Main Objective */}
              <div style={themedBuilder.sectionCard("#3B82F6")}>
                <label style={themedLabel("Main Objective", "question")}>
                  <span style={{ fontSize: 18 }}>🎮</span> {tr("app.builder.level.gaming.objectiveLabel")}
                </label>
                <TextArea data-coach="question" value={lvl.question} onChange={(v) => updateLevel(currentLevel, "question", v)}
                  placeholder={tr("app.builder.level.gaming.objectivePlaceholder")} rows={3} style={themedBuilder.input} />
              </div>

              {/* Winning Input */}
              <div style={themedBuilder.sectionCard("#22c55e")}>
                <label style={{ ...themedLabel("Winning Input", "answer"), color: "#4ade80" }}>{tr("app.builder.level.gaming.winningLabel")}</label>
                <Input value={lvl.answer} onChange={(v) => updateLevel(currentLevel, "answer", v)}
                  placeholder={tr("app.builder.level.gaming.winningPlaceholder")} maxLength={80} style={themedBuilder.input} />
              </div>

              {/* Unlock Hint */}
              <div style={{
                ...themedBuilder.sectionCard("#475569"),
                background: "rgba(30,41,59,0.4)", border: "1px dashed rgba(71,85,105,0.3)",
              }}>
                <label style={{ ...themedLabel("Unlock Hint", "hint"), color: "#94A3B8" }}>
                  <span style={{ fontSize: 18 }}>🕹️</span> {tr("app.builder.level.gaming.hintLabel")}
                </label>
                <Input value={lvl.hint} onChange={(v) => updateLevel(currentLevel, "hint", v)}
                  placeholder={tr("app.builder.level.gaming.hintPlaceholder")} maxLength={100}
                  style={{ ...themedBuilder.input, fontSize: 12, fontStyle: "italic" }} />
              </div>

              {renderCelebrationSection(lvl)}

              {error && <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13, color: "#ff3355", textAlign: "center" }}>{error}</div>}

              {/* Finalize/Next Quest button */}
              <button data-coach="next" onClick={() => {
                const err = validateLevel(lvl);
                if (err) { setError(err); return; }
                setError("");
                if (currentLevel < numLevels - 1) {
                  setCurrentLevel(currentLevel + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }} style={{
                width: "100%", padding: "18px 24px",
                background: "#A855F7", color: "#fff",
                border: "none", cursor: "pointer",
                ...themedBuilder.primaryBtn,
              }}>
                {currentLevel < numLevels - 1 ? tr("app.builder.level.gaming.nextQuest") : tr("app.builder.level.gaming.finalizeStage")}
              </button>
              {startOverBtn}
            </div>
          </div>
          {renderRestartConfirmModal()}
          {showCoach && <Coachmarks steps={COACH_STEPS} onDone={dismissCoach} />}
        </div>
      );
    }

    // Funny-themed level builder
    if (theme === "funny") {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: 24, ...themedBuilder.wrapper }}>
          <div style={{ width: "100%", maxWidth: 520 }}>
            {currentLevel > 0 && (
              <BackLevelButton onClick={() => setCurrentLevel(currentLevel - 1)} label={tr("app.builder.level.funny.backLevel")} />
            )}
            {/* Joke counter badge */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
              <div style={themedBuilder.badge}>{tr("app.builder.level.funny.jokeBadge", { n: currentLevel + 1, total: numLevels })}</div>
            </div>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <h2 style={{
                ...themedBuilder.heading, fontSize: 32, margin: "0 0 8px", lineHeight: 1.05,
              }}>
                {tr("app.builder.level.funny.headingPre")}<br />
                <span style={{
                  color: "#d11a55",
                  textDecoration: "underline", textDecorationStyle: "wavy",
                  textDecorationColor: "#ff2e6e", textUnderlineOffset: 8,
                }}>{tr("app.builder.level.funny.headingAccent")}</span> 😂
              </h2>
              <p style={{ color: "#3a1b28", fontWeight: 600, lineHeight: 1.5 }}>{tr("app.builder.level.funny.subhead")}</p>
            </div>

            <ProgressBar current={currentLevel + 1} total={numLevels} />

            <div style={{ ...themedBuilder.card, position: "relative" }}>
              {/* Corner burst sticker */}
              <div style={{
                position: "absolute", top: -18, right: -10, zIndex: 3,
                width: 64, height: 64, display: "grid", placeItems: "center",
                background: "#ff2e6e", color: "#fff", transform: "rotate(-12deg)",
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 11, lineHeight: 1, textAlign: "center",
                textShadow: "1.5px 1.5px 0 #1a0a14", clipPath: BURST_CLIP,
              }}>{tr("app.builder.level.funny.sticker").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 20, position: "relative", zIndex: 1 }}>
                {/* Level Title */}
                <div>
                  <label style={themedLabel("Level Title", "title")}>
                    {tr("app.builder.level.funny.titleLabel")} <span style={{ fontSize: 18 }}>🤡</span>
                  </label>
                  <Input value={lvl.title} onChange={(v) => updateLevel(currentLevel, "title", v)}
                    placeholder={tr("app.builder.level.funny.titlePlaceholder")} maxLength={60}
                    style={themedBuilder.input} />
                </div>
                {/* The Riddle */}
                <div>
                  <label style={themedLabel("The Riddle/Question", "question")}>
                    {tr("app.builder.level.funny.questionLabel")} <span style={{ fontSize: 18 }}>🎈</span>
                  </label>
                  <TextArea data-coach="question" value={lvl.question} onChange={(v) => updateLevel(currentLevel, "question", v)}
                    placeholder={tr("app.builder.level.funny.questionPlaceholder")} rows={3}
                    style={themedBuilder.input} />
                </div>
                {/* The Punchline */}
                <div>
                  <label style={themedLabel("The Punchline/Answer", "answer")}>
                    {tr("app.builder.level.funny.answerLabel")} <span style={{ fontSize: 18 }}>🥁</span>
                  </label>
                  <Input value={lvl.answer} onChange={(v) => updateLevel(currentLevel, "answer", v)}
                    placeholder={tr("app.builder.level.funny.answerPlaceholder")} maxLength={80}
                    style={{ ...themedBuilder.input, border: "2.5px dashed #1a0a14" }} />
                </div>
                {/* Hilarious Hint */}
                <div>
                  <label style={themedLabel("Hilarious Hint", "hint")}>
                    {tr("app.builder.level.funny.hintLabel")} <span style={{ fontSize: 18 }}>💡</span>
                  </label>
                  <Input value={lvl.hint} onChange={(v) => updateLevel(currentLevel, "hint", v)}
                    placeholder={tr("app.builder.level.funny.hintPlaceholder")} maxLength={100}
                    style={themedBuilder.input} />
                </div>

                {renderCelebrationSection(lvl)}

                {error && <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "#d11a55", textAlign: "center" }}>{error}</div>}

                <button data-coach="next" onClick={() => {
                  const err = validateLevel(lvl);
                  if (err) { setError(err); return; }
                  setError("");
                  if (currentLevel < numLevels - 1) {
                    setCurrentLevel(currentLevel + 1);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  } else {
                    setStep(2);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }
                }} style={{
                  width: "100%", padding: "16px 24px", cursor: "pointer",
                  ...themedBuilder.primaryBtn,
                  transition: "transform 0.1s",
                }}>
                  {currentLevel < numLevels - 1 ? tr("app.builder.level.funny.addAnother") : tr("app.builder.level.funny.seeReview")}
                </button>
                {startOverBtn}
              </div>
            </div>

            {/* Decorative BOOM badge */}
            <div style={{
              display: "flex", justifyContent: "center", marginTop: 24, marginBottom: 16,
            }}>
              <div style={{
                background: "#fff", color: "#d11a55", fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 800, fontSize: 16, padding: "8px 20px", borderRadius: 999,
                border: "2.5px solid #1a0a14", transform: "rotate(-3deg)",
                boxShadow: "4px 4px 0 #1a0a14",
              }}>
                {tr("app.builder.level.funny.boom")}
              </div>
            </div>
          </div>
          {renderRestartConfirmModal()}
          {showCoach && <Coachmarks steps={COACH_STEPS} onDone={dismissCoach} />}
        </div>
      );
    }

    // Birthday-themed level builder
    if (theme === "birthday") {
      return (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: 24, ...themedBuilder.wrapper }}>
          <div style={{ width: "100%", maxWidth: 520 }}>
            {currentLevel > 0 && (
              <BackLevelButton onClick={() => setCurrentLevel(currentLevel - 1)} />
            )}
            {/* Festive Hero Header */}
            <div style={{
              background: themedBuilder.heroGradient, borderRadius: "1rem", padding: "24px 28px",
              color: "#ffeff1", marginBottom: 24, position: "relative", overflow: "hidden",
              boxShadow: "0 8px 32px rgba(255,112,159,0.15)",
            }}>
              <div style={{ position: "relative", zIndex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, opacity: 0.8 }}>
                  <span style={{ fontSize: 18 }}>🎉</span>
                  <span style={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em" }}>
                    {tr("app.builder.level.birthday.editionEyebrow")}
                  </span>
                </div>
                <h2 style={{ ...themedBuilder.heading, fontSize: 28, margin: 0, color: "#fff" }}>
                  {tr("app.builder.level.birthday.heading")}
                </h2>
                <p style={{ fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 14, opacity: 0.8, marginTop: 4 }}>
                  {tr("app.builder.level.birthday.subhead")}
                </p>
              </div>
              {/* Decorative cake icon */}
              <span style={{ position: "absolute", right: -8, bottom: -8, fontSize: 72, opacity: 0.1, transform: "rotate(12deg)" }}>🎂</span>
            </div>

            {/* Level badge */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
              <div style={themedBuilder.badge}>{tr("app.builder.common.levelOfTotal", { n: currentLevel + 1, total: numLevels })}</div>
            </div>

            <ProgressBar current={currentLevel + 1} total={numLevels} />

            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
              {/* Level Title */}
              <div style={themedBuilder.sectionCard()}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: "#ffc87d",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>📝</div>
                  <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "#312e30" }}>{tr("app.builder.level.birthday.titleLabel")}</label>
                </div>
                <Input value={lvl.title} onChange={(v) => updateLevel(currentLevel, "title", v)}
                  placeholder={tr("app.builder.level.birthday.titlePlaceholder")} maxLength={60} style={themedBuilder.input} />
              </div>

              {/* Question */}
              <div style={themedBuilder.sectionCard()}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: "#b6a4ff",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>❓</div>
                  <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "#312e30" }}>{tr("app.builder.level.birthday.questionLabel")}</label>
                </div>
                <TextArea data-coach="question" value={lvl.question} onChange={(v) => updateLevel(currentLevel, "question", v)}
                  placeholder={tr("app.builder.level.birthday.questionPlaceholder")} rows={3} style={themedBuilder.input} />
              </div>

              {/* Answer (festive dashed border) */}
              <div style={{
                ...themedBuilder.sectionCard(),
                background: "rgba(255,112,159,0.05)",
                border: "2px dashed rgba(168,39,90,0.2)",
                textAlign: "center",
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: "50%",
                  background: "linear-gradient(135deg, #a8275a, #ff709f)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  margin: "0 auto 12px", boxShadow: "0 8px 32px rgba(255,112,159,0.15)",
                  fontSize: 22, color: "#fff",
                }}>✨</div>
                <label style={{ ...themedBuilder.label, marginBottom: 12, color: "#a8275a", fontWeight: 700, fontSize: 15, display: "block" }}>{tr("app.builder.level.birthday.answerLabel")}</label>
                <Input value={lvl.answer} onChange={(v) => updateLevel(currentLevel, "answer", v)}
                  placeholder={tr("app.builder.level.birthday.answerPlaceholder")} maxLength={80}
                  style={{ ...themedBuilder.input, textAlign: "center", borderRadius: 999, fontWeight: 700, color: "#a8275a" }} />
              </div>

              {/* Hint */}
              <div style={themedBuilder.sectionCard()}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: "#ffc87d",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                  }}>💡</div>
                  <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "#312e30" }}>{tr("app.builder.level.birthday.hintLabel")}</label>
                </div>
                <p style={{ fontSize: 13, color: "#5e5a5c", marginBottom: 8, lineHeight: 1.5 }}>
                  {tr("app.builder.level.birthday.hintHelp")}
                </p>
                <Input value={lvl.hint} onChange={(v) => updateLevel(currentLevel, "hint", v)}
                  placeholder={tr("app.builder.level.birthday.hintPlaceholder")} maxLength={100} style={themedBuilder.input} />
              </div>

              {renderCelebrationSection(lvl)}

              {error && <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "#b41340", textAlign: "center" }}>{error}</div>}

              <button data-coach="next" onClick={() => {
                const err = validateLevel(lvl);
                if (err) { setError(err); return; }
                setError("");
                if (currentLevel < numLevels - 1) {
                  setCurrentLevel(currentLevel + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                } else {
                  setStep(2);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }} style={{
                width: "100%", padding: "18px 32px",
                background: themedBuilder.heroGradient,
                color: "#fff", border: "none", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                ...themedBuilder.primaryBtn,
                transition: "all 0.2s",
              }}>
                <span>🎉</span>
                {currentLevel < numLevels - 1 ? tr("app.builder.level.birthday.generateNext") : tr("app.builder.level.birthday.generateLast")}
              </button>
              {startOverBtn}
            </div>
          </div>
          {renderRestartConfirmModal()}
          {showCoach && <Coachmarks steps={COACH_STEPS} onDone={dismissCoach} />}
        </div>
      );
    }

    // Romantic (default) level builder
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: 24, ...themedBuilder.wrapper }}>
        <div style={{ width: "100%", maxWidth: 520 }}>
          {currentLevel > 0 && <BackLevelButton onClick={() => setCurrentLevel(currentLevel - 1)} />}

          {/* Theme Edition badge */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <span style={{
              ...themedBuilder.badge,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <span style={{ fontSize: 14 }}>{t?.emoji || "💕"}</span> {tr("app.builder.level.default.editionBadge", { name: tr(`app.themes.${theme}.name`) })}
            </span>
          </div>

          {/* Heading */}
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <h2 style={{
              ...themedBuilder.heading, fontSize: 30, color: "var(--color-text)", margin: "0 0 8px", lineHeight: 1.2,
            }}>
              {mc.builderHeadingPrefix || "Craft Your"}{" "}
              <span style={{ color: "var(--color-primary)", fontStyle: "italic" }}>{mc.builderHeadingAccent || "Puzzle"}</span>
            </h2>
            <p style={{
              fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: 0, lineHeight: 1.6,
            }}>
              {mc.builderSubhead || "Design a personalized puzzle level that tells your story. Every piece is a memory waiting to be discovered."}
            </p>
          </div>

          {/* Level progress badge */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{
              background: "var(--color-glow)",
              color: "var(--color-primary)", fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 13,
              padding: "6px 18px", borderRadius: 999,
            }}>
              {tr("app.builder.common.levelOfTotal", { n: currentLevel + 1, total: numLevels })}
            </div>
          </div>

          <ProgressBar current={currentLevel + 1} total={numLevels} />

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Level Title — own card */}
            <div style={themedBuilder.sectionCard()}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>{t?.emoji || "💕"}</span>
                <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "var(--color-text)" }}>{tr("app.builder.level.default.titleLabel")}</label>
              </div>
              <Input value={lvl.title} onChange={(v) => updateLevel(currentLevel, "title", v)}
                placeholder={tr("app.builder.level.default.titlePlaceholder")} maxLength={60} style={themedBuilder.input} />
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 6 }}>
                {tr("app.builder.level.default.titleHelp")}
              </div>
            </div>

            {/* The Question — gradient card */}
            <div style={{
              background: "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
              borderRadius: "1rem", padding: 24,
              boxShadow: "0 8px 32px var(--color-glow)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{
                  width: 32, height: 32, borderRadius: "50%", background: "rgba(255,255,255,0.25)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                }}>❓</span>
                <label style={{
                  fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "#fff", margin: 0,
                }}>{tr("app.builder.level.default.questionLabel")}</label>
              </div>
              <TextArea data-coach="question" value={lvl.question} onChange={(v) => updateLevel(currentLevel, "question", v)}
                placeholder={tr("app.builder.level.default.questionPlaceholder")} rows={3}
                style={{ ...themedBuilder.input, background: "rgba(255,255,255,0.85)", color: "#312e30", border: "none" }} />
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "rgba(255,255,255,0.7)", marginTop: 6 }}>
                {tr("app.builder.level.default.questionHelp")}
              </div>
            </div>

            {/* The Answer — own card with key icon */}
            <div style={themedBuilder.sectionCard()}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{
                  width: 32, height: 32, borderRadius: "50%", background: "var(--color-glow)",
                  display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16,
                }}>🔑</span>
                <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "var(--color-text)" }}>{tr("app.builder.level.default.answerLabel")}</label>
              </div>
              <Input value={lvl.answer} onChange={(v) => updateLevel(currentLevel, "answer", v)}
                placeholder={tr("app.builder.level.default.answerPlaceholder")} maxLength={80} style={themedBuilder.input} />
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 6 }}>
                {tr("app.builder.level.default.answerHelp")}
              </div>
            </div>

            {/* Secret Hint — own card with pencil icon */}
            <div style={{
              ...themedBuilder.sectionCard(),
              background: dark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.7)",
              border: "1px dashed var(--color-glow)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>✏️</span>
                <label style={{ ...themedBuilder.label, marginBottom: 0, fontWeight: 700, fontSize: 15, color: "var(--color-text)" }}>{tr("app.builder.level.default.hintLabel")}</label>
              </div>
              <Input value={lvl.hint} onChange={(v) => updateLevel(currentLevel, "hint", v)}
                placeholder={tr("app.builder.level.default.hintPlaceholder")} maxLength={100} style={themedBuilder.input} />
              <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-primary)", marginTop: 6, fontStyle: "italic" }}>
                {t?.emoji || "💗"} {tr("app.builder.level.default.hintHelp")}
              </div>
            </div>

            {renderCelebrationSection(lvl)}

            {error && <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-wrong)", textAlign: "center" }}>{error}</div>}

            <Btn data-coach="next" onClick={() => {
              const err = validateLevel(lvl);
              if (err) { setError(err); return; }
              setError("");
              if (currentLevel < numLevels - 1) {
                setCurrentLevel(currentLevel + 1);
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                setStep(2);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}>
              {currentLevel < numLevels - 1 ? tr("app.builder.level.default.saveNext") : tr("app.builder.level.default.savePreview")}
            </Btn>
            {startOverBtn}
          </div>
        </div>
        {renderRestartConfirmModal()}
        {showCoach && <Coachmarks steps={COACH_STEPS} onDone={dismissCoach} />}
      </div>
    );
  }

  // Build config object for steps 2 and 3
  const config = {
    version: 1,
    _puzzleId: reservedPuzzleId,
    theme,
    title,
    password: password.trim().toLowerCase(),
    passwordHint: passwordHint.trim(),
    finalMessage,
    levels: levels.map((l) => ({
      id: l.id,
      title: l.title.trim(),
      question: l.question.trim(),
      answer: l.answer.trim(),
      hint: l.hint.trim(),
      celebrationType: l.celebrationType || "default",
      celebrationContent: l.celebrationContent || "",
    })),
  };

  // Step 2: Preview
  if (step === 2) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: 24 }}>
        <div style={{ width: "100%", maxWidth: 520 }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>👀</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, color: "var(--color-primary)", margin: "0 0 6px" }}>
              {tr("app.builder.preview.heading")}
            </h2>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: 0 }}>
              {tr("app.builder.preview.subtitle")}
            </p>
          </div>

          {/* Puzzle overview card */}
          <Card gaming={dark} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
              <div style={{
                width: 44, height: 44, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
                fontSize: 20,
              }}>{t.emoji}</div>
              <div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 18, fontWeight: 700, color: "var(--color-text)" }}>{title}</div>
                <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textMuted)" }}>{tr("app.builder.preview.themeSummary", { name: tr(`app.themes.${theme}.name`), n: numLevels })}</div>
              </div>
            </div>
            <div style={{
              padding: "10px 14px", borderRadius: 12, background: "var(--color-bgAlt)",
              fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <span>🔒</span> {tr("app.builder.preview.passwordLabel")} <span style={{ letterSpacing: "0.1em" }}>{"•".repeat(password.length)}</span>
            </div>
          </Card>

          {/* Levels list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            {config.levels.map((lvl, i) => (
              <Card gaming={dark} key={i} style={{ padding: 20, borderRadius: "1rem" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 28, height: 28, borderRadius: "50%",
                        background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
                        color: "var(--color-onPrimary)", fontFamily: "var(--font-display)",
                        fontSize: 12, fontWeight: 700,
                      }}>{i + 1}</span>
                      <span style={{ fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700, color: "var(--color-text)" }}>
                        {lvl.title}
                      </span>
                    </div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)", marginBottom: 4, fontStyle: "italic" }}>
                      "{lvl.question}"
                    </div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-correct)", display: "flex", alignItems: "center", gap: 4 }}>
                      <span>✓</span> {lvl.answer}
                    </div>
                    {lvl.hint && (
                      <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
                        <span>💡</span> {lvl.hint}
                      </div>
                    )}
                  </div>
                  <button onClick={() => { setCurrentLevel(i); setStep(1); }} style={{
                    background: "var(--color-bgAlt)", border: "none", borderRadius: 8,
                    padding: "6px 10px", cursor: "pointer", fontFamily: "var(--font-body)",
                    fontSize: 11, fontWeight: 600, color: "var(--color-primary)",
                    flexShrink: 0, marginLeft: 8,
                  }}>{tr("app.builder.preview.edit")}</button>
                </div>
              </Card>
            ))}
          </div>

          {/* Final message preview */}
          <Card gaming={dark} style={{ marginBottom: 20, background: dark ? "var(--color-surface)" : "rgba(168,39,90,0.03)" }}>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 600, color: "var(--color-primary)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
              {tr("app.builder.common.finalMessageLabel")}
            </div>
            <div style={{
              fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 600,
              color: "var(--color-text)", fontStyle: "italic", lineHeight: 1.5,
            }}>
              "{finalMessage}"
            </div>
          </Card>

          {/* Phase 3 / Task 5 — at-limit draft banner (preview step) */}
          {!canCreatePuzzle(profile) && (
            <div data-testid="draft-banner" style={{
              background: "var(--color-bgAlt)", color: "var(--color-text)", borderRadius: 14,
              padding: "12px 14px", fontFamily: "var(--font-body)", fontSize: 14, marginBottom: 14,
            }}>
              {tr("app.builder.common.atLimitBanner")}
            </div>
          )}

          {/* Action buttons */}
          <div style={{ display: "flex", flexDirection: "column", gap: 10, alignItems: "center" }}>
            <Btn onClick={async () => {
              // Phase 3 / Task 5 — at-limit: save as draft instead of publishing.
              // When a slot IS free, the existing publish path (setStep(3)) is unchanged.
              if (!canCreatePuzzle(profile)) {
                if (onSaveDraft) await onSaveDraft(config, "full");
                return;
              }
              setStep(3);
            }} style={{ width: "100%" }}>{canCreatePuzzle(profile) ? tr("app.builder.preview.sharePuzzle") : tr("app.common.saveAsDraft")}</Btn>
            <Btn onClick={() => { setStep(1); setCurrentLevel(numLevels - 1); }} variant="secondary" small>{tr("app.builder.preview.backToEditing")}</Btn>
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Share
  return <ShareScreen config={config} onBack={() => setStep(2)} onNewPuzzle={() => {
    resetCreator();
    if (onComplete) onComplete("new");
  }} onPublished={onPuzzlePublished} onPrivacy={onPrivacy} profile={profile} onCapBlocked={onCapBlocked} onShared={onShared} />;
}

// ── Share Screen ──
export function ShareScreen({ config, onBack, onNewPuzzle, onPublished, onPrivacy, profile, onCapBlocked, onInviteSignIn, onShared }) {
  const { t: tr } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [publishedId, setPublishedId] = useState(null);
  const [publishing, setPublishing] = useState(true);
  const [error, setError] = useState(null);
  const dark = isDarkTheme(config.theme);
  const publishedRef = useRef(false);
  // #124: dopo il secondo puzzle condiviso si chiede la recensione, quando chi
  // crea torna nell'app (da WhatsApp, dai messaggi, dal foglio di condivisione).
  const recensioneDopo = useRef(false);
  const appAttiva = useRef(true);
  const timerRecensione = useRef(null);
  const programmaRecensione = () => {
    clearTimeout(timerRecensione.current);
    timerRecensione.current = setTimeout(() => {
      if (!recensioneDopo.current || !appAttiva.current || externalFlowActive() || propostaPushAperta()) return;
      recensioneDopo.current = false;
      chiediRecensione();
    }, 1200);
  };
  const notaCondivisione = () => (publishedId
    ? segnaCondivisione(publishedId).then((ora) => { if (ora) recensioneDopo.current = true; })
    : Promise.resolve());
  useEffect(() => {
    if (!isNativeApp()) return undefined;
    const ascolto = CapApp.addListener("appStateChange", ({ isActive }) => {
      appAttiva.current = isActive;
      if (isActive && recensioneDopo.current) programmaRecensione();
    });
    return () => {
      clearTimeout(timerRecensione.current);
      Promise.resolve(ascolto).then((h) => h && h.remove()).catch(() => {});
    };
  }, []);

  // Publish puzzle to Firebase on mount
  useEffect(() => {
    if (publishedRef.current) return;
    publishedRef.current = true;

    (async () => {
      try {
        setPublishing(true);
        // Phase 2 / W3.T6 — pass profile so commitPuzzleCapWrite can produce
        // the correct Path A (increment) vs Path B (window reset) update. The
        // upstream CreatorWizard already pre-flighted canCreatePuzzle before
        // routing here, so this catch is exclusively the race-condition path.
        const result = await fbCreatePuzzle(config, { profile });
        setShareUrl(result.shareUrl);
        setPublishedId(result.id);
        // Notify App to add to in-session puzzle list
        if (onPublished) onPublished(result.id, result.shareUrl, config);
      } catch (err) {
        // Phase 2 / W3.T6 — Rules-side denial reroutes to CapBlockerModal
        // (parent App pops it and navigates back). Other errors stay inline.
        if (err && err.code === "permission-denied") {
          if (onCapBlocked) onCapBlocked();
          setPublishing(false);
          return;
        }
        // «Non ora» sulle regole (#65): niente è stato pubblicato, quindi non si resta
        // su una schermata «pronto» col link vuoto — si torna al passo prima.
        if (isRulesDeclined(err)) { if (onBack) onBack(); return; }
        console.error("Failed to publish puzzle:", err);
        setError(tr("app.builder.share.publishError"));
      } finally {
        setPublishing(false);
      }
    })();
  }, [config]);

  // #126 — dopo che il link è partito, App può chiedere delle notifiche.
  const shared = () => { if (onShared) onShared(); };

  const copy = async () => {
    if (!shareUrl) return;
    notaCondivisione();
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
    shared();
  };

  const t = THEMES[resolveThemeKey(config.theme)];

  const shareText = tr("app.builder.share.shareText", { url: shareUrl });
  const shareWhatsApp = () => { notaCondivisione(); window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, "_blank"); shared(); };
  const shareSMS = () => { notaCondivisione(); window.open(`sms:?body=${encodeURIComponent(shareText)}`, "_blank"); shared(); };
  const shareNative = async () => {
    const segnata = notaCondivisione();
    if (!(await condividi({ title: tr("app.builder.share.shareTitle", { title: config.title }), text: shareText, url: shareUrl }))) return copy();
    shared();
    // Su iPhone il foglio non porta fuori dall'app: si chiede quando si chiude.
    await segnata;
    if (recensioneDopo.current) programmaRecensione();
  };

  // Surprise-type summary label for the "N levels · X surprise" line.
  const lastCeleb = config.levels?.[config.levels.length - 1]?.celebrationType;
  const surpriseLabel = config.finalMessage ? tr("app.builder.share.surprise.text")
    : lastCeleb === "photo" ? tr("app.builder.share.surprise.photo")
    : lastCeleb === "song" ? tr("app.builder.share.surprise.song")
    : lastCeleb === "video" ? tr("app.builder.share.surprise.video")
    : tr("app.builder.share.surprise.default");
  const shortLink = shareUrl ? shareUrl.replace(/^https?:\/\//, "") : "";
  // #129: il link sta su una riga. Se non ci sta si accorcia il dominio, mai il codice.
  const cancelletto = shortLink.indexOf("#");
  const linkDominio = cancelletto >= 0 ? shortLink.slice(0, cancelletto) : shortLink;
  const linkCodice = cancelletto >= 0 ? shortLink.slice(cancelletto) : "";

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh",
      padding: "44px 22px calc(120px + env(safe-area-inset-bottom, 0px))",
    }}>
      <div style={{ width: "100%", maxWidth: 460 }}>
        {/* Eyebrow + title header (designer "Send it to them") — carded */}
        <Card style={{ marginBottom: 18, padding: "20px 24px" }}>
          <div style={{
            fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "var(--color-textMuted)", marginBottom: 6,
          }}>{tr("app.builder.share.eyebrow")}</div>
          <h2 style={{
            fontFamily: "var(--font-display)", fontSize: 28, fontWeight: 800, letterSpacing: "-0.025em",
            lineHeight: 1.05, margin: 0, color: "var(--color-text)",
          }}>{publishing ? tr("app.builder.share.headingPublishing") : error ? tr("app.builder.share.headingError") : tr("app.builder.share.headingReady")}</h2>
        </Card>

        {publishing ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{
              width: 48, height: 48, borderRadius: "50%", margin: "0 auto 16px",
              border: "4px solid var(--color-bgAlt)", borderTopColor: "var(--color-primary)",
              animation: "spin 0.8s linear infinite",
            }} />
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)" }}>{tr("app.builder.share.saving")}</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "20px 0" }}>
            <Btn onClick={() => { setError(null); publishedRef.current = false; setPublishing(true); }}>{tr("app.builder.share.tryAgain")}</Btn>
            {onBack && <Btn onClick={onBack} variant="secondary" small style={{ marginTop: 10 }}>{tr("app.builder.share.backToPreview")}</Btn>}
          </div>
        ) : (
        <>
        {/* Card: tile + recipient + link + share — gives the screen a container */}
        <div style={{
          background: "var(--color-surface)", borderRadius: 28, padding: "32px 22px 26px",
          boxShadow: "0 18px 44px -22px rgba(0,0,0,0.28), inset 0 0 0 1px var(--color-border)",
          position: "relative", overflow: "hidden",
        }}>
          {/* soft themed glow top-corner */}
          <div aria-hidden="true" style={{
            position: "absolute", top: -50, right: -40, width: 160, height: 160, borderRadius: "50%",
            background: t.colors.primaryBright, opacity: 0.1, filter: "blur(36px)", pointerEvents: "none",
          }} />
          <div style={{ position: "relative", zIndex: 1 }}>
            {/* Big gradient app-tile + recipient */}
            <div style={{ textAlign: "center" }}>
              <div style={{
                margin: "0 auto", width: 110, height: 110, borderRadius: 28, position: "relative",
                background: `linear-gradient(135deg, ${t.colors.primaryBright}, ${t.colors.primary}, ${t.colors.secondary || t.colors.primary})`,
                display: "grid", placeItems: "center",
                boxShadow: `0 20px 40px -16px ${t.colors.primary}`,
              }}>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 44, color: "#fff", transform: "rotate(-6deg)" }}>P</span>
                <span style={{ position: "absolute", top: -8, right: -8, fontSize: 26, transform: "rotate(12deg)", filter: "drop-shadow(0 4px 8px rgba(0,0,0,.2))" }}>{t.emoji}</span>
              </div>
              <div style={{ marginTop: 18, fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "var(--color-text)", letterSpacing: "-0.02em" }}>
                {config.title || tr("app.builder.share.titleFallback")}
              </div>
              <div style={{ marginTop: 4, fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)" }}>
                {tr("app.builder.share.levelsLine", { n: config.levels.length, levelWord: config.levels.length === 1 ? tr("app.builder.share.levelSingular") : tr("app.builder.share.levelPlural"), surprise: surpriseLabel })}
              </div>
            </div>

            {/* Inline link + Copy */}
            <div style={{
              margin: "22px 0 0", background: "var(--color-bgAlt)", borderRadius: 14, padding: "10px 12px",
              display: "flex", alignItems: "center", gap: 8, boxShadow: "inset 0 0 0 1px var(--color-border)",
            }}>
              <span data-testid="share-link" style={{ flex: 1, minWidth: 0, display: "flex", textAlign: "left", fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-text)", whiteSpace: "nowrap", userSelect: "all" }}>
                <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{linkDominio}</span>
                <span style={{ flexShrink: 0 }}>{linkCodice}</span>
              </span>
              <button onClick={copy} style={{
                flexShrink: 0, padding: "8px 14px", borderRadius: 9, border: "none", cursor: "pointer",
                background: `linear-gradient(135deg, ${t.colors.primary}, ${t.colors.primaryBright})`, color: "#fff",
                fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12,
              }}>{copied ? tr("app.builder.share.copied") : tr("app.builder.share.copy")}</button>
            </div>

            {/* Share-app squares */}
            <div style={{ display: "flex", justifyContent: "center", gap: 14, marginTop: 18 }}>
              {[
                { c: "#25D366", l: "WA", fn: shareWhatsApp },
                { c: "#5e60d9", l: "iM", fn: shareSMS },
                { c: "#1a1a1a", l: "@", fn: shareNative },
                { c: t.colors.primary, l: "···", fn: shareNative },
              ].map((b, i) => (
                <button key={i} onClick={b.fn} aria-label={b.l} style={{
                  width: 46, height: 46, borderRadius: 13, border: "none", cursor: "pointer",
                  background: b.c, color: "#fff", display: "grid", placeItems: "center",
                  fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13,
                  boxShadow: "0 6px 14px -6px rgba(0,0,0,.3)",
                }}>{b.l}</button>
              ))}
            </div>
          </div>
        </div>

        {/* #110 — without an account the video reactions have nowhere to go */}
        {onInviteSignIn && (
          <button onClick={() => { recensioneDopo.current = false; onInviteSignIn(); }} style={{
            display: "block", width: "100%", marginTop: 18, padding: "12px 16px", borderRadius: 16, cursor: "pointer",
            background: "var(--color-surface)", border: "1.5px dashed var(--color-primary)",
            fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--color-primary)",
          }}>{tr("app.builder.share.reactionsInvite")}</button>
        )}

        {/* Secondary actions — carded */}
        <Card style={{ marginTop: 18, padding: "14px 16px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "center" }}>
          {onNewPuzzle && (
            <button onClick={onNewPuzzle} style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--color-primary)", padding: "8px 16px",
            }}>{tr("app.builder.share.createAnother")}</button>
          )}
          {onBack && (
            <button onClick={onBack} style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 500, color: "var(--color-textMuted)", padding: "4px 12px",
            }}>{tr("app.builder.share.backToPreviewLower")}</button>
          )}
          {onPrivacy && (
            <button onClick={onPrivacy} style={{
              background: "transparent", border: "none", cursor: "pointer",
              fontFamily: "var(--font-body)", fontSize: 12, fontWeight: 500, color: "var(--color-textDim)",
              padding: "4px 8px", textDecoration: "underline", textUnderlineOffset: 3,
            }}>{tr("app.builder.share.privacyPolicy")}</button>
          )}
        </div>
        </Card>
        </>
        )}
      </div>
    </div>
  );
}

// Un hash che indica un elemento della pagina è un'ancora — /privacy#account-deletion,
// il link per la cancellazione dato a Google Play (#66) — e non il link di un
// puzzle. Gli ID dei puzzle non coincidono mai con un id della pagina.
function ancoraInPagina() {
  const id = window.location.hash.slice(1);
  return id ? document.getElementById(id) : null;
}

// ── Privacy Policy ──
function PrivacyPolicy({ onBack }) {
  const { t: tr } = useTranslation();
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "32px 24px" }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        <Card style={{ marginBottom: 14, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--color-primary)" }}>←</button>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800, color: "var(--color-text)", margin: 0 }}>{tr("app.legal.privacyTitle")}</h2>
          </div>
        </Card>
        {LEGAL.privacy.map((section, i) => (
          <Card key={i} style={{ marginBottom: 12 }}>
            <h3 id={section.id} style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-primary)", margin: "0 0 8px" }}>{section.title}</h3>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", lineHeight: 1.6, margin: 0 }}>{section.body}</p>
          </Card>
        ))}
        <Card style={{ marginTop: 12, padding: "12px 18px" }}>
          <p style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", textAlign: "center", margin: 0 }}>
            {tr("app.legal.lastUpdatedPrivacy")}
          </p>
        </Card>
      </div>
    </div>
  );
}

// ── Generic legal page (Terms / Cookies) — same card chrome as PrivacyPolicy ──
function LegalPolicy({ heading, sections, footer, onBack }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minHeight: "100vh", padding: "32px 24px" }}>
      <div style={{ width: "100%", maxWidth: 560 }}>
        <Card style={{ marginBottom: 14, padding: "16px 20px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button onClick={onBack} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "var(--color-primary)" }}>←</button>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800, color: "var(--color-text)", margin: 0 }}>{heading}</h2>
          </div>
        </Card>
        {sections.map((section, i) => (
          <Card key={i} style={{ marginBottom: 12 }}>
            <h3 id={section.id} style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-primary)", margin: "0 0 8px" }}>{section.title}</h3>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", lineHeight: 1.6, margin: 0 }}>{section.body}</p>
          </Card>
        ))}
        {footer && (
          <Card style={{ marginTop: 12, padding: "12px 18px" }}>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textDim)", textAlign: "center", margin: 0 }}>{footer}</p>
          </Card>
        )}
      </div>
    </div>
  );
}

const TERMS_SECTIONS = LEGAL.terms;

const COOKIES_SECTIONS = LEGAL.cookies;

// ── Puzzle History ──
export function PuzzleHistory({ onBack, onCreateNew, onView, puzzles, onDelete, currentUid, draft, onEditDraft, onShareDraft, focusPuzzleId }) {
  const { t: tr } = useTranslation();
  const history = puzzles || [];
  const [copied, setCopied] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  // #126 — aperta da una notifica: si scorre fino a quel puzzle. Dopo un attimo,
  // perché App, a ogni cambio di schermata, riporta la pagina in cima dopo di noi.
  useEffect(() => {
    if (!focusPuzzleId) return undefined;
    const t = setTimeout(() => {
      try {
        const card = document.querySelector(`[data-puzzle-id="${focusPuzzleId}"]`);
        if (card) card.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* ignore */ }
    }, 450);
    return () => clearTimeout(t);
  }, [focusPuzzleId, history.length]);

  const copyLink = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = url;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const handleDelete = async (puzzle) => {
    setDeleteInFlight(true);
    setDeleteError(null);
    try {
      if (onDelete) await onDelete(puzzle);
      setConfirmDelete(null);
    } catch (err) {
      setDeleteError(tr("app.history.deleteError"));
    } finally {
      setDeleteInFlight(false);
    }
  };

  const formatDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return ""; }
  };

  const count = history.length;

  return (
    <div style={{
      maxWidth: 600, margin: "0 auto", minHeight: "100vh", position: "relative", overflow: "hidden",
      display: "flex", flexDirection: "column",
      padding: "0 0 calc(120px + env(safe-area-inset-bottom, 0px))",
      background: "linear-gradient(180deg, var(--cream) 0%, #fff 26%, var(--color-bg) 100%)",
    }}>
      {/* ambient blobs */}
      <AmbientBlob size={320} color="var(--butter)" style={{ position: "absolute", top: -140, left: -110, opacity: 0.45, zIndex: 0 }} />
      <AmbientBlob size={280} color="var(--lilac)" style={{ position: "absolute", top: 200, right: -140, opacity: 0.35, zIndex: 0 }} />

      {/* Header */}
      <div style={{ position: "relative", zIndex: 2, padding: "26px 22px 6px" }}>
        <Card style={{ padding: "20px 22px" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <h1 style={{
              fontFamily: "var(--font-display)", fontWeight: 800,
              fontSize: "clamp(34px, 10vw, 50px)", lineHeight: 0.92, letterSpacing: "-0.04em",
              margin: 0, color: "var(--color-text)",
            }}>
              {tr("app.history.titlePre")} <span style={{ color: "var(--color-primary)", fontStyle: "italic" }}>{tr("app.history.titleAccent")}</span>
            </h1>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-textMuted)", margin: "12px 0 0" }}>
              {count === 0 ? tr("app.history.emptySubtitle") : tr("app.history.countSubtitle", { count, puzzleWord: count === 1 ? tr("app.history.puzzleSingular") : tr("app.history.puzzlePlural") })}
            </p>
          </div>
          <span style={{
            flexShrink: 0, marginTop: 8, minWidth: 34, height: 34, padding: "0 12px", borderRadius: 999,
            background: "var(--color-surface)", color: "var(--color-primary)",
            display: "grid", placeItems: "center", boxShadow: "0 6px 18px -10px rgba(0,0,0,0.25)",
            fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15,
          }}>{count}</span>
        </div>
        </Card>
      </div>

      {/* List */}
      <div style={{ position: "relative", zIndex: 2, flex: 1, padding: "22px 22px 8px", display: "flex", flexDirection: "column", gap: 14 }}>
        {draft && (
          <div key="__draft__" data-testid="draft-card" style={{
            position: "relative", background: "var(--color-surface)", borderRadius: 24, padding: 18,
            boxShadow: "0 18px 40px -22px rgba(0,0,0,0.28)", display: "flex", flexDirection: "column", gap: 16,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ width: 58, height: 58, borderRadius: 18, flexShrink: 0,
                background: `${(THEMES[draft.theme] || THEMES.romantic).colors.primaryBright}2e`,
                display: "grid", placeItems: "center", fontSize: 30, transform: "rotate(-2deg)" }}>
                {(THEMES[draft.theme] || THEMES.romantic).emoji}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 21, color: "var(--color-text)",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{draft.title || tr("app.history.draftTitleFallback")}</div>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 11.5, color: "#fff",
                  background: "var(--color-primary)", padding: "4px 10px", borderRadius: 999, marginTop: 6, display: "inline-block" }}>{tr("app.history.draftBadge")}</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 9 }}>
              <button onClick={() => onEditDraft && onEditDraft(draft)} style={{
                flex: 1, border: "none", cursor: "pointer", background: "var(--color-bgAlt)", color: "var(--color-primary)",
                fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, padding: "12px 16px", borderRadius: 14 }}>{tr("app.history.editDraft")}</button>
              <button onClick={() => onShareDraft && onShareDraft(draft)} style={{
                flex: 1, border: "none", cursor: "pointer", color: "#fff",
                background: "linear-gradient(160deg, var(--color-primaryBright), var(--color-primary))",
                fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, padding: "12px 16px", borderRadius: 14 }}>{tr("app.history.shareDraft")}</button>
            </div>
          </div>
        )}
        {count === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 24px", background: "var(--color-surface)", borderRadius: 24, boxShadow: "0 6px 18px -10px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>🧩</div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: "var(--color-text)" }}>{tr("app.history.emptyTitle")}</div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", marginTop: 4 }}>{tr("app.history.emptyBody")}</div>
          </div>
        ) : history.map((puzzle) => {
          const t = THEMES[puzzle.theme] || THEMES.romantic;
          const tint = `${t.colors.primaryBright}2e`;
          const isCopied = copied === puzzle.id;
          const inEvidenza = !!focusPuzzleId && puzzle.puzzleId === focusPuzzleId;
          return (
            <div key={puzzle.id} data-puzzle-id={puzzle.puzzleId} data-focused={inEvidenza ? "true" : undefined} style={{
              position: "relative", background: "var(--color-surface)", borderRadius: 24, padding: 18,
              boxShadow: inEvidenza
                ? "0 0 0 3px var(--color-primary), 0 18px 40px -22px rgba(0,0,0,0.28)"
                : "0 18px 40px -22px rgba(0,0,0,0.28)",
              display: "flex", flexDirection: "column", gap: 16,
            }}>
              {/* top row: glyph tile + meta */}
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 58, height: 58, borderRadius: 18, flexShrink: 0, background: tint,
                  display: "grid", placeItems: "center", fontSize: 30,
                  transform: "rotate(-2deg)", boxShadow: "inset 0 0 0 1px rgba(0,0,0,.04)",
                }}>{t.emoji}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 21, letterSpacing: "-0.02em", color: "var(--color-text)", lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{puzzle.title}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                    <span style={{
                      fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 11.5,
                      color: "var(--color-primary)", background: tint, padding: "4px 10px", borderRadius: 999,
                    }}>{tr(`app.themes.${puzzle.theme}.name`, { defaultValue: t.name })}</span>
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)" }}>{tr("app.history.levelsCount", { count: puzzle.levelCount })}</span>
                    <span style={{ width: 3, height: 3, borderRadius: "50%", background: "var(--color-textMuted)", opacity: 0.5 }} />
                    <span style={{ fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-textMuted)" }}>{formatDate(puzzle.createdAt)}</span>
                  </div>
                </div>
              </div>

              {/* actions */}
              <div style={{ display: "flex", gap: 9 }}>
                <button onClick={() => onView && onView(puzzle.shareUrl)} style={{
                  flex: "0 0 auto", display: "inline-flex", alignItems: "center", gap: 7, border: "none", cursor: "pointer",
                  background: "var(--color-bgAlt)", color: "var(--color-primary)",
                  fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, padding: "12px 16px", borderRadius: 14,
                }}>
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></svg>
                  {tr("app.history.view")}
                </button>
                <button onClick={() => copyLink(puzzle.shareUrl, puzzle.id)} style={{
                  flex: 1, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, border: "none", cursor: "pointer",
                  background: isCopied ? "var(--color-correct)" : "linear-gradient(160deg, var(--color-primaryBright), var(--color-primary))", color: "#fff",
                  fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, padding: "12px 16px", borderRadius: 14,
                  boxShadow: "0 10px 22px -10px var(--color-primary)", whiteSpace: "nowrap",
                }}>
                  {isCopied
                    ? <><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>{tr("app.history.copied")}</>
                    : <><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2.5" /><path d="M5 15a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2" /></svg>{tr("app.history.copyLink")}</>}
                </button>
                {puzzle.ownerUid && puzzle.ownerUid === currentUid ? (
                  <button
                    onClick={() => { setConfirmDelete(puzzle.id); setDeleteError(null); }}
                    aria-label={tr("app.history.deleteAria")}
                    style={{
                      flex: "0 0 auto", width: 46, display: "grid", placeItems: "center", border: "none", cursor: "pointer",
                      background: "var(--color-bgAlt)", color: "var(--color-textMuted)", borderRadius: 14,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" /></svg>
                  </button>
                ) : (
                  <button
                    disabled
                    aria-disabled="true"
                    aria-label={tr("app.history.deleteAria")}
                    title={puzzle.ownerUid
                      ? tr("app.history.deleteDisabledOther")
                      : tr("app.history.deleteDisabledLegacy")}
                    style={{
                      flex: "0 0 auto", width: 46, display: "grid", placeItems: "center", border: "none", cursor: "not-allowed",
                      background: "var(--color-bgAlt)", color: "var(--color-textMuted)", borderRadius: 14, opacity: 0.35,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6" /></svg>
                  </button>
                )}
              </div>

              {/* Delete confirmation inline */}
              {confirmDelete === puzzle.id && (
                <div
                  role="alert"
                  style={{
                    padding: "12px 14px", borderRadius: 12, background: "rgba(180,19,64,0.06)",
                    display: "flex", flexDirection: "column", gap: 8,
                  }}
                >
                  <div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 600, color: "var(--color-wrong)", marginBottom: 2 }}>
                      {tr("app.history.confirmDeleteTitle")}
                    </div>
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 13, fontWeight: 400, color: "var(--color-textMuted)", lineHeight: 1.5 }}>
                      {tr("app.history.confirmDeleteBody")}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      onClick={() => { setConfirmDelete(null); setDeleteError(null); }}
                      disabled={deleteInFlight}
                      style={{
                        padding: "5px 12px", borderRadius: 6, background: "var(--color-bgAlt)", border: "none",
                        cursor: deleteInFlight ? "not-allowed" : "pointer",
                        fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-textMuted)",
                      }}
                    >{tr("app.history.keepIt")}</button>
                    <button
                      onClick={() => handleDelete(puzzle)}
                      disabled={deleteInFlight}
                      aria-busy={deleteInFlight}
                      style={{
                        padding: "5px 12px", borderRadius: 6, background: "var(--color-wrong)", border: "none",
                        cursor: deleteInFlight ? "not-allowed" : "pointer",
                        fontFamily: "var(--font-body)", fontSize: 12, color: "#fff", fontWeight: 600,
                        opacity: deleteInFlight ? 0.7 : 1,
                      }}
                    >{deleteInFlight ? tr("app.history.deleting") : tr("app.history.confirmDelete")}</button>
                  </div>
                  {deleteError && (
                    <div style={{ fontFamily: "var(--font-body)", fontSize: 12, color: "var(--color-wrong)" }}>
                      {deleteError}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Create new puzzle — dashed CTA */}
        <button onClick={onCreateNew} style={{
          marginTop: 6, padding: 19, borderRadius: 20, cursor: "pointer",
          border: "2px dashed var(--color-border)", background: "rgba(255,255,255,.55)", color: "var(--color-primary)",
          fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, letterSpacing: "-0.01em",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 10, whiteSpace: "nowrap",
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14" /></svg>
          {tr("app.history.createNew")}
        </button>
      </div>
    </div>
  );
}

// ── Player: Password Gate ──
function PuzzlePreview({ config, onContinue, onBack }) {
  const { t: tr } = useTranslation();
  const [visible, setVisible] = useState(false);
  const dark = isDarkTheme(config.theme);
  const t = THEMES[config.theme] || THEMES.romantic;
  const levelCount = config.levels?.length || 0;
  useEffect(() => { setTimeout(() => setVisible(true), 80); }, []);

  useEffect(() => {
    logger.info("Puzzle preview shown", {
      puzzleId: config._id || "legacy",
      theme: config.theme,
      levelCount,
    });
  }, []);

  const tint = `${t.colors.primaryBright}33`;

  return (
    <div style={{
      maxWidth: 600, margin: "0 auto", minHeight: "100vh", position: "relative", overflow: "hidden",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      padding: "32px 24px calc(40px + env(safe-area-inset-bottom, 0px))", textAlign: "center",
      // Dark themes set light text: a cream-to-white top made the title unreadable.
      background: dark
        ? "linear-gradient(180deg, var(--color-bgAlt) 0%, var(--color-bg) 55%, var(--color-bg) 100%)"
        : "linear-gradient(180deg, var(--cream) 0%, #fff 24%, var(--color-bg) 100%)",
      opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(12px)",
      transition: "opacity 0.6s ease, transform 0.6s ease",
    }}>
      {onBack && (
        <button
          type="button"
          data-testid="preview-back"
          aria-label={tr("app.creator.nav.back")}
          onClick={onBack}
          style={{
            position: "absolute", top: 16, left: 16, zIndex: 3, width: 44, height: 44, borderRadius: 999, border: "none",
            background: "var(--color-surface)", color: "var(--color-text)", cursor: "pointer", display: "grid", placeItems: "center",
            boxShadow: "0 8px 20px -10px rgba(0,0,0,0.35)",
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 5l-7 7 7 7" /></svg>
        </button>
      )}
      {/* ambient blobs */}
      <AmbientBlob size={340} color={tint} style={{ position: "absolute", top: -120, left: -100, opacity: 0.7, zIndex: 0 }} />
      <AmbientBlob size={300} color="var(--lilac)" style={{ position: "absolute", bottom: -120, right: -120, opacity: 0.4, zIndex: 0 }} />

      <div style={{ position: "relative", zIndex: 2, width: "100%", maxWidth: 440, display: "flex", flexDirection: "column", alignItems: "center" }}>
        <div style={{
          fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13,
          letterSpacing: "0.2em", textTransform: "uppercase",
          color: "var(--color-primary)", marginBottom: 30, whiteSpace: "nowrap",
        }}>{tr("app.play.preview.received")}</div>

        {/* Avatar with pulsing rings */}
        <div style={{ position: "relative", width: 168, height: 168, display: "grid", placeItems: "center", marginBottom: 30 }}>
          {[0, 1, 2].map((i) => (
            <span key={i} style={{
              position: "absolute", inset: 0, borderRadius: "50%",
              border: "2px solid var(--color-primaryBright)", opacity: 0,
              animation: "pulse-ring 2.6s ease-out infinite", animationDelay: `${i * 0.7}s`,
            }} />
          ))}
          <div style={{
            width: 132, height: 132, borderRadius: "50%",
            background: `radial-gradient(circle at 32% 28%, var(--color-primaryBright), var(--color-primary) 62%, var(--color-primary))`,
            display: "grid", placeItems: "center", fontSize: 60,
            boxShadow: "inset 0 -6px 14px rgba(0,0,0,.18), inset 0 6px 10px rgba(255,255,255,.25), 0 18px 30px -12px var(--color-glow)",
            animation: "bob 5s ease-in-out infinite",
          }}>{t.emoji}</div>
        </div>

        {/* Name */}
        <h1 style={{
          fontFamily: "var(--font-display)", fontWeight: 800,
          fontSize: "clamp(38px, 11vw, 54px)", lineHeight: 0.95, letterSpacing: "-0.03em",
          margin: 0, color: "var(--color-text)",
        }}>{config.title || tr("app.play.preview.titleFallback")}</h1>

        {/* Theme chip */}
        <div style={{
          display: "inline-flex", alignItems: "center", gap: 10, marginTop: 16,
          background: "var(--color-surface)", borderRadius: 999, padding: "9px 18px",
          boxShadow: "0 6px 18px -10px rgba(0,0,0,0.25)",
          fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, color: "var(--color-primary)", whiteSpace: "nowrap", maxWidth: "100%",
        }}>
          <span>{tr("app.play.preview.themeChip", { name: tr(`app.themes.${config.theme}.name`, { defaultValue: t.name }) })}</span>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: "var(--color-textMuted)", opacity: 0.6 }} />
          <span style={{ color: "var(--color-textMuted)", fontWeight: 600 }}>{tr("app.play.preview.levelsToUnlock", { n: levelCount, levelWord: levelCount === 1 ? tr("app.play.preview.levelSingular") : tr("app.play.preview.levelPlural") })}</span>
        </div>

        {/* Invite card */}
        <div style={{
          marginTop: 32, width: "100%",
          background: "var(--color-surface)", borderRadius: 28, padding: "30px 26px 26px",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.3)",
        }}>
          <p style={{
            fontFamily: "var(--font-body)", fontSize: 17, lineHeight: 1.55, color: "var(--color-textMuted)", margin: "0 0 26px",
          }}>
            <strong style={{ color: "var(--color-text)", fontWeight: 700 }}>{tr("app.play.preview.inviteLeadName")}</strong>{tr("app.play.preview.inviteBody")}
          </p>
          <button onClick={onContinue} style={{
            width: "100%", padding: 20, borderRadius: 20, border: "none", cursor: "pointer",
            background: "linear-gradient(160deg, var(--color-primaryBright), var(--color-primary))", color: "var(--color-onPrimary, #fff)",
            fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            boxShadow: "0 18px 34px -14px var(--color-primary)", whiteSpace: "nowrap",
          }}>
            {tr("app.play.preview.open")} <span style={{ fontSize: 20, lineHeight: 1 }}>→</span>
          </button>
          {isSealed(config) && (
            <div data-testid="preview-need-secret" style={{
              marginTop: 16, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--color-textMuted)",
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="11" width="16" height="10" rx="2.5" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>
              {tr("app.play.preview.needSecret")}
            </div>
          )}
        </div>

        <div style={{
          marginTop: 26, fontFamily: "var(--font-display)", fontSize: 13, fontWeight: 600,
          color: "var(--color-textMuted)", letterSpacing: "0.02em",
        }}>
          {tr("app.play.preview.madeWith")} <span style={{ color: "var(--color-primary)", fontWeight: 800 }}>Puzzly</span>
        </div>
      </div>
    </div>
  );
}

// ── Romantic theme helpers (designer themes/romantic.html) ──
// Wax seal medallion: radial wine-red disc with embossed initial/✓.
function WaxSeal({ char = "M", size = 48, press = false, style }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      display: "grid", placeItems: "center",
      background: "radial-gradient(circle at 35% 30%, #c8345f, #9c1a44 60%, #6e0b1c)",
      color: "#fdeaf0", fontFamily: "'Cormorant Garamond', serif", fontWeight: 600,
      fontSize: size * 0.42, lineHeight: 1,
      boxShadow: "0 4px 12px -2px rgba(110,11,28,.5), inset 0 2px 4px rgba(255,255,255,.25), inset 0 -3px 6px rgba(0,0,0,.3)",
      textShadow: "0 1px 1px rgba(0,0,0,.35)",
      animation: press ? "waxPress .6s cubic-bezier(.34,1.4,.64,1) both" : undefined,
      ...style,
    }}>{char}</div>
  );
}
// 8 drifting pressed-flower petals for the romantic finale.
function RomanticPetals() {
  const petals = [
    { left: "10%", px: "20px", pr: "540deg", delay: "0s", c: "#f4b8c4" },
    { left: "24%", px: "-30px", pr: "-540deg", delay: "0.6s", c: "#c8345f" },
    { left: "42%", px: "40px", pr: "720deg", delay: "1.2s", c: "#f4b8c4" },
    { left: "58%", px: "-20px", pr: "540deg", delay: "1.8s", c: "#c9a14a" },
    { left: "74%", px: "30px", pr: "-720deg", delay: "2.4s", c: "#c8345f" },
    { left: "88%", px: "-40px", pr: "540deg", delay: "3s", c: "#f4b8c4" },
    { left: "16%", px: "18px", pr: "720deg", delay: "3.6s", c: "#c9a14a" },
    { left: "64%", px: "-26px", pr: "540deg", delay: "4.2s", c: "#c8345f" },
  ];
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {petals.map((p, i) => (
        <span key={i} style={{
          position: "absolute", top: -20, left: p.left, width: 12, height: 16,
          background: p.c, borderRadius: "0 100% 0 100%",
          "--px": p.px, "--pr": p.pr,
          animation: `petalFall 5.4s ease-in ${p.delay} infinite`,
        }} />
      ))}
    </div>
  );
}

// Drifting ghost glyphs for the spooky theme (gate uses 3, finale uses 6).
function GhostDrift({ count = 3 }) {
  const glyphs = ["✦", "◌", "◍", "✧", "❉", "○"];
  const items = Array.from({ length: count }).map((_, i) => ({
    left: `${12 + (i * 73) % 80}%`,
    bottom: `${18 + (i * 41) % 50}%`,
    gx: `${((i * 37) % 40) - 20}px`,
    delay: `${i * 0.6}s`,
    glyph: glyphs[i % glyphs.length],
  }));
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {items.map((g, i) => (
        <span key={i} style={{
          position: "absolute", left: g.left, bottom: g.bottom, fontSize: 20, color: "#b88dd6",
          "--gx": g.gx, opacity: 0, animation: `spookyGhost 4s ease-in-out ${g.delay} infinite`,
        }}>{g.glyph}</span>
      ))}
    </div>
  );
}

// Falling sprinkle confetti for the birthday finale (9 staggered pieces).
function BirthdaySprinkles() {
  const colors = ["#ff5b94", "#ffc94d", "#7ad8c4", "#7fb8e8"];
  const items = Array.from({ length: 9 }).map((_, i) => ({
    left: `${10 + (i * 71) % 82}%`, delay: `${(i * 0.3).toFixed(1)}s`, c: colors[i % colors.length],
  }));
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {items.map((s, i) => (
        <span key={i} style={{
          position: "absolute", top: -10, left: s.left, width: 6, height: 14, borderRadius: 3, background: s.c,
          opacity: 0, animation: `bdaySprinkle 3.2s ease-in ${s.delay} infinite`,
        }} />
      ))}
    </div>
  );
}

// Floating paper-chain rings for friendship finale (6, sage/butter alternating).
function ChainFloat() {
  const items = Array.from({ length: 6 }).map((_, i) => ({
    left: `${14 + (i * 73) % 76}%`, bottom: `${14 + (i * 47) % 40}%`,
    delay: `${(i * 0.5).toFixed(1)}s`, c: i % 2 === 0 ? "#5c8c6b" : "#f5c542",
  }));
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {items.map((r, i) => (
        <span key={i} style={{
          position: "absolute", left: r.left, bottom: r.bottom, width: 16, height: 16, borderRadius: "50%",
          border: `3px solid ${r.c}`, opacity: 0, animation: `friendChain 4s ease-in-out ${r.delay} infinite`,
        }} />
      ))}
    </div>
  );
}
// Drifting passport-stamp glyphs for travel finale.
function TravelDrift() {
  const glyphs = ["✈", "☀", "⚓", "🧭", "🗺", "⛰"];
  const items = Array.from({ length: 6 }).map((_, i) => ({
    left: `${12 + (i * 67) % 78}%`, bottom: `${16 + (i * 43) % 46}%`,
    tx: `${((i * 31) % 60) - 20}px`, ty: `${-60 - (i * 13) % 40}px`,
    delay: `${(i * 0.5).toFixed(1)}s`, glyph: glyphs[i % glyphs.length],
  }));
  return (
    <div aria-hidden="true" style={{ position: "fixed", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {items.map((g, i) => (
        <span key={i} style={{
          position: "absolute", left: g.left, bottom: g.bottom, fontSize: 18, color: "#3a7ca5",
          "--tx": g.tx, "--ty": g.ty, opacity: 0, animation: `travelDrift 4.5s ease-in-out ${g.delay} infinite`,
        }}>{g.glyph}</span>
      ))}
    </div>
  );
}

function GateHelp({ hint }) {
  const { t: tr } = useTranslation();
  const [showHint, setShowHint] = useState(false);
  const has = hint && String(hint).trim();
  return (
    <div style={{ marginTop: 12, fontFamily: "var(--font-body)", fontSize: 13, lineHeight: 1.45, color: "inherit", opacity: 0.85, textAlign: "center" }}>
      <div>🔒 {tr("app.play.gate.help")}</div>
      {has && (showHint ? (
        <div style={{ marginTop: 6 }}>
          <strong>{tr("app.play.gate.hintLabel")}:</strong> {hint}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setShowHint(true)}
          style={{ marginTop: 6, background: "none", border: "none", padding: 0, color: "inherit", opacity: 0.95, textDecoration: "underline", cursor: "pointer", font: "inherit" }}
        >
          {tr("app.play.gate.hintToggle")}
        </button>
      ))}
    </div>
  );
}

// ── Christmas gate art: a wrapped gift whose lid lifts and ribbon slides off on unlock ──
const CHRISTMAS_UNWRAP_MS = 800;
const XMAS = { cranberry: "#B3263A", cranberryDeep: "#9A2032", gold: "#C9A14A", goldDeep: "#A9822F", ink: "#1E3A2B", body: "#4F6658" };
function ChristmasGift({ unwrapping, shake }) {
  const dots = `radial-gradient(circle, rgba(246,231,200,.6) 2px, transparent 2.6px)`;
  const ribbon = `linear-gradient(90deg, ${XMAS.goldDeep}, #E4C77E 45%, ${XMAS.gold})`;
  const ease = "cubic-bezier(.3,1.25,.6,1)";
  const loop = (side) => ({
    position: "absolute", bottom: 22, [side]: "calc(50% - 4px)", width: 48, height: 32,
    border: `8px solid ${XMAS.gold}`, boxSizing: "border-box",
    borderRadius: side === "right" ? "70% 30% 30% 70% / 60% 50% 50% 60%" : "30% 70% 70% 30% / 50% 60% 60% 50%",
    transform: `rotate(${side === "right" ? 16 : -16}deg)`, transformOrigin: side === "right" ? "100% 100%" : "0% 100%",
    boxShadow: "inset 0 -2px 0 rgba(0,0,0,.08)",
  });
  return (
    <div data-testid="christmas-gift" data-state={unwrapping ? "unwrapping" : "wrapped"} aria-hidden="true"
      style={{ position: "relative", width: 168, height: 156, margin: "4px auto 22px", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
      {/* Warm light and a few sparks from inside the box once the lid is off */}
      <div style={{
        position: "absolute", left: "50%", top: -22, width: 230, height: 170, transform: "translateX(-50%)",
        background: "radial-gradient(closest-side, rgba(228,199,126,.95), rgba(228,199,126,.4) 45%, transparent)",
        opacity: unwrapping ? 1 : 0, transition: `opacity ${CHRISTMAS_UNWRAP_MS * 0.5}ms ease ${CHRISTMAS_UNWRAP_MS * 0.2}ms`,
      }} />
      {[[-34, 14, 0], [2, 20, 0.12], [36, 13, 0.22]].map(([dx, size, delay]) => (
        <span key={dx} style={{
          position: "absolute", left: `calc(50% + ${dx}px)`, top: 66, fontSize: size, lineHeight: 1, color: XMAS.goldDeep,
          transform: unwrapping ? "translate(-50%, -44px)" : "translate(-50%, 0)", opacity: unwrapping ? 1 : 0,
          transition: `transform ${CHRISTMAS_UNWRAP_MS * 0.6}ms ease-out ${CHRISTMAS_UNWRAP_MS * (0.3 + delay)}ms, opacity ${CHRISTMAS_UNWRAP_MS * 0.3}ms ease ${CHRISTMAS_UNWRAP_MS * (0.3 + delay)}ms`,
        }}>✦</span>
      ))}
      {/* Box */}
      <div style={{
        position: "absolute", left: 12, bottom: 0, width: 144, height: 94, overflow: "hidden",
        backgroundColor: XMAS.cranberry, backgroundImage: dots, backgroundSize: "16px 16px",
        borderRadius: "3px 3px 10px 10px", boxShadow: "0 16px 28px -14px rgba(30,58,43,.55)",
      }}>
        {/* The open top: a shadowed rim that shows once the lid is gone */}
        <div style={{
          position: "absolute", left: 0, right: 0, top: 0, height: 10, zIndex: 1,
          background: "linear-gradient(180deg, rgba(60,10,20,.45), transparent)",
          opacity: unwrapping ? 1 : 0, transition: `opacity ${CHRISTMAS_UNWRAP_MS * 0.3}ms ease`,
        }} />
        <div style={{
          position: "absolute", left: 0, right: 0, top: 34, height: 18, background: `linear-gradient(180deg, ${XMAS.goldDeep}, #E4C77E 45%, ${XMAS.gold})`,
          transform: unwrapping ? "scaleX(0)" : "scaleX(1)", transition: `transform ${CHRISTMAS_UNWRAP_MS * 0.55}ms ease-in`,
        }} />
        <div style={{
          position: "absolute", top: 0, bottom: 0, left: "calc(50% - 11px)", width: 22, background: ribbon,
          transform: unwrapping ? "scaleY(0)" : "scaleY(1)", transformOrigin: "50% 100%",
          transition: `transform ${CHRISTMAS_UNWRAP_MS * 0.55}ms ease-in`,
        }} />
      </div>
      {/* Lid with the bow — lifts and tips away */}
      <div style={{
        position: "absolute", left: 0, top: 0, width: 168, height: 70,
        transform: unwrapping ? "translate(-18px, -64px) rotate(-18deg)" : "none",
        opacity: unwrapping ? 0 : 1,
        transition: `transform ${CHRISTMAS_UNWRAP_MS}ms ${ease}, opacity ${CHRISTMAS_UNWRAP_MS * 0.4}ms ease ${CHRISTMAS_UNWRAP_MS * 0.6}ms`,
      }}>
        <div style={loop("right")} />
        <div style={loop("left")} />
        <div style={{
          position: "absolute", left: 4, bottom: 0, width: 160, height: 30, borderRadius: 6,
          backgroundColor: XMAS.cranberryDeep, backgroundImage: dots, backgroundSize: "16px 16px",
          boxShadow: "0 6px 10px -6px rgba(30,58,43,.6)",
        }}>
          <div style={{ position: "absolute", top: 0, bottom: 0, left: "calc(50% - 11px)", width: 22, background: ribbon }} />
        </div>
        {/* Ribbon tails falling over the lid */}
        {[-1, 1].map((d) => (
          <div key={d} style={{
            position: "absolute", bottom: -14, left: `calc(50% + ${d * 6 - 5}px)`, width: 11, height: 42,
            background: `linear-gradient(90deg, ${XMAS.goldDeep}, ${XMAS.gold})`, clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 84%, 0 100%)",
            transform: `rotate(${-d * 30}deg)`, transformOrigin: "50% 0%",
          }} />
        ))}
        <div style={{ position: "absolute", bottom: 22, left: "calc(50% - 10px)", width: 20, height: 18, borderRadius: 7, background: XMAS.goldDeep }} />
      </div>
    </div>
  );
}

// ── Christmas finale art: a decorated pine with gifts at its foot ──
// The final letter rests under it (see Finale). Pine, gold garland, cranberry/gold/cream
// baubles and a gold star; the star glows once the tree has appeared.
function ChristmasTree({ shown, animate }) {
  const pine = "#1E4D36", pineShade = "#173F2C", cream = "#FBF4EA";
  const tiers = [
    "M150 104 L58 222 Q150 238 242 222 Z",
    "M150 66 L78 170 Q150 184 222 170 Z",
    "M150 34 L98 118 Q150 130 202 118 Z",
  ];
  const garlands = ["M86 196 Q150 222 222 186", "M98 148 Q150 168 206 140", "M112 100 Q150 116 190 94"];
  const baubles = [
    [112, 208, XMAS.cranberry], [168, 214, XMAS.gold], [206, 202, cream], [86, 202, XMAS.gold],
    [126, 160, cream], [184, 156, XMAS.cranberry], [104, 152, XMAS.cranberry], [150, 176, XMAS.gold],
    [136, 108, XMAS.gold], [172, 112, cream], [150, 76, XMAS.cranberry],
  ];
  const gift = (x, w, h, paper, ribbon) => (
    <g key={x}>
      <rect x={x} y={256 - h} width={w} height={h} rx="3" fill={paper} />
      <rect x={x + w / 2 - 4} y={256 - h} width="8" height={h} fill={ribbon} />
      <rect x={x} y={256 - h * 0.62} width={w} height="7" fill={ribbon} />
      <path d={`M${x + w / 2} ${256 - h} q-14 -14 -18 -2 q4 8 18 2 q14 -14 18 -2 q-4 8 -18 2`} fill="none" stroke={ribbon} strokeWidth="4" strokeLinejoin="round" />
    </g>
  );
  return (
    <svg data-testid="christmas-tree" aria-hidden="true" viewBox="0 0 300 260" width="100%"
      style={{
        display: "block", maxWidth: 280, margin: "0 auto", overflow: "visible",
        opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(14px) scale(.94)", transformOrigin: "50% 100%",
        transition: animate ? "opacity .7s ease, transform .9s cubic-bezier(.3,1.25,.6,1)" : "none",
      }}>
      <ellipse cx="150" cy="256" rx="132" ry="9" fill="rgba(30,58,43,.12)" />
      <rect x="138" y="222" width="24" height="30" rx="3" fill="#6B4A2E" />
      {tiers.map((d) => <path key={d} d={d} fill={pine} />)}
      {/* shade on the right half of each tier */}
      {tiers.map((d) => <path key={`s${d}`} d={d} fill={pineShade} clipPath="url(#xmas-tree-right)" />)}
      <defs><clipPath id="xmas-tree-right"><rect x="150" y="0" width="150" height="260" /></clipPath></defs>
      {garlands.map((d) => <path key={d} d={d} fill="none" stroke={XMAS.gold} strokeWidth="3" strokeLinecap="round" strokeDasharray="1 6" />)}
      {garlands.map((d) => <path key={`g${d}`} d={d} fill="none" stroke="#E4C77E" strokeWidth="1.4" strokeLinecap="round" opacity=".8" />)}
      {baubles.map(([cx, cy, c]) => (
        <g key={`${cx}-${cy}`}>
          <circle cx={cx} cy={cy} r="6.5" fill={c} stroke={c === cream ? XMAS.gold : "none"} strokeWidth="1.5" />
          <circle cx={cx - 2} cy={cy - 2} r="1.8" fill="#fff" opacity=".55" />
        </g>
      ))}
      {/* star */}
      <circle cx="150" cy="30" r="22" fill="rgba(228,199,126,.45)"
        style={{ opacity: shown ? 1 : 0, transition: animate ? "opacity .6s ease .7s" : "none" }} />
      <path d="M150 12 L155.3 24.7 L169 25.8 L158.6 34.8 L161.8 48.2 L150 41 L138.2 48.2 L141.4 34.8 L131 25.8 L144.7 24.7 Z"
        fill={XMAS.gold} stroke={XMAS.goldDeep} strokeWidth="1.2" strokeLinejoin="round" />
      {/* gifts at the foot of the tree */}
      {gift(34, 50, 38, XMAS.cranberry, XMAS.gold)}
      {gift(88, 34, 26, XMAS.gold, XMAS.cranberryDeep)}
      {gift(182, 36, 28, cream, XMAS.cranberry)}
      {gift(222, 46, 42, pine, XMAS.gold)}
    </svg>
  );
}

// Gentle snow over the Christmas finale: a few slow flakes, decorative only.
function ChristmasSnow() {
  const flakes = Array.from({ length: 14 }).map((_, i) => ({
    left: `${(i * 37 + 5) % 96}%`, size: 10 + (i * 7) % 9, drift: `${((i * 23) % 40) - 20}px`,
    duration: `${9 + (i * 5) % 7}s`, delay: `${-((i * 1.7) % 9).toFixed(1)}s`,
  }));
  return (
    <div data-testid="christmas-snow" aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 0 }}>
      {flakes.map((f, i) => (
        <span key={i} style={{
          position: "absolute", top: -20, left: f.left, fontSize: f.size, lineHeight: 1, color: "rgba(30,77,54,.28)",
          "--drift": f.drift, animation: `xmasSnow ${f.duration} linear ${f.delay} infinite`,
        }}>❄</span>
      ))}
    </div>
  );
}

export function PasswordGate({ config, onUnlock }) {
  const { t: tr } = useTranslation();
  const [pw, setPw] = useState("");
  const [error, setError] = useState(false);
  const [shake, setShake] = useState(false);
  const [visible, setVisible] = useState(false);
  // Christmas: the gift unwraps for a moment before the puzzle opens.
  const [unwrapping, setUnwrapping] = useState(false);
  const unwrapTimer = useRef(null);
  // Christmas guards: `giftOpening` is set from the first submit until a wrong answer,
  // so a second click/Enter during the async check can't unlock twice; `gateMounted`
  // stops a check that resolves after unmount from unlocking or touching state.
  const giftOpening = useRef(false);
  const gateMounted = useRef(true);
  const dark = isDarkTheme(config.theme);
  // An unknown key (a newer seasonal theme) reads the romantic copy in the reader's language.
  const mcKey = resolveThemeKey(config.theme);
  const mc = useThemeMicrocopy(mcKey, THEMES[mcKey].microcopy);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);
  useEffect(() => {
    gateMounted.current = true;
    return () => { gateMounted.current = false; clearTimeout(unwrapTimer.current); };
  }, []);

  const unlock = () => {
    const reduceMotion = typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (config.theme !== "christmas" || reduceMotion) { onUnlock(); return; }
    setUnwrapping(true);
    unwrapTimer.current = setTimeout(onUnlock, CHRISTMAS_UNWRAP_MS + 100);
  };

  // Defined before any theme branch — `const` isn't hoisted, so branches that
  // render before this point (travel/friendship/birthday/spooky/romantic) would
  // otherwise hit a ReferenceError on `attempt` and crash via ErrorBoundary.
  const attempt = async () => {
    const gift = config.theme === "christmas";
    if (gift) {
      if (giftOpening.current) return;
      giftOpening.current = true;
    }
    logger.info("Password attempt", { puzzleId: config._id || "legacy" });
    let isCorrect = false;
    if (config._fromDb) {
      isCorrect = await fbCheckPassword(pw, config.password);
    } else {
      isCorrect = pw.trim().toLowerCase() === config.password;
    }
    if (gift && !gateMounted.current) return;
    if (gift && !isCorrect) giftOpening.current = false;
    if (isCorrect) {
      logger.info("Password correct", { puzzleId: config._id || "legacy" });
      unlock();
    } else {
      logger.warn("Password incorrect", { puzzleId: config._id || "legacy" });
      setError(true);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setTimeout(() => setError(false), 2000);
    }
  };

  // ── Travel "boarding pass" gate (designer themes/travel.html) ──
  if (config.theme === "travel") {
    const navy = "#1c2e52", navyDeep = "#0f1d3a", stamp = "#c0392b", muted = "#8c9bb5", text = "#1c2e52";
    const mono = "'JetBrains Mono', monospace";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", background: "var(--color-bg)", opacity: visible ? 1 : 0, transition: "opacity 0.6s",
        backgroundImage: "radial-gradient(circle at 12% 12%, rgba(28,46,82,.10), transparent 42%), radial-gradient(circle at 88% 86%, rgba(192,57,43,.08), transparent 44%), radial-gradient(circle at 50% 55%, rgba(212,167,42,.07), transparent 70%)",
      }}>
        <div style={{
          position: "relative", width: "100%", maxWidth: 360, background: "#fffaf0", borderRadius: 12,
          padding: "16px 18px", boxShadow: "0 10px 28px -10px rgba(28,46,82,.3)",
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12, fontFamily: mono, fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: muted }}>
            <span>{tr("app.play.gate.travel.boardingPass")}</span><span>PZ-1041</span>
          </div>
          <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 14 }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: "0.1em", textTransform: "uppercase", color: muted }}>{tr("app.play.gate.travel.from")}</span>
              <span style={{ fontFamily: mono, fontSize: 20, color: text }}>JFK</span>
            </div>
            <div style={{ flex: 1, textAlign: "center", paddingBottom: 4, color: navy }}>✈</div>
            <div style={{ display: "flex", flexDirection: "column", textAlign: "right" }}>
              <span style={{ fontFamily: mono, fontSize: 8, letterSpacing: "0.1em", textTransform: "uppercase", color: muted }}>{tr("app.play.gate.travel.to")}</span>
              <span style={{ fontFamily: mono, fontSize: 20, color: text }}>YOU</span>
            </div>
          </div>
          <div style={{ paddingBottom: 12, marginBottom: 12, borderBottom: "1px dashed rgba(28,46,82,.2)" }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>
              {mc.passwordLabel || "Show your passport"}
            </div>
            <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
              <input
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(false); }}
                onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
                placeholder={mc.passwordPlaceholder || "passport no."}
                type="password"
                style={{
                  width: "100%", background: "#fff", border: "none", borderRadius: 8, padding: "12px 14px",
                  fontFamily: mono, fontSize: 13, color: text, outline: "none", boxSizing: "border-box",
                  boxShadow: error ? "inset 0 0 0 2px #c0392b" : "inset 0 0 0 1px rgba(28,46,82,.15)",
                }}
              />
            </div>
            {error && (
              <div style={{ fontFamily: mono, fontSize: 11, color: stamp, marginTop: 10, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                {mc.wrongPassword || "Not on the manifest."}
              </div>
            )}
            <button
              onClick={attempt}
              disabled={!pw.trim()}
              style={{
                width: "100%", marginTop: 12, padding: "13px 18px", borderRadius: 8,
                background: `linear-gradient(135deg, ${navy}, ${navyDeep})`, color: "#fffaf0", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: pw.trim() ? "pointer" : "not-allowed",
                opacity: pw.trim() ? 1 : 0.6, boxShadow: "0 8px 18px -6px rgba(28,46,82,.5)",
              }}
            >{tr("app.play.gate.travel.board")}</button>
            <GateHelp hint={config.passwordHint} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", fontFamily: mono, fontSize: 9, color: muted }}>
            <span>FLIGHT PZ 24</span><span style={{ color: stamp }}>GATE B7</span><span>SEAT 7A</span>
          </div>
        </div>
        <div style={{ marginTop: 14, fontFamily: mono, fontSize: 10, color: muted, letterSpacing: "0.12em", textTransform: "uppercase" }}>{tr("app.play.gate.travel.tagline")}</div>
      </div>
    );
  }

  // ── Friendship "open the note" gate (designer themes/friendship.html) ──
  if (config.theme === "friendship") {
    const sage = "#5c8c6b", sageDeep = "#3d6b4c", text = "#2c3e34", muted = "#94a99e";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", textAlign: "center", background: "var(--color-bg)",
        backgroundImage: "repeating-linear-gradient(var(--color-bg), var(--color-bg) 27px, rgba(92,140,107,.1) 28px)",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <div style={{
          position: "relative", width: "100%", maxWidth: 330, background: "#fff", borderRadius: 8,
          padding: "30px 24px 24px", boxShadow: "0 10px 28px -10px rgba(60,80,68,.3)",
          animation: "friendWobble 6s ease-in-out infinite", transformOrigin: "50% 0%",
        }}>
          {/* Washi tape */}
          <div style={{
            position: "absolute", top: -8, left: "50%", transform: "translateX(-50%) rotate(-2deg)",
            width: 70, height: 18, background: "rgba(245,197,66,.7)", boxShadow: "0 1px 3px rgba(0,0,0,.1)",
          }} />
          <div style={{
            width: 62, height: 62, borderRadius: 18, margin: "6px auto 16px", display: "grid", placeItems: "center",
            fontSize: 30, background: `linear-gradient(135deg, ${sage}, ${sageDeep})`, boxShadow: "0 8px 20px -6px rgba(60,107,76,.4)",
          }}>🤝</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: sage, marginBottom: 6 }}>{tr("app.play.gate.friendship.eyebrow")}</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: text, lineHeight: 1.1, margin: "0 0 14px" }}>
            {config.title || <>{tr("app.play.gate.friendship.titleFallbackPre")} <span style={{ fontStyle: "italic", color: sageDeep }}>{tr("app.play.gate.friendship.titleFallbackAccent")}</span></>}
          </h2>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>
            {mc.passwordLabel || "The secret handshake"}
          </div>
          <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
            <input
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
              placeholder={mc.passwordPlaceholder || "we know what it is"}
              type="password"
              style={{
                width: "100%", background: "#f6faf2", border: "none", borderRadius: 12, padding: "12px 12px",
                fontFamily: "var(--font-body)", fontSize: 14, color: text, textAlign: "center", outline: "none",
                boxShadow: error ? "inset 0 0 0 2px #c84d6e" : "inset 0 0 0 1px rgba(92,140,107,.2)",
              }}
            />
          </div>
          {error && (
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "#c84d6e", marginTop: 12 }}>
              {mc.wrongPassword || "Hmm, that's not it!"}
            </div>
          )}
          <button
            onClick={attempt}
            disabled={!pw.trim()}
            style={{
              width: "100%", marginTop: 14, padding: "13px 18px", borderRadius: 12,
              background: `linear-gradient(135deg, ${sage}, ${sageDeep})`, color: "#fff", border: "none",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: pw.trim() ? "pointer" : "not-allowed",
              opacity: pw.trim() ? 1 : 0.6, boxShadow: "0 8px 18px -6px rgba(92,140,107,.5)",
            }}
          >{tr("app.play.gate.friendship.open")}</button>
          <GateHelp hint={config.passwordHint} />
        </div>
        <div style={{ marginTop: 16, fontFamily: "var(--font-body)", fontSize: 11, color: muted }}>{tr("app.play.gate.friendship.tagline")}</div>
      </div>
    );
  }

  // ── Christmas "gift to unwrap" gate (themes-canvas · Natale) ──
  if (config.theme === "christmas") {
    const serif = "'Cormorant Garamond', Georgia, serif";
    const busy = unwrapping;
    return (
      <div data-testid="gate-christmas" style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "40px 24px", textAlign: "center", background: "var(--color-bg)",
        backgroundImage: `radial-gradient(circle at 15% 10%, rgba(30,77,54,.10), transparent 40%), radial-gradient(circle at 85% 88%, rgba(201,161,74,.14), transparent 45%)`,
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <div style={{ width: "100%", maxWidth: 340 }}>
          <ChristmasGift unwrapping={unwrapping} shake={shake} />
          <div style={{
            background: "#fff", borderRadius: 18, border: "1px solid #EADFCB", padding: "22px 22px 18px",
            color: XMAS.ink, boxShadow: "0 12px 30px -16px rgba(30,58,43,.35)",
          }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: XMAS.cranberryDeep, marginBottom: 6 }}>{tr("app.play.gate.christmas.eyebrow")}</div>
            <h2 style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 28, color: XMAS.ink, lineHeight: 1.1, margin: "0 0 16px" }}>
              {config.title || <>{tr("app.play.gate.christmas.titleFallbackPre")} <span style={{ color: XMAS.cranberryDeep }}>{tr("app.play.gate.christmas.titleFallbackAccent")}</span></>}
            </h2>
            <label htmlFor="gate-christmas-word" style={{ display: "block", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: XMAS.body, marginBottom: 8 }}>
              {mc.passwordLabel || "The magic word"}
            </label>
            <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
              <input
                id="gate-christmas-word"
                value={pw}
                disabled={busy}
                onChange={(e) => { setPw(e.target.value); setError(false); }}
                onKeyDown={(e) => { if (e.key === "Enter" && pw.trim() && !busy) attempt(); }}
                placeholder={mc.passwordPlaceholder || "the one on the gift tag"}
                type="password"
                aria-invalid={error || undefined}
                aria-describedby={error ? "gate-christmas-error" : undefined}
                style={{
                  width: "100%", boxSizing: "border-box", background: "#FBF4EA", borderRadius: 12, padding: "12px 12px",
                  border: error ? `2px solid ${XMAS.cranberry}` : "1.5px dashed #CDBB95",
                  fontFamily: "var(--font-body)", fontSize: 15, color: XMAS.ink, textAlign: "center", outline: "none",
                }}
              />
            </div>
            {error && (
              <div id="gate-christmas-error" role="alert" style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 17, color: XMAS.cranberryDeep, marginTop: 10 }}>
                {mc.wrongPassword || "Not time to unwrap it yet."}
              </div>
            )}
            <button
              onClick={attempt}
              disabled={!pw.trim() || busy}
              style={{
                width: "100%", marginTop: 14, padding: "13px 18px", borderRadius: 999,
                background: XMAS.cranberry, color: "#fff", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15,
                cursor: pw.trim() && !busy ? "pointer" : "not-allowed", opacity: pw.trim() || busy ? 1 : 0.6,
                boxShadow: "0 8px 18px -8px rgba(179,38,58,.55)",
              }}
            >{tr("app.play.gate.christmas.open")}</button>
            <GateHelp hint={config.passwordHint} />
          </div>
          <div style={{ marginTop: 16, fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 17, color: XMAS.body }}>{tr("app.play.gate.christmas.tagline")}</div>
        </div>
      </div>
    );
  }

  // ── Birthday "open the card" gate (designer themes/birthday.html · "A card for someone special") ──
  if (config.theme === "birthday") {
    const pink = "#ff5b94", pinkDeep = "#d63a72", candle = "#ffc94d", text = "#5a1a3a", muted = "#b88aa0";
    const hand = "'Caveat', cursive";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", textAlign: "center", background: "var(--color-bg)",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <div style={{
          position: "relative", width: "100%", maxWidth: 340, background: "#fff", borderRadius: 18,
          padding: "36px 24px 24px", boxShadow: "0 12px 32px -10px rgba(214,58,114,.25)",
        }}>
          {/* Candle on top */}
          <div style={{ position: "absolute", top: -26, left: "50%", transform: "translateX(-50%)", width: 9, height: 30, background: "#fff", borderRadius: 2, boxShadow: "inset 0 0 0 1px rgba(90,26,58,.15)" }}>
            <div style={{
              position: "absolute", top: -14, left: "50%", width: 9, height: 16, background: candle,
              borderRadius: "50% 50% 50% 50% / 70% 70% 30% 30%", boxShadow: "0 0 14px rgba(255,201,77,.7)",
              animation: "bdayFlicker 1.4s ease-in-out infinite",
            }} />
          </div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: pink, marginBottom: 6 }}>{tr("app.play.gate.birthday.eyebrow")}</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: text, lineHeight: 1.1, margin: "0 0 6px" }}>
            {config.title || <>{tr("app.play.gate.birthday.titleFallbackPre")} <span style={{ color: pinkDeep }}>{tr("app.play.gate.birthday.titleFallbackAccent")}</span></>}
          </h2>
          <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 11, color: muted, marginBottom: 18 }}>{tr("app.play.gate.birthday.subtitle")}</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>
            {mc.passwordLabel || "Sign your name"}
          </div>
          <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
            <input
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
              placeholder={mc.passwordPlaceholder || "it's your special day"}
              type="password"
              style={{
                width: "100%", background: "#fff5f8", border: "none", borderRadius: 14, padding: "12px 16px",
                fontFamily: hand, fontSize: 18, color: text, textAlign: "center", outline: "none",
                boxShadow: error ? "inset 0 0 0 2px #d63a72" : "inset 0 0 0 1px rgba(214,58,114,.15)",
              }}
            />
          </div>
          {error && (
            <div style={{ fontFamily: hand, fontSize: 16, color: pinkDeep, marginTop: 12 }}>
              {mc.wrongPassword || "Not quite — try again!"}
            </div>
          )}
          <button
            onClick={attempt}
            disabled={!pw.trim()}
            style={{
              width: "100%", marginTop: 14, padding: "13px 18px", borderRadius: 14,
              background: `linear-gradient(135deg, ${pink}, ${pinkDeep})`, color: "#fff", border: "none",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: pw.trim() ? "pointer" : "not-allowed",
              opacity: pw.trim() ? 1 : 0.6, boxShadow: "0 8px 18px -6px rgba(255,91,148,.5)",
            }}
          >{tr("app.play.gate.birthday.open")}</button>
          <GateHelp hint={config.passwordHint} />
        </div>
        <div style={{ marginTop: 14, fontFamily: hand, fontSize: 16, color: text }}>{tr("app.play.gate.birthday.tagline")}</div>
      </div>
    );
  }

  // ── Spooky "séance" gate (designer themes/spooky.html · "Whisper the secret") ──
  if (config.theme === "spooky") {
    const accent = "#b88dd6", accentDeep = "#7d4ca8", textSoft = "#c9b8df", muted = "#8b7aa4";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", textAlign: "center", background: "var(--color-bg)", position: "relative", overflow: "hidden",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <GhostDrift count={3} />
        <div style={{ width: "100%", maxWidth: 340, position: "relative", zIndex: 1 }}>
          <div style={{
            width: 72, height: 72, borderRadius: 24, margin: "0 auto 24px", display: "grid", placeItems: "center",
            fontSize: 32, background: `linear-gradient(135deg, ${accent}, ${accentDeep})`,
            animation: "spookyGlow 3s ease-in-out infinite",
          }}>🎃</div>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: accent, marginBottom: 8 }}>{tr("app.play.gate.spooky.eyebrow")}</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontStyle: "italic", fontSize: 24, color: "var(--color-text)", lineHeight: 1.1, margin: "0 0 22px" }}>
            {config.title || tr("app.play.gate.spooky.titleFallback")}
          </h2>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 10 }}>
            {mc.passwordLabel || "Whisper the secret"}
          </div>
          <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
            <input
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
              placeholder={mc.passwordPlaceholder || "listening…"}
              type="password"
              style={{
                width: "100%", background: "var(--color-surface)", border: `1px solid ${accentDeep}`, borderRadius: 14,
                padding: "13px 16px", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-text)",
                textAlign: "center", outline: "none",
                boxShadow: error ? `0 0 0 1px ${accent}, 0 0 14px rgba(184,141,214,.4)` : "0 0 14px rgba(184,141,214,.18)",
              }}
            />
          </div>
          {error && (
            <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 14, color: accent, marginTop: 12 }}>
              {mc.wrongPassword || "The spirits stay silent…"}
            </div>
          )}
          <button
            onClick={attempt}
            disabled={!pw.trim()}
            style={{
              width: "100%", marginTop: 16, padding: "14px 18px", borderRadius: 14,
              background: `linear-gradient(135deg, ${accent}, ${accentDeep})`, color: "#1a1025", border: "none",
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: pw.trim() ? "pointer" : "not-allowed",
              opacity: pw.trim() ? 1 : 0.6, boxShadow: "0 0 18px rgba(184,141,214,.4)",
            }}
          >{tr("app.play.gate.spooky.enter")}</button>
          <GateHelp hint={config.passwordHint} />
          <div style={{ marginTop: 14, fontFamily: "var(--font-body)", fontSize: 11, color: muted }}>{tr("app.play.gate.spooky.tagline")}</div>
        </div>
      </div>
    );
  }

  // ── Romantic "sealed letter" gate (designer themes/romantic.html · "From someone who knows you well") ──
  if (config.theme === "romantic") {
    const wine = "#4a0a1f", roseDeep = "#9c1a44", textSoft = "#6e1e3a", muted = "#a87a8a";
    const serif = "'Cormorant Garamond', serif";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 12% 14%, rgba(200,52,95,.16), transparent 42%), radial-gradient(circle at 88% 84%, rgba(201,161,74,.12), transparent 46%), radial-gradient(circle at 50% 50%, rgba(244,184,196,.10), transparent 70%)",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <div style={{
          position: "relative", width: "100%", maxWidth: 360, background: "#ffffff", borderRadius: 4,
          padding: "44px 26px 26px", textAlign: "center",
          boxShadow: "0 12px 28px -10px rgba(74,10,31,.3), inset 0 0 0 1px rgba(74,10,31,.06)",
        }}>
          {/* Wax seal perched on top */}
          <div style={{ position: "absolute", top: -30, left: "50%", transform: "translateX(-50%)", zIndex: 3 }}>
            <WaxSeal char="♥" size={60} />
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 18, color: roseDeep, marginBottom: 4 }}>{tr("app.play.gate.romantic.eyebrow")}</div>
            <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: wine, letterSpacing: "-0.02em", lineHeight: 1.1, margin: "0 0 8px" }}>
              {config.title || <>{tr("app.play.gate.romantic.titleFallbackPre")} <span style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, color: roseDeep }}>{tr("app.play.gate.romantic.titleFallbackAccent")}</span></>}
            </h2>
            <p style={{ fontFamily: serif, fontStyle: "italic", fontSize: 13.5, color: textSoft, lineHeight: 1.3, margin: "0 0 18px" }}>
              {tr("app.play.gate.romantic.sealed").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}
            </p>
            <div style={{
              fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.12em",
              textTransform: "uppercase", color: muted, marginBottom: 8,
            }}>{mc.passwordLabel || "Tell me a secret"}</div>
            <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
              <input
                value={pw}
                onChange={(e) => { setPw(e.target.value); setError(false); }}
                onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
                placeholder={mc.passwordPlaceholder || "the one only you'd know"}
                type="password"
                style={{
                  width: "100%", background: "#fff", borderRadius: 14, padding: "12px 16px",
                  fontFamily: serif, fontStyle: "italic", fontSize: 15, color: wine, textAlign: "center",
                  border: "none", outline: "none",
                  boxShadow: error ? "0 4px 12px -4px rgba(74,10,31,.2), inset 0 0 0 2px #9c1a44" : "0 4px 12px -4px rgba(74,10,31,.2), inset 0 0 0 1px rgba(74,10,31,.12)",
                }}
              />
            </div>
            {error && (
              <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 14, color: roseDeep, marginTop: 12 }}>
                {mc.wrongPassword || "Not quite, my dear."}
              </div>
            )}
            <button
              onClick={attempt}
              disabled={!pw.trim()}
              style={{
                width: "100%", marginTop: 16, padding: "14px 18px", borderRadius: 14,
                background: "linear-gradient(135deg, #c8345f, #9c1a44)", color: "#fff", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, letterSpacing: "0.02em",
                cursor: pw.trim() ? "pointer" : "not-allowed", opacity: pw.trim() ? 1 : 0.6,
                boxShadow: "0 8px 18px -6px rgba(200,52,95,.5)",
              }}
            >{tr("app.play.gate.romantic.breakSeal")}</button>
            <GateHelp hint={config.passwordHint} />
          </div>
        </div>
        <div style={{ marginTop: 16, fontFamily: serif, fontStyle: "italic", fontSize: 14, color: textSoft }}>{tr("app.play.gate.romantic.signoff")}</div>
      </div>
    );
  }

  // ── Gaming "insert coin" gate (designer themes/gaming.html · "Insert coin") ──
  if (config.theme === "gaming") {
    const magenta = "#ff2eb8", cyan = "#00e5ff", coin = "#ffd84d", textSoft = "#a8a0d0", muted = "#6e6890";
    const pixel = "'JetBrains Mono', monospace";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "48px 24px", textAlign: "center", background: "#0a0a1f", position: "relative", overflow: "hidden",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        {/* CRT scanlines */}
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.12,
          backgroundImage: "linear-gradient(rgba(255,255,255,0) 50%, rgba(0,0,0,0.4) 50%)", backgroundSize: "100% 4px",
        }} />
        <div style={{ width: "100%", maxWidth: 360, position: "relative", zIndex: 1 }}>
          <div style={{
            width: 78, height: 78, borderRadius: 8, margin: "0 auto 16px", display: "grid", placeItems: "center",
            fontSize: 34, background: `linear-gradient(135deg, ${magenta}, #c91a92)`, border: "2px solid #fff",
            animation: "gmGlowPulse 2.6s ease-in-out infinite",
          }}>🎮</div>
          <div style={{ fontFamily: pixel, fontWeight: 700, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", color: magenta, marginBottom: 8, textShadow: "0 0 6px rgba(255,46,184,.5)" }}>{tr("app.play.gate.gaming.detected")}</div>
          <h2 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, color: "#fff", lineHeight: 1.1, margin: "0 0 14px" }}>
            {config.title || tr("app.play.gate.gaming.titleFallback")}
          </h2>
          <div style={{ fontFamily: pixel, fontSize: 10, color: coin, letterSpacing: "0.1em", marginBottom: 16, animation: "gmBlink 1s steps(1) infinite" }}>
            {tr("app.play.gate.gaming.insertCoin")}
          </div>
          <div style={{ fontFamily: pixel, fontWeight: 700, fontSize: 10, letterSpacing: "0.15em", textTransform: "uppercase", color: muted, marginBottom: 10 }}>
            {mc.passwordLabel || "// Enter cheat code"}
          </div>
          <div style={{ transform: shake ? "translateX(-8px)" : "translateX(0)", animation: shake ? "shake 0.4s ease-in-out" : "none" }}>
            <input
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
              placeholder={mc.passwordPlaceholder || "↑ ↑ ↓ ↓ ← → ← →"}
              type="password"
              style={{
                width: "100%", background: "#0a0a1f", border: `1px solid ${cyan}`, borderRadius: 0,
                padding: "13px 16px", fontFamily: pixel, fontSize: 14, letterSpacing: "0.15em", color: "#fff",
                textAlign: "center", outline: "none", boxShadow: error ? `0 0 0 1px ${magenta}, 0 0 12px rgba(255,46,184,.5)` : `0 0 10px rgba(0,229,255,.3)`,
              }}
            />
          </div>
          {error && (
            <div style={{ fontFamily: pixel, fontSize: 12, color: magenta, marginTop: 12, letterSpacing: "0.05em" }}>
              {mc.wrongPassword || "// ACCESS DENIED. RETRY."}
            </div>
          )}
          <button
            onClick={attempt}
            disabled={!pw.trim()}
            style={{
              width: "100%", marginTop: 16, padding: "14px 18px", borderRadius: 0,
              clipPath: "polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))",
              background: magenta, color: "#fff", border: "none",
              fontFamily: pixel, fontWeight: 700, fontSize: 13, letterSpacing: "0.2em", textTransform: "uppercase",
              cursor: pw.trim() ? "pointer" : "not-allowed", opacity: pw.trim() ? 1 : 0.6,
              boxShadow: "0 0 15px rgba(255,46,184,.5)",
            }}
          >{tr("app.play.gate.gaming.pressStart")}</button>
          <GateHelp hint={config.passwordHint} />
          <div style={{ marginTop: 14, fontFamily: pixel, fontSize: 8, color: muted, letterSpacing: "0.05em" }}>{tr("app.play.gate.gaming.copyright")}</div>
        </div>
      </div>
    );
  }

  // ── Funny "comic strip" gate (designer themes/funny.html · "Hey loser") ──
  // Butter-yellow halftone canvas, corner WHAT?! burst sticker, speech-bubble
  // greeting with tail, ink-outlined italic input + offset shadow, pink SEND IT.
  if (config.theme === "funny") {
    const ink = "var(--color-text)";
    const pink = "var(--color-primary)";
    const pinkDeep = "var(--color-wrong)";
    const cream = "var(--color-surface)";
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-start",
        padding: "24px 24px 48px", position: "relative", overflow: "hidden",
        background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle, rgba(26,10,20,.07) 1px, transparent 1.5px)",
        backgroundSize: "13px 13px",
        opacity: visible ? 1 : 0, transition: "opacity 0.6s",
      }}>
        <div style={{ width: "100%", maxWidth: 380, position: "relative", textAlign: "center" }}>
          {/* Corner burst sticker */}
          <div style={{
            position: "absolute", top: -16, right: 0, zIndex: 3,
            width: 84, height: 84, display: "grid", placeItems: "center",
            background: pink, color: "#fff", transform: "rotate(-12deg)",
            fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, lineHeight: 1, textAlign: "center",
            textShadow: `1.5px 1.5px 0 ${ink}`, clipPath: BURST_CLIP,
          }}>{tr("app.play.gate.funny.burst").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>

          <div style={{ fontSize: 50, marginBottom: 10 }}>{THEMES.funny.emoji}</div>

          {/* Speech-bubble greeting */}
          <div style={{ position: "relative", display: "inline-block", marginBottom: 30, maxWidth: 300 }}>
            <div style={{
              background: "#fff", border: `2.5px solid ${ink}`, borderRadius: 18,
              padding: "16px 20px", boxShadow: `4px 4px 0 ${ink}`,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 22, lineHeight: 1.1, color: ink,
            }}>
              {tr("app.play.gate.funny.greetingPre")}<br /><span style={{ color: pinkDeep }}>{tr("app.play.gate.funny.greetingAccent")}</span>
            </div>
            <div style={{
              position: "absolute", bottom: -10, left: 36, width: 18, height: 18, background: "#fff",
              borderRight: `2.5px solid ${ink}`, borderBottom: `2.5px solid ${ink}`, transform: "rotate(45deg)",
            }} />
          </div>

          <div style={{
            fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 11, letterSpacing: "0.16em",
            textTransform: "uppercase", color: pinkDeep, marginBottom: 10,
          }}>{mc.passwordLabel || "Prove it's you"}</div>

          <div style={{
            transform: shake ? "translateX(-8px)" : "translateX(0)",
            animation: shake ? "shake 0.4s ease-in-out" : "none",
          }}>
            <input
              value={pw}
              onChange={(e) => { setPw(e.target.value); setError(false); }}
              onKeyDown={(e) => { if (e.key === "Enter" && pw.trim()) attempt(); }}
              placeholder={mc.passwordPlaceholder || "come on, you know this"}
              type="password"
              style={{
                width: "100%", background: cream, border: `2.5px solid ${ink}`, borderRadius: 12,
                padding: "15px 16px", fontFamily: "var(--font-body)", fontSize: 15, fontStyle: "italic",
                color: ink, textAlign: "center", outline: "none",
                boxShadow: error ? `3px 3px 0 ${pink}` : `3px 3px 0 ${ink}`,
              }}
            />
          </div>
          {error && (
            <div style={{
              fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: pinkDeep, marginTop: 14,
            }}>{mc.wrongPassword || "LOL no. Try again."}</div>
          )}
          <button
            type="button"
            onClick={attempt}
            disabled={!pw.trim()}
            style={{
              width: "100%", marginTop: 16, padding: "15px 18px", borderRadius: 12,
              background: pink, color: "#fff", border: `2.5px solid ${ink}`, boxShadow: `4px 4px 0 ${ink}`,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, letterSpacing: "0.04em",
              textTransform: "uppercase", textShadow: `1px 1px 0 ${pinkDeep}`,
              cursor: pw.trim() ? "pointer" : "not-allowed", opacity: pw.trim() ? 1 : 0.6,
            }}
          >{mc.checkAnswer || "Send it"}</button>
          <GateHelp hint={config.passwordHint} />

          <div style={{
            marginTop: 18, fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12.5, color: ink,
          }}>{tr("app.play.gate.funny.wrongPre")} <span style={{ color: pinkDeep }}>{tr("app.play.gate.funny.wrongAccent")}</span></div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: 24, textAlign: "center",
      opacity: visible ? 1 : 0, transition: "opacity 0.6s",
    }}>
      <div style={{ width: "100%", maxWidth: 420 }}>
        {/* Gift icon area with gradient glow */}
        <div style={{
          width: 80, height: 80, borderRadius: "50%", margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
          boxShadow: `0 8px 40px var(--color-glow)`,
          fontSize: 36,
        }}>🎁</div>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 800, color: "var(--color-text)", margin: "0 0 4px" }}>
          {tr("app.play.gate.default.heading")}
        </h2>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-primary)", fontWeight: 600, margin: "0 0 4px" }}>
          {config.title}
        </p>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-textMuted)", margin: "0 0 28px" }}>
          {mc.passwordLabel || "Enter the secret password to start"}
        </p>
        <Card gaming={dark}>
          <div style={{
            transform: shake ? "translateX(-8px)" : "translateX(0)",
            animation: shake ? "shake 0.4s ease-in-out" : "none",
          }}>
            <Input
              value={pw}
              onChange={(v) => { setPw(v); setError(false); }}
              placeholder={tr("app.play.gate.default.placeholder")}
              type="password"
              style={error ? { boxShadow: "0 0 0 2px var(--color-wrong)" } : {}}
            />
          </div>
          {error && (
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-wrong)", marginTop: 10 }}>
              {mc.wrongPassword || "Wrong password — try again!"}
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Btn onClick={attempt} disabled={!pw.trim()} style={{ width: "100%" }}>{tr("app.play.gate.default.start")}</Btn>
          </div>
          <GateHelp hint={config.passwordHint} />
        </Card>
      </div>
{/* shake keyframes live in public/index.html */}
    </div>
  );
}

// ── Themed celebration messages ──
const correctMessages = {
  romantic: [
    "💕 You know me so well!",
    "🥰 That's my person right there!",
    "💖 My heart just skipped a beat!",
    "🌹 You remembered... I'm melting!",
    "✨ We really are made for each other!",
    "💗 How do you know me this well?!",
    "😘 You're perfect, you know that?",
    "💝 I'm blushing over here!",
    "🦋 Butterflies every time...",
    "💌 You pay attention to the little things!",
  ],
  funny: [
    "😂 Big brain energy right there!",
    "🔥 Someone's been paying attention!",
    "🧠 Galaxy brain moment!",
    "🎯 Bullseye, you absolute legend!",
    "⚡ Too fast, too smart, too good!",
    "🏆 Winner winner chicken dinner!",
    "🤯 Mind = Blown that you knew that!",
    "💪 Flexing those brain muscles!",
    "🎪 The crowd goes wild!",
    "🚀 Houston, we have a genius!",
  ],
  gaming: [
    "⚡ CRITICAL HIT! +100 XP",
    "🎮 ACHIEVEMENT UNLOCKED!",
    "🏆 FLAWLESS VICTORY!",
    "💎 LEGENDARY ANSWER!",
    "🔥 COMBO! KEEP GOING!",
    "⭐ PERFECT SCORE!",
    "🎯 HEADSHOT! Nailed it!",
    "🚀 LEVEL CLEARED!",
    "💥 POWER MOVE!",
    "🕹️ PRO GAMER MOVE!",
  ],
  birthday: [
    "🎂 Birthday genius right here!",
    "🎁 Unwrapping that knowledge!",
    "🥳 Party skills activated!",
    "🎈 You're on fire, birthday star!",
    "✨ Making wishes come true!",
    "🎊 Celebration mode!",
    "🎉 That's how we party!",
    "💫 Birthday magic at work!",
    "🧁 Sweet as cupcakes!",
    "🎵 Happy birthday to the smartest!",
  ],
  friendship: [
    "🤝 That's what best friends are for!",
    "💜 You really know me!",
    "⭐ Bestie status: confirmed!",
    "🎉 We're on the same wavelength!",
    "💪 Nobody knows me like you do!",
    "🔥 BFF energy right there!",
    "✨ Friendship goals!",
    "💫 You just get me!",
    "🤗 That's my person!",
    "🏆 Best friend of the year award!",
  ],
  spooky: [
    "👻 You survived that one!",
    "🔮 The spirits are impressed!",
    "💀 Nothing can scare you!",
    "🕯️ You've unlocked a dark secret!",
    "🦇 Spooky how well you know this!",
    "⚡ The curse is lifting!",
    "🌙 Midnight knowledge!",
    "🕸️ Caught in the web of truth!",
    "👁️ The all-seeing eye approves!",
    "🎃 Frightfully correct!",
  ],
  travel: [
    "✈️ You've been there! Next destination unlocked!",
    "🛂 Passport stamped!",
    "🗺️ You're a true travel companion!",
    "🌍 Adventure awaits — and you're ready!",
    "🧳 Miles apart, but you still know me!",
    "🏔️ Summit reached!",
    "🚂 Next stop: correct answer!",
    "🌅 Beautiful view from up here!",
    "⛵ Smooth sailing!",
    "🎒 Seasoned traveler confirmed!",
  ],
  christmas: [
    "🎁 Unwrapped! You know me so well.",
    "🎄 Right on the first try!",
    "⭐ That answer goes on top of the tree!",
    "❄️ Not a snowflake out of place!",
    "✨ The elves are impressed.",
    "🔔 Ding dong, that's the one!",
    "🧦 That one goes straight in your stocking!",
    "🍪 You've earned a cookie.",
    "💝 Nobody knows me like you.",
    "🌟 Another gift opened!",
  ],
};

const wrongMessages = {
  romantic: [
    "💭 Almost, my love... try again!",
    "🤔 Hmm, not quite... think harder, babe!",
    "💫 So close! I believe in you!",
    "🥺 Aww, try once more for me?",
    "💕 Don't give up, sweetheart!",
  ],
  funny: [
    "😬 Oof, swing and a miss!",
    "🤡 That ain't it, chief!",
    "💀 Bruh... try again!",
    "😅 Nice try, but nope!",
    "🧐 Are you even trying?! Haha",
  ],
  gaming: [
    "💔 MISS! Try again, warrior!",
    "⚠️ WRONG INPUT! Retry?",
    "🔄 RESPAWNING... Try again!",
    "❌ GAME OVER... just kidding, retry!",
    "🛡️ BLOCKED! Give it another shot!",
  ],
  birthday: [
    "🎂 Not quite — blow out those candles and try again!",
    "🎈 Oops! Pop that answer bubble and retry!",
    "🎁 Wrong gift! Unwrap another answer!",
    "🥳 Almost! The party's not over yet!",
    "🎊 Keep celebrating and try once more!",
  ],
  friendship: [
    "💜 Are we even friends?! Just kidding, try again.",
    "🤔 Hmm, bestie... think harder!",
    "😅 Not that one! You can do it!",
    "🤷 Close but no cigar! Try again!",
    "💭 Dig deeper into those memories!",
  ],
  spooky: [
    "👻 The shadows whisper... that's wrong.",
    "🕯️ The spirits say no... try again.",
    "💀 Wrong answer... the curse continues!",
    "🦇 The bats are restless. Try once more.",
    "🌙 The moon shakes its head. Retry!",
  ],
  travel: [
    "🗺️ Detour! That's not the right stop.",
    "✈️ Turbulence! Wrong answer, try again.",
    "🧳 Lost luggage! That's not it.",
    "🚂 Wrong platform! Try the next one.",
    "⛵ Off course! Navigate back and retry.",
  ],
  christmas: [
    "🎁 Not time to unwrap that one yet. Try again!",
    "🤔 Hmm, the elves say no.",
    "❄️ Cold, cold! Try again.",
    "🎄 Close, but it's not under the tree.",
    "🔔 Wrong bell. Give it another go!",
  ],
};

const lastLevelMessages = {
  romantic: [
    "🎉 That's the last one! I love you! 💝",
    "💕 Final answer... and it's perfect, like you!",
    "✨ You completed everything! My heart is full!",
  ],
  funny: [
    "🎊 LAST ONE! You absolute legend!",
    "🏆 Final boss DEFEATED! You're unstoppable!",
    "🎉 That's a wrap! Give yourself a round of applause!",
  ],
  gaming: [
    "🏆 FINAL BOSS DEFEATED! GG!",
    "⭐ ALL LEVELS CLEARED! TOTAL VICTORY!",
    "💎 GAME COMPLETE! You're a true champion!",
  ],
  birthday: [
    "🎂 FINAL CANDLE BLOWN! Happy Birthday! 🎉",
    "🎁 All gifts unwrapped! What a celebration!",
    "🥳 Best birthday puzzle EVER! You nailed it!",
  ],
  friendship: [
    "🤝 Best friends forever! You completed everything!",
    "💜 Friendship level: MAXED OUT!",
    "⭐ You really are my ride-or-die!",
  ],
  spooky: [
    "👻 You've survived all the trials! The curse is broken!",
    "🔮 The spirits grant you passage! All mysteries solved!",
    "💀 FINAL BOSS VANQUISHED! The haunting is over!",
  ],
  travel: [
    "✈️ Final destination reached! What a journey!",
    "🗺️ All stamps collected! You're a true explorer!",
    "🌍 Trip complete! Best travel buddy ever!",
  ],
  christmas: [
    "🎄 Every gift unwrapped! Merry Christmas!",
    "🎁 Nothing left under the tree. You found it all!",
    "🌟 You're the star on top of my tree!",
  ],
};

// ── Player: Level Play ──
// `startIndex` + `preview` exist for the creator's live preview (Phase 8): the
// reward screen embeds this very component so what the creator sees is what the
// recipient gets, with no second implementation to drift. In preview mode it
// opens on a given clue and is inert — the wrapper also blocks pointer events.
export function LevelPlay({ config, onFinale, startIndex = 0, preview = false }) {
  const { t: tr } = useTranslation();
  const [currentIdx, setCurrentIdx] = useState(startIndex);
  const [answer, setAnswer] = useState("");
  const [showHint, setShowHint] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'correct' | 'wrong'
  const [confetti, setConfetti] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);
  // Christmas guards (same idea as its gate): `packageOpening` holds from the first
  // submit until a wrong answer or the next package, so a second click/Enter during
  // the async check can't advance twice; `packageAdvancing` does the same for the
  // celebration button; `levelMounted` stops a late check or timer from advancing,
  // opening the finale or touching state after the screen is gone.
  const packageOpening = useRef(false);
  const packageAdvancing = useRef(false);
  const levelMounted = useRef(true);
  useEffect(() => {
    levelMounted.current = true;
    return () => { levelMounted.current = false; };
  }, []);
  const dark = isDarkTheme(config.theme);
  const t = THEMES[config.theme] || THEMES.romantic;
  // An unknown key (a newer seasonal theme) reads the romantic copy in the reader's language.
  const mcKey = resolveThemeKey(config.theme);
  const mc = useThemeMicrocopy(mcKey, THEMES[mcKey].microcopy);

  // Localized celebration / wrong / last-level message arrays (fetched via i18next
  // returnObjects; falls back to the romantic set when a theme key is missing).
  const pickMsgs = (kind) => {
    const arr = tr(`app.play.messages.${kind}.${config.theme}`, { returnObjects: true });
    return Array.isArray(arr) ? arr : tr(`app.play.messages.${kind}.romantic`, { returnObjects: true });
  };
  const wrongMsgs = pickMsgs("wrong");
  const correctMsgs = pickMsgs("correct");
  const lastLevelMsgs = pickMsgs("lastLevel");

  const level = config.levels[currentIdx];
  if (!level) return null;
  const isLast = currentIdx === config.levels.length - 1;
  const hasCustomCelebration = level.celebrationType && level.celebrationType !== "default" && level.celebrationContent;

  const advanceLevel = useCallback(() => {
    const gift = config.theme === "christmas";
    if (gift) {
      if (packageAdvancing.current) return;
      packageAdvancing.current = true;
    }
    setTransitioning(true);
    setTimeout(() => {
      if (gift && !levelMounted.current) return;
      if (isLast) {
        onFinale();
      } else {
        setCurrentIdx(currentIdx + 1);
        setAnswer("");
        setShowHint(false);
        setFeedback(null);
        setConfetti(false);
        setTransitioning(false);
        setShowCelebration(false);
        if (gift) { packageOpening.current = false; packageAdvancing.current = false; }
      }
    }, 400);
  }, [isLast, currentIdx, onFinale, config.theme]);

  const checkAnswerHandler = async () => {
    // The creator's live preview is a picture of this screen, not a game: never
    // hash an answer or log an attempt there.
    if (preview) return;
    const gift = config.theme === "christmas";
    if (gift) {
      if (packageOpening.current) return;
      packageOpening.current = true;
    }
    logger.info("Answer attempt", { puzzleId: config._id || "legacy", level: currentIdx + 1 });
    // Support both hashed (from DB) and plaintext (legacy) answers
    let isCorrect = false;
    if (config._fromDb) {
      isCorrect = await fbCheckAnswer(answer, level.answer);
    } else {
      isCorrect = answer.trim().toLowerCase() === level.answer.trim().toLowerCase();
    }
    if (gift && !levelMounted.current) return;
    // Late timers are dropped once the Christmas screen is gone.
    const later = (fn, ms) => setTimeout(() => { if (!gift || levelMounted.current) fn(); }, ms);
    if (isCorrect) {
      logger.info("Answer correct", { puzzleId: config._id || "legacy", level: currentIdx + 1 });
      setFeedback("correct");
      setConfetti(true);
      if (hasCustomCelebration) {
        later(() => setShowCelebration(true), 600);
      } else {
        later(() => advanceLevel(), 1200);
      }
    } else {
      logger.debug("Answer incorrect", { puzzleId: config._id || "legacy", level: currentIdx + 1 });
      if (gift) packageOpening.current = false;
      setFeedback("wrong");
      // Christmas: a quick right answer after a wrong one must not get wiped by this reset.
      later(() => (gift ? setFeedback((f) => (f === "wrong" ? null : f)) : setFeedback(null)), 1500);
    }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && answer.trim()) checkAnswerHandler();
  };

  // ── Shared sub-renders ──
  const feedbackMsg = feedback === "wrong" ? (
    <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-wrong)", textAlign: "center", marginTop: 10 }}>
      {wrongMsgs[currentIdx % wrongMsgs.length]}
    </div>
  ) : feedback === "correct" && !hasCustomCelebration ? (
    <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-correct)", textAlign: "center", marginTop: 10, fontWeight: 600 }}>
      {isLast
        ? lastLevelMsgs[currentIdx % lastLevelMsgs.length]
        : correctMsgs[currentIdx % correctMsgs.length]}
    </div>
  ) : feedback === "correct" && hasCustomCelebration ? (
    <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "var(--color-correct)", textAlign: "center", marginTop: 10, fontWeight: 600 }}>
      {tr("app.play.messages.nailed")}
    </div>
  ) : null;

  const answerInputStyle = feedback === "correct"
    ? { boxShadow: "0 0 0 2px var(--color-correct)", background: "rgba(46,125,50,0.06)" }
    : feedback === "wrong"
    ? { boxShadow: "0 0 0 2px var(--color-wrong)", background: "rgba(180,19,64,0.06)", animation: "shake 0.4s ease-in-out" }
    : {};

  const levelDots = (
    <div style={{ display: "flex", justifyContent: "center", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
      {config.levels.map((_, i) => (
        <div key={i} style={{
          width: 28, height: 28, borderRadius: config.theme === "gaming" ? 4 : "50%",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, fontFamily: config.theme === "gaming" ? "'JetBrains Mono', monospace" : "var(--font-body)",
          background: i < currentIdx ? "var(--color-correct)" : i === currentIdx ? "var(--color-primary)" : "var(--color-bgAlt)",
          color: i < currentIdx ? "#ffffff" : i === currentIdx ? (dark ? "var(--color-onPrimary)" : "#ffffff") : "var(--color-textDim)",
          transition: "all 0.3s",
        }}>
          {i < currentIdx ? "✓" : i + 1}
        </div>
      ))}
    </div>
  );

  const hintBlock = showHint && level.hint ? (
    <div style={{
      marginTop: 14, padding: "14px 18px",
      borderRadius: config.theme === "gaming" ? 0 : 12,
      background: config.theme === "gaming" ? "rgba(30,41,59,0.8)" : "var(--color-bgAlt)",
      fontFamily: config.theme === "gaming" ? "'JetBrains Mono', monospace" : "var(--font-body)",
      fontSize: config.theme === "gaming" ? 13 : 14,
      color: config.theme === "gaming" ? "#94A3B8" : "var(--color-accent)",
      fontStyle: "italic",
      ...(config.theme === "gaming" ? { border: "1px solid rgba(71,85,105,0.3)" } : {}),
    }}>
      {config.theme === "gaming" ? "🕹️" : "💡"} {level.hint}
    </div>
  ) : null;

  // ── Theme-specific render ──
  const wrapperStyle = {
    display: "flex", flexDirection: "column", alignItems: "center",
    // 100dvh (dynamic viewport) so mobile browser bars don't cause overflow,
    // plus bottom padding to clear the fixed BottomNav so nothing hides behind it.
    minHeight: "100dvh", padding: "16px 24px 120px",
    opacity: transitioning ? 0 : 1,
    transform: transitioning ? "translateY(20px)" : "translateY(0)",
    transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)",
    ...(config.theme === "gaming" ? { background: "#0F172A" } : {}),
  };

  // ── TRAVEL THEME (designer themes/travel.html · "Get it stamped") ──
  if (config.theme === "travel") {
    const navy = "#1c2e52", navyDeep = "#0f1d3a", stamp = "#c0392b", text = "#1c2e52", muted = "#8c9bb5";
    const mono = "'JetBrains Mono', monospace";
    const total = config.levels.length;
    return (
      <div style={{
        ...wrapperStyle, background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 12% 12%, rgba(28,46,82,.10), transparent 42%), radial-gradient(circle at 88% 86%, rgba(192,57,43,.08), transparent 44%), radial-gradient(circle at 50% 55%, rgba(212,167,42,.07), transparent 70%)",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
          {/* Itinerary header + stamped page progress */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
            <div style={{ fontFamily: mono, fontSize: 9, letterSpacing: "0.16em", textTransform: "uppercase", color: muted }}>{tr("app.play.level.travel.itinerary", { n: currentIdx + 1, total })}</div>
            <div style={{ display: "flex", gap: 6 }}>
              {config.levels.map((_, i) => (
                <div key={i} style={{
                  width: 18, height: 18, borderRadius: "50%",
                  border: `1.5px ${i <= currentIdx ? "solid" : "dashed"} ${i <= currentIdx ? stamp : "rgba(28,46,82,.3)"}`,
                  background: i < currentIdx ? "rgba(192,57,43,.12)" : "transparent",
                  display: "grid", placeItems: "center", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 10, color: stamp,
                }}>{i < currentIdx ? "✓" : ""}</div>
              ))}
            </div>
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{
            background: "var(--color-surface)", borderRadius: 28, padding: "26px 22px",
            boxShadow: "0 24px 50px -24px rgba(28,46,82,.4), inset 0 0 0 1px rgba(28,46,82,.06)",
          }}>
            <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>
              {tr("app.play.level.travel.fromItinerary")}{level.title ? <>{" · "}<span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, letterSpacing: "normal", textTransform: "none", color: text }}>{level.title}</span></> : ""}
            </div>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: text, lineHeight: 1.5, margin: "0 0 20px" }}>
              {level.question}
            </p>
            <div style={{ position: "relative", background: "var(--color-bgAlt)", borderRadius: 12, padding: 16, boxShadow: "inset 0 0 0 1px rgba(28,46,82,.12)" }}>
              {feedback === "correct" && (
                <div style={{
                  position: "absolute", top: 8, right: 8, fontFamily: mono, fontSize: 8, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                  color: stamp, border: `1.5px solid ${stamp}`, borderRadius: 4, padding: "3px 7px", transform: "rotate(-8deg)",
                  animation: "travelStamp .6s cubic-bezier(.34,1.56,.64,1) both",
                }}>{tr("app.play.level.travel.approved")}</div>
              )}
              <div onKeyDown={handleKey} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>🛂</span>
                <input
                  value={answer}
                  onChange={(e) => { setAnswer(e.target.value); setFeedback(null); }}
                  placeholder={tr("app.play.level.travel.answerPlaceholder")}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: mono, fontSize: 13, color: text }}
                />
              </div>
              {feedback === "correct" && (
                <div style={{ marginTop: 12, paddingTop: 10, borderTop: "1px dashed rgba(28,46,82,.18)", fontFamily: mono, fontSize: 10, color: stamp, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  {mc.correctAnswer || "Stamped ✓ Approved"}
                </div>
              )}
            </div>
            {feedback === "wrong" && (
              <div style={{ fontFamily: mono, fontSize: 13, color: stamp, marginTop: 12, letterSpacing: "0.05em" }}>
                {wrongMsgs[currentIdx % wrongMsgs.length]}
              </div>
            )}
            {level.hint && !showHint && (
              <button onClick={() => setShowHint(true)} style={{
                alignSelf: "flex-start", marginTop: 14, padding: "6px 12px", borderRadius: 6,
                background: "rgba(28,46,82,.08)", color: text, border: "none", cursor: "pointer",
                fontFamily: mono, fontWeight: 600, fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
              }}>
                📖 {mc.hintButton || "Open the guidebook"}
              </button>
            )}
            {showHint && level.hint && (
              <div style={{ marginTop: 12, fontFamily: mono, fontSize: 12, color: muted }}>📖 {level.hint}</div>
            )}
            <button
              onClick={checkAnswerHandler}
              disabled={!answer.trim() || feedback === "correct"}
              style={{
                width: "100%", marginTop: 20, padding: "14px 18px", borderRadius: 8,
                background: `linear-gradient(135deg, ${navy}, ${navyDeep})`, color: "#fffaf0", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(28,46,82,.5)",
                opacity: (!answer.trim() || feedback === "correct") ? 0.55 : 1,
              }}
            >{mc.checkAnswer || "Get it stamped"}</button>
          </div>
          </div>
        </div>
        {showCelebration && hasCustomCelebration && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(244,236,216,0.95)", backdropFilter: "blur(12px)",
            zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "celebFadeIn 0.4s ease-out",
          }}>
            <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>✈️</div>
              {level.celebrationType === "message" && (
                <div style={{ background: "#fffaf0", borderRadius: 12, padding: 24, marginBottom: 20 }}>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: 18, color: text, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{level.celebrationContent}</p>
                </div>
              )}
              {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
                <div style={{ background: "#fffaf0", borderRadius: 12, padding: 12, marginBottom: 20 }}>
                  <img src={safeMediaUrl(level.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 8, display: "block" }} />
                </div>
              )}
              {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
                <div style={{ background: "#fffaf0", borderRadius: 12, padding: 12, marginBottom: 20 }}>
                  <div style={{ borderRadius: 8, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                    <iframe src={getEmbedUrl(level.celebrationContent)} style={{ width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                </div>
              )}
              {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
                <div style={{ background: "#fffaf0", borderRadius: 12, padding: "20px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                  <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
                </div>
              )}
              <button onClick={advanceLevel} style={{
                padding: "14px 28px", borderRadius: 8, background: `linear-gradient(135deg, ${navy}, ${navyDeep})`,
                color: "#fffaf0", border: "none", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(28,46,82,.5)",
              }}>{isLast ? tr("app.play.level.travel.finalDestination") : (mc.levelComplete || "Next destination →")}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── CHRISTMAS THEME (themes-canvas · Natale · "Pacchetto 1 di 3") ──
  // Every clue is a package: the question hangs on a gift tag tied with gold string.
  if (config.theme === "christmas") {
    const serif = "'Cormorant Garamond', Georgia, serif";
    const total = config.levels.length;
    const unwrapped = feedback === "correct";
    const canCheck = Boolean(answer.trim()) && !unwrapped;
    const notch = 40;
    const shape = (n) => `polygon(${n}px 0, calc(100% - ${n}px) 0, 100% ${n}px, 100% 100%, 0 100%, 0 ${n}px)`;
    // Outer layer is the card's edge, the inner one the white face: a 1.5px border that follows the cut corners.
    const tagShape = shape(notch), innerShape = shape(notch - 0.6);
    const statusText = feedback === "wrong"
      ? wrongMsgs[currentIdx % wrongMsgs.length]
      : unwrapped
      ? (hasCustomCelebration ? tr("app.play.messages.nailed") : isLast ? lastLevelMsgs[currentIdx % lastLevelMsgs.length] : correctMsgs[currentIdx % correctMsgs.length])
      : "";
    const pill = {
      padding: "13px 26px", borderRadius: 999, background: XMAS.cranberry, color: "#fff", border: "none",
      fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, boxShadow: "0 8px 18px -8px rgba(179,38,58,.55)",
    };
    const smallCaps = { fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase" };
    return (
      <div data-testid="level-christmas" style={{
        ...wrapperStyle, background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 15% 10%, rgba(30,77,54,.10), transparent 40%), radial-gradient(circle at 85% 88%, rgba(201,161,74,.14), transparent 45%)",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
          {/* Progress: one small gift per package — opened, in hand, still wrapped */}
          <div aria-hidden="true" style={{ display: "flex", gap: 10, justifyContent: "center", alignItems: "flex-end", marginBottom: 14 }}>
            {config.levels.map((_, i) => {
              const done = i < currentIdx, now = i === currentIdx;
              return (
                <div key={i} style={{
                  position: "relative", width: now ? 24 : 18, height: now ? 22 : 16, borderRadius: 3, boxSizing: "border-box",
                  background: done ? "rgba(201,161,74,.22)" : now ? XMAS.cranberry : "transparent",
                  border: done ? `1.5px solid ${XMAS.gold}` : now ? "none" : "1.5px dashed rgba(179,38,58,.4)",
                  display: "grid", placeItems: "center", color: XMAS.goldDeep, fontSize: 10, fontWeight: 800, transition: "all .3s",
                }}>
                  {now && <>
                    <span style={{ position: "absolute", top: 0, bottom: 0, left: "calc(50% - 2.5px)", width: 5, background: XMAS.gold }} />
                    <span style={{ position: "absolute", left: 0, right: 0, top: "calc(50% - 2.5px)", height: 5, background: XMAS.gold }} />
                  </>}
                  {done ? "✓" : ""}
                </div>
              );
            })}
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ position: "relative", paddingTop: 34, filter: "drop-shadow(0 14px 18px rgba(30,58,43,.18))" }}>
              {/* Gold string running down into the tag's eyelet */}
              <svg aria-hidden="true" width="80" height="66" viewBox="0 0 80 66" style={{ position: "absolute", top: 0, left: "calc(50% - 40px)", zIndex: 2, overflow: "visible" }}>
                <path d="M40 60 C40 44 28 34 33 20 C37 9 48 6 44 -6" fill="none" stroke={XMAS.goldDeep} strokeWidth="2.6" strokeLinecap="round" />
                <path d="M40 60 C41 45 31 35 35.5 21 C39 10 49 7 46.5 -6" fill="none" stroke="#E4C77E" strokeWidth="1.1" strokeLinecap="round" />
              </svg>
              {unwrapped && (
                <div style={{ position: "absolute", top: 46, right: -8, zIndex: 3, transform: "rotate(9deg)" }}>
                  <div style={{
                    ...smallCaps, fontSize: 12, letterSpacing: "0.06em", textTransform: "none", padding: "6px 12px", borderRadius: 999,
                    background: `linear-gradient(90deg, ${XMAS.gold}, #E4C77E 50%, ${XMAS.gold})`, color: XMAS.ink,
                    boxShadow: "0 6px 12px -6px rgba(30,58,43,.5)", animation: "pop-in .45s cubic-bezier(.34,1.56,.64,1) both",
                  }}>{mc.correctAnswer || "Unwrapped! 🎁"}</div>
                </div>
              )}
              <div data-testid="christmas-tag" data-state={unwrapped ? "unwrapped" : "wrapped"} style={{
                position: "relative", background: "#E3D3B2", clipPath: tagShape, padding: 1.5,
                transformOrigin: "50% 0", animation: unwrapped ? "wiggle .45s ease-in-out 2" : feedback === "wrong" ? "shake 0.4s ease-in-out" : "none",
              }}>
              <div style={{ position: "relative", background: "#fff", clipPath: innerShape, padding: "50px 22px 22px", textAlign: "center", color: XMAS.ink }}>
                {/* Eyelet: a hole in the card with a gold ring */}
                <div aria-hidden="true" style={{
                  position: "absolute", top: 16, left: "calc(50% - 8px)", width: 16, height: 16, borderRadius: "50%",
                  background: "#FBF4EA", boxShadow: `0 0 0 3px ${XMAS.gold}, inset 0 1px 3px rgba(30,58,43,.35)`,
                }} />
                <div style={{ ...smallCaps, color: XMAS.cranberryDeep, marginBottom: 6 }}>
                  {tr("app.play.level.christmas.question", { n: currentIdx + 1, total })}
                </div>
                {level.title && (
                  <h2 style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 28, lineHeight: 1.1, color: XMAS.ink, margin: "0 0 10px" }}>{level.title}</h2>
                )}
                <div aria-hidden="true" style={{ width: 56, height: 0, borderTop: "1.5px dashed #CDBB95", margin: "6px auto 14px" }} />
                <p style={{ fontFamily: "var(--font-body)", fontSize: 16, lineHeight: 1.5, color: XMAS.ink, margin: "0 0 18px" }}>{level.question}</p>
                <label htmlFor="level-christmas-answer" style={{ ...smallCaps, fontWeight: 700, display: "block", color: XMAS.body, marginBottom: 8 }}>
                  {tr("app.play.level.christmas.answerLabel")}
                </label>
                <input
                  id="level-christmas-answer"
                  value={answer}
                  disabled={unwrapped}
                  onChange={(e) => { setAnswer(e.target.value); setFeedback(null); }}
                  onKeyDown={handleKey}
                  placeholder={tr("app.play.level.christmas.answerPlaceholder")}
                  aria-invalid={feedback === "wrong" || undefined}
                  aria-describedby="level-christmas-status"
                  autoComplete="off"
                  style={{
                    width: "100%", boxSizing: "border-box", background: unwrapped ? "rgba(46,125,91,.08)" : "#FBF4EA", borderRadius: 12, padding: "12px 12px",
                    border: feedback === "wrong" ? `2px solid ${XMAS.cranberry}` : unwrapped ? "2px solid #2E7D5B" : "1.5px dashed #CDBB95",
                    fontFamily: "var(--font-body)", fontSize: 16, color: XMAS.ink, textAlign: "center", outline: "none",
                  }}
                />
                <div id="level-christmas-status" role="status" aria-live="polite" style={{
                  fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 18, lineHeight: 1.25,
                  color: feedback === "wrong" ? XMAS.cranberryDeep : "#1E4D36", marginTop: statusText ? 12 : 0,
                }}>{statusText}</div>
                {level.hint && !showHint && (
                  <button type="button" onClick={() => setShowHint(true)} style={{
                    marginTop: 14, padding: "7px 14px", borderRadius: 999, background: "rgba(201,161,74,.16)", color: "#6E531A",
                    border: "none", cursor: "pointer", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13,
                  }}>
                    <span aria-hidden="true">🧝 </span>{mc.hintButton || "A little help from the elves"}
                  </button>
                )}
                {showHint && level.hint && (
                  <div style={{
                    marginTop: 14, padding: "10px 14px", borderRadius: 12, background: "#FBF4EA", borderLeft: `3px solid ${XMAS.gold}`,
                    fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 18, lineHeight: 1.3, color: XMAS.ink, textAlign: "left",
                  }}><span aria-hidden="true" style={{ color: XMAS.goldDeep }}>✦ </span>{level.hint}</div>
                )}
                <button
                  onClick={checkAnswerHandler}
                  disabled={!canCheck}
                  style={{ ...pill, width: "100%", marginTop: 16, cursor: canCheck ? "pointer" : "not-allowed", opacity: canCheck || unwrapped ? 1 : 0.6 }}
                >{mc.checkAnswer || "Unwrap"} <span aria-hidden="true">🎁</span></button>
              </div>
              </div>
            </div>
            <div style={{ marginTop: 16, textAlign: "center", fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 17, color: XMAS.body }}>
              {unwrapped ? (mc.levelComplete || "One more gift opened.") : (mc.levelTagline || "Untying the next ribbon...")}
            </div>
          </div>
        </div>
        {showCelebration && hasCustomCelebration && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(251,244,234,0.96)", backdropFilter: "blur(12px)",
            zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "celebFadeIn 0.4s ease-out",
          }}>
            <div style={{ maxWidth: 400, width: "100%", textAlign: "center", color: XMAS.ink }}>
              <div aria-hidden="true" style={{ fontSize: 48, marginBottom: 8 }}>🎁</div>
              <div style={{ ...smallCaps, color: XMAS.cranberryDeep, marginBottom: 16 }}>{mc.correctAnswer || "Unwrapped! 🎁"}</div>
              {level.celebrationType === "message" && (
                <div style={{ background: "#fff", border: "1px solid #EADFCB", borderRadius: 18, padding: 24, marginBottom: 20 }}>
                  <p style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 22, color: XMAS.ink, lineHeight: 1.45, margin: 0, whiteSpace: "pre-wrap" }}>{level.celebrationContent}</p>
                </div>
              )}
              {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
                <div style={{ background: "#fff", border: "1px solid #EADFCB", borderRadius: 18, padding: 12, marginBottom: 20 }}>
                  <img src={safeMediaUrl(level.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12, display: "block" }} />
                </div>
              )}
              {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
                <div style={{ background: "#fff", border: "1px solid #EADFCB", borderRadius: 18, padding: 12, marginBottom: 20 }}>
                  <div style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                    <iframe src={getEmbedUrl(level.celebrationContent)} style={{ width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                </div>
              )}
              {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
                <div style={{ background: "#fff", border: "1px solid #EADFCB", borderRadius: 18, padding: "20px 16px", marginBottom: 20 }}>
                  <div aria-hidden="true" style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                  <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
                </div>
              )}
              <button onClick={advanceLevel} style={{ ...pill, cursor: "pointer" }}>
                {isLast ? tr("app.play.level.christmas.readNote") : tr("app.play.level.christmas.next")}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── FRIENDSHIP THEME (designer themes/friendship.html · "Paper-chain progress") ──
  if (config.theme === "friendship") {
    const sage = "#5c8c6b", sageDeep = "#3d6b4c", text = "#2c3e34", muted = "#94a99e", success = "#2db678";
    const total = config.levels.length;
    return (
      <div style={{
        ...wrapperStyle, background: "var(--color-bg)",
        backgroundImage: "repeating-linear-gradient(var(--color-bg), var(--color-bg) 27px, rgba(92,140,107,.08) 28px)",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
          {/* Paper-chain ring progress */}
          <div style={{ display: "flex", gap: 8, marginBottom: 22, alignItems: "center", justifyContent: "center" }}>
            {config.levels.map((_, i) => (
              <Fragment key={i}>
                {i > 0 && <span style={{ width: 8, height: 1.5, background: i <= currentIdx ? sage : "rgba(92,140,107,.3)" }} />}
                <div style={{
                  width: 18, height: 18, borderRadius: "50%", border: `3px solid ${i <= currentIdx ? sage : "rgba(92,140,107,.3)"}`,
                  background: i < currentIdx ? sage : "transparent",
                }} />
              </Fragment>
            ))}
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{
            background: "var(--color-surface)", borderRadius: 28, padding: "26px 22px",
            boxShadow: "0 24px 50px -24px rgba(60,80,68,.4), inset 0 0 0 1px rgba(60,80,68,.06)",
          }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: sage, marginBottom: 8 }}>
              {tr("app.play.level.friendship.question", { n: currentIdx + 1, total })}{level.title ? <>{" · "}<span style={{ fontSize: 18, letterSpacing: "normal", textTransform: "none", color: text, fontWeight: 800 }}>{level.title}</span></> : ""}
            </div>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: text, lineHeight: 1.5, margin: "0 0 20px" }}>
              {level.question}
            </p>
            <div style={{ background: "var(--color-bgAlt)", borderRadius: 16, padding: 16, boxShadow: "inset 0 0 0 1px rgba(60,80,68,.12)" }}>
              <div onKeyDown={handleKey} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>✏️</span>
                <input
                  value={answer}
                  onChange={(e) => { setAnswer(e.target.value); setFeedback(null); }}
                  placeholder={tr("app.play.level.friendship.answerPlaceholder")}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: "var(--font-body)", fontSize: 15, color: text }}
                />
                {feedback === "correct" && (
                  <span style={{ width: 26, height: 26, borderRadius: "50%", background: success, color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 12 }}>✓</span>
                )}
              </div>
              {feedback === "correct" && (
                <div style={{ marginTop: 10, fontFamily: "var(--font-body)", fontWeight: 600, fontSize: 12, color: sageDeep }}>
                  {mc.correctAnswer || "I knew you'd get it!"}
                </div>
              )}
            </div>
            {feedback === "wrong" && (
              <div style={{ fontFamily: "var(--font-body)", fontSize: 14, color: "#c84d6e", marginTop: 12 }}>
                {wrongMsgs[currentIdx % wrongMsgs.length]}
              </div>
            )}
            {level.hint && !showHint && (
              <button onClick={() => setShowHint(true)} style={{
                alignSelf: "flex-start", marginTop: 14, padding: "6px 14px", borderRadius: 999,
                background: "rgba(245,197,66,.18)", color: "#8a6b14", border: "none", cursor: "pointer",
                fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12,
              }}>
                💡 {mc.hintButton || "Need a nudge?"}
              </button>
            )}
            {showHint && level.hint && (
              <div style={{ marginTop: 12, fontFamily: "var(--font-body)", fontSize: 14, color: muted }}>💡 {level.hint}</div>
            )}
            <button
              onClick={checkAnswerHandler}
              disabled={!answer.trim() || feedback === "correct"}
              style={{
                width: "100%", marginTop: 20, padding: "14px 18px", borderRadius: 12,
                background: `linear-gradient(135deg, ${sage}, ${sageDeep})`, color: "#fff", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(92,140,107,.5)",
                opacity: (!answer.trim() || feedback === "correct") ? 0.55 : 1,
              }}
            >{mc.checkAnswer || "Lock it in →"}</button>
          </div>
          </div>
        </div>
        {showCelebration && hasCustomCelebration && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(250,243,227,0.95)", backdropFilter: "blur(12px)",
            zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "celebFadeIn 0.4s ease-out",
          }}>
            <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🤝</div>
              {level.celebrationType === "message" && (
                <div style={{ background: "#fff", borderRadius: 16, padding: 24, marginBottom: 20 }}>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: 18, color: text, lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{level.celebrationContent}</p>
                </div>
              )}
              {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
                <div style={{ background: "#fff", borderRadius: 16, padding: 12, marginBottom: 20 }}>
                  <img src={safeMediaUrl(level.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12, display: "block" }} />
                </div>
              )}
              {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
                <div style={{ background: "#fff", borderRadius: 16, padding: 12, marginBottom: 20 }}>
                  <div style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                    <iframe src={getEmbedUrl(level.celebrationContent)} style={{ width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                </div>
              )}
              {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
                <div style={{ background: "#fff", borderRadius: 16, padding: "20px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                  <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
                </div>
              )}
              <button onClick={advanceLevel} style={{
                padding: "14px 28px", borderRadius: 12, background: `linear-gradient(135deg, ${sage}, ${sageDeep})`,
                color: "#fff", border: "none", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(92,140,107,.5)",
              }}>{isLast ? tr("app.play.level.friendship.readNote") : tr("app.play.level.friendship.next")}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── SPOOKY THEME (designer themes/spooky.html · "The veil lifts") ──
  if (config.theme === "spooky") {
    const accent = "#b88dd6", accentDeep = "#7d4ca8", textSoft = "#c9b8df", muted = "#8b7aa4", success = "#7ad7a4";
    const total = config.levels.length;
    return (
      <div style={{
        ...wrapperStyle,
        background: "var(--color-bg)",
        backgroundImage: "radial-gradient(ellipse at 50% -10%, rgba(184,141,214,.22), transparent 55%), radial-gradient(circle at 85% 80%, rgba(125,76,168,.14), transparent 50%)",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
          {/* Progress bars */}
          <div style={{ display: "flex", gap: 6, marginBottom: 22 }}>
            {config.levels.map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 5, borderRadius: 3,
                background: i <= currentIdx ? `linear-gradient(90deg, ${accent}, ${accentDeep})` : "rgba(184,141,214,.12)",
              }} />
            ))}
          </div>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div style={{
            background: "var(--color-surface)", borderRadius: 28, padding: "26px 22px",
            boxShadow: "0 24px 50px -24px rgba(0,0,0,.6), inset 0 0 0 1px rgba(184,141,214,.14)",
          }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: accent, marginBottom: 4 }}>
              {tr("app.play.level.spooky.chapter", { n: currentIdx + 1, total })}
            </div>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: accent, fontStyle: "italic", marginBottom: 8 }}>
              {tr("app.play.level.spooky.fromBeyond")}{level.title ? <>{" · "}<span style={{ fontStyle: "normal", fontSize: 18, letterSpacing: "normal", textTransform: "none", color: "var(--color-text)", fontWeight: 800 }}>{level.title}</span></> : ""}
            </div>
            <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-text)", lineHeight: 1.5, margin: "0 0 20px" }}>
              {level.question}
            </p>

            <div style={{ background: "var(--color-bgAlt)", borderRadius: 18, padding: 16, boxShadow: "inset 0 0 0 1px rgba(184,141,214,.18)" }}>
              <div onKeyDown={handleKey} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22, filter: "drop-shadow(0 0 8px rgba(184,141,214,.4))" }}>🕯️</span>
                <input
                  value={answer}
                  onChange={(e) => { setAnswer(e.target.value); setFeedback(null); }}
                  placeholder={tr("app.play.level.spooky.answerPlaceholder")}
                  style={{ flex: 1, background: "transparent", border: "none", outline: "none", fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-text)" }}
                />
                {feedback === "correct" && (
                  <span style={{ width: 24, height: 24, borderRadius: "50%", background: success, color: "var(--color-bg)", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 12 }}>✓</span>
                )}
              </div>
              {feedback === "correct" && (
                <div style={{ marginTop: 10, fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 12, color: muted }}>
                  {mc.correctAnswer || "The veil lifts."}
                </div>
              )}
            </div>

            {feedback === "wrong" && (
              <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 14, color: accent, marginTop: 12 }}>
                {wrongMsgs[currentIdx % wrongMsgs.length]}
              </div>
            )}

            {level.hint && !showHint && (
              <button onClick={() => setShowHint(true)} style={{
                alignSelf: "flex-start", marginTop: 14, padding: "6px 14px", borderRadius: 999,
                background: "rgba(184,141,214,.12)", color: accent, border: "none", cursor: "pointer",
                fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 14,
              }}>
                🔮 {mc.hintButton || "A whisper from the beyond"}
              </button>
            )}
            {showHint && level.hint && (
              <div style={{ marginTop: 12, fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 14, color: textSoft }}>
                🔮 {level.hint}
              </div>
            )}

            <button
              onClick={checkAnswerHandler}
              disabled={!answer.trim() || feedback === "correct"}
              style={{
                width: "100%", marginTop: 20, padding: "14px 18px", borderRadius: 14,
                background: `linear-gradient(135deg, ${accent}, ${accentDeep})`, color: "#1a1025", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: "pointer",
                boxShadow: "0 0 18px rgba(184,141,214,.4)",
                opacity: (!answer.trim() || feedback === "correct") ? 0.55 : 1,
              }}
            >{mc.checkAnswer || "Conjure"}</button>
          </div>
          </div>
        </div>

        {showCelebration && hasCustomCelebration && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(26,16,37,0.94)", backdropFilter: "blur(12px)",
            zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, animation: "celebFadeIn 0.4s ease-out",
          }}>
            <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🔮</div>
              {level.celebrationType === "message" && (
                <div style={{ background: "var(--color-surface)", borderRadius: 18, padding: 24, marginBottom: 20 }}>
                  <p style={{ fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)", lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap" }}>{level.celebrationContent}</p>
                </div>
              )}
              {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
                <div style={{ background: "var(--color-surface)", borderRadius: 18, padding: 12, marginBottom: 20 }}>
                  <img src={safeMediaUrl(level.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12, display: "block" }} />
                </div>
              )}
              {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
                <div style={{ background: "var(--color-surface)", borderRadius: 18, padding: 12, marginBottom: 20 }}>
                  <div style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                    <iframe src={getEmbedUrl(level.celebrationContent)} style={{ width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                </div>
              )}
              {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
                <div style={{ background: "var(--color-surface)", borderRadius: 18, padding: "20px 16px", marginBottom: 20 }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                  <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
                </div>
              )}
              <button onClick={advanceLevel} style={{
                padding: "14px 28px", borderRadius: 14, background: `linear-gradient(135deg, ${accent}, ${accentDeep})`,
                color: "#1a1025", border: "none", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, cursor: "pointer",
                boxShadow: "0 0 18px rgba(184,141,214,.4)",
              }}>{isLast ? tr("app.play.level.spooky.reveal") : tr("app.play.level.spooky.nextChapter")}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── ROMANTIC THEME (designer themes/romantic.html · "Sealed with a kiss") ──
  if (config.theme === "romantic") {
    const wine = "#4a0a1f", roseDeep = "#9c1a44", wax = "#a01a30", textSoft = "#6e1e3a", muted = "#a87a8a";
    const serif = "'Cormorant Garamond', serif";
    const total = config.levels.length;
    return (
      <div style={{
        ...wrapperStyle,
        background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 12% 14%, rgba(200,52,95,.16), transparent 42%), radial-gradient(circle at 88% 84%, rgba(201,161,74,.12), transparent 46%), radial-gradient(circle at 50% 50%, rgba(244,184,196,.10), transparent 70%)",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 420, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
          {/* Progress = wax-seal medallions */}
          <div style={{ display: "flex", gap: 10, marginBottom: 22, alignItems: "center", justifyContent: "center" }}>
            {config.levels.map((_, i) => (
              <Fragment key={i}>
                {i > 0 && <span style={{ width: 14, height: 1.5, background: i <= currentIdx ? roseDeep : "rgba(74,10,31,.2)", opacity: i <= currentIdx ? 0.5 : 1 }} />}
                {i < currentIdx ? (
                  <WaxSeal char="✓" size={24} />
                ) : i === currentIdx ? (
                  <WaxSeal char="✓" size={24} press />
                ) : (
                  <div style={{ width: 24, height: 24, borderRadius: "50%", border: "1.5px dashed rgba(74,10,31,.3)" }} />
                )}
              </Fragment>
            ))}
          </div>

          <div style={{
            flex: 1, display: "flex", flexDirection: "column", justifyContent: "center",
            maxHeight: "none",
          }}>
          <div style={{
            background: "var(--color-surface)", borderRadius: 28, padding: "26px 22px",
            boxShadow: "0 24px 50px -24px rgba(74,10,31,.35), inset 0 0 0 1px rgba(74,10,31,.06)",
          }}>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 15, color: roseDeep, marginBottom: 10, lineHeight: 1.35 }}>
              {tr("app.play.level.romantic.page", { n: currentIdx + 1, total })}
              {level.title ? <>{" — "}<span style={{ fontStyle: "normal", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, color: wine, letterSpacing: "-0.02em" }}>{level.title}</span></> : ""}
            </div>
            <p style={{ fontFamily: serif, fontSize: 16, color: wine, lineHeight: 1.5, margin: "0 0 20px" }}>
              {level.question}
            </p>

            {/* Answer field — inset on the letter */}
            <div style={{
              background: "var(--color-bgAlt)", borderRadius: 16, padding: 16,
              boxShadow: "inset 0 0 0 1px rgba(74,10,31,.1)", position: "relative",
            }}>
              <div onKeyDown={handleKey} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ fontSize: 22 }}>💌</span>
                <input
                  value={answer}
                  onChange={(e) => { setAnswer(e.target.value); setFeedback(null); }}
                  placeholder={tr("app.play.level.romantic.answerPlaceholder")}
                  style={{
                    flex: 1, background: "transparent", border: "none", outline: "none",
                    fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 16, color: wine,
                  }}
                />
              </div>
              {feedback === "correct" && (
                <div style={{
                  marginTop: 12, paddingTop: 10, borderTop: "1px dashed rgba(74,10,31,.15)",
                  display: "flex", alignItems: "center", gap: 8,
                }}>
                  <span style={{ color: wax, fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 15 }}>
                    {mc.correctAnswer || "Sealed with a kiss."}
                  </span>
                  <span style={{ marginLeft: "auto" }}>💋</span>
                </div>
              )}
            </div>

            {feedback === "wrong" && (
              <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 15, color: roseDeep, marginTop: 12 }}>
                {wrongMsgs[currentIdx % wrongMsgs.length]}
              </div>
            )}

            {level.hint && !showHint && (
              <button onClick={() => setShowHint(true)} style={{
                alignSelf: "flex-start", marginTop: 14, padding: "6px 14px", borderRadius: 999,
                background: "rgba(200,52,95,.1)", color: roseDeep, border: "none", cursor: "pointer",
                fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 14,
              }}>
                💡 {mc.hintButton || "Read between the lines"}
              </button>
            )}
            {showHint && level.hint && (
              <div style={{ marginTop: 12, fontFamily: serif, fontStyle: "italic", fontSize: 14, color: textSoft }}>
                💡 {level.hint}
              </div>
            )}

            <button
              onClick={checkAnswerHandler}
              disabled={!answer.trim() || feedback === "correct"}
              style={{
                width: "100%", marginTop: 20, padding: "14px 18px", borderRadius: 14,
                background: "linear-gradient(135deg, #c8345f, #9c1a44)", color: "#fff", border: "none",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 14, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(200,52,95,.5)",
                opacity: (!answer.trim() || feedback === "correct") ? 0.55 : 1,
              }}
            >{mc.checkAnswer || "Seal it"}</button>
          </div>
          </div>
        </div>

        {/* Celebration overlay (custom celebrations) reuses generic below via early returns? No — render inline */}
        {showCelebration && hasCustomCelebration && (
          <div style={{
            position: "fixed", inset: 0, background: "rgba(253,242,242,0.94)", backdropFilter: "blur(12px)",
            zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
            animation: "celebFadeIn 0.4s ease-out",
          }}>
            <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>💝</div>
              {level.celebrationType === "message" && (
                <div style={{ background: "#fff", borderRadius: 18, padding: 24, marginBottom: 20, boxShadow: "0 8px 24px -10px rgba(74,10,31,.22)" }}>
                  <p style={{ fontFamily: serif, fontStyle: "italic", fontSize: 18, color: wine, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{level.celebrationContent}</p>
                </div>
              )}
              {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
                <div style={{ background: "#fff", borderRadius: 18, padding: 12, marginBottom: 20, boxShadow: "0 8px 24px -10px rgba(74,10,31,.22)" }}>
                  <img src={safeMediaUrl(level.celebrationContent, "image")} alt={tr("app.builder.celebration.photoAlt")} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 12, display: "block" }} />
                </div>
              )}
              {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
                <div style={{ background: "#fff", borderRadius: 18, padding: 12, marginBottom: 20, boxShadow: "0 8px 24px -10px rgba(74,10,31,.22)" }}>
                  <div style={{ borderRadius: 12, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                    <iframe src={getEmbedUrl(level.celebrationContent)} style={{ width: "100%", height: "100%", border: "none" }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope" allowFullScreen />
                  </div>
                </div>
              )}
              {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
                <div style={{ background: "#fff", borderRadius: 18, padding: "20px 16px", marginBottom: 20, boxShadow: "0 8px 24px -10px rgba(74,10,31,.22)" }}>
                  <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                  <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
                </div>
              )}
              <button onClick={advanceLevel} style={{
                padding: "14px 28px", borderRadius: 14, background: "linear-gradient(135deg, #c8345f, #9c1a44)",
                color: "#fff", border: "none", fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, cursor: "pointer",
                boxShadow: "0 8px 18px -6px rgba(200,52,95,.5)",
              }}>{isLast ? tr("app.play.level.romantic.readLetter") : tr("app.play.level.romantic.nextPage")}</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── GAMING THEME ──
  if (config.theme === "gaming") {
    return (
      <div style={wrapperStyle}>
        {/* Scanline */}
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 100, opacity: 0.1,
          backgroundImage: "linear-gradient(to bottom, rgba(255,255,255,0) 50%, rgba(0,0,0,0.1) 50%)",
          backgroundSize: "100% 4px",
        }} />
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)", position: "relative", zIndex: 1 }}>
          {/* Header */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#94A3B8", letterSpacing: "0.2em", textTransform: "uppercase" }}>
              {tr("app.play.level.gaming.levelOfTotal", { n: currentIdx + 1, total: config.levels.length })}
            </span>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "#A855F7", letterSpacing: "0.3em", textTransform: "uppercase", fontWeight: 700 }}>
              {tr("app.play.level.gaming.brand")}
            </span>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {/* Level title */}
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 26, fontWeight: 800,
              color: "#F8FAFC", margin: "0 0 8px", textTransform: "uppercase", fontStyle: "italic",
            }}>
              {tr("app.play.level.gaming.levelTitle", { n: currentIdx + 1, title: level.title || tr("app.play.level.gaming.titleFallback") })}
            </h2>
            <div style={{
              display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20,
              background: "rgba(168,85,247,0.15)", padding: "4px 12px", borderRadius: 4,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#A855F7",
              textTransform: "uppercase", letterSpacing: "0.15em", fontWeight: 700, alignSelf: "flex-start",
            }}>
              {tr("app.play.level.gaming.questActive")}
            </div>

            {/* Quest Log card — nested dark containers */}
            <div style={{
              background: "#0F172A", padding: 4, marginBottom: 20,
              border: "4px solid rgba(79,70,229,0.4)",
              boxShadow: "8px 8px 0px 0px rgba(96,70,190,0.4)",
            }}>
              <div style={{
                background: "#1E293B", padding: 24,
                border: "2px solid rgba(99,102,241,0.2)",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                  <span style={{ fontSize: 16 }}>💻</span>
                  <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#818CF8", textTransform: "uppercase", letterSpacing: "0.2em", fontWeight: 700 }}>
                    {tr("app.play.level.gaming.incomingMessage")}
                  </span>
                </div>
                <p style={{
                  fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 18, fontWeight: 700,
                  color: "#F8FAFC", lineHeight: 1.6, margin: 0,
                }}>
                  "{level.question}"
                </p>
              </div>
            </div>

            {/* Answer input */}
            <div style={{
              background: "#1E293B", padding: 20, marginBottom: 16,
              clipPath: "polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))",
            }}>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.15em", marginBottom: 8 }}>
                {tr("app.play.level.gaming.playerAnswer")}
              </div>
              <div onKeyDown={handleKey}>
                <Input value={answer} onChange={(v) => { setAnswer(v); setFeedback(null); }}
                  placeholder={tr("app.play.level.gaming.answerPlaceholder")}
                  style={{
                    background: "#0F172A", border: "1px solid rgba(71,85,105,0.3)", borderRadius: 0,
                    fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: "#F8FAFC",
                    textTransform: "uppercase", letterSpacing: "0.1em",
                    ...answerInputStyle,
                  }} />
              </div>
            </div>

            {feedbackMsg}

            {/* Buttons */}
            <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
              {level.hint && !showHint && (
                <button onClick={() => setShowHint(true)} style={{
                  flex: 1, padding: "14px 16px", background: "transparent",
                  border: "1px solid rgba(71,85,105,0.3)", color: "#94A3B8", cursor: "pointer",
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 12, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.15em",
                  clipPath: "polygon(0 4px, 4px 0, calc(100% - 4px) 0, 100% 4px, 100% calc(100% - 4px), calc(100% - 4px) 100%, 4px 100%, 0 calc(100% - 4px))",
                }}>
                  {tr("app.play.level.gaming.unlockHint")}
                </button>
              )}
              <button onClick={checkAnswerHandler} disabled={!answer.trim() || feedback === "correct"}
                style={{
                  flex: 2, padding: "16px 24px", background: "#A855F7", color: "#fff",
                  border: "none", cursor: "pointer",
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 13, fontWeight: 700,
                  textTransform: "uppercase", letterSpacing: "0.2em",
                  boxShadow: "0 0 15px rgba(168,85,247,0.4)",
                  clipPath: "polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% calc(100% - 6px), calc(100% - 6px) 100%, 6px 100%, 0 calc(100% - 6px))",
                  opacity: (!answer.trim() || feedback === "correct") ? 0.5 : 1,
                }}>
                {tr("app.play.level.gaming.submitAnswer")}
              </button>
            </div>

            {hintBlock}
          </div>

          {/* Bottom stats */}
          <div style={{ paddingBottom: 8 }}>
            <div style={{
              display: "flex", justifyContent: "space-between", marginBottom: 12, padding: "10px 16px",
              background: "#1E293B", borderRadius: 4,
              fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: "#94A3B8",
              textTransform: "uppercase", letterSpacing: "0.1em",
            }}>
              <span>{tr("app.play.level.gaming.progress")}</span>
              <span style={{ color: "#A855F7" }}>{currentIdx}/{config.levels.length}</span>
            </div>
            {levelDots}
            <ProgressBar current={currentIdx} total={config.levels.length} />
          </div>
        </div>
      {/* Celebration Overlay */}
      {showCelebration && hasCustomCelebration && (
        <div style={{
          position: "fixed", inset: 0,
          background: dark ? "rgba(15,21,36,0.9)" : "rgba(252,245,247,0.92)",
          backdropFilter: "blur(12px)",
          zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, animation: "celebFadeIn 0.4s ease-out",
        }}>
          <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {["🏆","⚡","💎","🎮","🔥","⭐"][currentIdx % 6]}
            </div>

            {level.celebrationType === "message" && (
              <Card gaming={dark} style={{ marginBottom: 20 }}>
                <p style={{
                  fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)",
                  lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap",
                }}>
                  {level.celebrationContent}
                </p>
              </Card>
            )}

            {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: 12 }}>
                <img
                  src={safeMediaUrl(level.celebrationContent, "image")}
                  alt={tr("app.builder.celebration.photoAlt")}
                  style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 16, display: "block" }}
                />
              </Card>
            )}

            {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: 12 }}>
                <div style={{ borderRadius: 16, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                  <iframe
                    src={getEmbedUrl(level.celebrationContent)}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                </div>
              </Card>
            )}

            {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: "20px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 12 }}>
                  {tr("app.common.aSongForYou")}
                </div>
                <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
              </Card>
            )}

            <Btn onClick={advanceLevel}>
              {isLast ? tr("app.play.common.seeFinalMessage") : tr("app.common.continueArrow")}
            </Btn>
          </div>
        </div>
      )}

{/* shake + celebFadeIn keyframes live in public/index.html */}
    </div>
    );
  }

  // ── FUNNY THEME ──
  if (config.theme === "funny") {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column",
        background: "#ffe55c", position: "relative", overflow: "hidden",
        backgroundImage: "radial-gradient(circle, rgba(26,10,20,.07) 1px, transparent 1.5px)",
        backgroundSize: "13px 13px",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 480, margin: "0 auto", display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)", padding: "20px 24px 32px", position: "relative", zIndex: 1 }}>
          {/* Ink-bordered progress bars + eyebrow */}
          <div style={{ display: "flex", gap: 6, marginBottom: 14 }}>
            {config.levels.map((_, i) => (
              <div key={i} style={{
                flex: 1, height: 10, borderRadius: 6,
                background: i <= currentIdx ? "#ff2e6e" : "#fffbed", border: "2px solid #1a0a14",
              }} />
            ))}
          </div>
          <div style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 11,
            color: "#d11a55", textTransform: "uppercase", letterSpacing: "0.16em", marginBottom: 20,
          }}>
            {tr("app.play.level.funny.question", { n: currentIdx + 1, total: config.levels.length })}
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {/* Level title + question headline */}
            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 28, fontWeight: 800,
              color: "#1a0a14", margin: "0 0 8px", lineHeight: 1.1, letterSpacing: "-0.02em",
            }}>
              {level.title || tr("app.play.level.funny.levelFallback", { n: currentIdx + 1 })}
            </h2>
            <p style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 16, fontWeight: 600,
              color: "#1a0a14", margin: "0 0 22px", lineHeight: 1.4,
            }}>
              {level.question}
            </p>

            {/* Answer input with OK GENIUS. burst */}
            <div style={{ position: "relative" }}>
              <div style={{
                position: "absolute", top: -22, right: -6, zIndex: 3,
                width: 54, height: 54, display: "grid", placeItems: "center",
                background: "#ff2e6e", color: "#fff", transform: "rotate(-12deg)",
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 10, lineHeight: 1, textAlign: "center",
                textShadow: "1.5px 1.5px 0 #1a0a14", clipPath: BURST_CLIP,
              }}>{tr("app.play.level.funny.burst").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>
              <div onKeyDown={handleKey}>
                <Input value={answer} onChange={(v) => { setAnswer(v); setFeedback(null); }}
                  placeholder={tr("app.play.level.funny.answerPlaceholder")}
                  style={{
                    background: "#fffbed", border: "2.5px solid #1a0a14", borderRadius: 12,
                    fontFamily: "'Be Vietnam Pro', sans-serif", fontSize: 16, fontWeight: 600, color: "#1a0a14",
                    boxShadow: "3px 3px 0 #1a0a14",
                    ...answerInputStyle,
                  }} />
              </div>
            </div>

            {feedbackMsg}

            {level.hint && !showHint && (
              <button onClick={() => setShowHint(true)} style={{
                alignSelf: "flex-start", marginTop: 14, padding: "8px 16px", background: "#fff",
                border: "2.5px solid #1a0a14", borderRadius: 999, color: "#d11a55", cursor: "pointer",
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 12.5, fontWeight: 800,
                boxShadow: "3px 3px 0 #1a0a14",
              }}>
                {tr("app.play.level.funny.begHint")}
              </button>
            )}

            {hintBlock}

            <button onClick={checkAnswerHandler} disabled={!answer.trim() || feedback === "correct"}
              style={{
                width: "100%", marginTop: 20, padding: "16px",
                background: "#ff2e6e", color: "#fff", border: "2.5px solid #1a0a14", borderRadius: 12,
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 15, fontWeight: 800,
                letterSpacing: "0.04em", textTransform: "uppercase", textShadow: "1px 1px 0 #d11a55",
                cursor: "pointer", boxShadow: "4px 4px 0 #1a0a14",
                opacity: (!answer.trim() || feedback === "correct") ? 0.55 : 1,
              }}>
              {mc.checkAnswer || "Send it"}
            </button>
          </div>
        </div>
      {/* Celebration Overlay */}
      {showCelebration && hasCustomCelebration && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(252,245,247,0.92)",
          backdropFilter: "blur(12px)",
          zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, animation: "celebFadeIn 0.4s ease-out",
        }}>
          <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {["🎉","🥳","🎊","🔥","⭐","🤩"][currentIdx % 6]}
            </div>

            {level.celebrationType === "message" && (
              <Card style={{ marginBottom: 20 }}>
                <p style={{
                  fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)",
                  lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap",
                }}>
                  {level.celebrationContent}
                </p>
              </Card>
            )}

            {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
              <Card style={{ marginBottom: 20, padding: 12 }}>
                <img
                  src={safeMediaUrl(level.celebrationContent, "image")}
                  alt={tr("app.builder.celebration.photoAlt")}
                  style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 16, display: "block" }}
                />
              </Card>
            )}

            {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
              <Card style={{ marginBottom: 20, padding: 12 }}>
                <div style={{ borderRadius: 16, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                  <iframe
                    src={getEmbedUrl(level.celebrationContent)}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                </div>
              </Card>
            )}

            {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
              <Card style={{ marginBottom: 20, padding: "20px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 12 }}>
                  {tr("app.common.aSongForYou")}
                </div>
                <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
              </Card>
            )}

            <Btn onClick={advanceLevel}>
              {isLast ? tr("app.play.common.seeFinalMessage") : tr("app.common.continueArrow")}
            </Btn>
          </div>
        </div>
      )}

{/* shake + celebFadeIn keyframes live in public/index.html */}
    </div>
    );
  }

  // ── BIRTHDAY THEME ──
  if (config.theme === "birthday") {
    return (
      <div style={wrapperStyle}>
        {/* Confetti background pattern */}
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.08,
          backgroundImage: "radial-gradient(circle, #ff709f 10%, transparent 10.5%), radial-gradient(circle, #ffc87d 10%, transparent 10.5%), radial-gradient(circle, #6046be 10%, transparent 10.5%)",
          backgroundSize: "40px 40px", backgroundPosition: "0 0, 20px 20px, 10px 30px",
        }} />
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)", position: "relative", zIndex: 1 }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            {/* Level badge centered */}
            <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
              <span style={{
                background: "#ffc87d", color: "#644000", fontFamily: "'Be Vietnam Pro', sans-serif",
                fontWeight: 700, fontSize: 13, padding: "8px 22px", borderRadius: 999,
                letterSpacing: "0.18em", textTransform: "uppercase",
              }}>
                {tr("app.play.level.birthday.levelBadge", { n: currentIdx + 1 })}
              </span>
            </div>

            {/* Heading with gradient underline */}
            <div style={{ marginBottom: 24, textAlign: "center" }}>
              <h2 style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif", fontSize: 36, fontWeight: 800,
                color: "var(--color-text)", margin: "0 0 10px", lineHeight: 1.2,
              }}>
                {tr("app.play.level.birthday.headingPre")} <span style={{ color: "#a8275a", fontStyle: "italic" }}>{tr("app.play.level.birthday.headingAccent")}</span>{" "}{tr("app.play.level.birthday.headingPost")}
              </h2>
              <div style={{
                width: 128, height: 8, borderRadius: 999, margin: "0 auto",
                background: "linear-gradient(90deg, #a8275a, #7e5200)",
              }} />
            </div>

            {/* Decorative emoji area */}
            <div style={{
              display: "flex", justifyContent: "center", marginBottom: 20, fontSize: 48,
              filter: "drop-shadow(0 4px 12px rgba(168,39,90,0.2))",
            }}>
              {["🎂","🎁","🎈","🧁","🎉"][currentIdx % 5]}
            </div>

            {/* Question card — in a surface-container-low panel */}
            <div style={{
              background: "#f6eff1", borderRadius: "1rem", padding: "28px 24px",
              marginBottom: 20, position: "relative", overflow: "hidden",
            }}>
              {/* Floating decorative icons */}
              <div style={{
                position: "absolute", top: 12, right: 20, fontSize: 48, opacity: 0.08,
                transform: "rotate(12deg)", pointerEvents: "none",
              }}>🎉</div>
              <div style={{
                position: "absolute", bottom: 8, left: 16, fontSize: 56, opacity: 0.06,
                transform: "rotate(-12deg)", pointerEvents: "none",
              }}>🎂</div>

              <h2 style={{
                fontFamily: "var(--font-display)", fontSize: 26, fontWeight: 800, textAlign: "center",
                color: "var(--color-primary)", lineHeight: 1.2, margin: "0 0 8px",
                position: "relative", zIndex: 1,
              }}>
                {level.title || tr("app.play.level.birthday.levelFallback", { n: currentIdx + 1 })}
              </h2>
              <p style={{
                fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 700, textAlign: "center",
                color: "var(--color-text)", lineHeight: 1.5, margin: "0 0 20px",
                position: "relative", zIndex: 1,
              }}>
                "{level.question}"
              </p>

              <div onKeyDown={handleKey} style={{ position: "relative", zIndex: 1 }}>
                <Input value={answer} onChange={(v) => { setAnswer(v); setFeedback(null); }}
                  placeholder={tr("app.play.common.answerPlaceholder")}
                  style={{ padding: "20px 28px", fontSize: 18, textAlign: "center", ...answerInputStyle }} />
              </div>

              {feedbackMsg}

              <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16, position: "relative", zIndex: 1 }}>
                <button onClick={checkAnswerHandler} disabled={!answer.trim() || feedback === "correct"}
                  style={{
                    width: "100%", padding: "18px 24px", border: "none", cursor: "pointer",
                    background: "linear-gradient(135deg, #a8275a, #ff709f)", color: "#fff",
                    fontFamily: "var(--font-display)", fontSize: 17, fontWeight: 700,
                    borderRadius: "1rem", boxShadow: "0 8px 32px rgba(255,112,159,0.3)",
                    opacity: (!answer.trim() || feedback === "correct") ? 0.5 : 1,
                    transition: "all 0.15s",
                  }}>
                  {tr("app.play.level.birthday.checkAnswer")}
                </button>
                {level.hint && !showHint && (
                  <button onClick={() => setShowHint(true)} style={{
                    width: "100%", padding: "16px 24px", borderRadius: "1rem",
                    background: "#e2dbde", border: "none", cursor: "pointer",
                    fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700,
                    color: "#a8275a",
                  }}>
                    {tr("app.play.level.birthday.revealHint")}
                  </button>
                )}
              </div>

              {hintBlock}
            </div>

            {/* Candle progress indicator */}
            <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 4 }}>
              {config.levels.map((_, i) => (
                <div key={i} style={{
                  display: "flex", flexDirection: "column", alignItems: "center", gap: 4,
                  opacity: i <= currentIdx ? 1 : 0.35,
                  transition: "opacity 0.3s",
                }}>
                  <span style={{ fontSize: 20 }}>{i < currentIdx ? "🔥" : "🕯️"}</span>
                  <div style={{
                    width: 4, height: 16, borderRadius: 999,
                    background: i <= currentIdx ? "#ffc87d" : "#e2dbde",
                  }} />
                </div>
              ))}
            </div>
          </div>

          <div style={{ paddingBottom: 8 }}>
            <ProgressBar current={currentIdx} total={config.levels.length} />
          </div>
        </div>
      {/* Celebration Overlay */}
      {showCelebration && hasCustomCelebration && (
        <div style={{
          position: "fixed", inset: 0,
          background: "rgba(252,245,247,0.92)",
          backdropFilter: "blur(12px)",
          zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, animation: "celebFadeIn 0.4s ease-out",
        }}>
          <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {["🎂","🎁","🥳","🎈","🎉","🧁"][currentIdx % 6]}
            </div>

            {level.celebrationType === "message" && (
              <Card style={{ marginBottom: 20 }}>
                <p style={{
                  fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)",
                  lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap",
                }}>
                  {level.celebrationContent}
                </p>
              </Card>
            )}

            {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
              <Card style={{ marginBottom: 20, padding: 12 }}>
                <img
                  src={safeMediaUrl(level.celebrationContent, "image")}
                  alt={tr("app.builder.celebration.photoAlt")}
                  style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 16, display: "block" }}
                />
              </Card>
            )}

            {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
              <Card style={{ marginBottom: 20, padding: 12 }}>
                <div style={{ borderRadius: 16, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                  <iframe
                    src={getEmbedUrl(level.celebrationContent)}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                </div>
              </Card>
            )}

            {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
              <Card style={{ marginBottom: 20, padding: "20px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 12 }}>
                  {tr("app.common.aSongForYou")}
                </div>
                <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
              </Card>
            )}

            <Btn onClick={advanceLevel}>
              {isLast ? tr("app.play.common.seeFinalMessage") : tr("app.common.continueArrow")}
            </Btn>
          </div>
        </div>
      )}

{/* shake + celebFadeIn keyframes live in public/index.html */}
    </div>
    );
  }

  // ── ROMANTIC THEME (default) ──
  return (
    <div style={wrapperStyle}>
      <ConfettiBurst theme={config.theme} active={confetti} />
      <div style={{ width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", minHeight: "calc(100dvh - 136px)" }}>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
          {/* Level badge */}
          <div style={{ textAlign: "center", marginBottom: 10 }}>
            <span style={{
              display: "inline-flex", alignItems: "center", padding: "6px 18px", borderRadius: 999,
              background: "var(--color-glow)", color: "var(--color-primary)",
              fontFamily: "var(--font-body)", fontWeight: 700, fontSize: 12,
              letterSpacing: "0.2em", textTransform: "uppercase",
            }}>
              {tr("app.play.level.default.levelOfTotal", { n: currentIdx + 1, total: config.levels.length })}
            </span>
          </div>

          {/* Gradient progress bar */}
          <div style={{
            height: 4, borderRadius: 2, background: "var(--color-bgAlt)", marginBottom: 20, overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 2, transition: "width 0.4s ease",
              width: `${((currentIdx + 1) / config.levels.length) * 100}%`,
              background: "linear-gradient(90deg, var(--color-primary), var(--color-primaryBright))",
            }} />
          </div>

          {/* Heading — level title */}
          <div style={{ textAlign: "center", marginBottom: 6 }}>
            <h2 style={{
              fontFamily: "var(--font-display)", fontSize: 32, fontWeight: 800,
              color: "var(--color-primary)", margin: "0 0 4px", lineHeight: 1.2,
            }}>
              {level.title || tr("app.play.level.default.levelFallback", { n: currentIdx + 1 })}
            </h2>
            <p style={{
              fontFamily: "var(--font-body)", fontSize: 14, fontWeight: 500,
              color: "var(--color-textMuted)", margin: "0 0 16px",
            }}>
              {mc.levelTagline || "Unlocking the next level..."}
            </p>
          </div>

          {/* Question card — with themed watermark */}
          <Card style={{
            marginBottom: 20, position: "relative", overflow: "hidden",
            boxShadow: "0 8px 32px var(--color-glow)",
          }}>
            {/* Theme watermark */}
            <div style={{
              position: "absolute", top: 12, right: 16, fontSize: 56, lineHeight: 1,
              opacity: 0.12, pointerEvents: "none",
            }}>{t?.emoji || "❤️"}</div>

            {/* Accent bar */}
            <div style={{
              width: 48, height: 4, borderRadius: 2,
              background: "var(--color-glow)", marginBottom: 16,
            }} />

            <p style={{
              fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 700,
              color: "var(--color-text)", lineHeight: 1.5, margin: "0 0 8px",
              position: "relative", zIndex: 1,
            }}>
              {level.question}
            </p>

            <div onKeyDown={handleKey} style={{ position: "relative", zIndex: 1 }}>
              <Input value={answer} onChange={(v) => { setAnswer(v); setFeedback(null); }}
                placeholder={tr("app.play.common.answerPlaceholder")}
                style={{ padding: "18px 22px", fontSize: 16, ...answerInputStyle }} />
            </div>

            {feedbackMsg}

            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16, position: "relative", zIndex: 1 }}>
              <Btn onClick={checkAnswerHandler} disabled={!answer.trim() || feedback === "correct"} style={{
                width: "100%", borderRadius: 999, padding: "16px 24px",
              }}>
                {mc.checkAnswer || "Check Answer"}
              </Btn>
              {level.hint && !showHint && (
                <button onClick={() => setShowHint(true)} style={{
                  background: "var(--color-bgAlt)", border: "none", cursor: "pointer", borderRadius: 999,
                  fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
                  color: "var(--color-primary)", padding: "14px 20px",
                }}>
                  {t?.emoji || "❤️"} {mc.hintButton || "Reveal Hint"}
                </button>
              )}
            </div>

            {hintBlock}
          </Card>

          {/* Italic quote footer */}
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <p style={{
              fontFamily: "var(--font-display)", fontStyle: "italic", fontSize: 13,
              color: "var(--color-textMuted)", letterSpacing: "0.03em", opacity: 0.7,
            }}>
              "{mc.footerQuote || "Every puzzle piece brings them closer to you."}"
            </p>
          </div>
        </div>

        {/* Bottom progress */}
        <div style={{ paddingBottom: 8 }}>
          {levelDots}
          <ProgressBar current={currentIdx} total={config.levels.length} />
        </div>
      </div>
      {/* Celebration Overlay */}
      {showCelebration && hasCustomCelebration && (
        <div style={{
          position: "fixed", inset: 0,
          background: dark ? "rgba(15,21,36,0.9)" : "rgba(252,245,247,0.92)",
          backdropFilter: "blur(12px)",
          zIndex: 60, overflowY: "auto", display: "flex", alignItems: "center", justifyContent: "center",
          padding: 24, animation: "celebFadeIn 0.4s ease-out",
        }}>
          <div style={{ maxWidth: 420, width: "100%", textAlign: "center" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>
              {config.theme === "romantic"
                ? ["💖","🥰","💕","✨","💗","🌹"][currentIdx % 6]
                : config.theme === "funny"
                ? ["🎉","🥳","🎊","🔥","⭐","🤩"][currentIdx % 6]
                : config.theme === "birthday"
                ? ["🎂","🎁","🥳","🎈","🎉","🧁"][currentIdx % 6]
                : config.theme === "friendship"
                ? ["💜","🤝","⭐","✨","🎉","💪"][currentIdx % 6]
                : config.theme === "spooky"
                ? ["👻","🔮","💀","🕯️","🦇","⚡"][currentIdx % 6]
                : config.theme === "travel"
                ? ["✈️","🗺️","🌍","🧳","⛵","🏔️"][currentIdx % 6]
                : config.theme === "christmas"
                ? ["🎁","🎄","⭐","❄️","🔔","🧦"][currentIdx % 6]
                : ["🏆","⚡","💎","🎮","🔥","⭐"][currentIdx % 6]}
            </div>

            {level.celebrationType === "message" && (
              <Card gaming={dark} style={{ marginBottom: 20 }}>
                <p style={{
                  fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)",
                  lineHeight: 1.7, margin: 0, whiteSpace: "pre-wrap",
                }}>
                  {level.celebrationContent}
                </p>
              </Card>
            )}

            {level.celebrationType === "photo" && safeMediaUrl(level.celebrationContent, "image") && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: 12 }}>
                <img
                  src={safeMediaUrl(level.celebrationContent, "image")}
                  alt={tr("app.builder.celebration.photoAlt")}
                  style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 16, display: "block" }}
                />
              </Card>
            )}

            {level.celebrationType === "video" && getEmbedUrl(level.celebrationContent) && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: 12 }}>
                <div style={{ borderRadius: 16, overflow: "hidden", aspectRatio: "16/9", maxWidth: 480, margin: "0 auto" }}>
                  <iframe
                    src={getEmbedUrl(level.celebrationContent)}
                    style={{ width: "100%", height: "100%", border: "none" }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                    allowFullScreen
                  />
                </div>
              </Card>
            )}

            {level.celebrationType === "song" && safeMediaUrl(level.celebrationContent, "audio") && (
              <Card gaming={dark} style={{ marginBottom: 20, padding: "20px 16px", textAlign: "center" }}>
                <div style={{ fontSize: 36, marginBottom: 10 }}>🎵</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700, color: "var(--color-text)", marginBottom: 12 }}>
                  {tr("app.common.aSongForYou")}
                </div>
                <audio controls autoPlay src={safeMediaUrl(level.celebrationContent, "audio")} style={{ width: "100%", maxWidth: 320, margin: "0 auto" }} />
              </Card>
            )}

            <Btn onClick={advanceLevel}>
              {isLast ? tr("app.play.common.seeFinalMessage") : tr("app.common.continueArrow")}
            </Btn>
          </div>
        </div>
      )}

{/* shake + celebFadeIn keyframes live in public/index.html */}
    </div>
  );
}

// Keepsake print: save just the surprise MESSAGE as a tidy PDF (not the whole
// SPA page). Opens a minimal styled doc and prints it — works across every
// finale theme. window.print() of the app printed nav/video/buttons → useless.
function printKeepsake(config) {
  const title = (config && config.title) || i18n.t("app.keepsake.titleFallback");
  const message = (config && config.finalMessage) || "";
  const esc = (s) =>
    String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  let w;
  try { w = window.open("", "_blank"); } catch (_) { w = null; }
  if (!w) { try { window.print(); } catch (_) { /* ignore */ } return; }
  w.document.write(
    '<!doctype html><html><head><meta charset="utf-8">' +
    "<title>" + esc(title) + " · Puzzly</title><style>" +
    "*{margin:0;box-sizing:border-box}" +
    "body{font-family:'Be Vietnam Pro',-apple-system,system-ui,sans-serif;background:#fcf5f7;color:#3a2730;min-height:100vh;display:flex;align-items:center;justify-content:center;padding:48px}" +
    ".card{max-width:520px;width:100%;background:#fff;border-radius:28px;padding:48px 40px;box-shadow:0 18px 60px -20px rgba(168,39,90,.35);text-align:center}" +
    ".heart{font-size:34px;margin-bottom:14px}" +
    ".eyebrow{font-family:'Plus Jakarta Sans',sans-serif;font-weight:800;letter-spacing:.14em;text-transform:uppercase;font-size:11px;color:#a8275a;margin-bottom:18px}" +
    ".msg{font-family:'Plus Jakarta Sans',sans-serif;font-weight:800;font-size:24px;line-height:1.45;color:#7a0e3c;white-space:pre-wrap}" +
    ".foot{margin-top:26px;font-size:12px;color:#a98}" +
    "@media print{body{background:#fff;padding:0}.card{box-shadow:none}}" +
    "</style></head><body><div class=\"card\"><div class=\"heart\">💌</div>" +
    "<div class=\"eyebrow\">" + esc(title) + "</div>" +
    "<div class=\"msg\">" + esc(message) + "</div>" +
    "<div class=\"foot\">" + esc(i18n.t("app.keepsake.madeWith")) + "</div></div>" +
    "<scr" + "ipt>window.onload=function(){setTimeout(function(){window.print();},200);};<\/scr" + "ipt>" +
    "</body></html>"
  );
  w.document.close();
}

// ── Finale: Video Note Reaction ──
export function VideoReaction({ config }) {
  const { t: tr } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [videoBlob, setVideoBlob] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [seconds, setSeconds] = useState(0);
  const [sending, setSending] = useState(false);
  const [sentToOwner, setSentToOwner] = useState(false);
  // false = the creator never signed in, so the reaction can't reach them: only "Share" stays (#107).
  const [ownerCanReceive, setOwnerCanReceive] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const livePreviewRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    if (!config._id) return;
    let active = true;
    import("./services/reactionService")
      .then(({ canReceiveReaction }) => canReceiveReaction(config._id))
      .then((yes) => { if (active) setOwnerCanReceive(yes); })
      .catch(() => {});
    return () => { active = false; };
  }, [config._id]);

  // Clean up stream + object URLs on unmount
  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      if (videoUrl) URL.revokeObjectURL(videoUrl);
      clearInterval(timerRef.current);
    };
  }, []);

  // Attach the live camera stream to the preview AFTER the <video> mounts.
  // The element only renders once `recording` is true, so setting srcObject
  // inside startRecording (before setRecording) hit a null ref → black preview.
  useEffect(() => {
    if (recording && livePreviewRef.current && streamRef.current) {
      livePreviewRef.current.srcObject = streamRef.current;
      livePreviewRef.current.muted = true; // iOS inline autoplay requires muted
      livePreviewRef.current.play().catch(() => {});
    }
  }, [recording]);

  const startRecording = async () => {
    try {
      setError(null);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 720 }, height: { ideal: 720 } },
        audio: true,
      });
      streamRef.current = stream;

      // Show live camera preview
      if (livePreviewRef.current) {
        livePreviewRef.current.srcObject = stream;
        livePreviewRef.current.play().catch(() => {});
      }

      // Pick best supported video MIME type. The right order depends on device,
      // because WhatsApp (and most native share targets) accept mp4 but reject
      // webm — yet Chrome *desktop* lies about mp4 support: `isTypeSupported`
      // returns true while its MediaRecorder mp4 path emits empty/0-byte data
      // (the "empty recording blob" failure).
      //   - Mobile (where reactions get recorded + shared to WhatsApp): prefer
      //     mp4. iOS Safari only does mp4 anyway; modern Android Chrome records
      //     real mp4. If a device falsely reports mp4, the empty-capture guard
      //     below catches it and the webm fallbacks still produce a saveable file.
      //   - Desktop: keep webm first (reliable) — sharing to WhatsApp isn't the
      //     desktop use case, and webm dodges the Chrome empty-blob bug.
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent || "");
      const mp4First = ["video/mp4;codecs=h264,aac", "video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4", "video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm"];
      const webmFirst = ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
      const mimeType = (isMobile ? mp4First : webmFirst)
        .find((m) => MediaRecorder.isTypeSupported(m)) || "";
      logger.info("Video reaction: recorder MIME selected", { mimeType });
      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        try { stream.getTracks().forEach((t) => t.stop()); } catch (_) { /* ignore */ }
        streamRef.current = null;
        clearInterval(timerRef.current);

        const chunks = chunksRef.current || [];
        const totalBytes = chunks.reduce((n, c) => n + (c && c.size ? c.size : 0), 0);

        // A recording that captured nothing yields no chunks / 0 bytes (a too-
        // short capture, a covered/busy camera, or an unreliable codec). Build +
        // URL.createObjectURL on an empty result previously threw an uncaught
        // "Overload resolution failed" that crashed the app — now handled.
        if (chunks.length === 0 || totalBytes === 0) {
          logger.error("Video reaction: empty capture", {
            chunkCount: chunks.length,
            totalBytes,
            picked: mimeType,
            recorderMime: mediaRecorder.mimeType || null,
          });
          setError(tr("app.reply.errors.emptyCapture"));
          return;
        }
        // Build the blob and turn it into a URL. We DON'T pre-reject on
        // size/instanceof — the empty-capture case is already handled above by
        // totalBytes, and the only real failure that matters is createObjectURL
        // throwing. (A prior `blob.size === 0` pre-check false-rejected valid
        // multi-chunk recordings.)
        let blob;
        try {
          blob = new Blob(chunks, { type: mediaRecorder.mimeType || mimeType || "video/webm" });
        } catch (err) {
          logger.error("Video reaction: blob build failed", {
            error: err.message, chunkCount: chunks.length, totalBytes,
          });
          setError(tr("app.reply.errors.processFailed"));
          return;
        }
        logger.info("Video reaction: blob built", {
          blobSize: blob.size, blobType: blob.type, isBlob: blob instanceof Blob,
        });
        try {
          const url = URL.createObjectURL(blob);
          setVideoBlob(blob);
          setVideoUrl(url);
        } catch (err) {
          logger.error("Video reaction: createObjectURL failed", {
            error: err.message, blobSize: blob.size,
          });
          setError(tr("app.reply.errors.processFailed"));
        }
      };

      mediaRecorder.start(250);
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);

      // Auto-stop at 30 seconds
      setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
          setRecording(false);
        }
      }, 30000);
    } catch {
      setError(tr("app.reply.errors.cameraDenied"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const discardRecording = () => {
    setVideoBlob(null);
    if (videoUrl) URL.revokeObjectURL(videoUrl);
    setVideoUrl(null);
    setSeconds(0);
  };

  const saveToDevice = async () => {
    if (!(videoBlob instanceof Blob) || videoBlob.size === 0) {
      setError(tr("app.reply.errors.nothingToSave"));
      return;
    }
    const ext = videoBlob.type.includes("mp4") ? "mp4" : "webm";
    const filename = `puzzly-reaction.${ext}`;

    // iOS Safari ignores <a download> for blob URLs, so the anchor trick saves
    // nothing on mobile. Prefer the native share sheet (Web Share API w/ files)
    // — it lets the user save to Photos/Files. Falls back to anchor download on
    // desktop, then to opening the blob in a new tab as a last resort.
    try {
      const file = new File([videoBlob], filename, { type: videoBlob.type || "video/webm" });
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await withExternalFlow(() => navigator.share({ files: [file], title: tr("app.reply.shareTitle") }));
        setSaved(true);
        logger.info("Video reaction shared to device", { puzzleId: config._id || "legacy" });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return; // user dismissed the sheet
      logger.warn("Video reaction: share failed, falling back to download", { error: err.message });
    }

    try {
      const url = URL.createObjectURL(videoBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Some mobile browsers need the URL alive briefly after the click.
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      setSaved(true);
      logger.info("Video reaction saved to device", { puzzleId: config._id || "legacy" });
    } catch (err) {
      logger.error("Video reaction: save to device failed", { error: err.message });
      setError(tr("app.reply.errors.saveFailed"));
    }
  };

  // Share the recorded reaction straight to WhatsApp / socials via the native
  // share sheet (Web Share API with files). Falls back to save/download when
  // file-sharing isn't supported (most desktops).
  const shareVideo = async () => {
    if (!(videoBlob instanceof Blob) || videoBlob.size === 0) {
      setError(tr("app.reply.errors.nothingToShare"));
      return;
    }
    const ext = videoBlob.type.includes("mp4") ? "mp4" : "webm";
    const file = new File([videoBlob], `puzzly-reaction.${ext}`, { type: videoBlob.type || "video/webm" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] }) && navigator.share) {
        await withExternalFlow(() => navigator.share({ files: [file], title: tr("app.reply.shareTitle"), text: tr("app.reply.shareText") }));
        logger.info("Video reaction shared", { puzzleId: config._id || "legacy" });
        return;
      }
    } catch (err) {
      if (err && err.name === "AbortError") return; // user dismissed the sheet
      logger.warn("Video reaction: share failed, falling back to save", { error: err.message });
    }
    // No file-share support → fall back to saving to device.
    await saveToDevice();
  };

  // Send the recorded reaction to the puzzle creator: upload to Storage, then
  // ask the Cloud Function to email the owner a link (owner email resolved
  // server-side, never exposed to the client). Requires a Firebase puzzle (_id).
  const sendToOwner = async () => {
    if (!(videoBlob instanceof Blob) || videoBlob.size === 0) {
      setError(tr("app.reply.errors.nothingToSend"));
      return;
    }
    const puzzleId = config._id;
    if (!puzzleId) {
      setError(tr("app.reply.errors.cannotReceive"));
      return;
    }
    setSending(true);
    setError(null);
    try {
      const { uploadReaction, sendReactionToOwner } = await import("./services/reactionService");
      const url = await uploadReaction(puzzleId, videoBlob);
      await sendReactionToOwner(puzzleId, url);
      setSentToOwner(true);
      logger.info("Reaction sent to owner", { puzzleId });
    } catch (err) {
      logger.error("Send reaction to owner failed", { error: err.message, code: err.code });
      setError(
        err.code === "not-found"
          ? tr("app.reply.errors.noCreatorEmail")
          : tr("app.reply.errors.sendFailed")
      );
    } finally {
      setSending(false);
    }
  };

  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  // ── Saved confirmation ──
  if (saved) {
    return (
      <Card style={{ marginTop: 24, textAlign: "center" }}>
        <div style={{ fontSize: 36, marginBottom: 8 }}>🎬</div>
        <div style={{
          fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700,
          color: "var(--color-text)", marginBottom: 4,
        }}>
          {tr("app.reply.saved")}
        </div>
        <div style={{
          fontFamily: "var(--font-body)", fontSize: 13,
          color: "var(--color-textMuted)", lineHeight: 1.5,
        }}>
          {tr("app.reply.savedBody").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}
        </div>
      </Card>
    );
  }

  // ── Recording / Preview UI ──
  return (
    <Card style={{ marginTop: 24, textAlign: "center" }}>
      <div style={{ fontSize: 28, marginBottom: 8 }}>🎬</div>
      <div style={{
        fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700,
        color: "var(--color-text)", marginBottom: 4,
      }}>
        {tr("app.reply.recordReaction")}
      </div>
      <div style={{
        fontFamily: "var(--font-body)", fontSize: 13,
        color: "var(--color-textMuted)", marginBottom: 16,
      }}>
        {tr(config._id && ownerCanReceive !== false ? "app.reply.recordHintCreator" : "app.reply.recordHint")}
      </div>

      {!videoBlob ? (
        <div>
          {/* Live camera preview while recording */}
          {recording && (
            <div style={{
              position: "relative", width: "100%", maxWidth: 280,
              margin: "0 auto 14px", borderRadius: 20, overflow: "hidden",
              background: "#000", aspectRatio: "1/1",
            }}>
              <video
                ref={livePreviewRef}
                muted playsInline
                style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
              />
              {/* Recording timer overlay */}
              <div style={{
                position: "absolute", top: 10, left: 0, right: 0,
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
              }}>
                <span style={{
                  display: "inline-block", width: 10, height: 10, borderRadius: "50%",
                  background: "#ff3b30", animation: "recPulse 1s ease-in-out infinite",
                }} />
                <span style={{
                  fontFamily: "'JetBrains Mono', monospace", fontSize: 16, fontWeight: 700,
                  color: "#fff", textShadow: "0 1px 4px rgba(0,0,0,.5)",
                }}>
                  {formatTime(seconds)}
                </span>
              </div>
            </div>
          )}

          {recording ? (
            <button onClick={stopRecording} style={{
              padding: "14px 28px", borderRadius: 999, border: "none", cursor: "pointer",
              background: "#b41340", color: "#fff",
              fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700,
            }}>
              {tr("app.reply.stopRecording")}
            </button>
          ) : (
            <button onClick={startRecording} style={{
              padding: "14px 28px", borderRadius: 999, border: "none", cursor: "pointer",
              background: "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
              color: "#fff",
              fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700,
              boxShadow: "0 6px 24px var(--color-glow)",
            }}>
              {tr("app.reply.startRecording")}
            </button>
          )}
        </div>
      ) : (
        <div>
          {/* Recorded video playback */}
          <div style={{
            width: "100%", maxWidth: 280, margin: "0 auto 14px",
            borderRadius: 20, overflow: "hidden", background: "#000", aspectRatio: "1/1",
          }}>
            <video
              controls playsInline src={videoUrl}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 320, margin: "0 auto", width: "100%" }}>
            {config._id && ownerCanReceive !== false && (
              <button
                onClick={sendToOwner}
                disabled={sending || sentToOwner}
                style={{
                  padding: "13px 20px", borderRadius: 999, border: "none", width: "100%",
                  cursor: sending || sentToOwner ? "default" : "pointer",
                  background: sentToOwner
                    ? "var(--color-bgAlt)"
                    : "linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))",
                  color: sentToOwner ? "var(--color-textMuted)" : "#fff",
                  fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700,
                  boxShadow: sentToOwner ? "none" : "0 6px 24px var(--color-glow)",
                  opacity: sending ? 0.7 : 1,
                }}
              >
                {sentToOwner ? tr("app.reply.sentToCreator") : sending ? tr("app.reply.sending") : tr("app.reply.sendToCreator")}
              </button>
            )}
            <button onClick={shareVideo} style={{
              padding: "13px 20px", borderRadius: 999, width: "100%", cursor: "pointer",
              border: "1.5px solid var(--color-primary)", background: "transparent",
              color: "var(--color-primary)",
              fontFamily: "var(--font-display)", fontSize: 15, fontWeight: 700,
            }}>
              {tr("app.reply.share")}
            </button>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={discardRecording} style={{
                flex: 1, padding: "12px 14px", borderRadius: 999, border: "none", cursor: "pointer",
                background: "var(--color-bgAlt)", color: "var(--color-textMuted)",
                fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
              }}>
                {tr("app.reply.reRecord")}
              </button>
              <button onClick={saveToDevice} style={{
                flex: 1, padding: "12px 14px", borderRadius: 999, border: "none", cursor: "pointer",
                background: "var(--color-bgAlt)", color: "var(--color-text)",
                fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
              }}>
                {tr("app.reply.save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div style={{
          fontFamily: "var(--font-body)", fontSize: 13, color: "var(--color-wrong)",
          marginTop: 10,
        }}>
          {error}
        </div>
      )}

      <div style={{
        fontFamily: "var(--font-body)", fontSize: 11, color: "var(--color-textDim)",
        marginTop: 12,
      }}>
        {tr("app.reply.maxSeconds")}
      </div>

{/* recPulse keyframes live in public/index.html */}
    </Card>
  );
}

// ── Reveal media (Phase 8) ──
// The photo, song or video a creator attached to the reveal, shown under the message in
// every theme. Only Storage URLs are rendered: a legacy link carries its whole
// config client-side and could otherwise name any src.
function FinaleMedia({ finale, dark }) {
  const { t: tr } = useTranslation();
  if (!finale || !isStorageUrl(finale.url)) return null;
  if (finale.type === "photo") {
    return (
      <div data-testid="finale-photo" style={{ marginTop: 18 }}>
        <img src={finale.url} alt={tr("app.finale.common.photoAlt")} style={{
          display: "block", width: "100%", maxHeight: "60vh", objectFit: "cover", borderRadius: 18,
          boxShadow: dark ? "0 18px 36px -18px rgba(0,0,0,.8)" : "0 18px 36px -18px rgba(74,10,31,.45)",
        }} />
      </div>
    );
  }
  if (finale.type === "video") {
    return (
      <div data-testid="finale-video" style={{ marginTop: 18 }}>
        {/* `#t=0.1` makes Safari paint the first frame instead of a black box. */}
        <video controls playsInline preload="metadata" src={`${finale.url}#t=0.1`} aria-label={tr("app.finale.common.videoLabel")} style={{
          display: "block", width: "100%", maxHeight: "70vh", borderRadius: 18, background: "#000",
          boxShadow: dark ? "0 18px 36px -18px rgba(0,0,0,.8)" : "0 18px 36px -18px rgba(74,10,31,.45)",
        }} />
      </div>
    );
  }
  if (finale.type === "song") {
    return (
      <div data-testid="finale-song" style={{ marginTop: 18, display: "flex", justifyContent: "center" }}>
        <audio controls autoPlay src={finale.url} aria-label={tr("app.finale.common.songLabel")} style={{ width: "100%", maxWidth: 340 }} />
      </div>
    );
  }
  return null;
}

export function Finale({ config, onCreateOwn }) {
  const { t: tr } = useTranslation();
  // Localized theme microcopy for the finale (finalePrelude/finaleSignoff/etc).
  // Every theme branch below reads from `mc` so the closing line follows the
  // active language instead of falling back to the English THEMES base.
  // An unknown key (a newer seasonal theme) reads the romantic copy in the reader's language.
  const mcKey = resolveThemeKey(config.theme);
  const mc = useThemeMicrocopy(mcKey, THEMES[mcKey].microcopy);
  const [visible, setVisible] = useState(false);
  const [confetti, setConfetti] = useState(false);
  const dark = isDarkTheme(config.theme);
  useEffect(() => {
    const show = setTimeout(() => setVisible(true), 200);
    const burst = setTimeout(() => setConfetti(true), 400);
    return () => { clearTimeout(show); clearTimeout(burst); };
  }, []);

  const t = THEMES[resolveThemeKey(config.theme)];

  // Themed copy for the Creator→Creator referral CTA (localized; falls back to
  // the romantic set when a theme key is missing).
  const refRaw = tr(`app.finale.referral.${config.theme}`, { returnObjects: true });
  const ref = (refRaw && typeof refRaw === "object" && refRaw.eyebrow) ? refRaw : tr("app.finale.referral.romantic", { returnObjects: true });

  // ── Funny "Daily Roast" tabloid finale (designer themes/funny.html) ──
  if (config.theme === "funny") {
    const ink = "#1a0a14", pink = "#ff2e6e", pinkDeep = "#d11a55", teal = "#00b3b3", cream = "#fffbed";
    const fmc = mc;
    const haBubbles = [
      { t: "HA!", left: "8%", top: "9%", delay: "0s", color: pinkDeep },
      { t: "LOL", left: "76%", top: "13%", delay: "0.8s", color: teal },
      { t: "HA!", left: "12%", top: "82%", delay: "1.6s", color: teal },
      { t: "!!", left: "82%", top: "75%", delay: "2.2s", color: pinkDeep },
    ];
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: 24, position: "relative", overflow: "hidden",
        background: "#ffe55c",
        backgroundImage: "radial-gradient(circle, rgba(26,10,20,.07) 1px, transparent 1.5px)",
        backgroundSize: "13px 13px",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s",
      }}>
        <ConfettiBurst theme={config.theme} active={confetti} />
        {haBubbles.map((b, i) => (
          <span key={i} style={{
            position: "absolute", left: b.left, top: b.top, zIndex: 1,
            background: "#fff", border: `2.5px solid ${ink}`, borderRadius: 14,
            padding: "4px 10px", fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800,
            fontSize: 13, color: b.color, boxShadow: `3px 3px 0 ${ink}`,
            animation: `funnyHaPop 3.4s ease-out ${b.delay} infinite`, pointerEvents: "none",
          }}>{b.t}</span>
        ))}

        <div style={{ width: "100%", maxWidth: 420, position: "relative", zIndex: 2 }}>
          {/* EXTRA! star sticker */}
          <div style={{
            position: "absolute", top: -18, left: -8, zIndex: 4,
            width: 64, height: 64, display: "grid", placeItems: "center",
            background: teal, color: "#fff", transform: "rotate(-14deg)",
            fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 11, lineHeight: 1, textAlign: "center",
            textShadow: `1.5px 1.5px 0 ${ink}`, clipPath: BURST_CLIP,
          }}>{tr("app.finale.funny.sticker").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>

          {/* Tabloid front page */}
          <div style={{
            background: cream, border: `3px solid ${ink}`, borderRadius: 8,
            padding: "16px 18px 18px", boxShadow: `6px 6px 0 ${pink}, 0 16px 36px -12px rgba(0,0,0,.3)`,
            transform: "rotate(-1.5deg)", textAlign: "center",
          }}>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "baseline",
              borderBottom: `2px solid ${ink}`, paddingBottom: 6, marginBottom: 14,
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 9,
              letterSpacing: "0.22em", textTransform: "uppercase", color: pinkDeep,
            }}>
              <span>{tr("app.finale.funny.masthead")}</span>
              <span>{tr("app.finale.funny.volume")}</span>
            </div>

            <div style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 10,
              letterSpacing: "0.16em", textTransform: "uppercase", color: teal, marginBottom: 10,
            }}>· {fmc.finalePrelude || "The truth comes out"} ·</div>

            <h2 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 30,
              color: ink, margin: "0 0 12px", lineHeight: 1.0, letterSpacing: "-0.02em", textTransform: "uppercase",
            }}>
              {config.finalMessage || tr("app.finale.funny.finalMessageFallback")}
            </h2>
            <div style={{
              fontFamily: "'Be Vietnam Pro', sans-serif", fontStyle: "italic", fontSize: 13, color: "#3a1b28",
            }}>{tr("app.finale.funny.byline")}</div>
          </div>

          {/* Voice reaction recorder — only for Firebase puzzles */}
          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 12, marginTop: 18, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "14px 22px", background: "#fff", color: ink,
              border: `2.5px solid ${ink}`, borderRadius: 12, boxShadow: `4px 4px 0 ${ink}`,
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 14,
              textTransform: "uppercase", letterSpacing: "0.04em", cursor: "pointer",
            }}>{tr("app.finale.funny.fireBack")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "14px 22px", background: pink, color: "#fff",
              border: `2.5px solid ${ink}`, borderRadius: 12, boxShadow: `4px 4px 0 ${ink}`,
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 14,
              textTransform: "uppercase", letterSpacing: "0.04em", textShadow: `1px 1px 0 ${pinkDeep}`, cursor: "pointer",
            }}>{tr("app.finale.common.saveNote")}</button>
          </div>

          <div style={{
            marginTop: 18, textAlign: "center", fontFamily: "'Be Vietnam Pro', sans-serif",
            fontSize: 13, color: "#3a1b28",
          }}>
            <span style={{
              background: "#fff", border: `2px solid ${ink}`, borderRadius: 12, padding: "2px 8px",
              fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800, fontSize: 11, color: pinkDeep, marginRight: 6,
            }}>HA!</span>
            {fmc.finaleSignoff || "— love you anyway 🙄"}
          </div>
        </div>

      </div>
    );
  }

  // ── Romantic "unfolded letter" finale (designer themes/romantic.html · "What was always true") ──
  if (config.theme === "romantic") {
    const wine = "#4a0a1f", roseDeep = "#9c1a44", textSoft = "#6e1e3a";
    const serif = "'Cormorant Garamond', serif";
    const rmc = mc;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "48px 24px", position: "relative", overflow: "hidden",
        background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 12% 14%, rgba(200,52,95,.16), transparent 42%), radial-gradient(circle at 88% 84%, rgba(201,161,74,.12), transparent 46%), radial-gradient(circle at 50% 50%, rgba(244,184,196,.10), transparent 70%)",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s",
      }}>
        <RomanticPetals />
        <div style={{ width: "100%", maxWidth: 380, position: "relative", zIndex: 2 }}>
          <div style={{
            fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 18, color: roseDeep,
            textAlign: "center", marginBottom: 14,
          }}>{rmc.finalePrelude || "what was always true"}</div>

          {/* Unfolded letter */}
          <div style={{
            position: "relative", background: "#ffffff", borderRadius: 4, padding: "22px 22px 18px",
            textAlign: "left", boxShadow: "0 12px 28px -10px rgba(74,10,31,.3), inset 0 0 0 1px rgba(74,10,31,.06)",
            animation: "letterUnfold 1s cubic-bezier(.34,1.2,.64,1) both",
          }}>
            <div style={{ position: "absolute", top: -14, right: 14 }}>
              <WaxSeal char="♥" size={44} style={{ opacity: 0.9, transform: "rotate(-10deg) scale(.95)" }} />
            </div>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 14, color: textSoft, marginBottom: 10 }}>{tr("app.finale.romantic.salutation")}</div>
            <p style={{
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: wine,
              letterSpacing: "-0.02em", lineHeight: 1.3, margin: 0, whiteSpace: "pre-wrap",
            }}>{config.finalMessage}</p>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontSize: 15, color: textSoft, textAlign: "right", marginTop: 14 }}>
              {rmc.finaleSignoff || "— yours, always"}
            </div>
          </div>

          {/* Voice reaction recorder */}
          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "12px 20px", background: "#fff", color: roseDeep,
              border: "none", borderRadius: 14, boxShadow: "inset 0 0 0 2px #c8345f",
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>{tr("app.finale.romantic.writeBack")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "12px 20px", background: "linear-gradient(135deg, #c8345f, #9c1a44)", color: "#fff",
              border: "none", borderRadius: 14, fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13,
              boxShadow: "0 8px 18px -6px rgba(200,52,95,.5)", cursor: "pointer",
            }}>{tr("app.finale.common.saveNote")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Travel "wish you were here" finale (designer themes/travel.html) ──
  if (config.theme === "travel") {
    const navy = "#1c2e52", navyDeep = "#0f1d3a", stamp = "#c0392b", stampDeep = "#922518", text = "#1c2e52", textSoft = "#3d5278", muted = "#8c9bb5";
    const mono = "'JetBrains Mono', monospace";
    const tmc = mc;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "48px 24px", position: "relative", overflow: "hidden", background: "var(--color-bg)",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s", textAlign: "center",
      }}>
        <TravelDrift />
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 360, position: "relative", zIndex: 2 }}>
          <div style={{ fontFamily: mono, fontWeight: 600, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: stampDeep, marginBottom: 14 }}>
            · {tmc.finalePrelude || "Final destination"} ·
          </div>
          {/* Cleared customs round stamp */}
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{
              width: 78, height: 78, borderRadius: "50%", border: `2px solid ${stamp}`, color: stamp,
              display: "grid", placeItems: "center", fontFamily: mono, fontWeight: 700, fontSize: 9, lineHeight: 1.3,
              letterSpacing: "0.1em", textTransform: "uppercase", transform: "rotate(-12deg)",
              animation: "travelStamp .6s cubic-bezier(.34,1.56,.64,1) both",
            }}>{tr("app.finale.travel.customs").split("\n").map((line, i, arr) => (<Fragment key={i}>{line}{i < arr.length - 1 && <br />}</Fragment>))}</div>
          </div>
          {/* Postcard */}
          <div style={{ position: "relative", background: "#fffaf0", borderRadius: 8, padding: "20px 18px", textAlign: "left", boxShadow: "0 10px 28px -10px rgba(28,46,82,.3)" }}>
            <div style={{ position: "absolute", top: -6, right: 12, width: 38, height: 44, background: stamp, borderRadius: 3, border: "2px dashed #fffaf0", display: "grid", placeItems: "center", color: "#fffaf0", fontFamily: mono, fontSize: 7, fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>Puzzly<br />★24★</div>
            <div style={{ fontFamily: mono, fontWeight: 500, fontSize: 9, letterSpacing: "0.18em", textTransform: "uppercase", color: muted, marginBottom: 6 }}>{tr("app.finale.travel.greetings")}</div>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, color: text, letterSpacing: "-0.025em", lineHeight: 1.15, margin: "0 0 8px", whiteSpace: "pre-wrap" }}>
              {config.finalMessage}
            </p>
            <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 11.5, color: textSoft }}>
              {tmc.finaleSignoff || "— wish you were here"}
            </div>
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "13px 20px", background: "transparent", color: navy, border: `1px solid ${navy}`, borderRadius: 8,
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>{tr("app.finale.travel.sendReply")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "13px 20px", background: `linear-gradient(135deg, ${navy}, ${navyDeep})`, color: "#fffaf0", border: "none", borderRadius: 8,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, boxShadow: "0 8px 18px -6px rgba(28,46,82,.5)", cursor: "pointer",
            }}>{tr("app.finale.travel.keepTicket")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Christmas "sotto l'albero" finale (themes-canvas · Natale) ──
  // The tree appears, then the final letter slides out from under it, among the gifts.
  // Reduced motion: everything is in place from the first frame and no snow falls.
  if (config.theme === "christmas") {
    const serif = "'Cormorant Garamond', Georgia, serif";
    const pine = "#1E4D36", cream = "#FBF4EA";
    const reduceMotion = typeof window !== "undefined" && typeof window.matchMedia === "function"
      && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const shown = visible || reduceMotion;
    const animate = !reduceMotion;
    const smallCaps = { fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase" };
    return (
      <div data-testid="finale-christmas" style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "40px 20px 48px", position: "relative", overflow: "hidden", textAlign: "center", background: "var(--color-bg)",
        backgroundImage: "radial-gradient(circle at 15% 10%, rgba(30,77,54,.10), transparent 40%), radial-gradient(circle at 85% 88%, rgba(201,161,74,.14), transparent 45%)",
      }}>
        {animate && <ChristmasSnow />}
        <ConfettiBurst theme={config.theme} active={confetti && animate} />
        <div style={{ width: "100%", maxWidth: 360, position: "relative", zIndex: 2 }}>
          <div style={{ ...smallCaps, color: XMAS.cranberryDeep, marginBottom: 10 }}>
            · {mc.finalePrelude || "Under the tree"} ·
          </div>
          <ChristmasTree shown={shown} animate={animate} />
          {/* The letter under the tree */}
          <div data-testid="christmas-letter" data-state={shown ? "shown" : "hidden"} style={{
            position: "relative", margin: "-2px 10px 0", padding: "34px 22px 22px", background: "#fff",
            border: "1px solid #EADFCB", borderRadius: 6, color: XMAS.ink,
            boxShadow: "0 18px 34px -18px rgba(30,58,43,.45)",
            opacity: shown ? 1 : 0, transform: shown ? "none" : "translateY(28px)",
            transition: animate ? "opacity .6s ease .75s, transform .8s cubic-bezier(.2,.8,.2,1) .75s" : "none",
          }}>
            {/* Gold seal holding the letter closed */}
            <div aria-hidden="true" style={{
              position: "absolute", top: -17, left: "calc(50% - 17px)", width: 34, height: 34, borderRadius: "50%",
              background: `radial-gradient(circle at 35% 30%, #E4C77E, ${XMAS.gold} 60%, ${XMAS.goldDeep})`,
              boxShadow: "0 4px 10px -4px rgba(30,58,43,.5)", display: "grid", placeItems: "center", color: XMAS.ink, fontSize: 16,
            }}>✦</div>
            <p style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: "clamp(22px, 6.6vw, 26px)", lineHeight: 1.25, color: XMAS.ink, margin: "0 0 14px", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {config.finalMessage}
            </p>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 500, fontSize: 19, color: XMAS.body }}>
              {mc.finaleSignoff || "— with all my love ✦"}
            </div>
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <button onClick={() => printKeepsake(config)} style={{
            marginTop: 18, padding: "12px 22px", background: "#fff", color: XMAS.ink, border: `1.5px solid ${XMAS.gold}`, borderRadius: 999,
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}>{tr("app.finale.common.saveNote")}</button>

          {/* Creator→Creator referral: wrap one for someone else */}
          <div style={{ marginTop: 24, padding: "22px 20px 20px", borderRadius: 18, background: pine, color: cream, boxShadow: "0 14px 30px -16px rgba(30,58,43,.6)" }}>
            <div style={{ ...smallCaps, color: "#E4C77E", marginBottom: 8 }}>{ref.eyebrow}</div>
            <div style={{ fontFamily: serif, fontStyle: "italic", fontWeight: 600, fontSize: 26, lineHeight: 1.15, marginBottom: 8 }}>{ref.heading}</div>
            <div style={{ fontFamily: "var(--font-body)", fontSize: 14, lineHeight: 1.5, marginBottom: 16 }}>{ref.body}</div>
            <button
              onClick={() => {
                logger.info("Creator referral CTA clicked", { puzzleId: config._id || "legacy", theme: config.theme });
                if (onCreateOwn) onCreateOwn();
              }}
              style={{
                width: "100%", padding: "14px 20px", borderRadius: 999, border: "none", background: XMAS.cranberry, color: "#fff",
                fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 15, cursor: "pointer", boxShadow: "0 8px 18px -8px rgba(0,0,0,.5)",
              }}
            >{ref.cta}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Friendship "P.S. read this" finale (designer themes/friendship.html) ──
  if (config.theme === "friendship") {
    const sage = "#5c8c6b", sageDeep = "#3d6b4c", text = "#2c3e34", textSoft = "#4d6857", success = "#2db678";
    const frmc = mc;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "48px 24px", position: "relative", overflow: "hidden", background: "var(--color-bg)",
        backgroundImage: "repeating-linear-gradient(var(--color-bg), var(--color-bg) 27px, rgba(92,140,107,.08) 28px)",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s", textAlign: "center",
      }}>
        <ChainFloat />
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 340, position: "relative", zIndex: 2 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 12, letterSpacing: "0.2em", textTransform: "uppercase", color: sageDeep, marginBottom: 14 }}>
            · {frmc.finalePrelude || "P.S. — read this"} ·
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", background: success, color: "#fff", display: "grid", placeItems: "center", fontWeight: 800, fontSize: 22, boxShadow: "0 6px 16px -4px rgba(45,182,120,.5)" }}>✓</div>
          </div>
          {/* Folded note */}
          <div style={{ position: "relative", background: "#fffdf4", borderRadius: 8, padding: "24px 22px", boxShadow: "0 10px 28px -10px rgba(60,80,68,.3)" }}>
            <div style={{ position: "absolute", top: -8, left: "50%", transform: "translateX(-50%) rotate(-2deg)", width: 60, height: 16, background: sage, opacity: 0.85 }} />
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, color: text, letterSpacing: "-0.015em", lineHeight: 1.25, margin: "6px 0 10px", whiteSpace: "pre-wrap" }}>
              {config.finalMessage}
            </p>
            <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 13, color: textSoft }}>
              {frmc.finaleSignoff || "— love, your person"}
            </div>
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "13px 22px", background: "#fff", color: sageDeep, border: "none", borderRadius: 12,
              boxShadow: "inset 0 0 0 2px #5c8c6b", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>{tr("app.finale.common.reply")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "13px 22px", background: `linear-gradient(135deg, ${sage}, ${sageDeep})`, color: "#fff", border: "none", borderRadius: 12,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, boxShadow: "0 8px 18px -6px rgba(92,140,107,.5)", cursor: "pointer",
            }}>{tr("app.finale.common.saveNote")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Birthday "blow out the candle" finale (designer themes/birthday.html) ──
  if (config.theme === "birthday") {
    const pink = "#ff5b94", pinkDeep = "#d63a72", candle = "#ffc94d", text = "#5a1a3a", textSoft = "#7d3155";
    const hand = "'Caveat', cursive";
    const bmc = mc;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "48px 24px", position: "relative", overflow: "hidden", background: "var(--color-bg)",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s", textAlign: "center",
      }}>
        <BirthdaySprinkles />
        <ConfettiBurst theme={config.theme} active={confetti} />
        <div style={{ width: "100%", maxWidth: 360, position: "relative", zIndex: 2 }}>
          <div style={{ fontFamily: hand, fontWeight: 700, fontSize: 22, color: pinkDeep, marginBottom: 12 }}>
            {bmc.finalePrelude || "blow out the candle"}
          </div>
          {/* Cake slice */}
          <div style={{ position: "relative", width: 90, height: 70, margin: "0 auto 18px" }}>
            <div style={{ position: "absolute", bottom: 0, left: "50%", transform: "translateX(-50%)", width: 0, height: 0, borderLeft: "45px solid transparent", borderRight: "45px solid transparent", borderBottom: `56px solid ${pink}` }} />
            <div style={{ position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)", width: 6, height: 22, background: "#fff", borderRadius: 2 }}>
              <div style={{ position: "absolute", top: -12, left: "50%", width: 8, height: 14, background: candle, borderRadius: "50% 50% 50% 50% / 70% 70% 30% 30%", boxShadow: "0 0 14px rgba(255,201,77,.8)", animation: "bdayFlicker 1.4s ease-in-out infinite" }} />
            </div>
          </div>
          <div style={{ background: "#fff", borderRadius: 20, padding: "22px 20px", boxShadow: "0 12px 32px -10px rgba(214,58,114,.25)" }}>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 19, color: text, letterSpacing: "-0.02em", lineHeight: 1.2, margin: 0, whiteSpace: "pre-wrap" }}>
              {config.finalMessage}
            </p>
            <div style={{ fontFamily: hand, fontWeight: 700, fontSize: 18, color: pinkDeep, marginTop: 10 }}>
              {bmc.finaleSignoff || "— with love, on your day"}
            </div>
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "13px 22px", background: "#fff", color: pinkDeep, border: "none", borderRadius: 14,
              boxShadow: "inset 0 0 0 2px #ff5b94", fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>{tr("app.finale.common.reply")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "13px 22px", background: `linear-gradient(135deg, ${pink}, ${pinkDeep})`, color: "#fff", border: "none", borderRadius: 14,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, boxShadow: "0 8px 18px -6px rgba(255,91,148,.5)", cursor: "pointer",
            }}>{tr("app.finale.common.saveNote")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Spooky "pumpkin in the dark" finale (designer themes/spooky.html · "What was hidden") ──
  if (config.theme === "spooky") {
    const accent = "#b88dd6", accentDeep = "#7d4ca8", pumpkin = "#ff8c42", pumpkinDeep = "#d96820";
    const smc = mc;
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: "48px 24px", position: "relative", overflow: "hidden",
        background: "radial-gradient(ellipse at 50% 30%, #2f1d44 0%, var(--color-bg) 70%)",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s",
      }}>
        <GhostDrift count={6} />
        <div style={{ width: "100%", maxWidth: 360, position: "relative", zIndex: 2, textAlign: "center" }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: accent, opacity: 0.9, marginBottom: 10 }}>
            · {smc.finalePrelude || "What was hidden"} ·
          </div>
          {/* Pumpkin reveal card — only warmth in the theme */}
          <div style={{
            background: `linear-gradient(135deg, ${pumpkin}, ${pumpkinDeep})`, color: "#1a1025",
            borderRadius: 20, padding: "24px 22px", boxShadow: "0 16px 40px -12px rgba(217,104,32,.5)",
          }}>
            <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", opacity: 0.85, marginBottom: 8 }}>{tr("app.finale.spooky.fromDark")}</div>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 19, letterSpacing: "-0.015em", lineHeight: 1.2, margin: 0, whiteSpace: "pre-wrap" }}>
              {config.finalMessage}
            </p>
            <div style={{ fontFamily: "var(--font-body)", fontStyle: "italic", fontSize: 12, opacity: 0.9, marginTop: 10 }}>
              {smc.finaleSignoff || "— sent from beyond ✦"}
            </div>
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "13px 22px", background: "transparent", color: accent, border: `1px solid ${accentDeep}`, borderRadius: 14,
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, cursor: "pointer",
            }}>{tr("app.finale.common.reply")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "13px 22px", background: `linear-gradient(135deg, ${accent}, ${accentDeep})`, color: "#1a1025", border: "none", borderRadius: 14,
              fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 13, boxShadow: "0 0 18px rgba(184,141,214,.4)", cursor: "pointer",
            }}>{tr("app.finale.common.saveNote")}</button>
          </div>
        </div>
      </div>
    );
  }

  // ── Gaming "achievement unlocked" finale (designer themes/gaming.html) ──
  if (config.theme === "gaming") {
    const magenta = "#ff2eb8", cyan = "#00e5ff", coin = "#ffd84d", textSoft = "#a8a0d0";
    const pixel = "'JetBrains Mono', monospace";
    const gmc = mc;
    const sparks = [
      { left: "14%", bottom: "18%", sx: "18px", sy: "-110px", delay: "0s", c: coin },
      { left: "28%", bottom: "32%", sx: "-12px", sy: "-90px", delay: "0.3s", c: magenta },
      { left: "42%", bottom: "14%", sx: "14px", sy: "-130px", delay: "0.6s", c: cyan },
      { left: "58%", bottom: "38%", sx: "-22px", sy: "-100px", delay: "0.9s", c: coin },
      { left: "72%", bottom: "22%", sx: "10px", sy: "-110px", delay: "1.2s", c: magenta },
      { left: "84%", bottom: "42%", sx: "-16px", sy: "-90px", delay: "1.5s", c: cyan },
      { left: "20%", bottom: "50%", sx: "8px", sy: "-80px", delay: "1.8s", c: coin },
      { left: "64%", bottom: "54%", sx: "-10px", sy: "-100px", delay: "2.1s", c: magenta },
    ];
    return (
      <div style={{
        minHeight: "100vh", display: "flex", flexDirection: "column", justifyContent: "flex-start", alignItems: "center",
        padding: 24, position: "relative", overflow: "hidden", background: "#0a0a1f",
        opacity: visible ? 1 : 0, transition: "opacity 0.8s",
      }}>
        <div style={{
          position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.12,
          backgroundImage: "linear-gradient(rgba(255,255,255,0) 50%, rgba(0,0,0,0.4) 50%)", backgroundSize: "100% 4px",
        }} />
        <ConfettiBurst theme={config.theme} active={confetti} />
        {sparks.map((s, i) => (
          <span key={i} aria-hidden="true" style={{
            position: "absolute", left: s.left, bottom: s.bottom, width: 6, height: 6, background: s.c, zIndex: 1,
            "--sx": s.sx, "--sy": s.sy, boxShadow: `0 0 8px ${s.c}`,
            animation: `gmSpark 2.4s ease-out ${s.delay} infinite`,
          }} />
        ))}
        <div style={{ width: "100%", maxWidth: 400, position: "relative", zIndex: 2, textAlign: "center" }}>
          <div style={{ fontFamily: pixel, fontWeight: 700, fontSize: 10, letterSpacing: "0.12em", color: coin, marginBottom: 14, textShadow: "0 0 8px rgba(255,216,77,.5)" }}>
            // {gmc.finalePrelude || "Achievement unlocked"} //
          </div>
          {/* Achievement banner */}
          <div style={{
            background: "linear-gradient(135deg, #16143a, #221c4a)", border: `1px solid ${coin}`, padding: "22px 20px",
            clipPath: "polygon(0 8px, 8px 0, calc(100% - 8px) 0, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px))",
            boxShadow: "0 0 24px rgba(255,216,77,.25)",
          }}>
            <div style={{ fontFamily: pixel, fontSize: 8, color: coin, marginBottom: 8, letterSpacing: "0.08em" }}>{tr("app.finale.gaming.trophy")}</div>
            <p style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: "#fff", lineHeight: 1.15, letterSpacing: "-0.015em", margin: "0 0 8px", whiteSpace: "pre-wrap" }}>
              {config.finalMessage}
            </p>
            <div style={{ marginTop: 12, display: "flex", justifyContent: "center", gap: 8, alignItems: "center" }}>
              <span style={{ fontFamily: pixel, fontSize: 9, color: coin, letterSpacing: "0.06em" }}>{tr("app.finale.gaming.xp")}</span>
            </div>
          </div>
          <div style={{ marginTop: 14, fontFamily: pixel, fontSize: 9, color: cyan, letterSpacing: "0.06em" }}>
            {gmc.finaleSignoff || "— P1 // CO-OP FOR LIFE"}
          </div>

          <FinaleMedia finale={config.finale} dark={dark} />
          <div style={{ marginTop: 18 }}><VideoReaction config={config} /></div>

          <div style={{ marginTop: 18, display: "flex", gap: 12, justifyContent: "center" }}>
            <button onClick={onCreateOwn} style={{
              padding: "13px 20px", background: "transparent", color: cyan, border: `1px solid ${cyan}`, borderRadius: 0,
              clipPath: "polygon(0 5px, 5px 0, calc(100% - 5px) 0, 100% 5px, 100% calc(100% - 5px), calc(100% - 5px) 100%, 5px 100%, 0 calc(100% - 5px))",
              fontFamily: pixel, fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", cursor: "pointer",
            }}>{tr("app.finale.gaming.continue")}</button>
            <button onClick={() => printKeepsake(config)} style={{
              padding: "13px 20px", background: magenta, color: "#fff", border: "none", borderRadius: 0,
              clipPath: "polygon(0 5px, 5px 0, calc(100% - 5px) 0, 100% 5px, 100% calc(100% - 5px), calc(100% - 5px) 100%, 5px 100%, 0 calc(100% - 5px))",
              fontFamily: pixel, fontWeight: 700, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
              boxShadow: "0 0 15px rgba(255,46,184,.5)", cursor: "pointer",
            }}>{tr("app.finale.gaming.saveGame")}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      minHeight: "100vh", padding: 24, textAlign: "center",
      opacity: visible ? 1 : 0, transform: visible ? "scale(1)" : "scale(0.9)",
      transition: "all 1s cubic-bezier(0.4,0,0.2,1)",
    }}>
      <ConfettiBurst theme={config.theme} active={confetti} />
      <div style={{ width: "100%", maxWidth: 480 }}>
        {/* Sparkle icon in circular glow */}
        <div style={{
          width: 80, height: 80, borderRadius: "50%", margin: "0 auto 20px",
          display: "flex", alignItems: "center", justifyContent: "center",
          background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
          boxShadow: `0 8px 40px var(--color-glow)`,
          fontSize: 36,
        }}>✨</div>
        <h1 style={{
          fontFamily: "var(--font-display)", fontSize: "clamp(28px, 6vw, 42px)",
          fontWeight: 800, color: "var(--color-primary)", margin: "0 0 8px",
        }}>
          {tr("app.finale.default.heading")}
        </h1>
        <p style={{ fontFamily: "var(--font-body)", fontSize: 15, color: "var(--color-textMuted)", margin: "0 0 28px" }}>
          {tr("app.finale.default.allCompleted", { count: config.levels.length })}
        </p>

        <Card gaming={dark} style={{
          position: "relative", overflow: "hidden",
        }}>
          {/* Gradient top bar on card */}
          <div style={{
            position: "absolute", top: 0, left: 0, right: 0, height: 4,
            background: `linear-gradient(90deg, ${t.colors.primary}, ${t.colors.primaryBright})`,
          }} />
          {/* Quote marks decoration */}
          <div style={{
            fontSize: 40, lineHeight: 1, color: "var(--color-primary)", opacity: 0.15,
            fontFamily: "Georgia, serif", marginBottom: 4,
          }}>"</div>
          <p style={{
            fontFamily: "var(--font-body)", fontSize: 18, color: "var(--color-text)",
            lineHeight: 1.8, margin: "0 0 8px", whiteSpace: "pre-wrap",
          }}>
            {config.finalMessage}
          </p>
          <div style={{
            fontSize: 40, lineHeight: 1, color: "var(--color-primary)", opacity: 0.15,
            fontFamily: "Georgia, serif", textAlign: "right",
          }}>"</div>
        </Card>

        {/* Voice reaction recorder — only for Firebase puzzles */}
        <FinaleMedia finale={config.finale} dark={dark} />
        <VideoReaction config={config} />

        {/* Creator→Creator referral card */}
        <div style={{
          marginTop: 24,
          padding: "22px 22px 20px",
          borderRadius: 24,
          background: `linear-gradient(135deg, var(--color-primary), var(--color-primaryBright))`,
          boxShadow: `0 12px 40px var(--color-glow)`,
          color: "#fff",
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}>
          {/* Soft background sparkle */}
          <div style={{
            position: "absolute", top: -30, right: -30, width: 120, height: 120,
            borderRadius: "50%", background: "rgba(255,255,255,0.12)",
          }} />
          <div style={{
            position: "absolute", bottom: -40, left: -20, width: 100, height: 100,
            borderRadius: "50%", background: "rgba(255,255,255,0.08)",
          }} />

          <div style={{
            position: "relative",
            fontFamily: "var(--font-body)", fontSize: 11, fontWeight: 700,
            letterSpacing: "0.18em", textTransform: "uppercase",
            opacity: 0.85, marginBottom: 8,
          }}>
            {ref.eyebrow}
          </div>
          <div style={{
            position: "relative",
            fontFamily: "var(--font-display)", fontSize: 22, fontWeight: 800,
            lineHeight: 1.25, marginBottom: 8,
          }}>
            {ref.heading}
          </div>
          <div style={{
            position: "relative",
            fontFamily: "var(--font-body)", fontSize: 13, lineHeight: 1.5,
            opacity: 0.92, marginBottom: 18,
          }}>
            {ref.body}
          </div>
          <button
            onClick={() => {
              logger.info("Creator referral CTA clicked", {
                puzzleId: config._id || "legacy",
                theme: config.theme,
              });
              if (onCreateOwn) onCreateOwn();
            }}
            style={{
              position: "relative",
              width: "100%",
              padding: "14px 20px",
              borderRadius: 16,
              border: "none",
              background: "#fff",
              color: "var(--color-primary)",
              fontFamily: "var(--font-display)",
              fontSize: 15,
              fontWeight: 800,
              cursor: "pointer",
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              transition: "transform 0.15s ease",
            }}
            onMouseDown={(e) => { e.currentTarget.style.transform = "scale(0.97)"; }}
            onMouseUp={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }}
          >
            {ref.cta}
          </button>
        </div>

        <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 18 }}>
          <Btn onClick={() => {
            window.location.reload();
          }} variant="secondary" small>
            {tr("app.finale.default.playAgain")}
          </Btn>
        </div>
      </div>
    </div>
  );
}

// ── Player Entry (paste link) ──
function PlayerEntry({ onLoadPuzzle }) {
  const { t: tr } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [link, setLink] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  const [loading, setLoading] = useState(false);

  // A pasted/typed value looks like a puzzle link if it has a hash or url scheme.
  const looksLikeLink = (s) => /([a-z]+:\/\/|#)[\w./?=&%-]+/i.test(s.trim()) && s.trim().length > 6;
  const valid = looksLikeLink(link);

  const doPaste = async () => {
    try {
      const t = await navigator.clipboard.readText();
      if (t) { setLink(t); setError(""); }
    } catch {
      if (inputRef.current) inputRef.current.focus();
    }
  };

  const handleLoad = async () => {
    try {
      let hash = "";
      if (link.includes("#")) {
        hash = link.slice(link.indexOf("#") + 1);
      } else {
        hash = link.trim();
      }
      if (!hash) { setError(tr("app.play.entry.errorEmpty")); return; }

      // Try short hash first (Firebase puzzle ID)
      if (hash.length <= 30) {
        setLoading(true);
        setError("");
        const puzzle = await fbFetchPuzzle(hash);
        setLoading(false);
        if (puzzle && puzzle.version === 1) {
          onLoadPuzzle({ ...puzzle, _fromDb: true });
          return;
        }
      }

      // Fall back to legacy LZ-compressed hash
      const decoded = decodeConfig(hash);
      if (decoded && decoded.version === 1) {
        setError("");
        onLoadPuzzle(decoded);
      } else {
        setError(tr("app.play.entry.errorUnreadable"));
      }
    } catch {
      setLoading(false);
      setError(tr("app.play.entry.errorInvalid"));
    }
  };

  return (
    <div style={{
      maxWidth: 600, margin: "0 auto", minHeight: "100vh", position: "relative", overflow: "hidden",
      display: "flex", flexDirection: "column", justifyContent: "center",
      padding: "40px 26px calc(120px + env(safe-area-inset-bottom, 0px))",
      background: "linear-gradient(180deg, var(--cream) 0%, #fff 26%, var(--color-bg) 100%)",
      opacity: visible ? 1 : 0, transition: "opacity 0.6s",
    }}>
      {/* ambient blobs */}
      <AmbientBlob size={340} color="var(--mint)" style={{ position: "absolute", top: -150, right: -120, opacity: 0.45, zIndex: 0 }} />
      <AmbientBlob size={300} color="var(--sky)" style={{ position: "absolute", top: 240, left: -150, opacity: 0.4, zIndex: 0 }} />

      <div style={{ position: "relative", zIndex: 1 }}>
      {/* Floating puzzle-piece sticker */}
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 26 }}>
        <span style={{ display: "inline-block", animation: "bob 5s ease-in-out infinite", filter: "drop-shadow(0 14px 22px rgba(122,179,86,.4))" }}>
          <svg width="76" height="76" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <defs>
              <linearGradient id="ppPlay" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#a8e063" /><stop offset="1" stopColor="#56ab2f" />
              </linearGradient>
            </defs>
            <path d="M22 8 h10 a3 3 0 0 1 3 3 a5 5 0 0 0 10 0 a3 3 0 0 1 3 3 v10 a3 3 0 0 0 3 3 a5 5 0 0 1 0 10 a3 3 0 0 0 -3 3 v8 a3 3 0 0 1 -3 3 h-8 a3 3 0 0 1 -3 -3 a5 5 0 0 0 -10 0 a3 3 0 0 1 -3 3 h-8 a3 3 0 0 1 -3 -3 v-10 a3 3 0 0 0 -3 -3 a5 5 0 0 1 0 -10 a3 3 0 0 0 3 -3 v-10 a3 3 0 0 1 3 -3 z"
              fill="url(#ppPlay)" stroke="#4e9628" strokeWidth="2" strokeLinejoin="round" />
            <path d="M20 22 a4 4 0 0 1 6 -2" stroke="rgba(255,255,255,.6)" strokeWidth="3" strokeLinecap="round" fill="none" />
          </svg>
        </span>
      </div>

      <Card style={{ marginBottom: 6, padding: "26px 24px", textAlign: "center" }}>
        <h1 style={{
          fontFamily: "var(--font-display)", fontWeight: 800,
          fontSize: "clamp(38px, 12vw, 56px)", lineHeight: 0.92, letterSpacing: "-0.04em",
          margin: 0, color: "var(--color-text)", textAlign: "center",
        }}>
          {tr("common.playerEntry.headingPre")} <span style={{ color: "var(--color-primary)", fontStyle: "italic" }}>{tr("common.playerEntry.headingAccent")}</span>
        </h1>
        <p style={{
          fontFamily: "var(--font-body)", fontSize: 16, lineHeight: 1.5, color: "var(--color-textMuted)",
          margin: "16px auto 0", maxWidth: "30ch", textAlign: "center",
        }}>
          {tr("common.playerEntry.subtitle")}
        </p>
      </Card>

      {/* Paste field — carded */}
      <Card style={{ marginTop: 16, padding: "22px 20px" }}>
        <label style={{
          display: "block", fontFamily: "var(--font-display)", fontWeight: 700,
          fontSize: 11.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--color-textMuted)",
          marginBottom: 8, textAlign: "center",
        }}>{tr("common.playerEntry.linkLabel")}</label>

        <div style={{
          position: "relative", display: "flex", alignItems: "center", gap: 10,
          background: "var(--color-surface)",
          border: `2px solid ${valid ? "var(--color-primary)" : "var(--color-border)"}`,
          borderRadius: 18, padding: "6px 6px 6px 16px",
          boxShadow: valid ? "0 14px 30px -16px var(--color-primary)" : "0 6px 18px -10px rgba(0,0,0,0.2)",
          transition: "border-color .2s, box-shadow .2s",
        }}>
          <span style={{ color: valid ? "var(--color-primary)" : "var(--color-textMuted)", display: "grid", placeItems: "center" }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.07 0l3-3a5 5 0 0 0-7.07-7.07L11 4.93" /><path d="M14 11a5 5 0 0 0-7.07 0l-3 3a5 5 0 0 0 7.07 7.07L13 19.07" /></svg>
          </span>
          <input
            ref={inputRef}
            value={link}
            onChange={(e) => { setLink(e.target.value); setError(""); }}
            onKeyDown={(e) => { if (e.key === "Enter" && valid && !loading) handleLoad(); }}
            placeholder={tr("app.play.entry.placeholder")}
            autoComplete="off" spellCheck="false"
            style={{
              flex: 1, minWidth: 0, background: "transparent", border: "none", outline: "none",
              fontFamily: "ui-monospace, 'SF Mono', Menlo, monospace", fontWeight: 500, fontSize: 15,
              letterSpacing: "-0.01em", color: "var(--color-text)", padding: "12px 0",
            }}
          />
          {link ? (
            <button onClick={() => { setLink(""); setError(""); inputRef.current && inputRef.current.focus(); }} aria-label={tr("app.play.entry.clearAria")} style={{
              width: 36, height: 36, borderRadius: 11, border: "none", background: "transparent", cursor: "pointer",
              color: "var(--color-textMuted)", display: "grid", placeItems: "center", flexShrink: 0,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
            </button>
          ) : (
            <button onClick={doPaste} style={{
              flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, border: "none", cursor: "pointer",
              background: "var(--color-bgAlt)", color: "var(--color-primary)",
              fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, padding: "11px 14px", borderRadius: 12,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="3" width="8" height="4" rx="1" /><path d="M16 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" /></svg>
              {tr("app.play.entry.paste")}
            </button>
          )}
        </div>

        {/* status line */}
        <div style={{ minHeight: 22, marginTop: 10, textAlign: "center", fontFamily: "var(--font-body)", fontSize: 13 }}>
          {error ? (
            <span style={{ color: "var(--color-wrong)", fontWeight: 600 }}>{error}</span>
          ) : valid ? (
            <span style={{ color: "var(--color-primary)", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>
              {tr("app.play.entry.linkGood")}
            </span>
          ) : (
            <span style={{ color: "var(--color-textMuted)" }}>{tr("app.play.entry.pasteOrType")}</span>
          )}
        </div>

        {/* Open Puzzle CTA */}
        <button
          onClick={handleLoad}
          disabled={!valid || loading}
          style={{
            width: "100%", marginTop: 6, padding: 20, borderRadius: 20, border: "none",
            background: valid ? "linear-gradient(160deg, var(--color-primaryBright), var(--color-primary))" : "var(--color-bgAlt)",
            color: valid ? "#fff" : "var(--color-textMuted)",
            fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18, letterSpacing: "-0.01em",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            boxShadow: valid ? "0 16px 32px -14px var(--color-primary)" : "none",
            cursor: valid && !loading ? "pointer" : "not-allowed", whiteSpace: "nowrap",
            transition: "background .2s, box-shadow .2s",
          }}
        >
          {loading ? tr("app.play.entry.opening") : tr("app.play.entry.openPuzzle")}
          {valid && !loading && <span style={{ fontSize: 20, lineHeight: 1 }}>→</span>}
        </button>

        {/* secondary route */}
        <div style={{ marginTop: 18, textAlign: "center", display: "flex", justifyContent: "center", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "var(--font-body)", fontSize: 13.5, color: "var(--color-textMuted)" }}>{tr("app.play.entry.noLink")}</span>
          <button onClick={() => { window.location.hash = ""; window.location.href = "/app"; }} style={{
            background: "none", border: "none", cursor: "pointer",
            fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13.5,
            color: "var(--color-primary)", textDecoration: "underline", textUnderlineOffset: 3, textDecorationThickness: 2, whiteSpace: "nowrap",
          }}>{tr("app.play.entry.createInstead")}</button>
        </div>
      </Card>
      </div>
    </div>
  );
}

// ── Side Menu ──
// Crisp line icons for the floating nav (designer create.jsx · NavIcon — no emoji).
function NavIcon({ name, active }) {
  const common = {
    width: 24, height: 24, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor",
    strokeWidth: active ? 2.3 : 2, strokeLinecap: "round", strokeLinejoin: "round",
  };
  switch (name) {
    case "home":
      return <svg {...common}><path d="M3 10.5 12 3l9 7.5" /><path d="M5.5 9.5V20a1 1 0 0 0 1 1H10v-5h4v5h3.5a1 1 0 0 0 1-1V9.5" /></svg>;
    case "create":
      return <svg {...common}><path d="M12 5v14M5 12h14" /></svg>;
    case "play":
      return <svg {...common}><path d="M10 4.5c0 .7.6 1.3 1.3 1.3.6 0 1-.4 1.2-1 .2-.7.8-1.3 1.5-1.3h2.5a1 1 0 0 1 1 1V7c0 .7.6 1.2 1.3 1.4.6.1 1-.6 1.7-.6.8 0 1.5.9 1.5 2s-.7 2-1.5 2c-.7 0-1.1-.7-1.7-.6-.7.2-1.3.7-1.3 1.4v2.5a1 1 0 0 1-1 1H15c-.7 0-1.3-.6-1.5-1.3-.2-.6-.6-1-1.2-1-.7 0-1.3.6-1.3 1.3V19a1 1 0 0 1-1 1H6.5a1 1 0 0 1-1-1v-2.5c0-.7-.6-1.2-1.3-1.4" /></svg>;
    case "library":
      return <svg {...common}><rect x="4" y="4" width="6.5" height="6.5" rx="1.6" /><rect x="13.5" y="4" width="6.5" height="6.5" rx="1.6" /><rect x="4" y="13.5" width="6.5" height="6.5" rx="1.6" /><rect x="13.5" y="13.5" width="6.5" height="6.5" rx="1.6" /></svg>;
    case "profile":
      // Outline person — same stroke vibe as home/play/library (not a photo avatar).
      return <svg {...common}><circle cx="12" cy="8" r="3.6" /><path d="M5.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" /></svg>;
    default: return null;
  }
}

function BottomNav({ screen, onNavigate, theme, onProfile, accountOpen }) {
  const dark = isDarkTheme(theme);
  const { t: tr } = useTranslation();
  // Designer floating glass tab bar (create.jsx). Home → the in-app vibe list
  // ("theme"), which is also where a new puzzle starts — so the old "Create"
  // tab was redundant and has been removed. Profile lives here too (moved out
  // of the floating top-right avatar), styled as an outline icon like the rest.
  const tabs = [
    { icon: "home", label: tr("common.nav.home"), screen: "__home__" },
    { icon: "play", label: tr("common.nav.play"), screen: "player_entry" },
    { icon: "library", label: tr("common.nav.myPuzzles"), screen: "history" },
    { icon: "profile", label: tr("common.nav.profile"), screen: "__profile__" },
  ];

  const screenToNav = {
    splash: "__home__", role: "__home__", theme: "__home__",
    creator: "__home__", quick_theme: "__home__", quick_create: "__home__",
    player_entry: "player_entry", preview: "player_entry", password: "player_entry", play: "player_entry",
    finale: "player_entry", history: "history", share: "__home__",
  };
  const activeNav = screenToNav[screen] || "__home__";

  // Dynamic hide/show: tuck the bar away while scrolling down, reveal on
  // scroll-up / at top / near the bottom.
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  useEffect(() => {
    lastY.current = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      const atBottom = window.innerHeight + y >= document.body.scrollHeight - 80;
      if (y < 40 || atBottom) setHidden(false);
      else if (y > lastY.current + 6) setHidden(true);
      else if (y < lastY.current - 6) setHidden(false);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handle = (s) => {
    // Profile opens the account sheet (anon → sign-in) — owned by AppShell.
    if (s === "__profile__") { if (onProfile) onProfile(); return; }
    // Home returns to the in-app vibe list (theme), not the marketing landing.
    if (s === "__home__") { onNavigate("theme"); return; }
    onNavigate(s);
  };

  return (
    <>
    {/* Bottom scrim — content fades to base before reaching the floating pill,
        so cards never bleed through it (fixes first-load "messy" look). */}
    <div aria-hidden="true" style={{
      position: "fixed", left: 0, right: 0, bottom: 0, height: 130, zIndex: 99,
      pointerEvents: "none",
      background: dark
        ? "linear-gradient(to top, var(--color-bg) 22%, rgba(0,0,0,0) 100%)"
        : "linear-gradient(to top, var(--color-bg) 22%, rgba(0,0,0,0) 100%)",
      opacity: hidden ? 0 : 1,
      transition: "opacity 0.3s",
    }} />
    <nav
      aria-label={tr("app.nav.ariaPrimary")}
      style={{
        position: "fixed", left: 14, right: 14, zIndex: 100,
        bottom: "calc(14px + env(safe-area-inset-bottom, 0px))",
        display: "flex", gap: 4, padding: 7, borderRadius: 26,
        margin: "0 auto", maxWidth: 520,
        background: dark ? "rgba(22,20,58,0.92)" : "rgba(255,255,255,0.92)",
        backdropFilter: "blur(28px) saturate(1.6)", WebkitBackdropFilter: "blur(28px) saturate(1.6)",
        border: dark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(255,255,255,0.7)",
        boxShadow: dark
          ? "0 18px 40px -18px rgba(0,0,0,0.6)"
          : "0 18px 40px -18px rgba(122,14,60,0.5), inset 0 2px 0 rgba(255,255,255,0.6)",
        transform: hidden ? "translateY(calc(100% + 24px))" : "translateY(0)",
        opacity: hidden ? 0 : 1,
        transition: "transform 0.32s cubic-bezier(.4,0,.2,1), opacity 0.25s",
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      {tabs.map((t) => {
        // When the account sheet is open, ONLY Profile is highlighted — the
        // underlying screen tab (e.g. My Puzzles) must not stay lit too.
        const isActive = t.screen === "__profile__"
          ? !!accountOpen
          : (!accountOpen && activeNav === t.screen);
        return (
          <button
            key={t.label}
            onClick={() => handle(t.screen)}
            aria-label={t.label}
            aria-current={isActive ? "page" : undefined}
            style={{
              flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 5,
              padding: "10px 4px", borderRadius: 19, border: "none", cursor: "pointer",
              color: isActive ? "#fff" : (dark ? "var(--color-textMuted)" : "var(--color-textMuted)"),
              background: isActive
                ? "linear-gradient(160deg, var(--color-primaryBright), var(--color-primary))"
                : "transparent",
              boxShadow: isActive ? "0 10px 20px -8px var(--color-primary)" : "none",
              transition: "color 0.2s, background 0.2s",
            }}
          >
            <NavIcon name={t.icon} active={isActive} />
            <span style={{
              fontFamily: "var(--font-display)", fontWeight: isActive ? 800 : 600,
              fontSize: 11, letterSpacing: "0.01em", whiteSpace: "nowrap",
            }}>{t.label}</span>
          </button>
        );
      })}
    </nav>
    </>
  );
}

// ═══════════════════════════════════
// ERROR BOUNDARY
// ═══════════════════════════════════
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    logger.error("React Error Boundary caught error", {
      error: error.message,
      componentStack: errorInfo.componentStack,
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "#fcf5f7",
          padding: 32,
        }}>
          <div style={{
            textAlign: "center",
            maxWidth: 400,
            fontFamily: "'Be Vietnam Pro', sans-serif",
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>💔</div>
            <h1 style={{
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              fontSize: 24,
              fontWeight: 800,
              color: "#a8275a",
              marginBottom: 8,
            }}>{i18n.t("app.errors.boundaryTitle")}</h1>
            <p style={{
              color: "#5e5a5c",
              fontSize: 14,
              lineHeight: 1.6,
              marginBottom: 24,
            }}>
              {i18n.t("app.errors.boundaryBody")}
            </p>
            <button
              onClick={() => window.location.reload()}
              style={{
                background: "linear-gradient(135deg, #a8275a, #ff709f)",
                color: "white",
                border: "none",
                borderRadius: 999,
                padding: "14px 32px",
                fontSize: 16,
                fontWeight: 700,
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                cursor: "pointer",
              }}
            >
              {i18n.t("app.errors.boundaryReload")}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// ═══════════════════════════════════
// MAIN APP
// ═══════════════════════════════════
// Phase 4 W1.T2 — extracted from former `export default function App()`.
// The new top-level App() (further below) wraps this in <Routes>, mounting
// AppShell at /app/* (default) and at /privacy (with initialScreen="privacy").
// `hideBottomNav` lets marketing-style routes (e.g. /privacy when reached via
// header link from /) suppress the in-app BottomNav per Pitfall #4 / D-30.
// Theme CSS variables. Extracted so the creator's live preview can paint a
// scaled player in any theme without the rest of the app changing skin.
export function themeCssVars(theme) {
  const t = theme && theme.colors ? theme : THEMES.romantic;
  return {
    "--font-display": t.fonts.display,
    "--font-body": t.fonts.body,
    "--color-bg": t.colors.bg,
    "--color-bgAlt": t.colors.bgAlt,
    "--color-surface": t.colors.surface,
    "--color-surfaceHover": t.colors.surfaceHover,
    "--color-border": t.colors.border,
    "--color-primary": t.colors.primary,
    "--color-primaryBright": t.colors.primaryBright,
    "--color-secondary": t.colors.secondary,
    "--color-accent": t.colors.accent,
    "--color-text": t.colors.text,
    "--color-textMuted": t.colors.textMuted,
    "--color-textDim": t.colors.textDim,
    "--color-correct": t.colors.correct,
    "--color-wrong": t.colors.wrong,
    "--color-glow": t.colors.glow,
    "--color-onPrimary": t.colors.onPrimary,
  };
}

function AppShell({ initialScreen, hideBottomNav = false } = {}) {
  const { t: tr } = useTranslation();
  // /app lands directly on the Create vibe-picker ("theme"); the old SplashScreen
  // is retired (on the web "/" is the store page, #74). Shared puzzle links still
  // override this via the hash-detection effect → preview/password.
  const navigate = useNavigate();
  // Sul web Puzzly apre solo i puzzle ricevuti (#74): creare, accedere e
  // comprare si fa nell'app. Tutto il resto porta alla pagina degli store.
  const soloLettore = !isNativeApp();
  const [screen, setScreen] = useState(initialScreen || (soloLettore ? "caricamento" : "theme"));
  // The theme picked for a puzzle (pickers, drafts, a loaded puzzle); null until
  // one is picked. `theme` keeps the old romantic default for every consumer.
  const [pickedTheme, setTheme] = useState(null);
  const theme = pickedTheme || "romantic";
  // Recipient name typed on the Create vibe-picker (create.jsx) — seeds the
  // puzzle title in CreatorWizard step-0.
  const [pendingTitle, setPendingTitle] = useState("");
  const [config, setConfig] = useState(null);
  const [screenHistory, setScreenHistory] = useState([]);
  const [slideDirection, setSlideDirection] = useState(null);
  // #65 — il foglio «segnala o blocca» di chi riceve, e la richiesta delle
  // regole sui contenuti: la apre il servizio contentRules prima del primo
  // contenuto che parte, e aspetta la risposta di questa modale.
  const [reportOpen, setReportOpen] = useState(false);
  const [richiestaRegole, setRichiestaRegole] = useState(null);
  useEffect(() => {
    registraRichiestaConsenso(() => new Promise((risolvi) => setRichiestaRegole({ risolvi })));
    return () => registraRichiestaConsenso(null);
  }, []);
  // Always start a new screen at the top — solve/password/finale screens are
  // tall; without this the viewport could stay scrolled mid-page from the
  // previous screen (the "centered in the middle" report on mobile).
  // …a meno che l'URL non indichi una sezione della schermata: lì si va alla
  // sezione. Questo effetto gira dopo quelli dei figli, quindi deve deciderlo lui.
  useEffect(() => {
    const ancora = ancoraInPagina();
    if (ancora) { ancora.scrollIntoView(); return; }
    try { window.scrollTo(0, 0); } catch (_) { /* ignore */ }
  }, [screen]);
  const [creatorKey, setCreatorKey] = useState(0);
  const [createdPuzzles, setCreatedPuzzles] = useState([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  // Phase 1 — Auth state (AUTH-01 anon bootstrap)
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  // Phase 2 / W2.T4 — live users/{uid} snapshot for cap-state UX prediction.
  // Subscribed via onSnapshot below; re-renders on every counter increment so
  // CapUsageIndicator updates live.
  const [profile, setProfile] = useState(null);
  // ADMIN — email allowlist. Signing in with one of these accounts auto-unlocks
  // all themes/Pro UI. Anonymous users have no email so they never match.
  // Still does NOT write Firestore entitlements — nothing here grants paid Pro,
  // and no billing state changes. The matching isAdmin() in firestore.rules
  // waives the theme gate and slot cap server-side, so what the UI shows and
  // what the database accepts now agree. (The old public "Admin: unlock all
  // themes" dev toggle was removed — it let any visitor bypass the paywall.)
  const isAdmin = !!(user && user.email && ADMIN_EMAILS.includes(user.email.toLowerCase()));
  const { season, now: seasonNow } = useActiveSeason();
  useEffect(() => { applySeasonFavicon(season); }, [season]);
  // File pickers / share sheets count as "still in the app": no icon switch under them.
  useEffect(() => trackFilePickers(), []);
  // Native icon switching is OFF unless the build sets REACT_APP_SEASON_ICON
  // ("ios", "android" or "ios,android"). Turn it on only for a store build, after
  // the device checklist in task-11-report.md passes; the product owner may choose
  // iPhone-only.
  useEffect(() => watchNativeIcon(season), [season]);
  // Phase 8 — the redesigned creator, on for everyone since the flip (08-07).
  // ?creator=v1 falls back to CreatorWizard for two weeks (src/config/flags.js).
  const creatorV2 = useMemo(
    () => isCreatorV2Enabled({
      search: typeof window !== "undefined" ? window.location.search : "",
      storage: typeof window !== "undefined" ? window.localStorage : null,
      isAdmin,
    }),
    [isAdmin]
  );
  // Profile seen by entitlement-gated UI. Pro forced only for the admin email.
  const effectiveProfile = isAdmin
    ? { ...(profile || {}), entitlements: { ...(profile?.entitlements || {}), pro: true } }
    : profile;
  // #65 — appena il blocco è visibile (il profilo aggiornato arriva subito, prima
  // ancora della conferma del server) il foglio «segnala o blocca» si chiude.
  const bloccatoQui = !!(config && isBlocked(effectiveProfile, config.ownerUid));
  useEffect(() => {
    if (bloccatoQui) setReportOpen(false);
  }, [bloccatoQui]);
  // W3.T4 — gate the hash-URL puzzle-detection effect until the magic-link
  // handler has resolved. This prevents `?apiKey=...&mode=signIn&oobCode=...`
  // from confusing the 30-char puzzle-id heuristic.
  const [authHandlersChecked, setAuthHandlersChecked] = useState(false);
  // Hash di un puzzle arrivato da un App Link / Universal Link mentre l'app era
  // gia' aperta: la lettura di window.location.hash avviene una volta sola al
  // montaggio, quindi senza questo un secondo link non aprirebbe niente.
  const [deepLinkHash, setDeepLinkHash] = useState(null);
  // W3.T5 — sign-up prompt modal visibility (driven by W3.T6 triggers)
  const [showSignUpPrompt, setShowSignUpPrompt] = useState(false);
  const [signUpTrigger, setSignUpTrigger] = useState("unknown");
  // #126 — notifiche: la proposta dopo la prima condivisione, la reazione aperta
  // da una notifica ({ url, title }) e il puzzle da mettere in evidenza in «I miei puzzle».
  const [showPushOffer, setShowPushOffer] = useState(false);
  const [reactionOpen, setReactionOpen] = useState(null);
  const [focusPuzzleId, setFocusPuzzleId] = useState(null);
  // Android, app aperta: la notifica la mostra l'app ({ title, body, target }).
  const [pushNotice, setPushNotice] = useState(null);
  const closePushNotice = useCallback(() => setPushNotice(null), []);
  // Phase 2 / W3.T6 — paywall + cap-blocker modal visibility. Both modals are
  // mounted at App root and toggled by:
  //   - ThemeSelect tap on a locked theme (→ paywall, trigger="theme-lock")
  //   - QuickPuzzleCreator / ShareScreen publish gate (→ cap-blocker, either
  //     pre-flight via canCreatePuzzle or post-rules permission-denied)
  //   - AccountMenu Upgrade entry [W3.T7] (→ paywall, trigger="account-menu")
  // paywallTrigger is purely analytics-shaped — PaywallModal uses it for the
  // hidden data-trigger attribute future Phase 3 wiring will instrument.
  const [showPaywall, setShowPaywall] = useState(false);
  const [purchaseError, setPurchaseError] = useState("");
  // Dopo l'acquisto: null | "attesa" | "attivo" | "lento" (ProAttivazioneModal).
  const [attivazionePro, setAttivazionePro] = useState(null);
  const [showCapBlocker, setShowCapBlocker] = useState(false);
  // Task 7 — draft state: the single in-progress draft for this user.
  const [draft, setDraft] = useState(null);
  // resumeConfig seeds CreatorWizard/QuickPuzzleCreator when editing a draft.
  // Cleared after publish success or when a fresh Create flow starts.
  const [resumeConfig, setResumeConfig] = useState(null);
  // A vibe picked on Home: the new creator opens with it already chosen.
  const [pendingTheme, setPendingTheme] = useState(null);
  // Anon Profile-tap sign-in modal. The Profile tab no longer launches the
  // Google popup straight away — it opens this explainer modal first, whose CTA
  // then calls signInWithGoogle. Less jarring than an instant account-chooser jump.
  const [showSignInModal, setShowSignInModal] = useState(false);
  // Action to run after a successful sign-in triggered by a Pro gate
  // (withSignInGate) — e.g. open the paywall. Stored as a callback so email
  // magic-link (async) and Google (sync) both route through the SignInModal.
  const [pendingProceed, setPendingProceed] = useState(null);
  const [magicReentryUrl, setMagicReentryUrl] = useState(null);
  const [signingIn, setSigningIn] = useState(false);
  const [signInError, setSignInError] = useState("");
  const [appleConflict, setAppleConflict] = useState(false);
  const [paywallTrigger, setPaywallTrigger] = useState("unknown");
  // Phase 3 Wave 4 — account deletion modal state (LEGAL-07).
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  // Footer Profile sheet open state (account menu moved out of top-right).
  const [accountOpen, setAccountOpen] = useState(false);
  const touchRef = useRef(null);

  // Pro si compra solo nell'app, dagli store (#74). Serve un account vero: un
  // acquisto fatto da anonimo resterebbe su un uid usa e getta. `auth` e non
  // `user`, perché si arriva qui anche subito dopo l'accesso del cancello.
  const acquista = async (plan) => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) return;
    const billing = getBilling();
    const prodotti = await billing.getProducts();
    const prodotto = prodotti.find((x) => x.plan === plan) || prodotti[0];
    if (!prodotto) {
      logger.warn("acquista: nessun prodotto acquistabile su questa piattaforma");
      return;
    }
    const comprato = await billing.purchase(prodotto.id, { uid: u.uid });
    // Anche un «annullato» va nei log: certi rifiuti di Apple arrivano così.
    if (!comprato) logger.error("acquista: annullato dal foglio dello store", { code: prodotto.id });
    else setAttivazionePro("attesa");
  };

  // Il Pro arriva dal webhook qualche secondo dopo la conferma dello store: la
  // modale passa ad «attivo» quando il profilo lo mostra, a «lento» dopo l'attesa.
  const proSulProfilo = !!(profile && profile.entitlements && profile.entitlements.pro === true);
  useEffect(() => {
    if (attivazionePro === "attesa" && proSulProfilo) setAttivazionePro("attivo");
  }, [attivazionePro, proSulProfilo]);
  useEffect(() => {
    if (attivazionePro !== "attesa") return undefined;
    const timer = setTimeout(() => {
      logger.error("acquista: Pro non ancora sul profilo dopo l'attesa", { code: String(ATTESA_PRO_MS) });
      setAttivazionePro((s) => (s === "attesa" ? "lento" : s));
    }, ATTESA_PRO_MS);
    return () => clearTimeout(timer);
  }, [attivazionePro]);
  // Se il Pro c'è già (accesso, ripristino o acquisto), il paywall non serve più:
  // si chiude e lo dice. Prima restava aperto su «Pro è sul tuo account» (21/10).
  useEffect(() => {
    if (!showPaywall || !proSulProfilo) return;
    setShowPaywall(false);
    setPurchaseError("");
    setAttivazionePro((s) => s || "attivo");
  }, [showPaywall, proSulProfilo]);
  // «Ripristina acquisti» del paywall (#38). Serve un account vero: da anonimi
  // si accede prima, poi il paywall si riapre e si ripristina da lì.
  const ripristina = async () => {
    const u = auth.currentUser;
    if (!u || u.isAnonymous) {
      setShowPaywall(false);
      withSignInGate(() => setShowPaywall(true));
      return "login";
    }
    return (await getBilling().restore({ uid: u.uid })) ? "ok" : "none";
  };

  // Early sign-in gate for ALL purchase entry points (upgrade / buy-extra).
  // Anonymous users sign in with Google BEFORE seeing pricing/waiver, so Pro
  // and credits always attach to a permanent account (prevents the
  // "Pro lost on refresh" anon-uid stranding). `proceed` runs only once the
  // user is permanent. acquista keeps a backstop check too.
  const withSignInGate = useCallback((proceed) => {
    if (!user || user.isAnonymous) {
      // Open the sign-in modal (Google + email magic-link) instead of forcing a
      // Google-only popup, so non-Google users can reach the paywall. Google
      // completes synchronously (proceed runs on success, below); email is
      // async — the user returns signed-in and re-triggers the action, which
      // then passes this gate (user is permanent) and proceeds immediately.
      setPendingProceed(() => proceed);
      setShowSignInModal(true);
      return;
    }
    proceed();
  }, [user]);

  // Load puzzle history from persistent storage on mount
  useEffect(() => {
    loadPuzzleHistory().then((history) => {
      setCreatedPuzzles(history);
      setHistoryLoaded(true);
      logger.info("Puzzle history loaded", { count: history.length });
    });
  }, []);

  // #127: tre schermate alla prima apertura, solo nell'app. Chi aggiorna l'app e
  // ha già creato dei puzzle sa già come funziona: per lui si segna e basta.
  const [introVista, setIntroVista] = useState(true);
  useEffect(() => {
    if (soloLettore || !historyLoaded) return undefined;
    let attivo = true;
    introSeen().then((vista) => {
      if (!attivo || vista) return;
      if (createdPuzzles.length > 0) { markIntroSeen(); return; }
      setIntroVista(false);
    });
    return () => { attivo = false; };
  }, [historyLoaded]);
  const chiudiIntro = () => { setIntroVista(true); markIntroSeen(); };

  // Save puzzle history to persistent storage when it changes
  useEffect(() => {
    if (historyLoaded) {
      savePuzzleHistory(createdPuzzles);
    }
  }, [createdPuzzles, historyLoaded]);

  // Phase 1 — subscribe to Firebase auth state changes. Flips authReady
  // true on the first callback so downstream effects can react. Runs once
  // for the lifetime of the App.
  //
  // W2.T1 wiring — every callback that hands us a real user kicks off
  // `ensureUserProfile` so the canonical `users/{uid}` doc exists before
  // any downstream feature tries to read it (cross-device restore, Pro
  // entitlements, etc.). The call is fire-and-forget — failures are logged
  // but do NOT block UI; profile creation can be retried on the next auth
  // tick. ensureUserProfile is idempotent so repeated calls for the same
  // uid are cheap (one read, no write).
  useEffect(() => {
    const unsub = subscribeToAuthState((u) => {
      setUser(u);
      setAuthReady(true);
      logger.info("Auth state changed", {
        uid: u?.uid || null,
        isAnon: u?.isAnonymous,
      });
      if (u && u.uid) {
        ensureUserProfile(u).catch((err) =>
          logger.error("ensureUserProfile failed", {
            uid: u.uid,
            error: err.message,
            code: err.code,
          })
        );

        // W4.T5 — Cross-device restore (AUTH-06). When a permanent (non-anon)
        // user signs in (Google popup, magic link, redirect-result), pull the
        // `users/{uid}/puzzles/*` Firestore subcollection and merge with the
        // currently visible local list (dedupe by puzzleId). Firestore wins
        // on conflict because migrateLocalHistoryToUser flows local→Firestore
        // (any divergence reflects a more recent Firestore mirror update).
        // Errors are swallowed inside loadUserPuzzleHistory itself (returns
        // []), so this can never block the auth happy path.
        if (!u.isAnonymous) {
          loadUserPuzzleHistory(u.uid)
            .then((remote) => {
              if (!Array.isArray(remote) || remote.length === 0) return;
              setCreatedPuzzles((prev) => {
                const byId = new Map();
                for (const entry of prev || []) {
                  if (entry && entry.puzzleId) byId.set(entry.puzzleId, entry);
                }
                for (const entry of remote) {
                  if (entry && entry.puzzleId) byId.set(entry.puzzleId, entry);
                }
                return Array.from(byId.values());
              });
              logger.info("loadUserPuzzleHistory — merged into createdPuzzles", {
                uid: u.uid,
                remoteCount: remote.length,
              });
            })
            .catch((err) =>
              logger.error("loadUserPuzzleHistory merge failed", {
                uid: u.uid,
                error: err.message,
              })
            );
        }
      }
    });
    return unsub;
  }, []);

  // Phase 1 — if auth is ready but no user is signed in, bootstrap an
  // anonymous Firebase user. `bootstrapAnonymous` is idempotent so the
  // effect re-running is safe.
  useEffect(() => {
    if (authReady && !user) {
      bootstrapAnonymous().catch((err) =>
        logger.error("Anon bootstrap failed", { error: err.message, code: err.code })
      );
    }
  }, [authReady, user]);

  // Phase 2 / W2.T4 — live snapshot of users/{uid} for cap-state UX.
  //
  // Re-subscribes whenever the user (or their uid) changes; cleans up on
  // sign-out / sign-in handover so we never leak listeners. Re-renders on
  // every counter increment are intentional — CapUsageIndicator wants the
  // live count and useCapState's hourly ticker can't predict mid-session
  // mutations.
  //
  // Errors are non-fatal: we log and leave `profile` null. getCapState
  // tolerates null (treats as fresh Free user with no recentPuzzles).
  useEffect(() => {
    if (!user || !user.uid) {
      setProfile(null);
      return undefined;
    }
    const unsub = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        // Diagnostic (Pro-lost-on-refresh): record uid + whether the loaded
        // profile actually carries entitlements.pro, so the downloaded log
        // shows if the doc is missing pro vs a uid mismatch.
        logger.info("profile onSnapshot", {
          uid: user.uid,
          isAnon: user.isAnonymous,
          exists: snap.exists(),
          pro: snap.exists() ? !!(snap.data()?.entitlements?.pro) : null,
          source: snap.exists() ? (snap.data()?.entitlements?.source || null) : null,
        });
        setProfile(snap.exists() ? snap.data() : null);
      },
      (err) => {
        logger.error("profile onSnapshot failed", {
          uid: user.uid,
          error: err.message,
          code: err.code,
        });
        setProfile(null);
      }
    );
    return unsub;
  }, [user]);

  // W3.T4 — Magic-link return handler. Runs FIRST, before the hash-URL puzzle
  // detection effect, so `?apiKey=...&mode=signIn&oobCode=...` query params are
  // consumed and stripped before the puzzle-id heuristic runs;
  // `authHandlersChecked` flips true once it has resolved (success / null /
  // error all count). Sul web non si accede più (#74): lì si salta.
  useEffect(() => {
    let cancelled = false;
    const markResolved = () => {
      if (!cancelled) setAuthHandlersChecked(true);
    };
    if (soloLettore) {
      markResolved();
      return () => { cancelled = true; };
    }
    completeMagicLinkIfPresent()
      .then((u) => {
        if (u && u.status === "needs_email") {
          // Cross-device / cross-browser return: no stashed email. Open the
          // re-entry modal to collect it, then completeMagicLinkWithEmail.
          logger.info("Magic-link handler — needs_email, opening re-entry modal");
          setMagicReentryUrl(u.url);
        } else if (u && u.uid) {
          // Same-device magic-link return with a valid stashed email still
          // completes the sign-in transparently — preserved so users with
          // unconsumed pre-patch links land in their account instead of
          // hitting an error.
          logger.info("Magic-link handler signed in user", { uid: u.uid });
        }
      })
      .catch((err) =>
        logger.error("Magic-link handler failed", {
          error: err.message,
          code: err.code,
        })
      )
      .finally(markResolved);
    return () => {
      cancelled = true;
    };
  }, [soloLettore]);

  const handleMenuNavigate = (dest) => {
    logger.info("Navigation", { from: screen, to: dest, type: "menu" });
    setAccountOpen(false); // close the account sheet when switching tabs
    if (dest === "role" || dest === "theme" || dest === "creator") {
      setCreatorKey((k) => k + 1);
      setResumeConfig(null); // fresh Create — don't seed from a stale draft
    }
    setScreen(dest);
    setScreenHistory([]);
  };

  // Mirror screenHistory into a ref so the popstate listener (registered once)
  // always sees the current depth.
  const screenHistoryRef = useRef([]);
  useEffect(() => { screenHistoryRef.current = screenHistory; }, [screenHistory]);
  // A tab left open keeps running the build it loaded. When a newer one is
  // deployed, reload — but only on Home or My Puzzles, never in the creator or
  // while someone is solving a puzzle, where a reload would cost them work.
  const [updateReady, setUpdateReady] = useState(false);
  useEffect(() => {
    const check = () => {
      isUpdateAvailable().then((yes) => { if (yes) setUpdateReady(true); }).catch(() => {});
    };
    const onVisible = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisible);
    const id = setInterval(check, 30 * 60 * 1000);
    return () => { document.removeEventListener("visibilitychange", onVisible); clearInterval(id); };
  }, []);
  useEffect(() => {
    if (!updateReady || !SAFE_RELOAD_SCREENS.has(screen)) return;
    logger.info("New build deployed, reloading", { screen });
    window.location.reload();
  }, [updateReady, screen]);

  // While the v2 creator is open it takes every system back press (device Back,
  // swipe) itself and steps back one screen — see CreatorFlow's `backRef`.
  const creatorBackRef = useRef(null);

  const navigateTo = useCallback((newScreen) => {
    setScreenHistory((prev) => [...prev, screen]);
    setSlideDirection("in");
    setScreen(newScreen);
    setTimeout(() => setSlideDirection(null), 400);
    // Push a browser history entry so the device/browser Back button pops an
    // in-app screen instead of leaving /app for the marketing landing.
    try { window.history.pushState({ puzzlyNav: true }, ""); } catch { /* ignore */ }
  }, [screen]);

  const goBack = useCallback(() => {
    if (screenHistory.length === 0) return;
    setSlideDirection("out");
    const prev = screenHistory[screenHistory.length - 1];
    setScreenHistory((h) => h.slice(0, -1));
    setTimeout(() => {
      setScreen(prev);
      setSlideDirection(null);
    }, 300);
  }, [screenHistory]);

  // Back for legal pages (privacy/terms/cookies): pop in-app history if any,
  // else return to the marketing landing (handles direct /terms, /cookies entry).
  const legalBack = useCallback(() => {
    if (screenHistory.length > 0) { goBack(); return; }
    window.location.href = "/";
  }, [screenHistory, goBack]);

  // Intercept browser/device Back: if there's in-app history, go back one
  // screen and re-arm the trap; otherwise let the navigation proceed.
  useEffect(() => {
    const onPop = () => {
      if (creatorBackRef.current) {
        try { window.history.pushState({ puzzlyNav: true }, ""); } catch { /* ignore */ }
        creatorBackRef.current();
        return;
      }
      if (screenHistoryRef.current.length > 0) {
        // Cancel the exit by re-pushing, then step back in-app.
        try { window.history.pushState({ puzzlyNav: true }, ""); } catch { /* ignore */ }
        goBack();
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [goBack]);

  // Swipe-back gesture
  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchRef.current = { startX: touch.clientX, startY: touch.clientY, startTime: Date.now() };
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (!touchRef.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - touchRef.current.startX;
    const dy = Math.abs(touch.clientY - touchRef.current.startY);
    const dt = Date.now() - touchRef.current.startTime;
    touchRef.current = null;
    // Swipe right: dx > 80px, mostly horizontal, under 500ms
    if (dx > 80 && dy < 100 && dt < 500) {
      if (creatorBackRef.current) creatorBackRef.current();
      else if (screenHistory.length > 0) goBack();
    }
  }, [goBack, screenHistory]);

  // ── W3.T6 — SignUpPrompt triggers (AUTH-08) ──
  // Two and only two Phase-1 triggers: (A) Upgrade intent, (B) Save-across-
  // devices intent. Eligibility policy lives in services/signupTriggers.js
  // (pure helper) so it's unit-testable. The session-storage key
  // `puzzly_signup_shown` prevents the once-per-session re-show. Never
  // blocking — modal is dismissible. Never fires on first-puzzle-create
  // (publish handlers invoke this AFTER setCreatedPuzzles adds the new
  // puzzle — locked: AUTH-08 "prompted not forced").
  const showSignUpPromptIfEligible = useCallback(
    (trigger) => {
      const storage =
        typeof window !== "undefined" ? window.sessionStorage : undefined;
      if (!isSignUpPromptEligible(user, storage)) return false;
      setSignUpTrigger(trigger);
      setShowSignUpPrompt(true);
      markSignUpPromptShown(storage);
      logger.info("SignUpPrompt opened", { trigger });
      return true;
    },
    [user]
  );

  // Phase-2 hook: callers (entitlement gating, paywall, etc.) invoke this
  // to surface the prompt at "Upgrade" intent moments. Phase 1 ships the
  // helper but no paywall calls it yet.
  const triggerSignUpForUpgrade = useCallback(() => {
    return showSignUpPromptIfEligible("upgrade");
  }, [showSignUpPromptIfEligible]);

  const triggerSignUpForSaveAcrossDevices = useCallback(() => {
    return showSignUpPromptIfEligible("save-across-devices");
  }, [showSignUpPromptIfEligible]);

  // #110 — the share screen's own invite: the user asked for it, so no
  // once-per-session limit; only an anonymous creator ever sees the line.
  const openReactionsSignUp = useCallback(() => {
    setSignUpTrigger("reactions");
    setShowSignUpPrompt(true);
    logger.info("SignUpPrompt opened", { trigger: "reactions" });
  }, []);

  // #126 — chi crea ha appena mandato il link: «Vuoi sapere quando lo risolve?».
  // Una volta sola (shouldOfferPush), solo nell'app, mai sopra l'invito ad accedere.
  const pushOfferPending = useRef(false);
  const offerPushAfterShare = useCallback(() => {
    if (pushOfferPending.current || showSignUpPrompt) return;
    pushOfferPending.current = true;
    segnaPropostaPush(true);
    shouldOfferPush()
      .then((ok) => {
        if (!ok) { pushOfferPending.current = false; segnaPropostaPush(false); return; }
        // Il foglio di condivisione o WhatsApp sono ancora davanti: la proposta
        // aspetta un attimo, così la si trova al ritorno nell'app.
        setTimeout(() => setShowPushOffer(true), 900);
      })
      .catch(() => { pushOfferPending.current = false; segnaPropostaPush(false); });
  }, [showSignUpPrompt]);
  const closePushOffer = useCallback(() => {
    setShowPushOffer(false);
    segnaPropostaPush(false);
    markPushOffered();
  }, []);
  const acceptPushOffer = useCallback(() => {
    setShowPushOffer(false);
    segnaPropostaPush(false);
    enablePush().catch((err) => logger.warn("Push: attivazione non riuscita", { error: err && err.message }));
  }, []);

  // Task 7 — draft helpers
  const refreshDraft = useCallback(() => {
    fetchDraft().then(setDraft).catch((err) => {
      logger.warn("Draft fetch failed", { error: err && (err.code || err.message) });
      setDraft(null);
    });
  }, []);

  // Refresh draft whenever history screen is shown
  useEffect(() => {
    if (screen === "history") refreshDraft();
  }, [screen, refreshDraft]);

  const onSaveDraft = useCallback(async (config, creatorType) => {
    try {
      const existing = await fetchDraft();
      if (existing && !window.confirm(tr("app.modals.draftReplaceConfirm", { title: existing.title || tr("app.modals.historyTitleFallbacks.untitled") }))) return;
      await saveDraft(config, creatorType);
      await refreshDraft();
      navigateTo("history");
    } catch (e) {
      logger.error("Save draft failed", { error: e?.message });
    }
  }, [refreshDraft, navigateTo]);

  const onEditDraft = useCallback((d) => {
    setResumeConfig(d.config);
    setTheme(resolveThemeKey(d.config.theme));
    setCreatorKey((k) => k + 1);
    navigateTo(draftResumeScreen(d.creatorType, creatorV2));
  }, [navigateTo, creatorV2]);

  const onShareDraft = useCallback(async (d) => {
    if (!canCreatePuzzle(effectiveProfile)) { setShowCapBlocker(true); return; }
    try {
      const result = await fbCreatePuzzle(d.config, { profile: effectiveProfile });
      // Build the same history entry shape the creator paths use, so the
      // published puzzle immediately appears in My Puzzles with View/Copy-link.
      const entry = {
        id: Date.now(),
        puzzleId: result.id,
        title: d.config?.title || tr("app.modals.historyTitleFallbacks.untitled"),
        theme: d.config?.theme || "romantic",
        levelCount: d.config?.levels?.length || 0,
        createdAt: new Date().toISOString(),
        shareUrl: result.shareUrl,
        ownerUid: user?.uid || null,
      };
      setCreatedPuzzles((prev) => [entry, ...prev]);
      // Mirror to the account for permanent (non-anonymous) users so the puzzle
      // survives sign-out/sign-in. Same condition as the creator paths.
      if (user && !user.isAnonymous) {
        addPuzzleToUserHistory(user.uid, entry);
      }
      // Remove the draft only after confirmed publish success.
      await deleteDraft();
      await refreshDraft();
    } catch (e) {
      if (e?.code === "permission-denied") { setShowCapBlocker(true); return; } // draft preserved
      if (isRulesDeclined(e)) return; // «Non ora» sulle regole (#65): la bozza resta
      logger.error("Share draft failed", { error: e?.message });
    }
  }, [effectiveProfile, user, refreshDraft]);

  // Deep link: l'utente tocca https://puzzly.giadalabs.com/#<id> e Android/iOS
  // consegnano l'URL all'app invece che al browser. Qui si estrae l'hash e si
  // rida' in pasto all'effetto che carica il puzzle.
  useEffect(() => {
    if (!isNativeApp()) return;
    let remove = null;
    CapApp.addListener("appUrlOpen", ({ url }) => {
      logger.info("Deep link ricevuto", { url: url.split("?")[0] });
      // Il link di accesso arrivato per email (#67): si chiude l'accesso qui.
      if (url.includes("mode=signIn")) {
        completeMagicLinkIfPresent(url)
          .then((u) => { if (u && u.status === "needs_email") setMagicReentryUrl(u.url); })
          .catch((err) => logger.error("Link di accesso dall'app non riuscito", { error: err.message, code: err.code }));
        return;
      }
      const i = url.indexOf("#");
      if (i >= 0 && url.slice(i + 1)) setDeepLinkHash(url.slice(i + 1));
    }).then((handle) => { remove = handle; });
    return () => { if (remove) remove.remove(); };
  }, []);

  // #126 — il telefono sta sotto l'account in uso, anche anonimo: si riscrive a
  // ogni cambio d'account. Senza il permesso già dato non fa niente.
  useEffect(() => {
    if (!user?.uid || !isNativeApp()) return;
    registerPushToken().catch((err) => logger.warn("Push: registrazione non riuscita", { error: err && err.message }));
  }, [user?.uid]);

  // #126 — tocco su una notifica: «I miei puzzle», con quel puzzle in evidenza;
  // per una reazione, il video sopra. Un ref, perché l'ascolto parte una volta sola
  // e deve usare la navigazione e la lista di adesso.
  const pushOpenRef = useRef(null);
  pushOpenRef.current = (target) => {
    setFocusPuzzleId(target.puzzleId);
    if (screen !== "history") navigateTo("history");
    if (target.videoUrl) {
      const p = createdPuzzles.find((x) => x.puzzleId === target.puzzleId);
      setReactionOpen({ url: target.videoUrl, title: p ? p.title : "" });
    }
  };
  useEffect(() => listenToPush({
    onOpen: (t) => pushOpenRef.current && pushOpenRef.current(t),
    onForeground: (n) => setPushNotice(n),
  }), []);
  useEffect(() => {
    if (screen !== "history") setFocusPuzzleId(null);
  }, [screen]);

  // Rete di sicurezza: qualunque cosa succeda al gestore d'autenticazione,
  // dopo qualche secondo si procede comunque. Un'app che non parte e' peggio di
  // un caso limite d'autenticazione gestito male. (#40)
  useEffect(() => {
    const t = setTimeout(() => {
      setAuthHandlersChecked((gia) => {
        if (!gia) logger.warn("authHandlersChecked forzato dal tetto di tempo: un gestore non si e' risolto");
        return true;
      });
    }, 6000);
    return () => clearTimeout(t);
  }, []);

  // Check URL hash on mount for player mode
  // W3.T4 — gated on `authHandlersChecked` so that magic-link return params
  // (`?apiKey=...&mode=signIn&oobCode=...`) and pending Capacitor redirect
  // results are consumed + stripped BEFORE the hash heuristic runs.
  useEffect(() => {
    if (!authHandlersChecked) return;
    const hash = deepLinkHash || window.location.hash.slice(1);
    // Un link che arriva dal sistema (App Link) è sempre un puzzle; un hash
    // dell'URL che indica una sezione della pagina è un'ancora.
    if (!hash || (!deepLinkHash && ancoraInPagina())) {
      // Sul web /app senza un puzzle non ha niente da mostrare (#74).
      if (soloLettore) setScreen((s) => (s === "caricamento" ? "store" : s));
      return;
    }

    // #116: nell'app le schermate di chi riceve non hanno la barra in basso, e
    // la cronologia era vuota: il tasto indietro (o lo swipe) non faceva niente.
    // La Home diventa la schermata precedente, con il suo passo nella cronologia.
    const apriDaLink = (dest) => {
      if (!soloLettore) {
        setScreenHistory(["theme"]);
        try { window.history.pushState({ puzzlyNav: true }, ""); } catch { /* ignore */ }
      }
      setScreen(dest);
    };

    // Short hash = Firebase puzzle ID; long hash = legacy LZ-compressed
    if (hash.length <= 30) {
      // Fetch from Firebase
      logger.info("Loading puzzle from URL hash", { puzzleId: hash });
      fbFetchPuzzle(hash).then((puzzle) => {
        if (puzzle && puzzle.version === 1) {
          // Strip saved playerProgress so every link open plays the puzzle from the start.
          // (Progress is per-puzzle-document, not per-player, so auto-resume was both
          // surprising for returning players and broken for multi-player links.)
          const { playerProgress, ...rest } = puzzle;
          const puzzleConfig = { ...rest, _fromDb: true, _id: hash };
          setConfig(puzzleConfig);
          setTheme(resolveThemeKey(puzzle.theme));
          logger.info("Loaded puzzle, starting from preview", { puzzleId: hash });
          apriDaLink("preview");
        } else {
          logger.warn("Puzzle not found or invalid version", { puzzleId: hash });
          // Prima chi apriva un link sbagliato o rimosso non vedeva niente (#65).
          apriDaLink("unavailable");
        }
      }).catch((err) => {
        logger.error("Failed to load puzzle from hash", { puzzleId: hash, error: err.message });
        // Anche un puzzle nascosto da chi modera arriva qui (le regole negano la lettura).
        apriDaLink("unavailable");
      });
    } else {
      // Legacy LZ-compressed config
      const decoded = decodeConfig(hash);
      if (decoded && decoded.version === 1) {
        setConfig(decoded);
        setTheme(resolveThemeKey(decoded.theme));
        apriDaLink("preview");
      } else {
        // Sul web non c'è un'altra schermata dove restare.
        setScreen((s) => (s === "caricamento" ? "unavailable" : s));
      }
    }
  }, [authHandlersChecked, deepLinkHash, soloLettore]);

  // Someone solving a puzzle is inside that puzzle, not browsing the app.
  // The first screen on the web ("caricamento") counts too while a puzzle link is
  // loading (URL hash or App Link), so the season doesn't flash before its theme.
  const loadingPuzzle = screen === "caricamento"
    && !!(deepLinkHash || (typeof window !== "undefined" && window.location.hash.slice(1) && !ancoraInPagina()));
  const recipientScreen = loadingPuzzle || ["preview", "password", "play", "finale"].includes(screen);
  // Task 9 — whose colours the app chrome wears. A puzzle on screen always keeps
  // its own theme (`config` stays loaded after leaving it, so the screen decides);
  // while creating, a theme the user picked wins; otherwise the app's own
  // surfaces wear the active season, else the default look. A theme picked
  // earlier never follows the user back to Home.
  const showingPuzzle = recipientScreen && (!!config || loadingPuzzle);
  const creating = screen === "creator" || screen === "quick_create";
  const chromeTheme = resolveThemeKey(
    showingPuzzle ? config?.theme : (creating && pickedTheme) || season || "romantic"
  );
  // The season's look is on only where the chrome wears it, never on a puzzle.
  const seasonLook = !showingPuzzle && season && chromeTheme === season ? season : null;
  const activeTheme = THEMES[chromeTheme];
  const activeMicrocopy = useThemeMicrocopy(chromeTheme, activeTheme?.microcopy);

  // Fonts are preloaded via <link> in public/index.html — no runtime injection needed

  const themeVars = themeCssVars(activeTheme);

  // The browser bar follows the season on the app's own surfaces; the original
  // colour (index.html / manifest) comes back otherwise.
  useEffect(() => {
    const meta = typeof document !== "undefined" && document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    if (meta.dataset.puzzlyDefault === undefined) meta.dataset.puzzlyDefault = meta.getAttribute("content") || "";
    const seasonal = seasonLook && THEMES[seasonLook]?.colors.primary;
    meta.setAttribute("content", seasonal || meta.dataset.puzzlyDefault);
  }, [seasonLook]);

  // W4 closure UX patch — centralized sign-out handler used by AccountMenu.
  // (Original W4.T4 handler lived inline as a PuzzleHistory prop; that path
  // is removed because the in-list Account section was prone to accidental
  // clicks. AccountMenu now gates this through a confirm modal.)
  // Profile tab (BottomNav) handler. Anon → open the sign-in explainer modal
  // (SignInModal), which owns the Google CTA. Permanent → toggle the footer-
  // anchored account sheet (email + upgrade/manage/sign-out/delete).
  const handleProfileTab = useCallback(() => {
    // Finché l'accesso salvato non è ripristinato `user` è null anche per chi ha
    // un account, e gli si chiederebbe di accedere (#45).
    if (!authReady) return;
    if (!user || user.isAnonymous) { setShowSignInModal(true); return; }
    setAccountOpen((v) => !v);
  }, [user, authReady]);

  // CTA inside SignInModal (Google, or Apple on iOS). Awaits the sign-in, then
  // closes the modal on success. On failure we log, drop the spinner, show the
  // error in the modal (it used to stay silent) and leave it open for a retry.
  const handleSignInFromModal = useCallback(async (signIn) => {
    setSigningIn(true);
    setSignInError("");
    try {
      await signIn();
      setShowSignInModal(false);
      // If this sign-in was triggered by a Pro gate, run the pending action
      // (e.g. open the paywall) now that the user is permanent.
      if (pendingProceed) { const p = pendingProceed; setPendingProceed(null); p(); }
    } catch (err) {
      // error, non warn: così ogni accesso fallito arriva anche in Cloud Logging.
      logger.error("Sign-in modal: sign-in cancelled/failed", { error: err.message, code: err.code });
      setSignInError(err.message || "");
      if (err.code === APPLE_EMAIL_IN_USE) setAppleConflict(true);
    } finally {
      setSigningIn(false);
    }
  }, [pendingProceed]);

  // L'email di Apple ha già un account (di solito Google): si entra lì, poi si
  // collega Apple, così la volta dopo basta Apple. Se il collegamento non va si
  // resta comunque dentro con Google.
  const signInWithGoogleThenLinkApple = useCallback(async () => {
    await signInWithGoogle();
    try {
      await linkAppleToCurrentUser();
    } catch (err) {
      logger.error("Collegamento di Apple dopo Google non riuscito", { error: err.message, code: err.code });
    }
    setAppleConflict(false);
  }, []);

  // Il link via email completa l'accesso fuori dal riquadro (appUrlOpen): quando
  // l'utente diventa vero il riquadro si chiude e riparte l'azione che l'aveva
  // aperto. Prima restava aperto su un'app già entrata (prova del 21/10). Non
  // durante un accesso Google o Apple: lì chiude handleSignInFromModal, e un
  // acquisto in attesa partirebbe due volte.
  useEffect(() => {
    if (!showSignInModal || signingIn || !user || user.isAnonymous) return;
    setShowSignInModal(false);
    setSignInError("");
    setAppleConflict(false);
    if (pendingProceed) {
      const p = pendingProceed;
      setPendingProceed(null);
      p();
    }
  }, [showSignInModal, signingIn, user, pendingProceed]);

  const handleSignOut = useCallback(async () => {
    try {
      setAccountOpen(false);
      setCreatedPuzzles([]);
      // Reset the once-per-session sign-up nudge flag so the fresh anon session
      // that re-bootstraps after logout is eligible for the prompt again.
      try { clearSignUpPromptShown(window.sessionStorage); } catch (_) { /* ignore */ }
      // #126 — da dentro l'account: questo telefono smette di ricevere le sue notifiche.
      await unregisterPushToken();
      await signOutUser();
      navigateTo("theme");
    } catch (err) {
      logger.error("Sign-out flow failed", { error: err.message });
    }
  }, [navigateTo]);

  // Phase 3 Wave 4 — GDPR right-to-erasure cascade (LEGAL-07).
  // Called by DeleteAccountModal.onConfirmDelete after user types "DELETE".
  // The Cloud Function deletes puzzles + user doc + LS subscription + auth user.
  // On success, reset local state and navigate to splash — auth user is gone.
  const handleDeleteAccount = useCallback(async () => {
    await deleteMyAccount(); // throws on failure (modal will show error + stay open)
    // Wipe local in-memory state — auth user is permanently deleted server-side
    setCreatedPuzzles([]);
    setShowDeleteAccount(false);
    navigateTo("theme");
  }, [navigateTo]);

  // Recording a published puzzle: history entry, account mirror, draft cleanup and
  // the save-across-devices prompt. Shared by both creators.
  const recordPublished = useCallback((puzzleId, shareUrl, puzzleConfig) => {
          const entry = {
            id: Date.now(),
            puzzleId,
            title: puzzleConfig?.title || tr("app.modals.historyTitleFallbacks.untitled"),
            theme: puzzleConfig?.theme || theme,
            levelCount: puzzleConfig?.levels?.length || 0,
            createdAt: new Date().toISOString(),
            shareUrl,
            ownerUid: user?.uid || null,
          };
          setCreatedPuzzles((prev) => [entry, ...prev]);
          // Mirror to the account so signed-in creations survive sign-out/
          // sign-in (puzzle-lost-on-signout fix). Permanent users only.
          if (user && !user.isAnonymous) {
            addPuzzleToUserHistory(user.uid, entry);
          }
          // If this publish came from a draft resume, remove the draft.
          if (resumeConfig) { setResumeConfig(null); deleteDraft().then(refreshDraft).catch(() => {}); }
          // W3.T6 — Save-across-devices trigger (AUTH-08).
          triggerSignUpForSaveAcrossDevices();
  }, [tr, theme, user, resumeConfig, refreshDraft, triggerSignUpForSaveAcrossDevices]);

  // Phase 8 — the redesigned creator owns the whole flow, from "who is it for"
  // to the share screen, so it replaces both the vibe picker and the old wizard
  // wherever the flag is on. Built once; the switch arms below just return it.
  const creatorV2Screen = creatorV2 ? (
      <CreatorFlow
        key={creatorKey}
        initialConfig={resumeConfig}
        initialName={pendingTitle}
        // Only seed a theme when resuming a draft: `theme` defaults to romantic,
        // which is Pro-only, so a Free user would have started on a locked vibe
        // with the button already enabled.
        initialTheme={resumeConfig ? theme : pendingTheme}
        themes={THEMES}
        now={seasonNow}
        allSeasons={isAdmin}
        isPro={effectiveProfile?.entitlements?.pro === true}
        canCreate={canCreatePuzzle(effectiveProfile)}
        slotLimit={activeLimit(effectiveProfile)}
        puzzles={createdPuzzles}
        onDeletePuzzle={async (p) => {
          if (!user?.uid) return;
          await deletePuzzle(p.puzzleId, user.uid);
          setCreatedPuzzles((prev) => prev.filter((x) => x.puzzleId !== p.puzzleId));
        }}
        onUpgrade={() => { setPaywallTrigger("creator-limit"); setShowPaywall(true); }}
        onLocked={() => withSignInGate(() => { setPaywallTrigger("theme-lock"); setShowPaywall(true); })}
        onMediaLocked={() => { setPaywallTrigger("media-gate"); setShowPaywall(true); }}
        onExit={({ draftSaved } = {}) => {
          if (draftSaved) navigateTo("history");
          else if (screenHistory.length > 0) goBack();
          else navigateTo("theme");
        }}
        onNewPuzzle={() => navigateTo("theme")}
        backRef={creatorBackRef}
        onSaveDraftExplicit={onSaveDraft}
        onPublished={recordPublished}
        onPrivacy={() => navigateTo("privacy")}
        onInviteSignIn={user?.isAnonymous ? openReactionsSignUp : null}
        onShared={offerPushAfterShare}
        ShareScreen={ShareScreen}
        LevelPlay={LevelPlay}
        Finale={Finale}
        themeVarsFor={(key) => themeCssVars(THEMES[key] || THEMES.romantic)}
      />
  ) : null;

  // Phase 8 — Home: the vibes, one tap from a new puzzle. Replaces the old
  // vibe picker as what the Home tab opens; the creator itself is "creator".
  const startCreator = (vibe) => {
    setResumeConfig(null);
    setPendingTheme(vibe);
    if (vibe) setTheme(vibe);
    setPendingTitle("");
    setCreatorKey((k) => k + 1);
    navigateTo("creator");
  };
  const homeScreen = creatorV2 ? (
    <HomeScreen
      themes={THEMES}
      now={seasonNow}
      allSeasons={isAdmin}
      isPro={effectiveProfile?.entitlements?.pro === true}
      onPick={(vibe) => startCreator(vibe)}
      onLocked={() => withSignInGate(() => { setPaywallTrigger("theme-lock"); setShowPaywall(true); })}
    />
  ) : null;

  // Le schermate di chi riceve un puzzle: lì si segnala e si blocca (#65).
  const SCHERMATE_DI_CHI_RICEVE = ["preview", "password", "play", "finale"];
  // Le uniche che il web mostra (#74).
  const SCHERMATE_DEL_WEB = ["caricamento", "unavailable", ...SCHERMATE_DI_CHI_RICEVE, "privacy", "terms", "cookies"];
  // Via da un puzzle che non si può mostrare: niente hash, così ricaricando
  // non si riapre.
  const vaiAllaHome = () => {
    setReportOpen(false);
    if (soloLettore) { navigate("/"); return; }
    setConfig(null);
    setScreenHistory([]);
    setScreen("theme");
    try { window.history.replaceState(null, "", window.location.pathname); } catch (_) { /* ignore */ }
  };
  const renderScreen = () => {
    // Phase 1 (W1.T4) — gate creator flows on authReady.
    // Creator flows need `user.uid` for ownerUid stamping (Wave 2) so we
    // block their render until Firebase Auth has settled. Player screens
    // (splash/preview/password/play/finale/history/privacy/player_entry)
    // must remain unguarded — share links load puzzles without auth
    // (PATTERNS §4 gotcha).
    const caricamento = (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--color-bg)" }}>
        <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 28, color: "var(--color-primary)" }}>Puzzly</div>
      </div>
    );
    // #74 — sul web esistono solo il puzzle ricevuto e le pagine legali; il
    // resto porta alla pagina degli store (con la query, per i link di accesso).
    if (soloLettore && !SCHERMATE_DEL_WEB.includes(screen)) {
      if (screen === "caricamento") return caricamento;
      return <Navigate to={{ pathname: "/", search: window.location.search }} replace />;
    }
    const CREATOR_SCREENS = ["role", "theme", "creator", "quick_theme", "quick_create"];
    // Brief auth-settle loader (was SplashScreen, now retired).
    if (!authReady && CREATOR_SCREENS.includes(screen)) return caricamento;
    // #65 — chi ha bloccato il creatore di questo puzzle non lo vede più.
    if (SCHERMATE_DI_CHI_RICEVE.includes(screen) && config && isBlocked(effectiveProfile, config.ownerUid)) {
      return <ModerationNotice kind="blocked" onHome={vaiAllaHome} />;
    }
    switch (screen) {
      case "unavailable":
        return <ModerationNotice kind="unavailable" onHome={vaiAllaHome} />;
      case "quick_theme":
        return <ThemeSelect
          key={creatorKey}
          profile={effectiveProfile}
          onLocked={() => withSignInGate(() => { setPaywallTrigger("theme-lock"); setShowPaywall(true); })}
          onSelect={(t, name) => { setTheme(t); setPendingTitle(name || ""); navigateTo("quick_create"); }}
        />;
      case "quick_create":
        return <QuickPuzzleCreator
          user={user}
          authReady={authReady}
          theme={theme}
          initialConfig={resumeConfig}
          // Same as CreatorWizard below: this component gates publishing on
          // canCreatePuzzle(profile), so it needs the effective one.
          profile={effectiveProfile}
          onCapBlocked={() => setShowCapBlocker(true)}
          onSaveDraft={onSaveDraft}
          onBack={goBack}
          onPrivacy={() => navigateTo("privacy")}
          onPublished={(puzzleId, shareUrl, puzzleConfig) => {
          const entry = {
            id: Date.now(), puzzleId,
            title: puzzleConfig?.title || tr("app.modals.historyTitleFallbacks.quickPuzzle"),
            theme: puzzleConfig?.theme || theme,
            levelCount: 2,
            createdAt: new Date().toISOString(),
            shareUrl,
            ownerUid: user?.uid || null,
          };
          setCreatedPuzzles((prev) => [entry, ...prev]);
          // Mirror to the account so signed-in creations survive sign-out/
          // sign-in (puzzle-lost-on-signout fix). Permanent users only; anon
          // puzzles ride the local cache + sign-in migration. Fire-and-forget.
          if (user && !user.isAnonymous) {
            addPuzzleToUserHistory(user.uid, entry);
          }
          // If this publish came from a draft resume, remove the draft.
          if (resumeConfig) { setResumeConfig(null); deleteDraft().then(refreshDraft).catch(() => {}); }
          // W3.T6 — Save-across-devices trigger (AUTH-08).
          triggerSignUpForSaveAcrossDevices();
        }} />;
      // "splash"/"role" retired — both alias to the Create vibe-picker.
      case "splash":
      case "role":
      case "theme":
        if (homeScreen) return homeScreen;
        return <ThemeSelect
          key={creatorKey}
          profile={effectiveProfile}
          onLocked={() => withSignInGate(() => { setPaywallTrigger("theme-lock"); setShowPaywall(true); })}
          onSelect={(t, name) => { setTheme(t); setPendingTitle(name || ""); navigateTo("creator"); }}
          onBack={screenHistory.length > 0 ? goBack : undefined}
        />;
      case "creator":
        if (creatorV2Screen) return creatorV2Screen;
        return <CreatorWizard
          key={creatorKey}
          user={user}
          authReady={authReady}
          theme={theme}
          initialTitle={pendingTitle}
          initialConfig={resumeConfig}
          // effectiveProfile, like every other entitlement-gated component. The
          // builder's publish gate lives inside CreatorWizard, so passing the raw
          // profile here left an admin unlocked everywhere except the one screen
          // that decides whether "Share Puzzle" or "Save as draft" is offered.
          // For everyone else effectiveProfile IS profile, so nothing changes.
          profile={effectiveProfile}
          onCapBlocked={() => setShowCapBlocker(true)}
          onSaveDraft={onSaveDraft}
          onMediaLocked={() => { setPaywallTrigger("media-gate"); setShowPaywall(true); }}
          onComplete={(action) => {
          if (action === "new") {
            setCreatorKey((k) => k + 1);
            setResumeConfig(null);
            setScreen("theme");
            setScreenHistory([]);
          }
        }} onBack={goBack} onPrivacy={() => navigateTo("privacy")} onPuzzlePublished={recordPublished} onShared={offerPushAfterShare} />;
      case "privacy":
        return (
          <>
            <SEO
              title={tr("app.seo.privacy.title")}
              description={tr("app.seo.privacy.description")}
              canonical="https://puzzly.giadalabs.com/privacy"
            />
            <PrivacyPolicy onBack={legalBack} />
          </>
        );
      case "terms":
        return (
          <>
            <SEO
              title={tr("app.seo.terms.title")}
              description={tr("app.seo.terms.description")}
              canonical="https://puzzly.giadalabs.com/terms"
            />
            <LegalPolicy heading={tr("app.legal.termsTitle")} sections={TERMS_SECTIONS} footer={tr("app.legal.termsFooter")} onBack={legalBack} />
          </>
        );
      case "cookies":
        return (
          <>
            <SEO
              title={tr("app.seo.cookies.title")}
              description={tr("app.seo.cookies.description")}
              canonical="https://puzzly.giadalabs.com/cookies"
            />
            <LegalPolicy heading={tr("app.legal.cookiesTitle")} sections={COOKIES_SECTIONS} footer={tr("app.legal.cookiesFooter")} onBack={legalBack} />
          </>
        );
      case "history":
        return <PuzzleHistory
          onBack={goBack}
          puzzles={createdPuzzles}
          currentUid={user?.uid}
          draft={draft}
          onEditDraft={onEditDraft}
          onShareDraft={onShareDraft}
          focusPuzzleId={focusPuzzleId}
          onDelete={async (puzzle) => {
            if (!user?.uid) {
              throw new Error(tr("app.modals.signInNotReady"));
            }
            await deletePuzzle(puzzle.puzzleId, user.uid);
            setCreatedPuzzles((prev) => prev.filter((p) => p.puzzleId !== puzzle.puzzleId));
          }}
          onCreateNew={() => { setResumeConfig(null); setCreatorKey((k) => k + 1); navigateTo("theme"); }}
          onView={async (shareUrl) => {
            const hash = shareUrl.split("#")[1];
            if (!hash) return;

            // Try Firebase first (short hash)
            if (hash.length <= 30) {
              try {
                const puzzle = await fbFetchPuzzle(hash);
                if (puzzle && puzzle.version === 1) {
                  setConfig({ ...puzzle, _fromDb: true, _id: hash });
                  setTheme(resolveThemeKey(puzzle.theme));
                  // navigateTo, not setScreen: keep the way back to My Puzzles.
                  navigateTo("preview");
                  return;
                }
              } catch {}
            }

            // Legacy fallback
            const decoded = decodeConfig(hash);
            if (decoded && decoded.version === 1) {
              setConfig(decoded);
              setTheme(resolveThemeKey(decoded.theme));
              navigateTo("preview");
            }
          }}
        />;
      case "player_entry":
        return <PlayerEntry onLoadPuzzle={(decoded) => {
          setConfig(decoded);
          setTheme(resolveThemeKey(decoded.theme));
          navigateTo("preview");
        }} />;
      case "preview":
        if (!config) { setScreen("theme"); setScreenHistory([]); return null; }
        // Unsealed puzzles (no secret word) go straight to the first clue.
        return <PuzzlePreview
          config={config}
          onContinue={() => navigateTo(isSealed(config) ? "password" : "play")}
          // Opened from Play or My Puzzles: a way back, now that there is no nav.
          // Opened from a link: nothing to go back to.
          onBack={screenHistory.length > 0 ? goBack : undefined}
        />;
      case "password":
        if (!config) { setScreen("theme"); setScreenHistory([]); return null; }
        // A gate on an unsealed puzzle could never be passed (there is no word to
        // match), so never render it — e.g. if history or a stale state lands here.
        if (!isSealed(config)) { setScreen("play"); return null; }
        return <PasswordGate config={config} onUnlock={() => navigateTo("play")} />;
      case "play":
        if (!config || !config.levels || config.levels.length === 0) { setScreen("theme"); setScreenHistory([]); return null; }
        return <LevelPlay config={config} onFinale={() => {
          // #126 — arrivato in fondo: chi ha creato il puzzle riceve la notifica
          // (functions/push.js). Non quando a giocarlo è il creatore stesso.
          if (config._fromDb && config._id && !(config.ownerUid && config.ownerUid === user?.uid)) {
            updatePlayerProgress(config._id, config.levels.length, true);
          }
          navigateTo("finale");
        }} />;
      case "finale":
        if (!config) { setScreen("theme"); setScreenHistory([]); return null; }
        return <Finale config={config} onCreateOwn={() => {
          logger.info("Player converting to creator", { fromPuzzleId: config._id || "legacy" });
          // Sul web si crea nell'app: la pagina degli store (#74).
          if (soloLettore) { navigate("/"); return; }
          // Keep config + screenHistory so the player can go BACK to the revealed
          // message (← Back on ThemeSelect → finale). navigateTo pushes "finale"
          // onto the stack; the creator owns its own state and ignores `config`.
          setCreatorKey((k) => k + 1);
          navigateTo("theme");
        }} />;
      default:
        return <ThemeSelect
          key={creatorKey}
          profile={effectiveProfile}
          onLocked={() => withSignInGate(() => { setPaywallTrigger("theme-lock"); setShowPaywall(true); })}
          onSelect={(t, name) => { setTheme(t); setPendingTitle(name || ""); navigateTo("creator"); }}
        />;
    }
  };

  // In-app logo: shown on builder/library screens, hidden on immersive play and
  // on legal routes (hideBottomNav). Tap → marketing landing ("/").
  // Logo is always visible inside the app (only suppressed on standalone legal
  // routes, which set hideBottomNav). Previously hidden on play/finale/etc.;
  // the brand mark now persists as a stable header anchor across every screen.
  // While the redesigned creator is up it owns the whole screen: no app header,
  // no bottom nav, no page padding. Modals still sit above it.
  const creatorV2Active = creatorV2 && screen === "creator";
  // Opened from a tab there is no in-app history, so no browser entry either:
  // arm one, or the first device Back would leave /app before the creator sees it.
  useEffect(() => {
    if (!creatorV2Active || screenHistoryRef.current.length > 0) return;
    try { window.history.pushState({ puzzlyNav: true }, ""); } catch { /* ignore */ }
  }, [creatorV2Active]);
  const showAppLogo = !hideBottomNav && !creatorV2Active;
  // No bottom nav on a recipient screen (`recipientScreen`, above): the preview,
  // the secret word, the clues or the reveal. The logo stays; the finale offers
  // "make your own" as the way into the app.

  const slideStyle = slideDirection === "in"
    ? { animation: "slideInRight 0.4s cubic-bezier(0.4,0,0.2,1) forwards" }
    : slideDirection === "out"
    ? { animation: "slideOutRight 0.3s cubic-bezier(0.4,0,0.2,1) forwards" }
    : {};

  return (
    <div
      data-season={season || "none"}
      style={{
        ...themeVars,
        // The season's chassis palette cascades to Home and to the creator before
        // a theme is picked (CreatorShell overrides it with the picked one).
        ...paletteVars(seasonLook),
        background: activeTheme.colors.bg,
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden",
      }}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <AmbientGlow theme={chromeTheme} />
        <Particles theme={chromeTheme} />
        {showAppLogo && (
          <header
            data-testid="app-header"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1200,
              boxSizing: "border-box",
              // Opaque, full-width band that also fills the safe-area inset so
              // NOTHING shows above it; scrolling content tucks cleanly under.
              height: "calc(env(safe-area-inset-top, 0px) + 56px)",
              paddingTop: "env(safe-area-inset-top, 0px)",
              paddingLeft: 16,
              paddingRight: 16,
              display: "flex",
              alignItems: "center",
              background: "var(--color-surface)",
              boxShadow: "0 6px 22px -12px rgba(0,0,0,0.28)",
            }}
          >
            <button
              type="button"
              aria-label={tr("app.nav.ariaHome")}
              data-testid="app-logo-home"
              onClick={() => navigate("/")}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                padding: 0,
              }}
            >
              <span
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  background:
                    "linear-gradient(135deg, var(--color-primaryBright), var(--color-primary))",
                  display: "grid",
                  placeItems: "center",
                  color: "#fff",
                  fontFamily: "var(--font-display)",
                  fontWeight: 800,
                  fontSize: 15,
                  transform: "rotate(-6deg)",
                  boxShadow: "0 6px 16px -6px var(--color-primary)",
                }}
              >
                P
              </span>
              <span
                style={{
                  fontFamily: "var(--font-display)",
                  fontWeight: 800,
                  fontSize: 19,
                  letterSpacing: "-0.02em",
                  color: "var(--color-text)",
                }}
              >
                Puzzly
              </span>
            </button>
          </header>
        )}
        <div style={{ position: "relative", zIndex: 1,
          // Dove c'è la barra in basso, lo spazio sotto il contenuto deve superarla:
          // è flottante, RICOMPARE quando si arriva in fondo, sta a `14px + area
          // sicura` da terra ed è alta ~74px — con meno, l'ultimo elemento finisce
          // coperto (#41). Il creator nuovo e la schermata di chi riceve la barra
          // non ce l'hanno, e lì lo spazio sarebbe solo un vuoto.
          paddingBottom: soloLettore || creatorV2Active || recipientScreen ? 0 : "calc(110px + env(safe-area-inset-bottom, 0px))",
          paddingTop: showAppLogo ? "calc(env(safe-area-inset-top, 0px) + 70px)" : undefined, ...slideStyle }}>
          {renderScreen()}
        </div>
        {/* W3.T5 / W3.T6 — sign-up prompt modal (mounted at App root, sibling
            to renderScreen output, never inside a wizard body — see PATTERNS
            §Gotchas #1) */}
        {/* #65 — segnala o blocca, solo su chi riceve un puzzle salvato (i link
            vecchi compressi nell'URL non hanno un id da segnalare). */}
        {SCHERMATE_DI_CHI_RICEVE.includes(screen) && config && config._id && !isBlocked(effectiveProfile, config.ownerUid) && (
          <button
            type="button"
            aria-label={tr("ui.moderation.reportAria")}
            onClick={() => setReportOpen(true)}
            data-testid="report-open"
            style={{
              // Nell'angolo destro della testata col logo (alta 56 + area sicura,
              // zIndex 1200), centrato in altezza; senza testata, in alto a destra.
              position: "fixed", right: 16,
              top: showAppLogo ? "calc(env(safe-area-inset-top, 0px) + 6px)" : "calc(env(safe-area-inset-top, 0px) + 16px)",
              zIndex: 1201,
              width: 44, height: 44, borderRadius: 999, border: "none", cursor: "pointer",
              background: "rgba(255,255,255,0.85)", boxShadow: "0 4px 14px rgba(0,0,0,0.12)",
              display: "flex", alignItems: "center", justifyContent: "center", color: "var(--color-textMuted)",
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 22V4" /><path d="M4 4h12l-2 4 2 4H4" />
            </svg>
          </button>
        )}
        {/* Solo sulla Home: un puzzle aperto da un link non resta coperto dalla guida. */}
        {!introVista && screen === "theme" && <WelcomeIntro onDone={chiudiIntro} />}
        <ReportSheet
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          onReport={({ reason, note }) => reportPuzzle({ puzzleId: config._id, reason, note })}
          onBlock={config && config.ownerUid && config.ownerUid !== (user && user.uid)
            ? async () => { await blockOwner(config.ownerUid); setReportOpen(false); }
            : null}
        />
        <ContentRulesModal
          open={!!richiestaRegole}
          onAccept={() => { richiestaRegole.risolvi(true); setRichiestaRegole(null); }}
          onDecline={() => { richiestaRegole.risolvi(false); setRichiestaRegole(null); }}
        />
        <PushOfferModal open={showPushOffer} onAccept={acceptPushOffer} onClose={closePushOffer} />
        <PushBanner
          notice={pushNotice}
          onOpen={() => { const n = pushNotice; setPushNotice(null); if (n && pushOpenRef.current) pushOpenRef.current(n.target); }}
          onDismiss={closePushNotice}
        />
        <ReactionViewer
          videoUrl={reactionOpen ? reactionOpen.url : null}
          title={reactionOpen ? reactionOpen.title : ""}
          onClose={() => setReactionOpen(null)}
        />
        <SignUpPrompt
          open={showSignUpPrompt}
          onClose={() => setShowSignUpPrompt(false)}
          onSuccess={() => setShowSignUpPrompt(false)}
          microcopy={activeMicrocopy}
          trigger={signUpTrigger}
          dark={isDarkTheme(chromeTheme)}
        />
        <EmailReentryModal
          open={!!magicReentryUrl}
          url={magicReentryUrl}
          onClose={() => setMagicReentryUrl(null)}
          onSuccess={() => setMagicReentryUrl(null)}
        />
        {/* Account sheet — moved out of the floating top-right avatar into the
            footer Profile tab. Footer variant renders no trigger of its own; it
            is opened by the BottomNav Profile button (handleProfileTab) and
            anchored above the nav. Anon users sign in directly from the tab, so
            this only renders its sheet for permanent users. Sign-out still
            gated by the confirm modal owned by AccountMenu. */}
        {!soloLettore && !hideBottomNav && !creatorV2Active && !recipientScreen && (
          <AccountMenu
            variant="footer"
            open={accountOpen}
            onOpenChange={setAccountOpen}
            user={user}
            profile={profile}
            onSignOut={handleSignOut}
            onUpgrade={() => { setAccountOpen(false); setPaywallTrigger("account-menu"); setShowPaywall(true); }}
            onManageSubscription={(store) => { setAccountOpen(false); getBilling().openManageSubscription(store); }}
            onDeleteAccount={() => { setAccountOpen(false); setShowDeleteAccount(true); }}
            onLegal={(pagina) => { setAccountOpen(false); navigateTo(pagina); }}
            onRecommend={() => { setAccountOpen(false); consigliaPuzzly(tr); }}
            onRate={() => { setAccountOpen(false); apriSchedaStore(); }}
          />
        )}
        {/* Anon Profile-tap sign-in modal — explainer + Google CTA shown
            BEFORE the popup launches (handleProfileTab no longer auto-popups). */}
        <SignInModal
          open={showSignInModal}
          busy={signingIn}
          error={signInError}
          onResolveAppleConflict={appleConflict ? () => handleSignInFromModal(signInWithGoogleThenLinkApple) : null}
          onClose={() => { setShowSignInModal(false); setPendingProceed(null); setSignInError(""); setAppleConflict(false); }}
          onSignIn={() => handleSignInFromModal(signInWithGoogle)}
          onAppleSignIn={appleSignInAvailable() ? () => handleSignInFromModal(signInWithApple) : null}
          dark={isDarkTheme(chromeTheme)}
          onLegal={(pagina) => { setShowSignInModal(false); setPendingProceed(null); setSignInError(""); setAppleConflict(false); navigateTo(pagina); }}
          onRecommend={() => consigliaPuzzly(tr)}
        />
        {/* Phase 3 Wave 4 — DeleteAccountModal: two-step typed-DELETE confirm
            before the GDPR erasure cascade fires (T-03-19 repudiation mitigation).
            zIndex 340 — above AccountMenu dropdown (200) and CapBlockerModal (320). */}
        <DeleteAccountModal
          open={showDeleteAccount}
          onClose={() => setShowDeleteAccount(false)}
          onConfirmDelete={handleDeleteAccount}
        />
        {/* Paywall + cap-blocker, mounted at the root (sibling to renderScreen).
            The paywall passes the chosen plan; the purchase goes to the store
            (#74: no web checkout, no withdrawal waiver — the stores handle it). */}
        <PaywallModal
          open={showPaywall}
          onClose={() => { setShowPaywall(false); setPurchaseError(""); }}
          errore={purchaseError}
          onUpgrade={(plan) => {
            setShowPaywall(false);
            setPurchaseError("");
            // Un acquisto rifiutato dallo store prima spariva senza traccia (21/10):
            // va nei log con i codici di RevenueCat, e il paywall si riapre con l'errore.
            withSignInGate(() => acquista(plan || "yearly").catch((err) => {
              const e = err || {};
              logger.error("acquista: acquisto non riuscito", {
                error: [e.message, e.underlyingErrorMessage].filter(Boolean).join(" | "),
                code: [e.code, e.readableErrorCode].filter((x) => x !== undefined && x !== null && x !== "").join(" "),
              });
              setPurchaseError(e.message || "");
              setShowPaywall(true);
            }));
          }}
          onRestore={ripristina}
          trigger={paywallTrigger}
        />
        <ProAttivazioneModal stato={attivazionePro} onClose={() => setAttivazionePro(null)} />
        <CapBlockerModal
          open={showCapBlocker}
          onClose={() => setShowCapBlocker(false)}
          onGoToHistory={() => { setShowCapBlocker(false); navigateTo("history"); }}
          onUpgrade={() => {
            setShowCapBlocker(false);
            setShowPaywall(true);
          }}
          profile={effectiveProfile}
          trigger={paywallTrigger}
        />
        {/* Bottom Navigation Bar — hidden on marketing routes (Pitfall #4 / D-30).
            When AppShell mounts at /privacy via Routes, hideBottomNav=true so the
            footer-style legal page reads cleanly without the in-app nav bar. */}
        {!soloLettore && !hideBottomNav && !creatorV2Active && !recipientScreen && (
          <BottomNav
            screen={screen}
            onNavigate={handleMenuNavigate}
            theme={chromeTheme}
            onProfile={handleProfileTab}
            accountOpen={accountOpen}
          />
        )}
        <style>{`
          @keyframes slideInRight {
            from { transform: translateX(100%); opacity: 0; }
            to { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideOutRight {
            from { transform: translateX(0); opacity: 1; }
            to { transform: translateX(100%); opacity: 0; }
          }
        `}</style>
    </div>
  );
}

// ── Phase 4 W1.T2 — HashRedirect (D-07 + Pitfall #2 + A9) ───────────────────
//
// Legacy v1.0 share URLs encoded the puzzle inside a fragment (e.g.
//   puzzly.giadalabs.com/#LkddobhWUGA5         (12-char Firestore ID, post-LZ era)
//   puzzly.giadalabs.com/#N4IgZg9hCmCmA2BLA... (200+ char LZ-string, pre-Firestore)
// ). After the Phase 4 routing migration, the puzzle reader lives at /app, so
// any non-empty hash arriving at `/` must be forwarded to `/app#<hash>` BEFORE
// the store page renders. <Navigate replace> avoids polluting the browser
// history with a page the user never actually saw.
// Anchors of the old marketing landing: links to them still circulate, and
// they are not puzzle ids — they stay on the store page instead of ending up
// as "puzzle not available".
const HOME_SECTION_ANCHORS = new Set(["how", "themes", "pricing", "faq", "cookies"]);

function HashRedirect() {
  const { pathname, hash } = useLocation();
  if (pathname === "/" && hash && hash.length > 1) {
    const id = hash.slice(1);
    // Let same-page section links scroll instead of redirecting to /app.
    if (HOME_SECTION_ANCHORS.has(id)) return null;
    return <Navigate to={`/app${hash}`} replace />;
  }
  return null;
}

// ── Phase 4 W1.T2 — top-level App() wrapping AppShell in <Routes> ───────────
//
// - `/`            → AppShell in the app, StoreLanding on the web (#74)
// - `/app/*`       → AppShell (on the web: only the received puzzle)
// - `/privacy`, `/terms`, `/cookies` → AppShell with initialScreen, no BottomNav
// - `*` catch-all  → redirect to /
//
// HashRedirect runs first so legacy `/#hash` short URLs are forwarded before
// any Route matches. ErrorBoundary lives at this top level so a render crash
// inside StoreLanding OR AppShell triggers the same friendly fallback.
export default function App() {
  return (
    <ErrorBoundary>
      <HashRedirect />
      <Routes>
        {/* Nell'app la radice è il prodotto; sul web è la pagina degli store,
            perché Puzzly si usa dall'app (#74). */}
        <Route path="/" element={isNativeApp() ? <AppShell /> : <StoreLanding />} />
        <Route path="/app/*" element={<AppShell />} />
        <Route path="/privacy" element={<AppShell initialScreen="privacy" hideBottomNav />} />
        <Route path="/terms" element={<AppShell initialScreen="terms" hideBottomNav />} />
        <Route path="/cookies" element={<AppShell initialScreen="cookies" hideBottomNav />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ErrorBoundary>
  );
}
